import * as i0 from '@angular/core';
import { Injectable, Component, Input, NgModule, InjectionToken, Inject, Pipe, EventEmitter, HostBinding, Output, ViewChild, Directive, forwardRef, HostListener, ContentChildren, TemplateRef, ContentChild } from '@angular/core';
import { BehaviorSubject, Subject, timer } from 'rxjs';
import { take, takeUntil, filter, map, delay, debounceTime, distinctUntilChanged, finalize } from 'rxjs/operators';
import * as i1 from '@angular/common';
import { CommonModule, DOCUMENT } from '@angular/common';
import * as i2 from '@angular/forms';
import { FormsModule, NgControl, NG_VALUE_ACCESSOR, Validators, NG_VALIDATORS, ReactiveFormsModule } from '@angular/forms';
import * as i7 from 'ngx-bootstrap/dropdown';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import * as i8 from 'ngx-bootstrap/typeahead';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import * as i1$1 from '@angular/router';
import { PRIMARY_OUTLET, NavigationEnd, RouterModule, NavigationStart } from '@angular/router';
import * as i2$1 from 'ngx-bootstrap/collapse';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import * as i1$2 from 'ngx-bootstrap/modal';
import { ModalModule } from 'ngx-bootstrap/modal';
import moment from 'moment';
import * as i2$2 from 'ngx-bootstrap/datepicker';
import { BsDatepickerDirective, BsDatepickerConfig, BsDatepickerModule, BsDatepickerInlineConfig } from 'ngx-bootstrap/datepicker';
import * as i7$1 from 'ngx-bootstrap/tooltip';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { defineLocale } from 'ngx-bootstrap/chronos';
import { ptBrLocale } from 'ngx-bootstrap/locale';
import { Base64 } from 'js-base64';
import * as i1$3 from '@angular/common/http';
import * as _ from 'lodash';
import * as i2$3 from 'ngx-bootstrap/popover';
import { PopoverModule } from 'ngx-bootstrap/popover';
import * as i5 from '@ng-select/ng-select';
import { NgSelectModule } from '@ng-select/ng-select';
import * as i4 from '@swimlane/ngx-datatable';
import { deepValueGetter, SelectionType, ColumnMode, NgxDatatableModule } from '@swimlane/ngx-datatable';
import * as FileSaver from 'file-saver';

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class AlertMessageService {
    constructor() {
        this.messages = [];
        this.messagesSource = new BehaviorSubject(this.messages);
        this.messages$ = this.messagesSource.asObservable();
        this.showMoreSource = new Subject();
        this.showMore$ = this.showMoreSource.asObservable();
    }
    getMessages$() {
        return this.messages$;
    }
    info(title, description, details, options, target) {
        return this.push('INFO', title, description, details, options, target);
    }
    warn(title, description, details, options, target) {
        return this.push('WARN', title, description, details, options, target);
    }
    error(title, description, details, options, target) {
        return this.push('ERROR', title, description, details, options, target);
    }
    dismiss(message) {
        this.messages = this.messages.filter((alertMessage) => {
            return alertMessage !== message;
        });
        this.messagesSource.next(this.messages);
    }
    dismissAll() {
        this.messages.splice(0, this.messages.length);
        this.messagesSource.next(this.messages);
    }
    showMore(message) {
        this.showMoreSource.next(message);
    }
    onShowMore() {
        return this.showMore$;
    }
    push(type, title, description, details, options, target) {
        let message = this.messages.find(msg => msg.type === type
            && msg.title === title
            && msg.description === description
            && msg.target === target);
        if (!message) {
            message = { type, title, description, details, count: 1, target,
                options: options || { dismissible: true, delay: 0, hasMore: false }
            };
            this.messages.push(message);
        }
        else {
            message.count++;
            if (details) {
                if (!message.details) {
                    message.details = [];
                }
                message.details.push(...details);
            }
        }
        this.messagesSource.next(this.messages);
        if (this.isAutoDismissible(message)) {
            timer(message.options.delay)
                .pipe(take(1))
                .subscribe(() => this.dismiss(message));
        }
        return message;
    }
    isAutoDismissible(message) {
        return message.options && message.options.delay > 0;
    }
}
AlertMessageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: AlertMessageService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
AlertMessageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: AlertMessageService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: AlertMessageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class WindowService {
    get() {
        return window;
    }
}
WindowService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: WindowService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
WindowService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: WindowService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: WindowService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class AlertMessagesComponent {
    constructor(alertMessageService, windowService) {
        this.alertMessageService = alertMessageService;
        this.windowService = windowService;
        this.messages = [];
        this.ngUnsubscribe = new Subject();
    }
    ngOnInit() {
        this.alertMessageService.getMessages$()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((messages) => {
            this.messages = messages.filter(message => this.onlyFor === message.target);
            if (this.messages && this.messages.length) {
                this.window.scrollTo(0, 0);
            }
        });
    }
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
    dismiss(message) {
        this.alertMessageService.dismiss(message);
    }
    showMore(message) {
        this.alertMessageService.showMore(message);
    }
    get window() {
        return this.windowService.get();
    }
}
AlertMessagesComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: AlertMessagesComponent, deps: [{ token: AlertMessageService }, { token: WindowService }], target: i0.ɵɵFactoryTarget.Component });
AlertMessagesComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: AlertMessagesComponent, selector: "suite-rfb-alert-messages", inputs: { onlyFor: "onlyFor" }, ngImport: i0, template: "<div class=\"alert\" [ngClass]=\"{'alert-success': message.type === 'INFO', 'alert-warning': message.type === 'WARN', 'alert-danger': message.type === 'ERROR'}\" role=\"alert\" *ngFor=\"let message of messages\">\n  <div class=\"alert-icone\">\n    <span *ngIf=\"message.type === 'INFO'\" class=\"material-icons icon-48\">&#xE86C;</span>\n    <span *ngIf=\"message.type === 'WARN'\" class=\"material-icons icon-48\">&#xE002;</span>\n    <span *ngIf=\"message.type === 'ERROR'\" class=\"material-icons icon-48\">&#xE000;</span>\n  </div>\n  <div class=\"alert-mensagem\">\n    <div class=\"title\">{{message.title}}</div>\n    <div class=\"description\" *ngIf=\"message.description\">{{message.description}}</div>\n    <ul class=\"details\" *ngIf=\"message.details && message.details.length > 0\">\n      <li class=\"detail\" *ngFor=\"let detail of message.details\">{{detail}}</li>\n    </ul>\n    <div class=\"more\" *ngIf=\"message.options && message.options.hasMore\">\n      <button type=\"button\" class=\"btn btn-primary show-more\" (click)=\"showMore(message)\">Ver mais</button>\n    </div>\n  </div>\n  <div class=\"close\" *ngIf=\"message.options && message.options.dismissible\">\n    <span class=\"close-icon material-icons icon-24\" (click)=\"dismiss(message)\">&#xE5CD;</span>\n  </div>\n</div>\n", styles: [".alert{display:flex;flex-direction:row;align-items:stretch}.alert-mensagem{flex:1}.close{cursor:pointer;float:right}.title{font-weight:700}.show-more{margin:5px 0}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: AlertMessagesComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-alert-messages', template: "<div class=\"alert\" [ngClass]=\"{'alert-success': message.type === 'INFO', 'alert-warning': message.type === 'WARN', 'alert-danger': message.type === 'ERROR'}\" role=\"alert\" *ngFor=\"let message of messages\">\n  <div class=\"alert-icone\">\n    <span *ngIf=\"message.type === 'INFO'\" class=\"material-icons icon-48\">&#xE86C;</span>\n    <span *ngIf=\"message.type === 'WARN'\" class=\"material-icons icon-48\">&#xE002;</span>\n    <span *ngIf=\"message.type === 'ERROR'\" class=\"material-icons icon-48\">&#xE000;</span>\n  </div>\n  <div class=\"alert-mensagem\">\n    <div class=\"title\">{{message.title}}</div>\n    <div class=\"description\" *ngIf=\"message.description\">{{message.description}}</div>\n    <ul class=\"details\" *ngIf=\"message.details && message.details.length > 0\">\n      <li class=\"detail\" *ngFor=\"let detail of message.details\">{{detail}}</li>\n    </ul>\n    <div class=\"more\" *ngIf=\"message.options && message.options.hasMore\">\n      <button type=\"button\" class=\"btn btn-primary show-more\" (click)=\"showMore(message)\">Ver mais</button>\n    </div>\n  </div>\n  <div class=\"close\" *ngIf=\"message.options && message.options.dismissible\">\n    <span class=\"close-icon material-icons icon-24\" (click)=\"dismiss(message)\">&#xE5CD;</span>\n  </div>\n</div>\n", styles: [".alert{display:flex;flex-direction:row;align-items:stretch}.alert-mensagem{flex:1}.close{cursor:pointer;float:right}.title{font-weight:700}.show-more{margin:5px 0}\n"] }]
        }], ctorParameters: function () { return [{ type: AlertMessageService }, { type: WindowService }]; }, propDecorators: { onlyFor: [{
                type: Input
            }] } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbAlertMessagesModule {
}
SuiteRfbAlertMessagesModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbAlertMessagesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbAlertMessagesModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbAlertMessagesModule, declarations: [AlertMessagesComponent], imports: [CommonModule], exports: [AlertMessagesComponent] });
SuiteRfbAlertMessagesModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbAlertMessagesModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbAlertMessagesModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                    ],
                    declarations: [
                        AlertMessagesComponent,
                    ],
                    exports: [
                        AlertMessagesComponent,
                    ],
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class ApplicationsMenuService {
    constructor() {
        this.applications = [];
        this.applicationsSource = new BehaviorSubject(this.applications);
        this.applications$ = this.applicationsSource.asObservable();
    }
    set(applications) {
        this.applications = applications;
        this.applicationsSource.next(this.applications);
    }
    get() {
        return this.applications$;
    }
}
ApplicationsMenuService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: ApplicationsMenuService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
ApplicationsMenuService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: ApplicationsMenuService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: ApplicationsMenuService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
const SUITE_RFB_MODULE_CONFIG = new InjectionToken('ngx-suite-rfb.config');

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class ConfigService {
    constructor(config) {
        this.config = config;
    }
    isShowLoadingNavigationForUrl(url) {
        if (this.config?.showLoadingNavigationForUrlFn) {
            return this.config.showLoadingNavigationForUrlFn(url);
        }
        return true;
    }
    isApplicationsMenuHidden() {
        return this.config && this.config.applicationsMenuHidden;
    }
    isApplicationsMenuDisabled() {
        return this.config && this.config.applicationsMenuDisabled;
    }
    isCollapsedMenu() {
        return this.config && this.config.collapsedMenu;
    }
    isExpandedMenuHierarchical() {
        return this.config && this.config.expandedMenuHierarchical;
    }
    isContextualHelpHidden() {
        return this.config && this.config.contextualHelpHidden;
    }
    isContextualHelpDisabled() {
        return this.config && this.config.contextualHelpDisabled;
    }
    getEnvironmentId() {
        return this.config ? this.config.environmentId : undefined;
    }
    isIgnoreLoadingBackdropClick() {
        return this.config && this.config.ignoreLoadingBackdropClick;
    }
    isInputMaskInvalidAllowed() {
        return this.config && this.config.inputMaskInvalidAllowed;
    }
    isLogoutHidden() {
        return this.config && this.config.logoutHidden;
    }
    isLogoutDisabled() {
        return this.config && this.config.logoutDisabled;
    }
    isMenuOptionsHidden() {
        return this.config && this.config.menuOptionsHidden;
    }
    isMenuOptionsDisabled() {
        return this.config && this.config.menuOptionsDisabled;
    }
    isNotificationsHidden() {
        return this.config && this.config.notificationsHidden;
    }
    isNotificationsDisabled() {
        return this.config && this.config.notificationsDisabled;
    }
    isNotificationsShowOverflowedText() {
        return this.config && this.config.notificationsShowOverflowedText;
    }
    isShowLoadingOnNavigation() {
        return this.config && this.config.showLoadingOnNavigation;
    }
    isShowUserProfiles() {
        return this.config && this.config.showUserProfiles;
    }
    isUserConfigurationsHidden() {
        return this.config && this.config.userConfigurationsHidden;
    }
    isUserConfigurationsDisabled() {
        return this.config && this.config.userConfigurationsDisabled;
    }
    getIgnoredElementClassesInMenuShortcuts() {
        return this.config && this.config.ignoredElementClassesInMenuShortcuts;
    }
}
ConfigService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: ConfigService, deps: [{ token: SUITE_RFB_MODULE_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
ConfigService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: ConfigService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: ConfigService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [SUITE_RFB_MODULE_CONFIG]
                }] }]; } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class LocationService {
    get() {
        return location;
    }
}
LocationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: LocationService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
LocationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: LocationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: LocationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class FavoriteApplicationsPipe {
    transform(applications) {
        return (!applications) ? undefined : applications.filter(application => application.favorite === true);
    }
}
FavoriteApplicationsPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: FavoriteApplicationsPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
FavoriteApplicationsPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: FavoriteApplicationsPipe, name: "favoriteApplications" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: FavoriteApplicationsPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'favoriteApplications'
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class ApplicationsMenuComponent {
    constructor(applicationsMenuService, configService, document, windowService, locationService) {
        this.applicationsMenuService = applicationsMenuService;
        this.configService = configService;
        this.document = document;
        this.windowService = windowService;
        this.locationService = locationService;
        this.buttonClass = 'btn btn-default dropdown-toggle';
        this.suiteRfbUrl = 'https://www.suiterfb.receita.fazenda/';
        this.applications = [];
        this.status = { isopen: false };
        this.ngUnsubscribe = new Subject();
    }
    ngOnInit() {
        this.hidden = this.configService.isApplicationsMenuHidden();
        this.disabled = this.configService.isApplicationsMenuDisabled();
        this.applicationsMenuService.get()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((applications) => this.applications = applications);
        this._handleOutsideClick();
    }
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
    isOpenChange(isopen) {
        this.status.isopen = isopen;
    }
    gotoApplication(application) {
        if (application && application.url) {
            this.location.assign(application.url);
        }
    }
    _handleOutsideClick() {
        this.window.addEventListener('click', (event) => {
            if (this.status.isopen) {
                const target = event.target;
                const menuAplicacoes = this.document.getElementById('suite-rfb-applications-menu-container');
                const outsideClick = !menuAplicacoes.contains(target);
                if (outsideClick) {
                    this.status.isopen = false;
                }
            }
        });
    }
    get window() {
        return this.windowService.get();
    }
    get location() {
        return this.locationService.get();
    }
}
ApplicationsMenuComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: ApplicationsMenuComponent, deps: [{ token: ApplicationsMenuService }, { token: ConfigService }, { token: DOCUMENT }, { token: WindowService }, { token: LocationService }], target: i0.ɵɵFactoryTarget.Component });
ApplicationsMenuComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: ApplicationsMenuComponent, selector: "suite-rfb-applications-menu", inputs: { buttonClass: "buttonClass", suiteRfbUrl: "suiteRfbUrl" }, ngImport: i0, template: "<div id=\"suite-rfb-applications-menu-container\" class=\"dropdown\" dropdown [isOpen]=\"status.isopen\" (isOpenChange)=\"isOpenChange($event)\"\n  [autoClose]=\"false\" *ngIf=\"!hidden\" [isDisabled]=\"disabled\">\n  <button type=\"button\" [class]=\"buttonClass\" data-toggle=\"dropdown\" aria-haspopup=\"true\" aria-expanded=\"true\" dropdownToggle>\n    <span class=\"material-icons icon-24\">&#xE5C3;</span>\n  </button>\n  <ul *dropdownMenu class=\"dropdown-menu aplicacoes\" role=\"menu\">\n    <li role=\"menuitem\">\n      <div class=\"home\">\n        <a [href]=\"suiteRfbUrl\">Su\u00EDte de Aplicativos</a>\n      </div>\n      <hr>\n      <div class=\"busca\">\n        <label for=\"busca\" class=\"sr-only\">Buscar aplica\u00E7\u00E3o</label>\n        <input type=\"search\" placeholder=\"Busca r\u00E1pida\" id=\"busca\" class=\"form-control\" [(ngModel)]=\"applicationName\" [typeahead]=\"applications\"\n          typeaheadOptionField=\"name\" [typeaheadItemTemplate]=\"applicationTemplate\" (typeaheadOnSelect)=\"gotoApplication($event.item)\">\n        <ng-template #applicationTemplate let-application=\"item\">\n          <h5>{{application.name}}</h5>\n        </ng-template>\n      </div>\n      <div class=\"favoritos\" *ngIf=\"applications|favoriteApplications as favorites\">\n        <ng-container *ngIf=\"favorites.length\">\n          <div class=\"title\">Favoritos</div>\n          <ul>\n            <li class=\"favorite\" *ngFor=\"let favorite of favorites\">\n              <a [href]=\"favorite.url\">{{favorite.name}}</a>\n            </li>\n          </ul>\n        </ng-container>\n      </div>\n    </li>\n  </ul>\n</div>\n", styles: [".dropdown-menu.aplicacoes{background-color:#bfc7d6;padding:3px 20px;border:1px solid #7f8fad;min-width:300px}.dropdown-menu.aplicacoes .home,.dropdown-menu.aplicacoes .busca{margin-top:10px;margin-bottom:10px}.dropdown-menu.aplicacoes .busca .go,.dropdown-menu.aplicacoes a{color:#00205b}.dropdown-menu.aplicacoes hr{margin-top:5px;margin-bottom:5px;border-color:#00205b}.dropdown-menu.aplicacoes .favoritos .title{background-color:#00205b;color:#fff;padding:10px}.dropdown-menu.aplicacoes .favoritos ul{padding-left:0}.dropdown-menu.aplicacoes .favoritos ul li{list-style:none;padding:10px}.dropdown-menu.aplicacoes .favoritos ul li:hover{background-color:#c0cee9}.dropdown-menu.aplicacoes .favoritos ul li:not(:last-child){border-bottom:1px solid #00205b}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "directive", type: i7.BsDropdownMenuDirective, selector: "[bsDropdownMenu],[dropdownMenu]", exportAs: ["bs-dropdown-menu"] }, { kind: "directive", type: i7.BsDropdownToggleDirective, selector: "[bsDropdownToggle],[dropdownToggle]", exportAs: ["bs-dropdown-toggle"] }, { kind: "directive", type: i7.BsDropdownDirective, selector: "[bsDropdown], [dropdown]", inputs: ["placement", "triggers", "container", "dropup", "autoClose", "isAnimated", "insideClick", "isDisabled", "isOpen"], outputs: ["isOpenChange", "onShown", "onHidden"], exportAs: ["bs-dropdown"] }, { kind: "directive", type: i8.TypeaheadDirective, selector: "[typeahead]", inputs: ["typeahead", "typeaheadMinLength", "adaptivePosition", "isAnimated", "typeaheadWaitMs", "typeaheadOptionsLimit", "typeaheadOptionField", "typeaheadGroupField", "typeaheadOrderBy", "typeaheadAsync", "typeaheadLatinize", "typeaheadSingleWords", "typeaheadWordDelimiters", "typeaheadMultipleSearch", "typeaheadMultipleSearchDelimiters", "typeaheadPhraseDelimiters", "typeaheadItemTemplate", "optionsListTemplate", "typeaheadScrollable", "typeaheadOptionsInScrollableView", "typeaheadHideResultsOnBlur", "typeaheadSelectFirstItem", "typeaheadIsFirstItemActive", "container", "dropup"], outputs: ["typeaheadLoading", "typeaheadNoResults", "typeaheadOnSelect", "typeaheadOnPreview", "typeaheadOnBlur"], exportAs: ["bs-typeahead"] }, { kind: "pipe", type: FavoriteApplicationsPipe, name: "favoriteApplications" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: ApplicationsMenuComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-applications-menu', template: "<div id=\"suite-rfb-applications-menu-container\" class=\"dropdown\" dropdown [isOpen]=\"status.isopen\" (isOpenChange)=\"isOpenChange($event)\"\n  [autoClose]=\"false\" *ngIf=\"!hidden\" [isDisabled]=\"disabled\">\n  <button type=\"button\" [class]=\"buttonClass\" data-toggle=\"dropdown\" aria-haspopup=\"true\" aria-expanded=\"true\" dropdownToggle>\n    <span class=\"material-icons icon-24\">&#xE5C3;</span>\n  </button>\n  <ul *dropdownMenu class=\"dropdown-menu aplicacoes\" role=\"menu\">\n    <li role=\"menuitem\">\n      <div class=\"home\">\n        <a [href]=\"suiteRfbUrl\">Su\u00EDte de Aplicativos</a>\n      </div>\n      <hr>\n      <div class=\"busca\">\n        <label for=\"busca\" class=\"sr-only\">Buscar aplica\u00E7\u00E3o</label>\n        <input type=\"search\" placeholder=\"Busca r\u00E1pida\" id=\"busca\" class=\"form-control\" [(ngModel)]=\"applicationName\" [typeahead]=\"applications\"\n          typeaheadOptionField=\"name\" [typeaheadItemTemplate]=\"applicationTemplate\" (typeaheadOnSelect)=\"gotoApplication($event.item)\">\n        <ng-template #applicationTemplate let-application=\"item\">\n          <h5>{{application.name}}</h5>\n        </ng-template>\n      </div>\n      <div class=\"favoritos\" *ngIf=\"applications|favoriteApplications as favorites\">\n        <ng-container *ngIf=\"favorites.length\">\n          <div class=\"title\">Favoritos</div>\n          <ul>\n            <li class=\"favorite\" *ngFor=\"let favorite of favorites\">\n              <a [href]=\"favorite.url\">{{favorite.name}}</a>\n            </li>\n          </ul>\n        </ng-container>\n      </div>\n    </li>\n  </ul>\n</div>\n", styles: [".dropdown-menu.aplicacoes{background-color:#bfc7d6;padding:3px 20px;border:1px solid #7f8fad;min-width:300px}.dropdown-menu.aplicacoes .home,.dropdown-menu.aplicacoes .busca{margin-top:10px;margin-bottom:10px}.dropdown-menu.aplicacoes .busca .go,.dropdown-menu.aplicacoes a{color:#00205b}.dropdown-menu.aplicacoes hr{margin-top:5px;margin-bottom:5px;border-color:#00205b}.dropdown-menu.aplicacoes .favoritos .title{background-color:#00205b;color:#fff;padding:10px}.dropdown-menu.aplicacoes .favoritos ul{padding-left:0}.dropdown-menu.aplicacoes .favoritos ul li{list-style:none;padding:10px}.dropdown-menu.aplicacoes .favoritos ul li:hover{background-color:#c0cee9}.dropdown-menu.aplicacoes .favoritos ul li:not(:last-child){border-bottom:1px solid #00205b}\n"] }]
        }], ctorParameters: function () { return [{ type: ApplicationsMenuService }, { type: ConfigService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: WindowService }, { type: LocationService }]; }, propDecorators: { buttonClass: [{
                type: Input
            }], suiteRfbUrl: [{
                type: Input
            }] } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbCommonModule {
}
SuiteRfbCommonModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbCommonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbCommonModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbCommonModule });
SuiteRfbCommonModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbCommonModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbCommonModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [],
                    declarations: [],
                    exports: [],
                    providers: []
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbApplicationsMenuModule {
}
SuiteRfbApplicationsMenuModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbApplicationsMenuModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbApplicationsMenuModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbApplicationsMenuModule, declarations: [ApplicationsMenuComponent,
        FavoriteApplicationsPipe], imports: [CommonModule,
        FormsModule, i7.BsDropdownModule, i8.TypeaheadModule, SuiteRfbCommonModule], exports: [ApplicationsMenuComponent,
        FavoriteApplicationsPipe] });
SuiteRfbApplicationsMenuModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbApplicationsMenuModule, imports: [CommonModule,
        FormsModule,
        BsDropdownModule.forRoot(),
        TypeaheadModule.forRoot(),
        SuiteRfbCommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbApplicationsMenuModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        BsDropdownModule.forRoot(),
                        TypeaheadModule.forRoot(),
                        SuiteRfbCommonModule,
                    ],
                    declarations: [
                        ApplicationsMenuComponent,
                        FavoriteApplicationsPipe,
                    ],
                    exports: [
                        ApplicationsMenuComponent,
                        FavoriteApplicationsPipe,
                    ],
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
const ROUTE_DATA_BREADCRUMB$1 = 'breadcrumb';
const ROUTE_DATA_BREADCRUMB_NAVIGABLE = 'navigableBreadcrumb';
class BreadcrumbsService {
    constructor() {
        this.breadcrumbsSource = new Subject();
        this.breadcrumbs$ = this.breadcrumbsSource.asObservable();
    }
    setRoute(route) {
        const root = route.root;
        const breadcrumbs = this.parseRoute(root);
        this.breadcrumbsSource.next(breadcrumbs);
    }
    getBreadcrumbs() {
        return this.breadcrumbs$;
    }
    parseRoute(route, url = '', breadcrumbs = []) {
        // get the child routes
        const children = route.children;
        // return if there are no more children
        if (children.length === 0) {
            return breadcrumbs;
        }
        // iterate over each children
        for (const child of children) {
            // verify primary route
            if (child.outlet !== PRIMARY_OUTLET) {
                continue;
            }
            // verify the custom data property 'breadcrumb' is specified on the route
            if (!child.data.hasOwnProperty(ROUTE_DATA_BREADCRUMB$1)) {
                return this.parseRoute(child, url, breadcrumbs);
            }
            // verify if the breadcrumb is enabled for this route
            if (child.data[ROUTE_DATA_BREADCRUMB$1] === false) {
                return this.parseRoute(child, url, breadcrumbs);
            }
            // check for duplicated paths
            if (child.url.map(segment => segment.path).length === 0) {
                return this.parseRoute(child, url, breadcrumbs);
            }
            // get the route's URL segment
            const routeURL = child.url.map(segment => segment.path).join('/');
            // append route URL to URL
            url += `/${routeURL}`;
            const navigableBreadcrumb = child.data[ROUTE_DATA_BREADCRUMB_NAVIGABLE] === false
                ? false
                : true;
            // add breadcrumb
            const breadcrumb = {
                label: child.data[ROUTE_DATA_BREADCRUMB$1],
                route: navigableBreadcrumb ? url : null,
                fragment: child.fragment,
                queryParams: child.queryParams
            };
            breadcrumbs.push(breadcrumb);
            // recursive
            return this.parseRoute(child, url, breadcrumbs);
        }
        return breadcrumbs;
    }
}
BreadcrumbsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: BreadcrumbsService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
BreadcrumbsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: BreadcrumbsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: BreadcrumbsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class BreadcrumbsComponent {
    constructor(activatedRoute, router, breadcrumbsService) {
        this.activatedRoute = activatedRoute;
        this.router = router;
        this.breadcrumbsService = breadcrumbsService;
        this.breadcrumbs = [];
        this.ngUnsubscribe = new Subject();
    }
    ngOnInit() {
        this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(() => this.breadcrumbsService.setRoute(this.activatedRoute.snapshot));
        this.breadcrumbsService.getBreadcrumbs()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(breadcrumbs => this.breadcrumbs = breadcrumbs);
    }
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
}
BreadcrumbsComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: BreadcrumbsComponent, deps: [{ token: i1$1.ActivatedRoute }, { token: i1$1.Router }, { token: BreadcrumbsService }], target: i0.ɵɵFactoryTarget.Component });
BreadcrumbsComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: BreadcrumbsComponent, selector: "suite-rfb-breadcrumbs", ngImport: i0, template: "<!-- Breadcrumbs -->\n<ol class=\"breadcrumb hidden-xs\">\n  <li [class.last]=\"last\" *ngFor=\"let breadcrumb of breadcrumbs; let last = last\">\n    <a *ngIf=\"!last && breadcrumb.route; else noRouteTemplate\"\n        [routerLink]=\"breadcrumb.route\"\n        [queryParams]=\"breadcrumb.queryParams\"\n        [fragment]=\"breadcrumb.fragment\"\n        href=\"#\">{{breadcrumb.label}}</a>\n    <ng-template #noRouteTemplate>\n      <span class=\"active\">{{breadcrumb.label}}</span>\n    </ng-template>\n  </li>\n</ol>", styles: [""], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: BreadcrumbsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-breadcrumbs', template: "<!-- Breadcrumbs -->\n<ol class=\"breadcrumb hidden-xs\">\n  <li [class.last]=\"last\" *ngFor=\"let breadcrumb of breadcrumbs; let last = last\">\n    <a *ngIf=\"!last && breadcrumb.route; else noRouteTemplate\"\n        [routerLink]=\"breadcrumb.route\"\n        [queryParams]=\"breadcrumb.queryParams\"\n        [fragment]=\"breadcrumb.fragment\"\n        href=\"#\">{{breadcrumb.label}}</a>\n    <ng-template #noRouteTemplate>\n      <span class=\"active\">{{breadcrumb.label}}</span>\n    </ng-template>\n  </li>\n</ol>" }]
        }], ctorParameters: function () { return [{ type: i1$1.ActivatedRoute }, { type: i1$1.Router }, { type: BreadcrumbsService }]; } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbBreadcrumbsModule {
}
SuiteRfbBreadcrumbsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbBreadcrumbsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbBreadcrumbsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbBreadcrumbsModule, declarations: [BreadcrumbsComponent], imports: [CommonModule, i1$1.RouterModule], exports: [BreadcrumbsComponent] });
SuiteRfbBreadcrumbsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbBreadcrumbsModule, imports: [CommonModule,
        RouterModule.forChild([])] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbBreadcrumbsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        RouterModule.forChild([]),
                    ],
                    declarations: [
                        BreadcrumbsComponent,
                    ],
                    exports: [
                        BreadcrumbsComponent,
                    ],
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class CollapseComponent {
    constructor() {
        this.vertical = false;
        this.disabled = false;
        // Remover o atributo title do component para evitar que os componentes
        // internos fiquem com o title definido atributo de entrada
        this.attr_title = undefined;
        this.collapsed = false;
        this.collapsedChange = new EventEmitter();
        this.showIcon = true;
    }
    ngOnInit() { }
    toggle() {
        if (this.disabled)
            return;
        this.collapsed = !this.collapsed;
        this.collapsedChange.emit(this.collapsed);
    }
}
CollapseComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: CollapseComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CollapseComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: CollapseComponent, selector: "suite-rfb-collapse", inputs: { title: "title", vertical: "vertical", disabled: "disabled", collapsed: "collapsed", showIcon: "showIcon" }, outputs: { collapsedChange: "collapsedChange" }, host: { properties: { "attr.title": "this.attr_title" } }, ngImport: i0, template: "<h2 data-toggle=\"collapse\" aria-expanded=\"false\"\n    [class.collapsed]=\"collapsed\" [class.vertical]=\"vertical\" [class.disabled]=\"disabled\">\n  <div (click)=\"toggle()\" class=\"collapse-container\">\n    <span *ngIf=\"showIcon\" class=\"material-icons collapse-icon\">{{collapsed ? '&#xE313;' : '&#xE316;'}}</span>\n    <ng-content select=\"[before-title]\"></ng-content>\n    <span class=\"title\">{{title}}</span>\n    <ng-content select=\"[after-title]\"></ng-content>\n  </div>\n</h2>\n<div class=\"collapse in\" [collapse]=\"collapsed\">\n  <ng-content></ng-content>\n</div>\n", styles: [".collapse-container{cursor:pointer}.disabled .collapse-container{cursor:default}.collapse-container .material-icons.collapse-icon{transform:unset}.vertical .collapse-container .material-icons.collapse-icon{transform:rotate(90deg)}.collapsed.vertical>div{display:flex;flex-direction:column;align-items:center}.collapsed.vertical span.title{writing-mode:vertical-rl}h2.collapsed.vertical{border:none}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2$1.CollapseDirective, selector: "[collapse]", inputs: ["display", "isAnimated", "collapse"], outputs: ["collapsed", "collapses", "expanded", "expands"], exportAs: ["bs-collapse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: CollapseComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-collapse', template: "<h2 data-toggle=\"collapse\" aria-expanded=\"false\"\n    [class.collapsed]=\"collapsed\" [class.vertical]=\"vertical\" [class.disabled]=\"disabled\">\n  <div (click)=\"toggle()\" class=\"collapse-container\">\n    <span *ngIf=\"showIcon\" class=\"material-icons collapse-icon\">{{collapsed ? '&#xE313;' : '&#xE316;'}}</span>\n    <ng-content select=\"[before-title]\"></ng-content>\n    <span class=\"title\">{{title}}</span>\n    <ng-content select=\"[after-title]\"></ng-content>\n  </div>\n</h2>\n<div class=\"collapse in\" [collapse]=\"collapsed\">\n  <ng-content></ng-content>\n</div>\n", styles: [".collapse-container{cursor:pointer}.disabled .collapse-container{cursor:default}.collapse-container .material-icons.collapse-icon{transform:unset}.vertical .collapse-container .material-icons.collapse-icon{transform:rotate(90deg)}.collapsed.vertical>div{display:flex;flex-direction:column;align-items:center}.collapsed.vertical span.title{writing-mode:vertical-rl}h2.collapsed.vertical{border:none}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { title: [{
                type: Input
            }], vertical: [{
                type: Input
            }], disabled: [{
                type: Input
            }], attr_title: [{
                type: HostBinding,
                args: ['attr.title']
            }], collapsed: [{
                type: Input
            }], collapsedChange: [{
                type: Output
            }], showIcon: [{
                type: Input
            }] } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbCollapseModule {
}
SuiteRfbCollapseModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbCollapseModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbCollapseModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbCollapseModule, declarations: [CollapseComponent], imports: [CommonModule, i2$1.CollapseModule], exports: [CollapseComponent] });
SuiteRfbCollapseModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbCollapseModule, imports: [CommonModule,
        CollapseModule.forRoot()] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbCollapseModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        CollapseModule.forRoot(),
                    ],
                    declarations: [
                        CollapseComponent,
                    ],
                    exports: [
                        CollapseComponent,
                    ],
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class ConfirmationComponent {
    constructor(modalService) {
        this.modalService = modalService;
        this.acceptLabel = 'Sim';
        this.customBody = undefined;
        this.disabled = false;
        this.ignoreBackdropClick = false;
        this.ignoreEscapeKey = false;
        this.popupTitle = 'Confirmação';
        this.question = 'Confirma?';
        this.rejectLabel = 'Não';
        this.showAcceptButton = true;
        this.disableAcceptButton = false;
        this.showRejectButton = true;
        this.showButtons = true;
        this.accept = new EventEmitter();
        this.reject = new EventEmitter();
    }
    ngOnInit() { }
    show() {
        if (!this.disabled) {
            this.modalRef = this.modalService.show(this.template, {
                ignoreBackdropClick: this.ignoreBackdropClick,
                keyboard: !this.ignoreEscapeKey,
                class: this.modalClass
            });
        }
    }
    hide() {
        if (!this.disabled && this.modalRef) {
            this.modalRef.hide();
            this.modalRef = null;
        }
    }
    onAccept() {
        if (!this.disabled && !this.disableAcceptButton) {
            this.hide();
            this.accept.emit();
        }
    }
    onReject() {
        if (!this.disabled) {
            this.hide();
            this.reject.emit();
        }
    }
}
ConfirmationComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: ConfirmationComponent, deps: [{ token: i1$2.BsModalService }], target: i0.ɵɵFactoryTarget.Component });
ConfirmationComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: ConfirmationComponent, selector: "suite-rfb-confirmation", inputs: { acceptLabel: "acceptLabel", customBody: "customBody", disabled: "disabled", ignoreBackdropClick: "ignoreBackdropClick", ignoreEscapeKey: "ignoreEscapeKey", popupTitle: "popupTitle", question: "question", rejectLabel: "rejectLabel", showAcceptButton: "showAcceptButton", disableAcceptButton: "disableAcceptButton", showRejectButton: "showRejectButton", showButtons: "showButtons", modalClass: "modalClass" }, outputs: { accept: "accept", reject: "reject" }, viewQueries: [{ propertyName: "template", first: true, predicate: ["template"], descendants: true, static: true }], ngImport: i0, template: "<ng-template #template>\n  <div class=\"modal-header\">\n    <h4 class=\"modal-title pull-left\">{{popupTitle}}</h4>\n  </div>\n  <div class=\"modal-body\">\n    <div *ngIf=\"customBody\">\n      <ng-container *ngTemplateOutlet=\"customBody\"></ng-container>\n    </div>\n    <div *ngIf=\"!customBody\">\n      <span>{{question}}</span>\n    </div>\n  </div>\n  <div *ngIf=\"showButtons\" class=\"modal-footer\">\n    <button *ngIf=\"showAcceptButton\" type=\"button\" class=\"btn btn-primary accept\"\n      [attr.aria-label]=\"acceptLabel\" [disabled]=\"disableAcceptButton\"\n      (click)=\"onAccept()\">{{acceptLabel}}</button>\n    <button *ngIf=\"showRejectButton\" type=\"button\" class=\"btn btn-primary reject\"\n      [attr.aria-label]=\"rejectLabel\"\n      (click)=\"onReject()\">{{rejectLabel}}</button>\n  </div>\n</ng-template>\n", styles: ["", "::ng-deep .modal.fade{opacity:1}::ng-deep .modal.fade .modal-dialog{transform:translate(0)}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: ConfirmationComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-confirmation', template: "<ng-template #template>\n  <div class=\"modal-header\">\n    <h4 class=\"modal-title pull-left\">{{popupTitle}}</h4>\n  </div>\n  <div class=\"modal-body\">\n    <div *ngIf=\"customBody\">\n      <ng-container *ngTemplateOutlet=\"customBody\"></ng-container>\n    </div>\n    <div *ngIf=\"!customBody\">\n      <span>{{question}}</span>\n    </div>\n  </div>\n  <div *ngIf=\"showButtons\" class=\"modal-footer\">\n    <button *ngIf=\"showAcceptButton\" type=\"button\" class=\"btn btn-primary accept\"\n      [attr.aria-label]=\"acceptLabel\" [disabled]=\"disableAcceptButton\"\n      (click)=\"onAccept()\">{{acceptLabel}}</button>\n    <button *ngIf=\"showRejectButton\" type=\"button\" class=\"btn btn-primary reject\"\n      [attr.aria-label]=\"rejectLabel\"\n      (click)=\"onReject()\">{{rejectLabel}}</button>\n  </div>\n</ng-template>\n", styles: ["::ng-deep .modal.fade{opacity:1}::ng-deep .modal.fade .modal-dialog{transform:translate(0)}\n"] }]
        }], ctorParameters: function () { return [{ type: i1$2.BsModalService }]; }, propDecorators: { acceptLabel: [{
                type: Input
            }], customBody: [{
                type: Input
            }], disabled: [{
                type: Input
            }], ignoreBackdropClick: [{
                type: Input
            }], ignoreEscapeKey: [{
                type: Input
            }], popupTitle: [{
                type: Input
            }], question: [{
                type: Input
            }], rejectLabel: [{
                type: Input
            }], showAcceptButton: [{
                type: Input
            }], disableAcceptButton: [{
                type: Input
            }], showRejectButton: [{
                type: Input
            }], showButtons: [{
                type: Input
            }], accept: [{
                type: Output
            }], reject: [{
                type: Output
            }], modalClass: [{
                type: Input
            }], template: [{
                type: ViewChild,
                args: ['template', { static: true }]
            }] } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class ConfirmationButtonComponent {
    constructor() {
        this.acceptLabel = 'Sim';
        this.customBody = undefined;
        this.customClass = 'btn btn-default';
        this.disabled = false;
        this.ignoreBackdropClick = false;
        this.ignoreEscapeKey = false;
        this.question = 'Confirma?';
        this.rejectLabel = 'Não';
        this.showAcceptButton = true;
        this.disableAcceptButton = false;
        this.showRejectButton = true;
        this.accept = new EventEmitter();
        this.reject = new EventEmitter();
    }
    ngOnInit() { }
    show() {
        if (!this.disabled) {
            this.confirmationComponent.show();
        }
    }
    hide() {
        if (!this.disabled) {
            this.confirmationComponent.hide();
        }
    }
    onAccept() {
        if (!this.disabled) {
            this.hide();
            this.accept.emit();
        }
    }
    onReject() {
        if (!this.disabled) {
            this.hide();
            this.reject.emit();
        }
    }
}
ConfirmationButtonComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: ConfirmationButtonComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
ConfirmationButtonComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: ConfirmationButtonComponent, selector: "suite-rfb-confirmation-button", inputs: { acceptLabel: "acceptLabel", customBody: "customBody", customClass: "customClass", disabled: "disabled", icon: "icon", ignoreBackdropClick: "ignoreBackdropClick", ignoreEscapeKey: "ignoreEscapeKey", popupTitle: "popupTitle", question: "question", rejectLabel: "rejectLabel", showAcceptButton: "showAcceptButton", disableAcceptButton: "disableAcceptButton", showRejectButton: "showRejectButton", title: "title", modalClass: "modalClass" }, outputs: { accept: "accept", reject: "reject" }, viewQueries: [{ propertyName: "confirmationComponent", first: true, predicate: ["confirmation"], descendants: true, static: true }], ngImport: i0, template: "<button type=\"button\" [class]=\"customClass\"\n  (click)=\"show()\" [disabled]=\"disabled\">\n  <ng-container *ngIf=\"title || icon\">\n    <span *ngIf=\"icon\" class=\"material-icons\">{{icon}}</span> {{title}}\n  </ng-container>\n  <ng-content></ng-content>\n</button>\n\n<suite-rfb-confirmation #confirmation\n  [acceptLabel]=\"acceptLabel\"\n  [customBody]=\"customBody\"\n  [disabled]=\"disabled\"\n  [ignoreBackdropClick]=\"ignoreBackdropClick\"\n  [ignoreEscapeKey]=\"ignoreEscapeKey\"\n  [popupTitle]=\"popupTitle || title\"\n  [question]=\"question\"\n  [rejectLabel]=\"rejectLabel\"\n  [showAcceptButton]=\"showAcceptButton\"\n  [disableAcceptButton]=\"disableAcceptButton\"\n  [showRejectButton]=\"showRejectButton\" \n  [modalClass]=\"modalClass\" \n  (accept)=\"onAccept()\"\n  (reject)=\"onReject()\"\n></suite-rfb-confirmation>\n", styles: ["::ng-deep .btn+suite-rfb-confirmation-button,::ng-deep suite-rfb-confirmation-button+.btn,::ng-deep suite-rfb-confirmation-button+suite-rfb-confirmation-button{margin-left:1em}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: ConfirmationComponent, selector: "suite-rfb-confirmation", inputs: ["acceptLabel", "customBody", "disabled", "ignoreBackdropClick", "ignoreEscapeKey", "popupTitle", "question", "rejectLabel", "showAcceptButton", "disableAcceptButton", "showRejectButton", "showButtons", "modalClass"], outputs: ["accept", "reject"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: ConfirmationButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-confirmation-button', template: "<button type=\"button\" [class]=\"customClass\"\n  (click)=\"show()\" [disabled]=\"disabled\">\n  <ng-container *ngIf=\"title || icon\">\n    <span *ngIf=\"icon\" class=\"material-icons\">{{icon}}</span> {{title}}\n  </ng-container>\n  <ng-content></ng-content>\n</button>\n\n<suite-rfb-confirmation #confirmation\n  [acceptLabel]=\"acceptLabel\"\n  [customBody]=\"customBody\"\n  [disabled]=\"disabled\"\n  [ignoreBackdropClick]=\"ignoreBackdropClick\"\n  [ignoreEscapeKey]=\"ignoreEscapeKey\"\n  [popupTitle]=\"popupTitle || title\"\n  [question]=\"question\"\n  [rejectLabel]=\"rejectLabel\"\n  [showAcceptButton]=\"showAcceptButton\"\n  [disableAcceptButton]=\"disableAcceptButton\"\n  [showRejectButton]=\"showRejectButton\" \n  [modalClass]=\"modalClass\" \n  (accept)=\"onAccept()\"\n  (reject)=\"onReject()\"\n></suite-rfb-confirmation>\n", styles: ["::ng-deep .btn+suite-rfb-confirmation-button,::ng-deep suite-rfb-confirmation-button+.btn,::ng-deep suite-rfb-confirmation-button+suite-rfb-confirmation-button{margin-left:1em}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { acceptLabel: [{
                type: Input
            }], customBody: [{
                type: Input
            }], customClass: [{
                type: Input
            }], disabled: [{
                type: Input
            }], icon: [{
                type: Input
            }], ignoreBackdropClick: [{
                type: Input
            }], ignoreEscapeKey: [{
                type: Input
            }], popupTitle: [{
                type: Input
            }], question: [{
                type: Input
            }], rejectLabel: [{
                type: Input
            }], showAcceptButton: [{
                type: Input
            }], disableAcceptButton: [{
                type: Input
            }], showRejectButton: [{
                type: Input
            }], title: [{
                type: Input
            }], modalClass: [{
                type: Input
            }], accept: [{
                type: Output
            }], reject: [{
                type: Output
            }], confirmationComponent: [{
                type: ViewChild,
                args: ['confirmation', { static: true }]
            }] } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class ConfirmationLinkComponent {
    constructor() {
        this.acceptLabel = 'Sim';
        this.customBody = undefined;
        this.customClass = '';
        this.disabled = false;
        this.ignoreBackdropClick = false;
        this.ignoreEscapeKey = false;
        this.question = 'Confirma?';
        this.rejectLabel = 'Não';
        this.showAcceptButton = true;
        this.disableAcceptButton = false;
        this.showRejectButton = true;
        this.accept = new EventEmitter();
        this.reject = new EventEmitter();
    }
    ngOnInit() { }
    show() {
        if (!this.disabled && this.confirmationComponent) {
            this.confirmationComponent.show();
        }
    }
    hide() {
        if (!this.disabled && this.confirmationComponent) {
            this.confirmationComponent.hide();
        }
    }
    onAccept() {
        if (!this.disabled) {
            this.hide();
            this.accept.emit();
        }
    }
    onReject() {
        if (!this.disabled) {
            this.hide();
            this.reject.emit();
        }
    }
}
ConfirmationLinkComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: ConfirmationLinkComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
ConfirmationLinkComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: ConfirmationLinkComponent, selector: "suite-rfb-confirmation-link", inputs: { acceptLabel: "acceptLabel", customBody: "customBody", customClass: "customClass", disabled: "disabled", ignoreBackdropClick: "ignoreBackdropClick", ignoreEscapeKey: "ignoreEscapeKey", popupTitle: "popupTitle", question: "question", rejectLabel: "rejectLabel", showAcceptButton: "showAcceptButton", disableAcceptButton: "disableAcceptButton", showRejectButton: "showRejectButton", title: "title", modalClass: "modalClass" }, outputs: { accept: "accept", reject: "reject" }, viewQueries: [{ propertyName: "confirmationComponent", first: true, predicate: ["confirmation"], descendants: true, static: true }], ngImport: i0, template: "<a href=\"javascript:void(0)\"\n  [class]=\"customClass\" [class.confirmation-link-disabled]=\"disabled\"\n  (click)=\"show()\">\n  <ng-container *ngIf=\"title\">{{title}}</ng-container>\n  <ng-content></ng-content>\n</a>\n\n<suite-rfb-confirmation #confirmation\n  [acceptLabel]=\"acceptLabel\"\n  [customBody]=\"customBody\"\n  [disabled]=\"disabled\"\n  [ignoreBackdropClick]=\"ignoreBackdropClick\"\n  [ignoreEscapeKey]=\"ignoreEscapeKey\"\n  [popupTitle]=\"popupTitle || title\"\n  [question]=\"question\"\n  [rejectLabel]=\"rejectLabel\"\n  [showAcceptButton]=\"showAcceptButton\"\n  [disableAcceptButton]=\"disableAcceptButton\"\n  [showRejectButton]=\"showRejectButton\" \n  [modalClass]=\"modalClass\" \n  (accept)=\"onAccept()\"\n  (reject)=\"onReject()\"\n></suite-rfb-confirmation>\n", styles: [".confirmation-link-disabled{cursor:not-allowed;filter:alpha(opacity=65);box-shadow:none;opacity:.65;text-decoration:none}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: ConfirmationComponent, selector: "suite-rfb-confirmation", inputs: ["acceptLabel", "customBody", "disabled", "ignoreBackdropClick", "ignoreEscapeKey", "popupTitle", "question", "rejectLabel", "showAcceptButton", "disableAcceptButton", "showRejectButton", "showButtons", "modalClass"], outputs: ["accept", "reject"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: ConfirmationLinkComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-confirmation-link', template: "<a href=\"javascript:void(0)\"\n  [class]=\"customClass\" [class.confirmation-link-disabled]=\"disabled\"\n  (click)=\"show()\">\n  <ng-container *ngIf=\"title\">{{title}}</ng-container>\n  <ng-content></ng-content>\n</a>\n\n<suite-rfb-confirmation #confirmation\n  [acceptLabel]=\"acceptLabel\"\n  [customBody]=\"customBody\"\n  [disabled]=\"disabled\"\n  [ignoreBackdropClick]=\"ignoreBackdropClick\"\n  [ignoreEscapeKey]=\"ignoreEscapeKey\"\n  [popupTitle]=\"popupTitle || title\"\n  [question]=\"question\"\n  [rejectLabel]=\"rejectLabel\"\n  [showAcceptButton]=\"showAcceptButton\"\n  [disableAcceptButton]=\"disableAcceptButton\"\n  [showRejectButton]=\"showRejectButton\" \n  [modalClass]=\"modalClass\" \n  (accept)=\"onAccept()\"\n  (reject)=\"onReject()\"\n></suite-rfb-confirmation>\n", styles: [".confirmation-link-disabled{cursor:not-allowed;filter:alpha(opacity=65);box-shadow:none;opacity:.65;text-decoration:none}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { acceptLabel: [{
                type: Input
            }], customBody: [{
                type: Input
            }], customClass: [{
                type: Input
            }], disabled: [{
                type: Input
            }], ignoreBackdropClick: [{
                type: Input
            }], ignoreEscapeKey: [{
                type: Input
            }], popupTitle: [{
                type: Input
            }], question: [{
                type: Input
            }], rejectLabel: [{
                type: Input
            }], showAcceptButton: [{
                type: Input
            }], disableAcceptButton: [{
                type: Input
            }], showRejectButton: [{
                type: Input
            }], title: [{
                type: Input
            }], modalClass: [{
                type: Input
            }], accept: [{
                type: Output
            }], reject: [{
                type: Output
            }], confirmationComponent: [{
                type: ViewChild,
                args: ['confirmation', { static: true }]
            }] } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbConfirmationsModule {
}
SuiteRfbConfirmationsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbConfirmationsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbConfirmationsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbConfirmationsModule, declarations: [ConfirmationComponent,
        ConfirmationButtonComponent,
        ConfirmationLinkComponent], imports: [CommonModule, i1$2.ModalModule], exports: [ConfirmationComponent,
        ConfirmationButtonComponent,
        ConfirmationLinkComponent] });
SuiteRfbConfirmationsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbConfirmationsModule, imports: [CommonModule,
        ModalModule.forRoot()] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbConfirmationsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        ModalModule.forRoot(),
                    ],
                    declarations: [
                        ConfirmationComponent,
                        ConfirmationButtonComponent,
                        ConfirmationLinkComponent,
                    ],
                    exports: [
                        ConfirmationComponent,
                        ConfirmationButtonComponent,
                        ConfirmationLinkComponent,
                    ],
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class ContactService {
    constructor() {
        this.contactSource = new Subject();
        this.contact$ = this.contactSource.asObservable();
    }
    contact(text) {
        this.contactSource.next(text);
    }
}
ContactService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: ContactService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
ContactService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: ContactService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: ContactService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
const ROUTE_DATA_BREADCRUMB = 'contextualHelp';
class ContextualHelpService {
    constructor() {
        this.contextualHelpSource = new Subject();
        this.contextualHelp$ = this.contextualHelpSource.asObservable();
    }
    getContextualHelp() {
        return this.contextualHelp$;
    }
    setRoute(route) {
        const contextualHelp = this.parseRoute(route);
        this.contextualHelpSource.next(contextualHelp);
    }
    parseRoute(route) {
        let contextualHelp;
        if (route.data.hasOwnProperty(ROUTE_DATA_BREADCRUMB)) {
            contextualHelp = route.data[ROUTE_DATA_BREADCRUMB];
        }
        else if (route.parent) {
            contextualHelp = this.parseRoute(route.parent);
        }
        return contextualHelp;
    }
}
ContextualHelpService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: ContextualHelpService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
ContextualHelpService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: ContextualHelpService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: ContextualHelpService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class ContextualHelpComponent {
    constructor(modalService, activatedRoute, router, configService, contactService, contextualHelpService) {
        this.modalService = modalService;
        this.activatedRoute = activatedRoute;
        this.router = router;
        this.configService = configService;
        this.contactService = contactService;
        this.contextualHelpService = contextualHelpService;
        this.buttonClass = 'btn btn-default';
        this.hidden = false;
        this.disabled = false;
        this.ngUnsubscribe = new Subject();
        this.hidden = this.configService.isContextualHelpHidden();
        this.disabled = this.configService.isContextualHelpDisabled();
    }
    ngOnInit() {
        this.router.events
            .pipe(takeUntil(this.ngUnsubscribe), filter(event => event instanceof NavigationEnd), map(() => this.activatedRoute), filter(route => route.outlet === 'primary'), map(this.firstChild))
            .subscribe(route => this.contextualHelpService.setRoute(route.snapshot));
        this.contextualHelpService.getContextualHelp()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(help => this.help = help);
    }
    firstChild(route) {
        while (route.firstChild) {
            route = route.firstChild;
        }
        return route;
    }
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
    show() {
        this.modalRef = this.modalService.show(this.template, { class: 'modal-lg' });
    }
    dismiss() {
        if (this.modalRef) {
            this.modalRef.hide();
        }
    }
    contact(text) {
        this.contactService.contact(text);
    }
}
ContextualHelpComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: ContextualHelpComponent, deps: [{ token: i1$2.BsModalService }, { token: i1$1.ActivatedRoute }, { token: i1$1.Router }, { token: ConfigService }, { token: ContactService }, { token: ContextualHelpService }], target: i0.ɵɵFactoryTarget.Component });
ContextualHelpComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: ContextualHelpComponent, selector: "suite-rfb-contextual-help", inputs: { buttonClass: "buttonClass" }, viewQueries: [{ propertyName: "template", first: true, predicate: ["template"], descendants: true, static: true }], ngImport: i0, template: "<div *ngIf=\"!hidden\">\n  <button [class]=\"buttonClass\" (click)=\"show()\" type=\"button\" [disabled]=\"disabled\">\n    <span class=\"material-icons icon-24\">&#xE887;</span>\n  </button>\n</div>\n\n<ng-template #template>\n  <!-- Cabe\u00E7alho do modal -->\n  <div class=\"modal-header\">\n    <button type=\"button\" class=\"close\" data-dismiss=\"modal\" aria-label=\"Close\" (click)=\"dismiss()\">\n      <span class=\"material-icons\">&#xE5CD;</span>\n    </button>\n    <p class=\"modal-title\">\n      <span class=\"material-icons\">&#xE887;</span> Ajuda contextual\n    </p>\n  </div>\n  <!-- Corpo do modal -->\n  <div class=\"modal-body\">\n    <ng-container *ngIf=\"help\">\n      <table class=\"table table-bordered\">\n        <tr>\n          <td>\n            <span>Sistema:</span>\n            <strong class=\"text-uppercase\">{{help.system}}</strong>\n          </td>\n          <td>\n            <span>Funcionalidade:</span>\n            <strong class=\"text-uppercase\">{{help.feature}}</strong>\n          </td>\n        </tr>\n      </table>\n      <h2>{{help.title}}</h2>\n      <div [innerHtml]=\"help.descriptionHtml\"></div>\n      <ul class=\"faq\" *ngIf=\"help.faqs\">\n        <li *ngFor=\"let faq of help.faqs\">\n          <a role=\"button\" data-toggle=\"collapse\" href=\"javascript:void(0)\" (click)=\"faq.collapsed = !faq.collapsed\">\n            <span>{{faq.question}}</span>\n          </a>\n          <div class=\"collapse\" [collapse]=\"faq.collapsed\" id=\"pergunta-1\">\n            <div class=\"well\">{{faq.answer}}</div>\n          </div>\n        </li>\n      </ul>\n      <p *ngIf=\"help.manualUrl\">\n        <span>Para mais informa\u00E7\u00F5es, consulte o <a [href]=\"help.manualUrl\" target=\"_blank\">Manual do Sistema {{help.system}}</a></span>\n        <span *ngIf=\"!help.hideContactForm\"> ou mande sua d\u00FAvida para o Suporte Web:</span>\n      </p>\n      <form *ngIf=\"!help.hideContactForm\">\n        <div class=\"form-group\">\n          <label class=\"control-label\" for=\"duvida\">Sua d\u00FAvida</label>\n          <textarea #duvida class=\"form-control\" id=\"duvida\"></textarea>\n        </div>\n        <div class=\"form-group\">\n          <button type=\"button\" class=\"btn btn-primary\" (click)=\"contact(duvida.value)\">Enviar</button>\n        </div>\n      </form>\n    </ng-container>\n    <ng-container *ngIf=\"!help\">\n      <h2>Ooops</h2>\n      <div>\n        <p>N\u00E3o h\u00E1 nenhuma ajuda definida para esse contexto.</p>\n      </div>\n    </ng-container>\n    <p class=\"acione-css\">\n      <strong class=\"text-danger\">O sistema est\u00E1 inacess\u00EDvel ou voc\u00EA encontrou um erro inesperado?</strong>\n      <a href=\"http://www.serpro.gov.br/menu/suporte1/#acionecss\" target=\"_blank\">Acionar a Central de Servi\u00E7os <span class=\"material-icons icon-24\">open_in_new</span></a>\n    </p>\n  </div>\n</ng-template>\n", styles: ["", "::ng-deep .modal.fade{opacity:1}::ng-deep .modal.fade .modal-dialog{transform:translate(0)}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2$1.CollapseDirective, selector: "[collapse]", inputs: ["display", "isAnimated", "collapse"], outputs: ["collapsed", "collapses", "expanded", "expands"], exportAs: ["bs-collapse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: ContextualHelpComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-contextual-help', template: "<div *ngIf=\"!hidden\">\n  <button [class]=\"buttonClass\" (click)=\"show()\" type=\"button\" [disabled]=\"disabled\">\n    <span class=\"material-icons icon-24\">&#xE887;</span>\n  </button>\n</div>\n\n<ng-template #template>\n  <!-- Cabe\u00E7alho do modal -->\n  <div class=\"modal-header\">\n    <button type=\"button\" class=\"close\" data-dismiss=\"modal\" aria-label=\"Close\" (click)=\"dismiss()\">\n      <span class=\"material-icons\">&#xE5CD;</span>\n    </button>\n    <p class=\"modal-title\">\n      <span class=\"material-icons\">&#xE887;</span> Ajuda contextual\n    </p>\n  </div>\n  <!-- Corpo do modal -->\n  <div class=\"modal-body\">\n    <ng-container *ngIf=\"help\">\n      <table class=\"table table-bordered\">\n        <tr>\n          <td>\n            <span>Sistema:</span>\n            <strong class=\"text-uppercase\">{{help.system}}</strong>\n          </td>\n          <td>\n            <span>Funcionalidade:</span>\n            <strong class=\"text-uppercase\">{{help.feature}}</strong>\n          </td>\n        </tr>\n      </table>\n      <h2>{{help.title}}</h2>\n      <div [innerHtml]=\"help.descriptionHtml\"></div>\n      <ul class=\"faq\" *ngIf=\"help.faqs\">\n        <li *ngFor=\"let faq of help.faqs\">\n          <a role=\"button\" data-toggle=\"collapse\" href=\"javascript:void(0)\" (click)=\"faq.collapsed = !faq.collapsed\">\n            <span>{{faq.question}}</span>\n          </a>\n          <div class=\"collapse\" [collapse]=\"faq.collapsed\" id=\"pergunta-1\">\n            <div class=\"well\">{{faq.answer}}</div>\n          </div>\n        </li>\n      </ul>\n      <p *ngIf=\"help.manualUrl\">\n        <span>Para mais informa\u00E7\u00F5es, consulte o <a [href]=\"help.manualUrl\" target=\"_blank\">Manual do Sistema {{help.system}}</a></span>\n        <span *ngIf=\"!help.hideContactForm\"> ou mande sua d\u00FAvida para o Suporte Web:</span>\n      </p>\n      <form *ngIf=\"!help.hideContactForm\">\n        <div class=\"form-group\">\n          <label class=\"control-label\" for=\"duvida\">Sua d\u00FAvida</label>\n          <textarea #duvida class=\"form-control\" id=\"duvida\"></textarea>\n        </div>\n        <div class=\"form-group\">\n          <button type=\"button\" class=\"btn btn-primary\" (click)=\"contact(duvida.value)\">Enviar</button>\n        </div>\n      </form>\n    </ng-container>\n    <ng-container *ngIf=\"!help\">\n      <h2>Ooops</h2>\n      <div>\n        <p>N\u00E3o h\u00E1 nenhuma ajuda definida para esse contexto.</p>\n      </div>\n    </ng-container>\n    <p class=\"acione-css\">\n      <strong class=\"text-danger\">O sistema est\u00E1 inacess\u00EDvel ou voc\u00EA encontrou um erro inesperado?</strong>\n      <a href=\"http://www.serpro.gov.br/menu/suporte1/#acionecss\" target=\"_blank\">Acionar a Central de Servi\u00E7os <span class=\"material-icons icon-24\">open_in_new</span></a>\n    </p>\n  </div>\n</ng-template>\n", styles: ["::ng-deep .modal.fade{opacity:1}::ng-deep .modal.fade .modal-dialog{transform:translate(0)}\n"] }]
        }], ctorParameters: function () { return [{ type: i1$2.BsModalService }, { type: i1$1.ActivatedRoute }, { type: i1$1.Router }, { type: ConfigService }, { type: ContactService }, { type: ContextualHelpService }]; }, propDecorators: { buttonClass: [{
                type: Input
            }], template: [{
                type: ViewChild,
                args: ['template', { static: true }]
            }] } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbContextualHelpModule {
}
SuiteRfbContextualHelpModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbContextualHelpModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbContextualHelpModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbContextualHelpModule, declarations: [ContextualHelpComponent], imports: [CommonModule, i1$1.RouterModule, i1$2.ModalModule, i2$1.CollapseModule], exports: [ContextualHelpComponent] });
SuiteRfbContextualHelpModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbContextualHelpModule, imports: [CommonModule,
        RouterModule.forChild([]),
        ModalModule.forRoot(),
        CollapseModule.forRoot()] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbContextualHelpModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        RouterModule.forChild([]),
                        ModalModule.forRoot(),
                        CollapseModule.forRoot(),
                    ],
                    declarations: [
                        ContextualHelpComponent,
                    ],
                    exports: [
                        ContextualHelpComponent,
                    ],
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SimpleMaskMoneyService {
    constructor() {
        this.args = {
            fixed: true,
            fractionDigits: 2,
            whitespace: ' ',
            decimalSeparator: ',',
            thousandsSeparator: '.',
            emptyOrInvalid: '0,00',
            completer: '0'
        };
        this.format = (value) => {
            const { negative, newValue } = this.removeNegative(value);
            return negative + this.numberToText(this.textToNumber(newValue));
        };
        this.formatToNumber = (input) => {
            let retorno = '0';
            let value = this.textToNumber(input);
            if (!Number.isNaN(parseFloat(value))) {
                if (value.length <= this.args.fractionDigits) {
                    value = this.formatDecimal(value, '0', '.');
                }
                else {
                    const lengthWithoutDecimals = value.length - this.args.fractionDigits;
                    value = value.replace(new RegExp(`(\\d{${lengthWithoutDecimals}})(\\d{${this.args.fractionDigits}})`), '$1.$2');
                }
                retorno = value;
            }
            return parseFloat(retorno);
        };
        this.onlyNumber = value => {
            let retorno = '';
            for (let i = 0; i < value.length; i++) {
                if (isFinite(value[i])) {
                    retorno += value[i];
                }
            }
            return retorno;
        };
        this.addingCompleterFromStart = (value, completer) => {
            while (value.length < this.args.fractionDigits) {
                value = `${completer}${value}`;
            }
            return value;
        };
        this.removingCompleterFromStart = (value, completer) => {
            while (value[0] === completer) {
                value = value.replace(completer, '');
            }
            return value;
        };
        this.addingDecimalSeparator = (value, completer, separator) => {
            const length = value.length - this.args.fractionDigits;
            const character = isFinite(completer) ? 'd' : 'w';
            let regexpFraction;
            let decimals = '$1';
            let dezenas = completer;
            regexpFraction = `(\\${character}{${this.args.fractionDigits}})`;
            if (length > 0) {
                regexpFraction = `(\\${character}{${length}})${regexpFraction}`;
                dezenas = decimals;
                decimals = '$2';
            }
            return value.replace(new RegExp(regexpFraction), `${dezenas}${separator}${decimals}`);
        };
        this.addingHundredsSeparator = value => {
            let size = value.length - this.args.fractionDigits;
            const hundreds = Math.ceil(size / 3);
            let regexpHundreds = '(\\d)';
            let replacement = `${this.args.decimalSeparator}$${hundreds + 1}`;
            for (let i = hundreds; i !== 0; i--) {
                if (size >= 3) {
                    regexpHundreds = `(\\d{3})${regexpHundreds}`;
                    size -= 3;
                }
                else {
                    regexpHundreds = `(\\d{${size}})${regexpHundreds}`;
                }
                if (i > 1) {
                    replacement = `${this.args.thousandsSeparator}$${i}${replacement}`;
                }
                else {
                    replacement = `$${i}${replacement}`;
                }
            }
            return value.replace(new RegExp(regexpHundreds), replacement);
        };
        this.removeSeparator = (value, separator) => {
            return value.replace(new RegExp(`\\${separator}`, 'g'), '');
        };
        this.formatDecimal = (value, completer, separator) => {
            value = this.addingCompleterFromStart(value, completer);
            value = this.addingDecimalSeparator(value, completer, separator);
            return value;
        };
        this.textToNumber = input => {
            const normalizedInput = this.normalizeNumber(input);
            let retorno = normalizedInput.toString();
            retorno = retorno.trim();
            retorno = this.removeSeparator(retorno, this.args.whitespace);
            retorno = this.removeSeparator(retorno, this.args.thousandsSeparator);
            retorno = this.removeSeparator(retorno, this.args.decimalSeparator);
            retorno = this.onlyNumber(retorno);
            retorno = this.removingCompleterFromStart(retorno, this.args.completer);
            return retorno || '0';
        };
        this.numberToText = input => {
            let retorno = this.args.emptyOrInvalid;
            if (!Number.isNaN(parseFloat(input))) {
                if (input.length <= this.args.fractionDigits) {
                    retorno = this.formatDecimal(input, '0', this.args.decimalSeparator);
                }
                else {
                    retorno = this.addingHundredsSeparator(input);
                }
            }
            return retorno;
        };
    }
    removeNegative(value) {
        let newValue = value;
        let negative = '';
        if (value) {
            if ((typeof value == 'number')) {
                if (value < 0) {
                    newValue = -newValue;
                    negative = '-';
                }
            }
            else {
                const str = value.toString();
                newValue = str.replace(/\-/g, '');
                const removidoQtdImparChars = (str.length - newValue.length) % 2 != 0;
                const valorZero = !str.match(/[1-9]/);
                if (removidoQtdImparChars && !valorZero) {
                    negative = '-';
                }
            }
        }
        return { negative, newValue };
    }
    /**
     *  12     returns  1200
     *  12.3   returns  1203
     *  12.34  returns  12.34
     * '12.34' returns '12.34'
     *  TODO usar mesma impl de CurrencyMaskDirective.formatNumberToText
     */
    normalizeNumber(input) {
        if ((typeof input !== 'number')) {
            return input;
        }
        const fraction = this.getFractionDigits(input);
        if (fraction === undefined) {
            return `${input}00`;
        }
        if (fraction.length === 1) {
            return `${input}0`;
        }
        return input;
    }
    getFractionDigits(input) {
        return input.toString().split('.')[1];
    }
}
SimpleMaskMoneyService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SimpleMaskMoneyService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SimpleMaskMoneyService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SimpleMaskMoneyService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SimpleMaskMoneyService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class FileSizePipe {
    transform(fileSizeInBytes) {
        return this.formatBytes(fileSizeInBytes, 2);
    }
    formatBytes(bytes, decimals) {
        if (bytes === 0) {
            return '0 Bytes';
        }
        const k = 1024;
        const dm = decimals || 2;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    }
}
FileSizePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: FileSizePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
FileSizePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: FileSizePipe, name: "fileSize" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: FileSizePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'fileSize'
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
const regex = new RegExp('{-?[0-9]+}', 'g');
class StringFormatPipe {
    transform(value, ...args) {
        return value.replace(regex, (item) => {
            const intVal = parseInt(item.substring(1, item.length - 1));
            let replace;
            if (intVal >= 0) {
                replace = args[intVal];
            }
            else if (intVal === -1) {
                replace = '{';
            }
            else if (intVal === -2) {
                replace = '}';
            }
            else {
                replace = '';
            }
            return replace;
        });
    }
}
StringFormatPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: StringFormatPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
StringFormatPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: StringFormatPipe, name: "stringFormat" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: StringFormatPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'stringFormat'
                }]
        }] });

const DEFAULT_ERRORS = {
    required: 'Campo obrigatório.',
    invalid: 'Formato inválido.',
    email: 'Email inválido.',
    cnpj: 'CNPJ inválido.',
    cpf: 'CPF inválido.',
    date: 'Data inválida.',
    minDate: 'Data deve ser maior ou igual a {0}.',
    maxDate: 'Data deve ser menor ou igual a {0}.',
    fileSize: 'Tamanho do arquivo deve ser menor ou igual a {0}.',
    fileType: 'Tipo inválido, tipos aceitos {0}.',
    decendio: 'Decêndio inválido.',
};
class LocalMessagesComponent {
    constructor() {
        this.requiredMessage = DEFAULT_ERRORS.required;
        this.patternMessage = DEFAULT_ERRORS.invalid;
        this.minlengthMessage = DEFAULT_ERRORS.invalid;
        this.maxlengthMessage = DEFAULT_ERRORS.invalid;
        this.emailMessage = DEFAULT_ERRORS.email;
        this.cnpjMessage = DEFAULT_ERRORS.cnpj;
        this.cpfMessage = DEFAULT_ERRORS.cpf;
        this.dateMessage = DEFAULT_ERRORS.date;
        this.dayMessage = DEFAULT_ERRORS.date;
        this.monthMessage = DEFAULT_ERRORS.date;
        this.yearMessage = DEFAULT_ERRORS.date;
        this.minDateMessage = DEFAULT_ERRORS.minDate;
        this.maxDateMessage = DEFAULT_ERRORS.maxDate;
        this.fileSizeMessage = DEFAULT_ERRORS.fileSize;
        this.fileTypeMessage = DEFAULT_ERRORS.fileType;
        this.decendioMessage = DEFAULT_ERRORS.decendio;
    }
    ngOnInit() { }
    hasOnlyError(key) {
        return this.control.hasError(key)
            && Object.keys(this.control.errors)
                .length === 1;
    }
}
LocalMessagesComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: LocalMessagesComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
LocalMessagesComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: LocalMessagesComponent, selector: "suite-rfb-local-messages", inputs: { requiredMessage: "requiredMessage", patternMessage: "patternMessage", minlengthMessage: "minlengthMessage", maxlengthMessage: "maxlengthMessage", emailMessage: "emailMessage", cnpjMessage: "cnpjMessage", cpfMessage: "cpfMessage", dateMessage: "dateMessage", dayMessage: "dayMessage", monthMessage: "monthMessage", yearMessage: "yearMessage", minDateMessage: "minDateMessage", maxDateMessage: "maxDateMessage", fileSizeMessage: "fileSizeMessage", fileTypeMessage: "fileTypeMessage", decendioMessage: "decendioMessage", control: "control" }, ngImport: i0, template: "<div class=\"local-messages\" *ngIf=\"control.invalid && (control.dirty || control.touched)\">\n  <ng-content></ng-content>\n  <div *ngIf=\"hasOnlyError('required')\">\n    {{requiredMessage}}\n  </div>\n  <div *ngIf=\"control.errors.pattern\">\n    {{patternMessage}}\n  </div>\n  <div *ngIf=\"control.errors.minlength\">\n    {{minlengthMessage}}\n  </div>\n  <div *ngIf=\"control.errors.maxlength\">\n    {{maxlengthMessage}}\n  </div>\n  <div *ngIf=\"control.errors.email\">\n    {{emailMessage}}\n  </div>\n  <div *ngIf=\"control.errors.invalidCnpj\">\n    {{cnpjMessage}}\n  </div>\n  <div *ngIf=\"control.errors.invalidCpf\">\n    {{cpfMessage}}\n  </div>\n  <div *ngIf=\"control.errors.invalidDate\">\n    {{dateMessage}}\n  </div>\n  <div *ngIf=\"control.errors.invalidDay\">\n    {{dayMessage}}\n  </div>\n  <div *ngIf=\"control.errors.invalidMonth\">\n    {{monthMessage}}\n  </div>\n  <div *ngIf=\"control.errors.invalidYear\">\n    {{yearMessage}}\n  </div>\n  <div *ngIf=\"control.errors.invalidMinDate\">\n    {{minDateMessage|stringFormat:(control.errors.invalidMinDate.minDate|date:'dd/MM/yyyy')}}\n  </div>\n  <div *ngIf=\"control.errors.invalidMaxDate\">\n    {{maxDateMessage|stringFormat:(control.errors.invalidMaxDate.maxDate|date:'dd/MM/yyyy')}}\n  </div>\n  <div *ngIf=\"control.errors.invalidFileSize\">\n    {{fileSizeMessage|stringFormat:(control.errors.invalidFileSize.maxSize|fileSize)}}\n  </div>\n  <div *ngIf=\"control.errors.invalidFileType\">\n    {{fileTypeMessage|stringFormat:(control.errors.invalidFileType.accept)}}\n  </div>\n  <div *ngIf=\"control.errors.invalidDecendio\">\n    {{decendioMessage}}\n  </div>\n</div>\n", styles: [".local-messages{color:#a8001f}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: i1.DatePipe, name: "date" }, { kind: "pipe", type: FileSizePipe, name: "fileSize" }, { kind: "pipe", type: StringFormatPipe, name: "stringFormat" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: LocalMessagesComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-local-messages', template: "<div class=\"local-messages\" *ngIf=\"control.invalid && (control.dirty || control.touched)\">\n  <ng-content></ng-content>\n  <div *ngIf=\"hasOnlyError('required')\">\n    {{requiredMessage}}\n  </div>\n  <div *ngIf=\"control.errors.pattern\">\n    {{patternMessage}}\n  </div>\n  <div *ngIf=\"control.errors.minlength\">\n    {{minlengthMessage}}\n  </div>\n  <div *ngIf=\"control.errors.maxlength\">\n    {{maxlengthMessage}}\n  </div>\n  <div *ngIf=\"control.errors.email\">\n    {{emailMessage}}\n  </div>\n  <div *ngIf=\"control.errors.invalidCnpj\">\n    {{cnpjMessage}}\n  </div>\n  <div *ngIf=\"control.errors.invalidCpf\">\n    {{cpfMessage}}\n  </div>\n  <div *ngIf=\"control.errors.invalidDate\">\n    {{dateMessage}}\n  </div>\n  <div *ngIf=\"control.errors.invalidDay\">\n    {{dayMessage}}\n  </div>\n  <div *ngIf=\"control.errors.invalidMonth\">\n    {{monthMessage}}\n  </div>\n  <div *ngIf=\"control.errors.invalidYear\">\n    {{yearMessage}}\n  </div>\n  <div *ngIf=\"control.errors.invalidMinDate\">\n    {{minDateMessage|stringFormat:(control.errors.invalidMinDate.minDate|date:'dd/MM/yyyy')}}\n  </div>\n  <div *ngIf=\"control.errors.invalidMaxDate\">\n    {{maxDateMessage|stringFormat:(control.errors.invalidMaxDate.maxDate|date:'dd/MM/yyyy')}}\n  </div>\n  <div *ngIf=\"control.errors.invalidFileSize\">\n    {{fileSizeMessage|stringFormat:(control.errors.invalidFileSize.maxSize|fileSize)}}\n  </div>\n  <div *ngIf=\"control.errors.invalidFileType\">\n    {{fileTypeMessage|stringFormat:(control.errors.invalidFileType.accept)}}\n  </div>\n  <div *ngIf=\"control.errors.invalidDecendio\">\n    {{decendioMessage}}\n  </div>\n</div>\n", styles: [".local-messages{color:#a8001f}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { requiredMessage: [{
                type: Input
            }], patternMessage: [{
                type: Input
            }], minlengthMessage: [{
                type: Input
            }], maxlengthMessage: [{
                type: Input
            }], emailMessage: [{
                type: Input
            }], cnpjMessage: [{
                type: Input
            }], cpfMessage: [{
                type: Input
            }], dateMessage: [{
                type: Input
            }], dayMessage: [{
                type: Input
            }], monthMessage: [{
                type: Input
            }], yearMessage: [{
                type: Input
            }], minDateMessage: [{
                type: Input
            }], maxDateMessage: [{
                type: Input
            }], fileSizeMessage: [{
                type: Input
            }], fileTypeMessage: [{
                type: Input
            }], decendioMessage: [{
                type: Input
            }], control: [{
                type: Input
            }] } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class HasErrorDirective {
    constructor(el, renderer) {
        this.el = el;
        this.renderer = renderer;
    }
    ngDoCheck() {
        const hasError = this.control ? this.control.invalid && (this.control.dirty || this.control.touched) : false;
        if (hasError) {
            this.renderer.addClass(this.el.nativeElement, 'has-error');
        }
        else {
            this.renderer.removeClass(this.el.nativeElement, 'has-error');
        }
    }
}
HasErrorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: HasErrorDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive });
HasErrorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.1.2", type: HasErrorDirective, selector: "[suiteRfbHasError]", inputs: { control: ["suiteRfbHasError", "control"] }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: HasErrorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[suiteRfbHasError]'
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }]; }, propDecorators: { control: [{
                type: Input,
                args: ['suiteRfbHasError']
            }] } });

// max chars including separation symbols
const SAFE_MAX_MASKED_LENGTH = 20;
const SAFE_MAX_INTEGER_PART = 13;
class CurrencyComponent {
    set maxIntegerPart(value) {
        this.maxLength = this.calcularMaxLength(+value);
    }
    set required(value) {
        this._required = !(value === false || value === 'false');
    }
    get required() {
        return this._required;
    }
    constructor(renderer, simpleMaskMoneyService, injector) {
        this.renderer = renderer;
        this.simpleMaskMoneyService = simpleMaskMoneyService;
        this.injector = injector;
        this.maxLength = SAFE_MAX_MASKED_LENGTH;
        this.inline = false;
        this.label = '';
        this.onChange = () => { };
        this.onTouched = () => { };
    }
    ngOnInit() {
        this.ngControl = this.injector.get(NgControl);
    }
    get value() {
        return this.modelValue;
    }
    set value(value) {
        this.modelValue = value;
        this.onChange(value);
        this.onTouched();
    }
    get formattedValue() {
        return this.viewValue ? this.viewValue : '';
    }
    writeValue(value) {
        if (value || value === 0) {
            this.modelValue = value;
            this.viewValue = this.simpleMaskMoneyService.format(value);
        }
        else {
            this.modelValue = undefined;
            this.viewValue = '';
        }
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        if (this.currencyInput) {
            this.renderer.setProperty(this.currencyInput.nativeElement, 'disabled', isDisabled);
        }
        else {
            setTimeout(() => this.setDisabledState(isDisabled));
        }
    }
    onInput(event) {
        const eventValue = event.target.value;
        let viewValue = this.simpleMaskMoneyService.format(eventValue);
        if (viewValue.length > SAFE_MAX_MASKED_LENGTH) {
            viewValue = this.simpleMaskMoneyService.format(viewValue.slice(0, SAFE_MAX_MASKED_LENGTH));
        }
        const modelValue = this.simpleMaskMoneyService.formatToNumber(viewValue);
        event.target.value = viewValue;
        this.viewValue = viewValue;
        this.value = modelValue;
    }
    /*
    * Calcular a quantidade maxima de caracteres que o campo
    * deverá ter. considerar os separadores de milhares e decimal e
    * que sempre será um número com duas decimais.
    *
    * Ex:    n,nn :: calcularMaxLength(1) -> 4
    *       nn,nn :: calcularMaxLength(2) -> 5
    *      nnn,nn :: calcularMaxLength(3) -> 6
    *    n,nnn,nn :: calcularMaxLength(3) -> 8
    */
    calcularMaxLength(_maxIntegerPart) {
        // normalizar, deve ficar entre 1 e 16;
        const maxIntegerPart = Math.max(1, Math.min(_maxIntegerPart, SAFE_MAX_INTEGER_PART));
        // fazer o calculo
        return Math.floor((maxIntegerPart - 1) / 3) + maxIntegerPart + 3;
    }
    onKeydown(event) {
        // Se nao for digitos continue
        if (!event.key.match('[0-9]')) {
            return true;
        }
        // Se já atingiu o valor máximo ignore o evento
        if (this.viewValue
            && this.viewValue.length === this.maxLength
            && !this.viewValue.startsWith('0')) {
            event.preventDefault();
            return false;
        }
        return true;
    }
    onKeyup(event) {
        const eventValue = event.target.value;
        const eventKey = event.key;
        const isBlankOrZero = (eventValue === '' || eventValue === '0,00');
        const isBackspace = (eventKey === 'Backspace');
        if (isBlankOrZero && isBackspace) {
            event.target.value = '';
            this.viewValue = '';
            this.value = undefined;
        }
    }
    onBlur() {
        this.ngControl.control.markAsTouched();
    }
}
CurrencyComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: CurrencyComponent, deps: [{ token: i0.Renderer2 }, { token: SimpleMaskMoneyService }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Component });
CurrencyComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: CurrencyComponent, selector: "suite-rfb-currency", inputs: { inline: "inline", label: "label", maxIntegerPart: "maxIntegerPart", required: "required", value: "value" }, providers: [{
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => CurrencyComponent)
        }], viewQueries: [{ propertyName: "currencyInput", first: true, predicate: ["currencyInput"], descendants: true }], ngImport: i0, template: "<ng-container *ngIf=\"inline\">\n  <ng-container *ngTemplateOutlet=\"template\"></ng-container>\n</ng-container>\n\n<ng-container *ngIf=\"!inline\">\n  <div class=\"form-group\" [suiteRfbHasError]=\"ngControl.control\">\n    <label *ngIf=\"label\" class=\"control-label\" [class.obrigatorio]=\"required\">{{label}}</label>\n    <ng-container *ngTemplateOutlet=\"template\"></ng-container>\n    <suite-rfb-local-messages [control]=\"ngControl.control\"></suite-rfb-local-messages>\n  </div>\n</ng-container>\n\n<ng-template #template>\n  <input type=\"text\" #currencyInput\n    class=\"form-control\"\n    (input)=\"onInput($event)\"\n    (keyup)=\"onKeyup($event)\"\n    (keydown)=\"onKeydown($event)\"\n    [required]=\"required\"\n    (blur)=\"onBlur()\"\n    [value]=\"formattedValue\">\n</ng-template>\n", styles: [""], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: LocalMessagesComponent, selector: "suite-rfb-local-messages", inputs: ["requiredMessage", "patternMessage", "minlengthMessage", "maxlengthMessage", "emailMessage", "cnpjMessage", "cpfMessage", "dateMessage", "dayMessage", "monthMessage", "yearMessage", "minDateMessage", "maxDateMessage", "fileSizeMessage", "fileTypeMessage", "decendioMessage", "control"] }, { kind: "directive", type: HasErrorDirective, selector: "[suiteRfbHasError]", inputs: ["suiteRfbHasError"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: CurrencyComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-currency', providers: [{
                            provide: NG_VALUE_ACCESSOR,
                            multi: true,
                            useExisting: forwardRef(() => CurrencyComponent)
                        }], template: "<ng-container *ngIf=\"inline\">\n  <ng-container *ngTemplateOutlet=\"template\"></ng-container>\n</ng-container>\n\n<ng-container *ngIf=\"!inline\">\n  <div class=\"form-group\" [suiteRfbHasError]=\"ngControl.control\">\n    <label *ngIf=\"label\" class=\"control-label\" [class.obrigatorio]=\"required\">{{label}}</label>\n    <ng-container *ngTemplateOutlet=\"template\"></ng-container>\n    <suite-rfb-local-messages [control]=\"ngControl.control\"></suite-rfb-local-messages>\n  </div>\n</ng-container>\n\n<ng-template #template>\n  <input type=\"text\" #currencyInput\n    class=\"form-control\"\n    (input)=\"onInput($event)\"\n    (keyup)=\"onKeyup($event)\"\n    (keydown)=\"onKeydown($event)\"\n    [required]=\"required\"\n    (blur)=\"onBlur()\"\n    [value]=\"formattedValue\">\n</ng-template>\n" }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: SimpleMaskMoneyService }, { type: i0.Injector }]; }, propDecorators: { inline: [{
                type: Input
            }], label: [{
                type: Input
            }], maxIntegerPart: [{
                type: Input
            }], required: [{
                type: Input
            }], currencyInput: [{
                type: ViewChild,
                args: ['currencyInput']
            }], value: [{
                type: Input,
                args: ['value']
            }] } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
const BLACK_LIST$1 = [
    '00000000000000',
    '11111111111111',
    '22222222222222',
    '33333333333333',
    '44444444444444',
    '55555555555555',
    '66666666666666',
    '77777777777777',
    '88888888888888',
    '99999999999999'
];
class CnpjValidatorDirective {
    constructor() {
        this.valFn = Validators.nullValidator;
    }
    ngOnInit() {
        this.valFn = cnpjValidator();
    }
    validate(control) {
        return this.valFn(control);
    }
}
CnpjValidatorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: CnpjValidatorDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
CnpjValidatorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.1.2", type: CnpjValidatorDirective, selector: "[suiteRfbValidCnpj]", providers: [{ provide: NG_VALIDATORS, useExisting: CnpjValidatorDirective, multi: true }], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: CnpjValidatorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[suiteRfbValidCnpj]',
                    providers: [{ provide: NG_VALIDATORS, useExisting: CnpjValidatorDirective, multi: true }]
                }]
        }] });
function cnpjValidator() {
    return (control) => {
        const value = control.value;
        const validCnpj = isValidCnpj(value);
        return validCnpj ? null : { 'invalidCnpj': { value } };
    };
}
function isValidCnpj(maskedCnpj) {
    if (!maskedCnpj) {
        return true;
    }
    const unmaskedCnpj = maskedCnpj.replace(/[^\d]+/g, '');
    if (!unmaskedCnpj) {
        return true;
    }
    if (unmaskedCnpj.length !== 14 || isBlackListed$1(unmaskedCnpj)) {
        return false;
    }
    const dvs = unmaskedCnpj.substring(12);
    const dv1 = calculateDv$1(unmaskedCnpj.substring(0, 12));
    const dv2 = calculateDv$1(unmaskedCnpj.substring(0, 13));
    return (dvs.charAt(0) === dv1.toString() && dvs.charAt(1) === dv2.toString());
}
function isBlackListed$1(cnpj) {
    return BLACK_LIST$1.indexOf(cnpj) >= 0;
}
function calculateDv$1(base) {
    const length = base.length;
    let sum = 0;
    let pos = length - 7;
    for (let i = length; i >= 1; i--) {
        sum += Number.parseInt(base.charAt(length - i)) * pos--;
        if (pos < 2) {
            pos = 9;
        }
    }
    const result = sum % 11 < 2 ? 0 : 11 - sum % 11;
    return result;
}

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
const BLACK_LIST = [
    '00000000000',
    '11111111111',
    '22222222222',
    '33333333333',
    '44444444444',
    '55555555555',
    '66666666666',
    '77777777777',
    '88888888888',
    '99999999999'
];
class CpfValidatorDirective {
    constructor() {
        this.valFn = Validators.nullValidator;
    }
    ngOnInit() {
        this.valFn = cpfValidator();
    }
    validate(control) {
        return this.valFn(control);
    }
}
CpfValidatorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: CpfValidatorDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
CpfValidatorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.1.2", type: CpfValidatorDirective, selector: "[suiteRfbValidCpf]", providers: [{ provide: NG_VALIDATORS, useExisting: CpfValidatorDirective, multi: true }], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: CpfValidatorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[suiteRfbValidCpf]',
                    providers: [{ provide: NG_VALIDATORS, useExisting: CpfValidatorDirective, multi: true }]
                }]
        }] });
function cpfValidator() {
    return (control) => {
        const value = control.value;
        const validCpf = isValidCpf(value);
        return validCpf ? null : { 'invalidCpf': { value } };
    };
}
function isValidCpf(maskedCpf) {
    if (!maskedCpf) {
        return true;
    }
    const unmaskedCpf = maskedCpf.replace(/[^\d]+/g, '');
    if (!unmaskedCpf) {
        return true;
    }
    if (unmaskedCpf.length !== 11 || isBlackListed(unmaskedCpf)) {
        return false;
    }
    const dvs = unmaskedCpf.substring(9);
    const dv1 = calculateDv(unmaskedCpf.substring(0, 9));
    const dv2 = calculateDv(unmaskedCpf.substring(0, 10));
    return (dvs.charAt(0) === dv1.toString() && dvs.charAt(1) === dv2.toString());
}
function isBlackListed(unmaskedCpf) {
    return BLACK_LIST.indexOf(unmaskedCpf) >= 0;
}
function calculateDv(base) {
    const length = base.length;
    let sum = 0;
    for (let i = 0; i < length; i++) {
        sum += Number.parseInt(base.charAt(i)) * ((length + 1) - i);
    }
    let result = 11 - (sum % 11);
    if (result === 10 || result === 11) {
        result = 0;
    }
    return result;
}

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class DateValidatorDirective {
    constructor() {
        this.format = 'DD/MM/YYYY';
        this.valFn = Validators.nullValidator;
    }
    ngOnInit() {
        this.valFn = dateValidator(this.format);
    }
    ngOnChanges(changes) {
        const formatChanged = changes['suiteRfbValidDate'];
        const newFormat = (formatChanged) ? formatChanged.currentValue : this.format;
        this.valFn = dateValidator(newFormat);
    }
    validate(control) {
        return this.valFn(control);
    }
}
DateValidatorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: DateValidatorDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
DateValidatorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.1.2", type: DateValidatorDirective, selector: "[suiteRfbValidDate]", inputs: { format: ["suiteRfbValidDate", "format"] }, providers: [{ provide: NG_VALIDATORS, useExisting: DateValidatorDirective, multi: true }], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: DateValidatorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[suiteRfbValidDate]',
                    providers: [{ provide: NG_VALIDATORS, useExisting: DateValidatorDirective, multi: true }]
                }]
        }], propDecorators: { format: [{
                type: Input,
                args: ['suiteRfbValidDate']
            }] } });
function dateValidator(format) {
    return (control) => {
        let errors = null;
        const value = control.value;
        if (value) {
            const date = moment(value, format, true);
            if (!date.isValid()) {
                errors = { 'invalidDate': { value } };
            }
        }
        return errors;
    };
}

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class DisabledDaysValidatorDirective {
    constructor() {
        this.valFn = Validators.nullValidator;
    }
    ngOnInit() {
        this.valFn = disabledDaysValidator(this.disabledDays);
    }
    ngOnChanges(changes) {
        const disabledDaysChanged = changes['suiteRfbDisabledDays'];
        const newDisabledDays = (disabledDaysChanged) ? disabledDaysChanged.currentValue : this.disabledDays;
        this.valFn = disabledDaysValidator(newDisabledDays);
    }
    validate(control) {
        return this.valFn(control);
    }
}
DisabledDaysValidatorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: DisabledDaysValidatorDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
DisabledDaysValidatorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.1.2", type: DisabledDaysValidatorDirective, selector: "[suiteRfbDisabledDays]", inputs: { disabledDays: ["suiteRfbDisabledDays", "disabledDays"] }, providers: [{ provide: NG_VALIDATORS, useExisting: DisabledDaysValidatorDirective, multi: true }], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: DisabledDaysValidatorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[suiteRfbDisabledDays]',
                    providers: [{ provide: NG_VALIDATORS, useExisting: DisabledDaysValidatorDirective, multi: true }]
                }]
        }], propDecorators: { disabledDays: [{
                type: Input,
                args: ['suiteRfbDisabledDays']
            }] } });
function disabledDaysValidator(disabledDays) {
    return (control) => {
        let errors = null;
        const value = control.value;
        if (value && value instanceof Date && disabledDays) {
            const validDay = isValidDay(value, disabledDays);
            if (!validDay) {
                errors = { 'invalidDay': { value } };
            }
        }
        return errors;
    };
}
function isValidDay(date, disabledDays) {
    const dateMoment = moment(date);
    return disabledDays.every(day => !dateMoment.isSame(day, 'day'));
}

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class DisabledMonthsValidatorDirective {
    constructor() {
        this.valFn = Validators.nullValidator;
    }
    ngOnInit() {
        this.valFn = disabledMonthsValidator(this.disabledMonths);
    }
    ngOnChanges(changes) {
        const disabledMonthsChanged = changes['suiteRfbDisabledMonths'];
        const newDisabledMonths = (disabledMonthsChanged) ? disabledMonthsChanged.currentValue : this.disabledMonths;
        this.valFn = disabledMonthsValidator(newDisabledMonths);
    }
    validate(control) {
        return this.valFn(control);
    }
}
DisabledMonthsValidatorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: DisabledMonthsValidatorDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
DisabledMonthsValidatorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.1.2", type: DisabledMonthsValidatorDirective, selector: "[suiteRfbDisabledMonths]", inputs: { disabledMonths: ["suiteRfbDisabledMonths", "disabledMonths"] }, providers: [{ provide: NG_VALIDATORS, useExisting: DisabledMonthsValidatorDirective, multi: true }], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: DisabledMonthsValidatorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[suiteRfbDisabledMonths]',
                    providers: [{ provide: NG_VALIDATORS, useExisting: DisabledMonthsValidatorDirective, multi: true }]
                }]
        }], propDecorators: { disabledMonths: [{
                type: Input,
                args: ['suiteRfbDisabledMonths']
            }] } });
function disabledMonthsValidator(disabledMonths) {
    return (control) => {
        let errors = null;
        const value = control.value;
        if (value && value instanceof Date && disabledMonths) {
            const validMonth = isValidMonth(value, disabledMonths);
            if (!validMonth) {
                errors = { 'invalidMonth': { value } };
            }
        }
        return errors;
    };
}
function isValidMonth(date, disabledMonths) {
    const dateMoment = moment(date);
    return disabledMonths.every(month => !dateMoment.isSame(month, 'month'));
}

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class DisabledYearsValidatorDirective {
    constructor() {
        this.valFn = Validators.nullValidator;
    }
    ngOnInit() {
        this.valFn = disabledYearsValidator(this.disabledYears);
    }
    ngOnChanges(changes) {
        const disabledYearsChanged = changes['suiteRfbDisabledYears'];
        const newDisabledYears = (disabledYearsChanged) ? disabledYearsChanged.currentValue : this.disabledYears;
        this.valFn = disabledYearsValidator(newDisabledYears);
    }
    validate(control) {
        return this.valFn(control);
    }
}
DisabledYearsValidatorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: DisabledYearsValidatorDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
DisabledYearsValidatorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.1.2", type: DisabledYearsValidatorDirective, selector: "[suiteRfbDisabledYears]", inputs: { disabledYears: ["suiteRfbDisabledYears", "disabledYears"] }, providers: [{ provide: NG_VALIDATORS, useExisting: DisabledYearsValidatorDirective, multi: true }], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: DisabledYearsValidatorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[suiteRfbDisabledYears]',
                    providers: [{ provide: NG_VALIDATORS, useExisting: DisabledYearsValidatorDirective, multi: true }]
                }]
        }], propDecorators: { disabledYears: [{
                type: Input,
                args: ['suiteRfbDisabledYears']
            }] } });
function disabledYearsValidator(disabledYears) {
    return (control) => {
        let errors = null;
        const value = control.value;
        if (value && value instanceof Date && disabledYears) {
            const validMonth = isValidYear(value, disabledYears);
            if (!validMonth) {
                errors = { 'invalidYear': { value } };
            }
        }
        return errors;
    };
}
function isValidYear(date, disabledYears) {
    const dateMoment = moment(date);
    return !disabledYears ? true : disabledYears.every(year => !dateMoment.isSame(year, 'year'));
}

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class MaxFileSizeValidatorDirective {
    constructor() {
        this.valFn = Validators.nullValidator;
    }
    ngOnInit() {
        this.valFn = fileSizeValidator(this.maxSize);
    }
    ngOnChanges(changes) {
        const maxSizeChanged = changes['suiteRfbMaxFileSize'];
        const newMaxSize = (maxSizeChanged) ? maxSizeChanged.currentValue : this.maxSize;
        this.valFn = fileSizeValidator(newMaxSize);
    }
    validate(control) {
        return this.valFn(control);
    }
}
MaxFileSizeValidatorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: MaxFileSizeValidatorDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
MaxFileSizeValidatorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.1.2", type: MaxFileSizeValidatorDirective, selector: "[suiteRfbMaxFileSize]", inputs: { maxSize: ["suiteRfbMaxFileSize", "maxSize"] }, providers: [{ provide: NG_VALIDATORS, useExisting: MaxFileSizeValidatorDirective, multi: true }], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: MaxFileSizeValidatorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[suiteRfbMaxFileSize]',
                    providers: [{ provide: NG_VALIDATORS, useExisting: MaxFileSizeValidatorDirective, multi: true }]
                }]
        }], propDecorators: { maxSize: [{
                type: Input,
                args: ['suiteRfbMaxFileSize']
            }] } });
function fileSizeValidator(maxSize) {
    return (control) => {
        let errors = null;
        const value = control.value;
        if (value && value instanceof File && maxSize) {
            const size = value.size;
            if (size > maxSize) {
                errors = { 'invalidFileSize': { value, 'maxSize': maxSize } };
            }
        }
        return errors;
    };
}

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class MaxDateValidatorDirective {
    constructor() {
        this.valFn = Validators.nullValidator;
    }
    ngOnInit() {
        this.valFn = maxDateValidator(this.maxDate);
    }
    ngOnChanges(changes) {
        const maxDateChanged = changes['suiteRfbMaxDate'];
        const newMaxDate = (maxDateChanged) ? maxDateChanged.currentValue : this.maxDate;
        this.valFn = maxDateValidator(newMaxDate);
    }
    validate(control) {
        return this.valFn(control);
    }
}
MaxDateValidatorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: MaxDateValidatorDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
MaxDateValidatorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.1.2", type: MaxDateValidatorDirective, selector: "[suiteRfbMaxDate]", inputs: { maxDate: ["suiteRfbMaxDate", "maxDate"] }, providers: [{ provide: NG_VALIDATORS, useExisting: MaxDateValidatorDirective, multi: true }], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: MaxDateValidatorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[suiteRfbMaxDate]',
                    providers: [{ provide: NG_VALIDATORS, useExisting: MaxDateValidatorDirective, multi: true }]
                }]
        }], propDecorators: { maxDate: [{
                type: Input,
                args: ['suiteRfbMaxDate']
            }] } });
function maxDateValidator(maxDate) {
    return (control) => {
        let errors = null;
        const value = control.value;
        if (value && value instanceof Date && maxDate) {
            const dateMoment = moment(value);
            const maxDateMoment = moment(maxDate);
            if (dateMoment.isAfter(maxDateMoment, 'day')) {
                errors = { 'invalidMaxDate': { value, 'maxDate': maxDate } };
            }
        }
        return errors;
    };
}

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class MinDateValidatorDirective {
    constructor() {
        this.valFn = Validators.nullValidator;
    }
    ngOnInit() {
        this.valFn = minDateValidator(this.minDate);
    }
    ngOnChanges(changes) {
        const minDateChanged = changes['suiteRfbMinDate'];
        const newMinDate = (minDateChanged) ? minDateChanged.currentValue : this.minDate;
        this.valFn = minDateValidator(newMinDate);
    }
    validate(control) {
        return this.valFn(control);
    }
}
MinDateValidatorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: MinDateValidatorDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
MinDateValidatorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.1.2", type: MinDateValidatorDirective, selector: "[suiteRfbMinDate]", inputs: { minDate: ["suiteRfbMinDate", "minDate"] }, providers: [{ provide: NG_VALIDATORS, useExisting: MinDateValidatorDirective, multi: true }], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: MinDateValidatorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[suiteRfbMinDate]',
                    providers: [{ provide: NG_VALIDATORS, useExisting: MinDateValidatorDirective, multi: true }]
                }]
        }], propDecorators: { minDate: [{
                type: Input,
                args: ['suiteRfbMinDate']
            }] } });
function minDateValidator(minDate) {
    return (control) => {
        let errors = null;
        const value = control.value;
        if (value && value instanceof Date && minDate) {
            const dateMoment = moment(value);
            const minDateMoment = moment(minDate);
            if (dateMoment.isBefore(minDateMoment, 'day')) {
                errors = { 'invalidMinDate': { value, 'minDate': minDate } };
            }
        }
        return errors;
    };
}

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class FromNowPipe {
    constructor(changeDetectorRef, ngZone, windowService) {
        this.changeDetectorRef = changeDetectorRef;
        this.ngZone = ngZone;
        this.windowService = windowService;
    }
    transform(date) {
        this.removeTimer();
        this.timer = this.ngZone.runOutsideAngular(() => {
            return this.window.setTimeout(() => this.ngZone.run(() => this.changeDetectorRef.markForCheck()), 1000);
        });
        return !date ? undefined : moment(date).fromNow();
    }
    ngOnDestroy() {
        this.removeTimer();
    }
    removeTimer() {
        if (this.timer) {
            this.window.clearTimeout(this.timer);
            this.timer = null;
        }
    }
    get window() {
        return this.windowService.get();
    }
}
FromNowPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: FromNowPipe, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.NgZone }, { token: WindowService }], target: i0.ɵɵFactoryTarget.Pipe });
FromNowPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: FromNowPipe, name: "fromNow", pure: false });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: FromNowPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'fromNow',
                    pure: false
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.NgZone }, { type: WindowService }]; } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class JoinPipe {
    transform(input, separator = '') {
        return (!input) ? undefined : input.join(separator);
    }
}
JoinPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: JoinPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
JoinPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: JoinPipe, name: "join" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: JoinPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'join'
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class OutputMaskPipe {
    transform(originalValue, mask) {
        const stringValue = '' + originalValue;
        let value = stringValue.replace(/\D/g, '');
        const pad = mask.replace(/\D/g, '').replace(/9/g, '0');
        const maskedValue = pad.substring(0, pad.length - value.length) + value;
        let maskedValuePos = 0;
        value = '';
        for (let i = 0; i < mask.length; i++) {
            if (isNaN(parseInt(mask.charAt(i)))) {
                value += mask.charAt(i);
            }
            else {
                value += maskedValue[maskedValuePos++];
            }
        }
        if (value.indexOf('_') > -1) {
            value = value.substr(0, value.indexOf('_'));
        }
        return value;
    }
}
OutputMaskPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: OutputMaskPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
OutputMaskPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: OutputMaskPipe, name: "outputMask" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: OutputMaskPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'outputMask'
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class StripHtmlPipe {
    transform(htmlString) {
        return !htmlString ? (htmlString === '' ? '' : undefined) : htmlString.replace(/<.*?>/g, '');
    }
}
StripHtmlPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: StripHtmlPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
StripHtmlPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: StripHtmlPipe, name: "stripHtml" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: StripHtmlPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'stripHtml'
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbOutputMaskModule {
}
SuiteRfbOutputMaskModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbOutputMaskModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbOutputMaskModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbOutputMaskModule, declarations: [FileSizePipe,
        FromNowPipe,
        JoinPipe,
        OutputMaskPipe,
        StringFormatPipe,
        StripHtmlPipe], imports: [CommonModule], exports: [FileSizePipe,
        FromNowPipe,
        JoinPipe,
        OutputMaskPipe,
        StringFormatPipe,
        StripHtmlPipe] });
SuiteRfbOutputMaskModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbOutputMaskModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbOutputMaskModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                    ],
                    declarations: [
                        FileSizePipe,
                        FromNowPipe,
                        JoinPipe,
                        OutputMaskPipe,
                        StringFormatPipe,
                        StripHtmlPipe,
                    ],
                    exports: [
                        FileSizePipe,
                        FromNowPipe,
                        JoinPipe,
                        OutputMaskPipe,
                        StringFormatPipe,
                        StripHtmlPipe,
                    ],
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbInputValidationModule {
}
SuiteRfbInputValidationModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbInputValidationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbInputValidationModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbInputValidationModule, declarations: [LocalMessagesComponent,
        CnpjValidatorDirective,
        CpfValidatorDirective,
        DateValidatorDirective,
        DisabledDaysValidatorDirective,
        DisabledMonthsValidatorDirective,
        DisabledYearsValidatorDirective,
        MaxFileSizeValidatorDirective,
        HasErrorDirective,
        MaxDateValidatorDirective,
        MinDateValidatorDirective], imports: [CommonModule,
        FormsModule,
        SuiteRfbOutputMaskModule], exports: [LocalMessagesComponent,
        CnpjValidatorDirective,
        CpfValidatorDirective,
        DateValidatorDirective,
        DisabledDaysValidatorDirective,
        DisabledMonthsValidatorDirective,
        DisabledYearsValidatorDirective,
        MaxFileSizeValidatorDirective,
        HasErrorDirective,
        MaxDateValidatorDirective,
        MinDateValidatorDirective] });
SuiteRfbInputValidationModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbInputValidationModule, imports: [CommonModule,
        FormsModule,
        SuiteRfbOutputMaskModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbInputValidationModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        SuiteRfbOutputMaskModule,
                    ],
                    declarations: [
                        LocalMessagesComponent,
                        CnpjValidatorDirective,
                        CpfValidatorDirective,
                        DateValidatorDirective,
                        DisabledDaysValidatorDirective,
                        DisabledMonthsValidatorDirective,
                        DisabledYearsValidatorDirective,
                        MaxFileSizeValidatorDirective,
                        HasErrorDirective,
                        MaxDateValidatorDirective,
                        MinDateValidatorDirective,
                    ],
                    exports: [
                        LocalMessagesComponent,
                        CnpjValidatorDirective,
                        CpfValidatorDirective,
                        DateValidatorDirective,
                        DisabledDaysValidatorDirective,
                        DisabledMonthsValidatorDirective,
                        DisabledYearsValidatorDirective,
                        MaxFileSizeValidatorDirective,
                        HasErrorDirective,
                        MaxDateValidatorDirective,
                        MinDateValidatorDirective,
                    ],
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbCurrencyModule {
}
SuiteRfbCurrencyModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbCurrencyModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbCurrencyModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbCurrencyModule, declarations: [CurrencyComponent], imports: [CommonModule,
        FormsModule,
        SuiteRfbInputValidationModule], exports: [CurrencyComponent] });
SuiteRfbCurrencyModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbCurrencyModule, imports: [CommonModule,
        FormsModule,
        SuiteRfbInputValidationModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbCurrencyModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        SuiteRfbInputValidationModule,
                    ],
                    declarations: [
                        CurrencyComponent,
                    ],
                    exports: [
                        CurrencyComponent,
                    ],
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class DateService {
    constructor() { }
    format(date, format) {
        return (!date) ? undefined : moment(date).format(format);
    }
    defParse(date) {
        const momentValue = moment(date);
        return momentValue && momentValue.isValid() ? momentValue.toDate() : undefined;
    }
    parse(date, format) {
        const momentValue = date ? moment(date, format, true) : undefined;
        return momentValue && momentValue.isValid() ? momentValue.toDate() : undefined;
    }
    buildDisabledDates(disabledDays, disabledMonths, disabledYears) {
        const disabledDates = [];
        disabledDays.forEach(day => disabledDates.push({ date: day, mode: 'day' }));
        disabledMonths.forEach(month => disabledDates.push({ date: month, mode: 'month' }));
        disabledYears.forEach(year => disabledDates.push({ date: year, mode: 'year' }));
        return disabledDates;
    }
    generateDates(disabledDates) {
        const ret = [];
        disabledDates.forEach(dd => {
            if (dd.mode === 'day') {
                ret.push(dd.date);
            }
            else if (dd.mode === 'month') {
                const ini = moment(dd.date).startOf('month');
                const fim = moment(dd.date).endOf('month');
                ret.push(...this.dateRange(ini, fim));
            }
            else if (dd.mode === 'year') {
                const ini = moment(dd.date).startOf('year');
                const fim = moment(dd.date).endOf('year');
                ret.push(...this.dateRange(ini, fim));
            }
        });
        return ret;
    }
    dateRange(ini, fim) {
        const ret = [];
        let m = moment(ini);
        while (m.isSameOrBefore(fim)) {
            ret.push(m.toDate());
            m = m.add(1, 'day');
        }
        return ret;
    }
    today() {
        return moment().startOf('day').toDate();
    }
    isNotBetween(date, minDate, maxDate) {
        const todayMoment = moment(date);
        const minDateMoment = minDate ? moment(minDate) : undefined;
        const maxDateMoment = maxDate ? moment(maxDate) : undefined;
        if (minDateMoment && maxDateMoment) {
            return !todayMoment.isBetween(minDateMoment, maxDateMoment, 'day', '[]');
        }
        else if (minDateMoment) {
            return todayMoment.isBefore(minDateMoment, 'day');
        }
        else if (maxDateMoment) {
            return todayMoment.isAfter(maxDateMoment, 'day');
        }
        else {
            return false;
        }
    }
    isSameDay(d1, d2) {
        const m1 = moment(d1);
        const m2 = moment(d2);
        return m1.isSame(m2, 'day');
    }
}
DateService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: DateService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
DateService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: DateService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: DateService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class InputMaskService {
    constructor() { }
    maskInput(input, mask) {
        let value = input.replace(/\D/g, '');
        const pad = mask.replace(/\D/g, '').replace(/9/g, '_');
        const maskedValue = value + pad.substring(0, pad.length - value.length);
        let maskedValuePos = 0;
        value = '';
        for (let i = 0; i < mask.length; i++) {
            if (Number.isNaN(parseInt(mask.charAt(i)))) {
                value += mask.charAt(i);
            }
            else {
                value += maskedValue[maskedValuePos++];
            }
        }
        if (value.indexOf('_') > -1) {
            value = value.substr(0, value.indexOf('_'));
        }
        return value;
    }
}
InputMaskService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: InputMaskService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
InputMaskService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: InputMaskService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: InputMaskService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class InputMaskDirective {
    constructor(renderer, el, configService, inputMaskService) {
        this.renderer = renderer;
        this.el = el;
        this.configService = configService;
        this.inputMaskService = inputMaskService;
    }
    ngOnInit() {
        this.setMaxLength();
        this.setInvalidAllowed();
        this.formatarValorSeNecessario();
    }
    formatarValorSeNecessario() {
        const value = this.el.nativeElement.value;
        if (value) {
            const maskedValue = this.inputMaskService.maskInput(this.el.nativeElement.value, this.mask);
            if (maskedValue != value) {
                setTimeout(() => {
                    this.setValue(maskedValue);
                }, 0);
            }
        }
    }
    onKeyup($event) {
        // retorna caso pressionado backspace/tab/del/left/right/home
        if ($event.key === 'Backspace' || $event.key === 'Tab' || $event.key === 'Delete'
            || $event.key === 'Del' || $event.key === 'Left' || $event.key === 'Right'
            || $event.key === 'Home') {
            return;
        }
        const maskedValue = this.inputMaskService.maskInput($event.target.value, this.mask);
        this.setValue(maskedValue);
    }
    onBlur($event) {
        if (!this._invalidAllowed) {
            const isWrongLength = ($event.target.value.length !== this.mask.length);
            if (isWrongLength) {
                $event.stopPropagation();
                this.setValue('');
            }
        }
    }
    setValue(value) {
        this.el.nativeElement.value = value;
        this.el.nativeElement.dispatchEvent(new CustomEvent('input'));
    }
    setMaxLength() {
        this._maxlength = this.mask.length;
        this.renderer.setAttribute(this.el.nativeElement, 'maxLength', this._maxlength.toString());
    }
    setInvalidAllowed() {
        const localConfig = this.invalidAllowed;
        const globalConfig = this.configService.isInputMaskInvalidAllowed();
        this._invalidAllowed = (localConfig !== undefined) ? localConfig : globalConfig;
    }
}
InputMaskDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: InputMaskDirective, deps: [{ token: i0.Renderer2 }, { token: i0.ElementRef }, { token: ConfigService }, { token: InputMaskService }], target: i0.ɵɵFactoryTarget.Directive });
InputMaskDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.1.2", type: InputMaskDirective, selector: "[suiteRfbInputMask]", inputs: { mask: ["suiteRfbInputMask", "mask"], invalidAllowed: ["suiteRfbInputMaskInvalidAllowed", "invalidAllowed"] }, host: { listeners: { "keyup": "onKeyup($event)", "blur": "onBlur($event)" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: InputMaskDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[suiteRfbInputMask]' }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: i0.ElementRef }, { type: ConfigService }, { type: InputMaskService }]; }, propDecorators: { mask: [{
                type: Input,
                args: ['suiteRfbInputMask']
            }], invalidAllowed: [{
                type: Input,
                args: ['suiteRfbInputMaskInvalidAllowed']
            }], onKeyup: [{
                type: HostListener,
                args: ['keyup', ['$event']]
            }], onBlur: [{
                type: HostListener,
                args: ['blur', ['$event']]
            }] } });

class DatePickerComponent {
    get value() {
        return this.modelValue;
    }
    set value(value) {
        const changed = !this.sameValues(this.modelValue, value);
        this.modelValue = value;
        if (changed) {
            this.onTouched();
            this.onChange(value);
        }
    }
    get formattedValue() {
        return this.viewValue ? this.viewValue : '';
    }
    set formattedValue(viewValue) {
        this.viewValue = viewValue;
    }
    writeValue(value) {
        if (value && !value.getTime) {
            value = this.dateService.defParse(value);
        }
        this.viewValue = this.dateService.format(value, this.format);
        this.modelValue = value;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    set required(value) {
        this._required = !(value === false || value === 'false');
    }
    get required() {
        return this._required;
    }
    constructor(injector, dateService) {
        this.injector = injector;
        this.dateService = dateService;
        this.onChange = () => { };
        this.onTouched = () => { };
        // ... ControlValueAccessor methods
        this.direction = 'bottom';
        this.adaptivePosition = false;
        this.format = 'DD/MM/YYYY';
        this.inline = false;
        this.iconSize = '32px';
        this.inputGroupClass = 'input-group date';
        this.label = '';
        this.outsideClick = true;
        this.showWeeks = false;
        this.disabledDays = [];
        this.disabledMonths = [];
        this.disabledYears = [];
        this.withTime = false;
        this.disabled = false;
        this.minMode = 'day';
        this._dateValidator = (control) => {
            let errors = null;
            const isInvalidDate = this.formattedValue && !control.value;
            if (isInvalidDate) {
                errors = { 'invalidDate': { value: this.formattedValue } };
            }
            return errors;
        };
    }
    ngOnInit() {
        this.ngControl = this.injector.get(NgControl);
        this.iconSize = this.inline ? '24px' : '32px';
        this.bsConfig = {
            selectFromOtherMonth: true,
            dayLabel: 'DD',
            monthLabel: 'MMM',
            dateInputFormat: this.format,
            showWeekNumbers: this.showWeeks,
            showTodayButton: !this.disabled,
            showClearButton: !this.disabled,
            clearButtonLabel: 'Limpar',
            todayButtonLabel: 'Hoje',
            minMode: this.minMode,
            adaptivePosition: this.adaptivePosition,
            withTimepicker: this.withTime,
            keepDatepickerOpened: this.withTime,
            customTodayClass: 'today',
            returnFocusToInput: true
        };
        this.inputMask = this.format.replace(/[DMYHS]/gi, '9');
    }
    ngAfterViewInit() {
        this._setValidators();
    }
    ngOnChanges(changes) {
        // days, months or years disabled change
        const disabledDays = this._getDisabledDays(changes);
        const disabledMonths = this._getDisabledMonths(changes);
        const disabledYears = this._getDisabledYears(changes);
        if (disabledDays || disabledMonths || disabledYears) {
            const disabledDates = this.dateService.buildDisabledDates(disabledDays || [], disabledMonths || [], disabledYears || []);
            this.disabledDates = this.dateService.generateDates(disabledDates);
        }
    }
    onInput(viewValue) {
        if (viewValue.length === 0 || viewValue.length === this.format.length) {
            this.formattedValue = viewValue;
            this.value = this.dateService.parse(viewValue, this.format);
        }
    }
    show() {
        if (!this.disabled) {
            this.datepicker.show();
        }
    }
    hide() {
        if (!this.disabled) {
            this.datepicker?.hide();
        }
    }
    toggle() {
        if (!this.disabled) {
            this.datepicker.toggle();
        }
    }
    selectionDone(value) {
        if (value == null)
            return;
        const fmt = this.dateService.format(value, this.format);
        if (fmt) {
            this.formattedValue = fmt;
            this.value = value;
            if (!this.withTime || !this.dateService.isSameDay(value, this.value)) {
                this.hide();
            }
        }
    }
    reset() {
        this.ngControl.reset();
        this.onTouched();
        this.onChange(undefined);
    }
    selectionInlineDone(value) {
        if (!value)
            return;
        const changed = !this.sameValues(this.modelValue, value);
        this.modelValue = value;
        if (changed) {
            this.onChange(value);
            this.onTouched();
        }
    }
    sameValues(a, b) {
        try {
            return a === b || a.getTime() === b.getTime();
        }
        catch {
            return false;
        }
    }
    _getDisabledDays(changes) {
        const disabledDaysChange = changes['disabledDays'];
        const disabledDaysChanged = disabledDaysChange && disabledDaysChange.currentValue;
        return disabledDaysChanged ? disabledDaysChange.currentValue : undefined;
    }
    _getDisabledMonths(changes) {
        const disabledMonthsChange = changes['disabledMonths'];
        const disabledMonthsChanged = disabledMonthsChange && disabledMonthsChange.currentValue;
        return disabledMonthsChanged ? disabledMonthsChange.currentValue : undefined;
    }
    _getDisabledYears(changes) {
        const disabledYearsChange = changes['disabledYears'];
        const disabledYearsChanged = disabledYearsChange && disabledYearsChange.currentValue;
        return disabledYearsChanged ? disabledYearsChange.currentValue : undefined;
    }
    _setValidators() {
        const validators = [this._dateValidator];
        if (this.ngControl.control.validator) {
            validators.push(this.ngControl.control.validator);
        }
        this.ngControl.control.setValidators(validators);
    }
}
DatePickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: DatePickerComponent, deps: [{ token: i0.Injector }, { token: DateService }], target: i0.ɵɵFactoryTarget.Component });
DatePickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: DatePickerComponent, selector: "suite-rfb-date-picker", inputs: { value: "value", direction: "direction", adaptivePosition: "adaptivePosition", format: "format", inline: "inline", iconSize: "iconSize", inputGroupClass: "inputGroupClass", label: "label", outsideClick: "outsideClick", required: "required", showWeeks: "showWeeks", disabledDays: ["suiteRfbDisabledDays", "disabledDays"], disabledMonths: ["suiteRfbDisabledMonths", "disabledMonths"], disabledYears: ["suiteRfbDisabledYears", "disabledYears"], maxDate: ["suiteRfbMaxDate", "maxDate"], minDate: ["suiteRfbMinDate", "minDate"], withTime: "withTime", disabled: "disabled" }, providers: [{
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => DatePickerComponent)
        }], viewQueries: [{ propertyName: "datepicker", first: true, predicate: BsDatepickerDirective, descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div class=\"form-group\" [suiteRfbHasError]=\"ngControl.control\">\n  <label *ngIf=\"label\" class=\"control-label\"\n    [class.obrigatorio]=\"required\">{{label}}</label>\n  <div [class]=\"inputGroupClass\">\n    <bs-datepicker-inline *ngIf=\"inline\"\n      [bsConfig]=\"bsConfig\"\n      [bsValue]=\"value\"\n      [isDisabled]=\"disabled\"\n      [datesDisabled]=\"disabledDates\"\n      [minDate]=\"minDate\"\n      [maxDate]=\"maxDate\"\n      (bsValueChange)=\"selectionInlineDone($event)\">\n    </bs-datepicker-inline>\n    <ng-container *ngIf=\"!inline\">\n      <input #dp=\"bsDatepicker\" bsDatepicker\n        type=\"text\"\n        class=\"form-control\"\n        (keyup)=\"dp.hide()\"\n        (input)=\"onInput($any($event).target.value)\"\n        (bsValueChange)=\"selectionDone($event)\"\n        [suiteRfbInputMask]=\"inputMask\"\n        [disabled]=\"disabled\"\n        [datesDisabled]=\"disabledDates\"\n        [minDate]=\"minDate\"\n        [maxDate]=\"maxDate\"\n        [placement]=\"direction\"\n        [outsideClick]=\"outsideClick\"\n        [bsConfig]=\"bsConfig\"\n        [bsValue]=\"value\"\n        [value]=\"formattedValue\">\n      <span class=\"input-group-addon\">\n        <span tooltip=\"Selecionar data do calend\u00E1rio\" container=\"body\">\n          <span class=\"material-icons date-picker-icon\"\n            [style.font-size]=\"iconSize\"\n            [class.disabled]=\"disabled\"\n            (click)=\"toggle()\">&#xE916;</span>\n        </span>\n      </span>\n    </ng-container>\n  </div>\n  <suite-rfb-local-messages [control]=\"ngControl.control\"></suite-rfb-local-messages>\n</div>", styles: [".date-picker-icon{cursor:pointer}.date-picker-icon.disabled{cursor:not-allowed;color:rgba(#00205b,.3)!important}.local-messages{color:#a8001f}::ng-deep bs-datepicker-container{z-index:1051}::ng-deep .bs-datepicker{padding:3px;background-color:#fff;border:1px solid rgba(0,0,0,.15);border-radius:4px;box-shadow:0 6px 12px #0000002d}::ng-deep .bs-datepicker-container{background-color:#f2e9d8}::ng-deep .bs-datepicker-head{background-color:#00205b;border-radius:0;color:#fff}::ng-deep .bs-datepicker-head bs-datepicker-navigation-view{display:flex;justify-content:space-between;align-items:center}::ng-deep .bs-datepicker-head bs-datepicker-navigation-view>button{background-color:#00205b;border:none;color:#fff;padding:7px 16px;font-weight:700}::ng-deep .bs-datepicker-head bs-datepicker-navigation-view>button[disabled]{opacity:.3;cursor:not-allowed}::ng-deep .bs-datepicker-body>table>thead>tr{background-color:#64a70b;border-radius:0;color:#fff;font-weight:600}::ng-deep .bs-datepicker-body>table>thead>tr>th{text-align:center;padding:4px 5px}::ng-deep .bs-datepicker-body>table>tbody>tr>td{background-color:#f2e9d8;color:#333;border-radius:0;border:none;text-align:center}::ng-deep .bs-datepicker-body>table>tbody>tr>td>span{padding:0;cursor:pointer;font-weight:600;line-height:26px;width:100%;display:block}::ng-deep .bs-datepicker-body>table{width:100%}::ng-deep .bs-datepicker-body>table>tbody>tr>td>span.disabled{cursor:not-allowed;font-weight:400;background-color:#f9f4e5}::ng-deep .bs-datepicker-body .is-other-month{opacity:.8;font-weight:400}::ng-deep .bs-datepicker-body .disabled{opacity:.65}::ng-deep .bs-datepicker-body>table>tbody>tr>td>span:hover,::ng-deep .bs-datepicker-body>table>tbody>tr>td>span:focus{background-color:#d1e5b6}::ng-deep .bs-datepicker-body>table>tbody>tr>td>.selected,::ng-deep .bs-datepicker-body>table>tbody>tr>td>.selected:hover,::ng-deep .bs-datepicker-body>table>tbody>tr>td>.selected:focus{background:#a66c00;color:#fff}::ng-deep .tabela .date-picker-container tbody tr td,::ng-deep .tabela .date-picker-container tbody tr th{border-right:inherit;border-top:inherit;padding:inherit}::ng-deep .tabela .date-picker-container thead tr th{background-color:inherit;border:inherit;border-right:inherit;color:inherit;font-weight:inherit;padding:inherit;vertical-align:inherit;white-space:inherit}::ng-deep .bs-datepicker-buttons{display:flex;flex-flow:row-reverse;justify-content:space-between;background-color:#e9dbc0}::ng-deep .bs-datepicker-buttons button,::ng-deep .bs-datepicker-buttons button:hover{background-color:inherit;color:#039;font-weight:400;border:none}::ng-deep timepicker table{margin:0 auto}::ng-deep .bs-datepicker-body .today{background:#64a70b;color:#fff}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2$2.BsDatepickerDirective, selector: "[bsDatepicker]", inputs: ["placement", "triggers", "outsideClick", "container", "outsideEsc", "isDisabled", "minDate", "maxDate", "minMode", "daysDisabled", "datesDisabled", "datesEnabled", "dateCustomClasses", "dateTooltipTexts", "isOpen", "bsValue", "bsConfig"], outputs: ["onShown", "onHidden", "bsValueChange"], exportAs: ["bsDatepicker"] }, { kind: "directive", type: i2$2.BsDatepickerInlineDirective, selector: "bs-datepicker-inline", inputs: ["bsConfig", "isDisabled", "minDate", "maxDate", "dateCustomClasses", "dateTooltipTexts", "datesEnabled", "datesDisabled", "bsValue"], outputs: ["bsValueChange"], exportAs: ["bsDatepickerInline"] }, { kind: "directive", type: i2$2.BsDatepickerInputDirective, selector: "input[bsDatepicker]" }, { kind: "directive", type: InputMaskDirective, selector: "[suiteRfbInputMask]", inputs: ["suiteRfbInputMask", "suiteRfbInputMaskInvalidAllowed"] }, { kind: "component", type: LocalMessagesComponent, selector: "suite-rfb-local-messages", inputs: ["requiredMessage", "patternMessage", "minlengthMessage", "maxlengthMessage", "emailMessage", "cnpjMessage", "cpfMessage", "dateMessage", "dayMessage", "monthMessage", "yearMessage", "minDateMessage", "maxDateMessage", "fileSizeMessage", "fileTypeMessage", "decendioMessage", "control"] }, { kind: "directive", type: HasErrorDirective, selector: "[suiteRfbHasError]", inputs: ["suiteRfbHasError"] }, { kind: "directive", type: i7$1.TooltipDirective, selector: "[tooltip], [tooltipHtml]", inputs: ["adaptivePosition", "tooltip", "placement", "triggers", "container", "containerClass", "boundariesElement", "isOpen", "isDisabled", "delay", "tooltipHtml", "tooltipPlacement", "tooltipIsOpen", "tooltipEnable", "tooltipAppendToBody", "tooltipAnimation", "tooltipClass", "tooltipContext", "tooltipPopupDelay", "tooltipFadeDuration", "tooltipTrigger"], outputs: ["tooltipChange", "onShown", "onHidden", "tooltipStateChanged"], exportAs: ["bs-tooltip"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: DatePickerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-date-picker', providers: [{
                            provide: NG_VALUE_ACCESSOR,
                            multi: true,
                            useExisting: forwardRef(() => DatePickerComponent)
                        }], template: "<div class=\"form-group\" [suiteRfbHasError]=\"ngControl.control\">\n  <label *ngIf=\"label\" class=\"control-label\"\n    [class.obrigatorio]=\"required\">{{label}}</label>\n  <div [class]=\"inputGroupClass\">\n    <bs-datepicker-inline *ngIf=\"inline\"\n      [bsConfig]=\"bsConfig\"\n      [bsValue]=\"value\"\n      [isDisabled]=\"disabled\"\n      [datesDisabled]=\"disabledDates\"\n      [minDate]=\"minDate\"\n      [maxDate]=\"maxDate\"\n      (bsValueChange)=\"selectionInlineDone($event)\">\n    </bs-datepicker-inline>\n    <ng-container *ngIf=\"!inline\">\n      <input #dp=\"bsDatepicker\" bsDatepicker\n        type=\"text\"\n        class=\"form-control\"\n        (keyup)=\"dp.hide()\"\n        (input)=\"onInput($any($event).target.value)\"\n        (bsValueChange)=\"selectionDone($event)\"\n        [suiteRfbInputMask]=\"inputMask\"\n        [disabled]=\"disabled\"\n        [datesDisabled]=\"disabledDates\"\n        [minDate]=\"minDate\"\n        [maxDate]=\"maxDate\"\n        [placement]=\"direction\"\n        [outsideClick]=\"outsideClick\"\n        [bsConfig]=\"bsConfig\"\n        [bsValue]=\"value\"\n        [value]=\"formattedValue\">\n      <span class=\"input-group-addon\">\n        <span tooltip=\"Selecionar data do calend\u00E1rio\" container=\"body\">\n          <span class=\"material-icons date-picker-icon\"\n            [style.font-size]=\"iconSize\"\n            [class.disabled]=\"disabled\"\n            (click)=\"toggle()\">&#xE916;</span>\n        </span>\n      </span>\n    </ng-container>\n  </div>\n  <suite-rfb-local-messages [control]=\"ngControl.control\"></suite-rfb-local-messages>\n</div>", styles: [".date-picker-icon{cursor:pointer}.date-picker-icon.disabled{cursor:not-allowed;color:rgba(#00205b,.3)!important}.local-messages{color:#a8001f}::ng-deep bs-datepicker-container{z-index:1051}::ng-deep .bs-datepicker{padding:3px;background-color:#fff;border:1px solid rgba(0,0,0,.15);border-radius:4px;box-shadow:0 6px 12px #0000002d}::ng-deep .bs-datepicker-container{background-color:#f2e9d8}::ng-deep .bs-datepicker-head{background-color:#00205b;border-radius:0;color:#fff}::ng-deep .bs-datepicker-head bs-datepicker-navigation-view{display:flex;justify-content:space-between;align-items:center}::ng-deep .bs-datepicker-head bs-datepicker-navigation-view>button{background-color:#00205b;border:none;color:#fff;padding:7px 16px;font-weight:700}::ng-deep .bs-datepicker-head bs-datepicker-navigation-view>button[disabled]{opacity:.3;cursor:not-allowed}::ng-deep .bs-datepicker-body>table>thead>tr{background-color:#64a70b;border-radius:0;color:#fff;font-weight:600}::ng-deep .bs-datepicker-body>table>thead>tr>th{text-align:center;padding:4px 5px}::ng-deep .bs-datepicker-body>table>tbody>tr>td{background-color:#f2e9d8;color:#333;border-radius:0;border:none;text-align:center}::ng-deep .bs-datepicker-body>table>tbody>tr>td>span{padding:0;cursor:pointer;font-weight:600;line-height:26px;width:100%;display:block}::ng-deep .bs-datepicker-body>table{width:100%}::ng-deep .bs-datepicker-body>table>tbody>tr>td>span.disabled{cursor:not-allowed;font-weight:400;background-color:#f9f4e5}::ng-deep .bs-datepicker-body .is-other-month{opacity:.8;font-weight:400}::ng-deep .bs-datepicker-body .disabled{opacity:.65}::ng-deep .bs-datepicker-body>table>tbody>tr>td>span:hover,::ng-deep .bs-datepicker-body>table>tbody>tr>td>span:focus{background-color:#d1e5b6}::ng-deep .bs-datepicker-body>table>tbody>tr>td>.selected,::ng-deep .bs-datepicker-body>table>tbody>tr>td>.selected:hover,::ng-deep .bs-datepicker-body>table>tbody>tr>td>.selected:focus{background:#a66c00;color:#fff}::ng-deep .tabela .date-picker-container tbody tr td,::ng-deep .tabela .date-picker-container tbody tr th{border-right:inherit;border-top:inherit;padding:inherit}::ng-deep .tabela .date-picker-container thead tr th{background-color:inherit;border:inherit;border-right:inherit;color:inherit;font-weight:inherit;padding:inherit;vertical-align:inherit;white-space:inherit}::ng-deep .bs-datepicker-buttons{display:flex;flex-flow:row-reverse;justify-content:space-between;background-color:#e9dbc0}::ng-deep .bs-datepicker-buttons button,::ng-deep .bs-datepicker-buttons button:hover{background-color:inherit;color:#039;font-weight:400;border:none}::ng-deep timepicker table{margin:0 auto}::ng-deep .bs-datepicker-body .today{background:#64a70b;color:#fff}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.Injector }, { type: DateService }]; }, propDecorators: { datepicker: [{
                type: ViewChild,
                args: [BsDatepickerDirective, { static: false }]
            }], value: [{
                type: Input,
                args: ['value']
            }], direction: [{
                type: Input
            }], adaptivePosition: [{
                type: Input
            }], format: [{
                type: Input
            }], inline: [{
                type: Input
            }], iconSize: [{
                type: Input
            }], inputGroupClass: [{
                type: Input
            }], label: [{
                type: Input
            }], outsideClick: [{
                type: Input
            }], required: [{
                type: Input
            }], showWeeks: [{
                type: Input
            }], disabledDays: [{
                type: Input,
                args: ['suiteRfbDisabledDays']
            }], disabledMonths: [{
                type: Input,
                args: ['suiteRfbDisabledMonths']
            }], disabledYears: [{
                type: Input,
                args: ['suiteRfbDisabledYears']
            }], maxDate: [{
                type: Input,
                args: ['suiteRfbMaxDate']
            }], minDate: [{
                type: Input,
                args: ['suiteRfbMinDate']
            }], withTime: [{
                type: Input
            }], disabled: [{
                type: Input
            }] } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
defineLocale('pt-br', ptBrLocale);
class DatePickerConfig extends BsDatepickerConfig {
    constructor() {
        super(...arguments);
        this.locale = 'pt-br';
    }
}
DatePickerConfig.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: DatePickerConfig, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
DatePickerConfig.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: DatePickerConfig });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: DatePickerConfig, decorators: [{
            type: Injectable
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class CurrencyMaskDirective {
    constructor(el, simpleMaskMoneyService) {
        this.el = el;
        this.simpleMaskMoneyService = simpleMaskMoneyService;
        this.amountChange = new EventEmitter();
    }
    ngOnInit() {
        setTimeout(() => {
            if (this.amount || this.amount === 0) {
                const modelValue = this.amount;
                const viewValue = this.formatNumberToText(modelValue);
                this.updateView(viewValue);
            }
            else if (this.el.nativeElement.value) {
                const viewValue = this.el.nativeElement.value;
                const modelValue = this.simpleMaskMoneyService.formatToNumber(viewValue);
                this.updateModel(modelValue);
            }
        }, 0);
    }
    onInput(event) {
        const viewValue = this.simpleMaskMoneyService.format(event.target.value);
        const modelValue = this.simpleMaskMoneyService.formatToNumber(event.target.value);
        event.stopPropagation();
        this.updateView(viewValue, false);
        this.updateModel(modelValue);
    }
    onKeyup(event) {
        if (this.isBlankOrZero(event.target.value) && event.key === 'Backspace') {
            const viewValue = '';
            const modelValue = undefined;
            event.stopPropagation();
            this.updateView(viewValue, false);
            this.updateModel(modelValue);
        }
    }
    updateView(value, raiseEvent = true) {
        this.el.nativeElement.value = value;
        if (raiseEvent) {
            this.el.nativeElement.dispatchEvent(new CustomEvent('input'));
        }
    }
    updateModel(value) {
        this.amount = value;
        this.amountChange.emit(value);
    }
    isBlankOrZero(value) {
        return value === '' || value === '0,00';
    }
    formatNumberToText(number) {
        const decimalPlaces = this.decimalPlaces(number);
        let text;
        if (decimalPlaces === 0) {
            text = `${number}.00`;
        }
        else if (decimalPlaces === 1) {
            text = `${number}0`;
        }
        else if (decimalPlaces >= 2) {
            text = (Math.round(number * 100) / 100).toString();
        }
        return this.simpleMaskMoneyService.format(text);
    }
    decimalPlaces(amount) {
        const match = ('' + amount).match(/(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/);
        if (!match) {
            return 0;
        }
        return Math.max(0, (match[1] ? match[1].length : 0) - (match[2] ? +match[2] : 0));
    }
}
CurrencyMaskDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: CurrencyMaskDirective, deps: [{ token: i0.ElementRef }, { token: SimpleMaskMoneyService }], target: i0.ɵɵFactoryTarget.Directive });
CurrencyMaskDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.1.2", type: CurrencyMaskDirective, selector: "[suiteRfbCurrencyMask]", inputs: { amount: "amount" }, outputs: { amountChange: "amountChange" }, host: { listeners: { "input": "onInput($event)", "keyup": "onKeyup($event)" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: CurrencyMaskDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[suiteRfbCurrencyMask]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: SimpleMaskMoneyService }]; }, propDecorators: { amount: [{
                type: Input
            }], amountChange: [{
                type: Output
            }], onInput: [{
                type: HostListener,
                args: ['input', ['$event']]
            }], onKeyup: [{
                type: HostListener,
                args: ['keyup', ['$event']]
            }] } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class NumbersOnlyDirective {
    constructor(el) {
        this.el = el;
    }
    ngOnInit() { }
    onInputChange(event) {
        const initalValue = this.el.nativeElement.value;
        const modifiedValue = initalValue.replace(/[^0-9]*/g, '');
        if (initalValue !== modifiedValue) {
            event.stopPropagation();
            this.setValue(modifiedValue);
        }
    }
    setValue(value) {
        this.el.nativeElement.value = value;
        this.el.nativeElement.dispatchEvent(new CustomEvent('input'));
    }
}
NumbersOnlyDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: NumbersOnlyDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
NumbersOnlyDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.1.2", type: NumbersOnlyDirective, selector: "[suiteRfbNumbersOnly]", host: { listeners: { "input": "onInputChange($event)" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: NumbersOnlyDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[suiteRfbNumbersOnly]' }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { onInputChange: [{
                type: HostListener,
                args: ['input', ['$event']]
            }] } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbInputMaskModule {
}
SuiteRfbInputMaskModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbInputMaskModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbInputMaskModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbInputMaskModule, declarations: [CurrencyMaskDirective,
        InputMaskDirective,
        NumbersOnlyDirective], imports: [CommonModule,
        FormsModule,
        ReactiveFormsModule], exports: [CurrencyMaskDirective,
        InputMaskDirective,
        NumbersOnlyDirective] });
SuiteRfbInputMaskModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbInputMaskModule, imports: [CommonModule,
        FormsModule,
        ReactiveFormsModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbInputMaskModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        ReactiveFormsModule,
                    ],
                    declarations: [
                        CurrencyMaskDirective,
                        InputMaskDirective,
                        NumbersOnlyDirective,
                    ],
                    exports: [
                        CurrencyMaskDirective,
                        InputMaskDirective,
                        NumbersOnlyDirective,
                    ],
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbDatePickerModule {
}
SuiteRfbDatePickerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbDatePickerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbDatePickerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbDatePickerModule, declarations: [DatePickerComponent], imports: [CommonModule,
        FormsModule,
        ReactiveFormsModule,
        BsDatepickerModule,
        SuiteRfbInputMaskModule,
        SuiteRfbInputValidationModule,
        SuiteRfbOutputMaskModule,
        TooltipModule], exports: [DatePickerComponent] });
SuiteRfbDatePickerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbDatePickerModule, providers: [
        {
            provide: BsDatepickerConfig,
            useClass: DatePickerConfig
        }
    ], imports: [CommonModule,
        FormsModule,
        ReactiveFormsModule,
        BsDatepickerModule,
        SuiteRfbInputMaskModule,
        SuiteRfbInputValidationModule,
        SuiteRfbOutputMaskModule,
        TooltipModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbDatePickerModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        ReactiveFormsModule,
                        BsDatepickerModule,
                        SuiteRfbInputMaskModule,
                        SuiteRfbInputValidationModule,
                        SuiteRfbOutputMaskModule,
                        TooltipModule
                    ],
                    declarations: [
                        DatePickerComponent,
                    ],
                    exports: [
                        DatePickerComponent,
                    ],
                    providers: [
                        {
                            provide: BsDatepickerConfig,
                            useClass: DatePickerConfig
                        }
                    ]
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class YearPickerComponent extends DatePickerComponent {
    constructor() {
        super(...arguments);
        this.disabledDays = [];
        this.disabledMonths = [];
        this.format = 'YYYY';
        this.minMode = 'year';
        this.inputMask = '9999';
    }
}
YearPickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: YearPickerComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
YearPickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: YearPickerComponent, selector: "suite-rfb-year-picker", inputs: { disabledDays: "disabledDays", disabledMonths: "disabledMonths", format: "format" }, providers: [{
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => YearPickerComponent)
        }], usesInheritance: true, ngImport: i0, template: "<div class=\"form-group\" [suiteRfbHasError]=\"ngControl.control\">\n  <label *ngIf=\"label\" class=\"control-label\"\n    [class.obrigatorio]=\"required\">{{label}}</label>\n  <div [class]=\"inputGroupClass\">\n    <bs-datepicker-inline *ngIf=\"inline\"\n      [bsConfig]=\"bsConfig\"\n      [bsValue]=\"value\"\n      [isDisabled]=\"disabled\"\n      [datesDisabled]=\"disabledDates\"\n      [minDate]=\"minDate\"\n      [maxDate]=\"maxDate\"\n      (bsValueChange)=\"selectionInlineDone($event)\">\n    </bs-datepicker-inline>\n    <ng-container *ngIf=\"!inline\">\n      <input #dp=\"bsDatepicker\" bsDatepicker\n        type=\"text\"\n        class=\"form-control\"\n        (keyup)=\"dp.hide()\"\n        (input)=\"onInput($any($event).target.value)\"\n        (bsValueChange)=\"selectionDone($event)\"\n        [suiteRfbInputMask]=\"inputMask\"\n        [disabled]=\"disabled\"\n        [datesDisabled]=\"disabledDates\"\n        [minDate]=\"minDate\"\n        [maxDate]=\"maxDate\"\n        [placement]=\"direction\"\n        [outsideClick]=\"outsideClick\"\n        [bsConfig]=\"bsConfig\"\n        [bsValue]=\"value\"\n        [value]=\"formattedValue\">\n      <span class=\"input-group-addon\">\n        <span tooltip=\"Selecionar data do calend\u00E1rio\" container=\"body\">\n          <span class=\"material-icons date-picker-icon\"\n            [style.font-size]=\"iconSize\"\n            [class.disabled]=\"disabled\"\n            (click)=\"toggle()\">&#xE916;</span>\n        </span>\n      </span>\n    </ng-container>\n  </div>\n  <suite-rfb-local-messages [control]=\"ngControl.control\"></suite-rfb-local-messages>\n</div>", styles: [".date-picker-icon{cursor:pointer}.date-picker-icon.disabled{cursor:not-allowed;color:rgba(#00205b,.3)!important}.local-messages{color:#a8001f}::ng-deep bs-datepicker-container{z-index:1051}::ng-deep .bs-datepicker{padding:3px;background-color:#fff;border:1px solid rgba(0,0,0,.15);border-radius:4px;box-shadow:0 6px 12px #0000002d}::ng-deep .bs-datepicker-container{background-color:#f2e9d8}::ng-deep .bs-datepicker-head{background-color:#00205b;border-radius:0;color:#fff}::ng-deep .bs-datepicker-head bs-datepicker-navigation-view{display:flex;justify-content:space-between;align-items:center}::ng-deep .bs-datepicker-head bs-datepicker-navigation-view>button{background-color:#00205b;border:none;color:#fff;padding:7px 16px;font-weight:700}::ng-deep .bs-datepicker-head bs-datepicker-navigation-view>button[disabled]{opacity:.3;cursor:not-allowed}::ng-deep .bs-datepicker-body>table>thead>tr{background-color:#64a70b;border-radius:0;color:#fff;font-weight:600}::ng-deep .bs-datepicker-body>table>thead>tr>th{text-align:center;padding:4px 5px}::ng-deep .bs-datepicker-body>table>tbody>tr>td{background-color:#f2e9d8;color:#333;border-radius:0;border:none;text-align:center}::ng-deep .bs-datepicker-body>table>tbody>tr>td>span{padding:0;cursor:pointer;font-weight:600;line-height:26px;width:100%;display:block}::ng-deep .bs-datepicker-body>table{width:100%}::ng-deep .bs-datepicker-body>table>tbody>tr>td>span.disabled{cursor:not-allowed;font-weight:400;background-color:#f9f4e5}::ng-deep .bs-datepicker-body .is-other-month{opacity:.8;font-weight:400}::ng-deep .bs-datepicker-body .disabled{opacity:.65}::ng-deep .bs-datepicker-body>table>tbody>tr>td>span:hover,::ng-deep .bs-datepicker-body>table>tbody>tr>td>span:focus{background-color:#d1e5b6}::ng-deep .bs-datepicker-body>table>tbody>tr>td>.selected,::ng-deep .bs-datepicker-body>table>tbody>tr>td>.selected:hover,::ng-deep .bs-datepicker-body>table>tbody>tr>td>.selected:focus{background:#a66c00;color:#fff}::ng-deep .tabela .date-picker-container tbody tr td,::ng-deep .tabela .date-picker-container tbody tr th{border-right:inherit;border-top:inherit;padding:inherit}::ng-deep .tabela .date-picker-container thead tr th{background-color:inherit;border:inherit;border-right:inherit;color:inherit;font-weight:inherit;padding:inherit;vertical-align:inherit;white-space:inherit}::ng-deep .bs-datepicker-buttons{display:flex;flex-flow:row-reverse;justify-content:space-between;background-color:#e9dbc0}::ng-deep .bs-datepicker-buttons button,::ng-deep .bs-datepicker-buttons button:hover{background-color:inherit;color:#039;font-weight:400;border:none}::ng-deep timepicker table{margin:0 auto}::ng-deep .bs-datepicker-body .today{background:#64a70b;color:#fff}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2$2.BsDatepickerDirective, selector: "[bsDatepicker]", inputs: ["placement", "triggers", "outsideClick", "container", "outsideEsc", "isDisabled", "minDate", "maxDate", "minMode", "daysDisabled", "datesDisabled", "datesEnabled", "dateCustomClasses", "dateTooltipTexts", "isOpen", "bsValue", "bsConfig"], outputs: ["onShown", "onHidden", "bsValueChange"], exportAs: ["bsDatepicker"] }, { kind: "directive", type: i2$2.BsDatepickerInlineDirective, selector: "bs-datepicker-inline", inputs: ["bsConfig", "isDisabled", "minDate", "maxDate", "dateCustomClasses", "dateTooltipTexts", "datesEnabled", "datesDisabled", "bsValue"], outputs: ["bsValueChange"], exportAs: ["bsDatepickerInline"] }, { kind: "directive", type: i2$2.BsDatepickerInputDirective, selector: "input[bsDatepicker]" }, { kind: "directive", type: InputMaskDirective, selector: "[suiteRfbInputMask]", inputs: ["suiteRfbInputMask", "suiteRfbInputMaskInvalidAllowed"] }, { kind: "component", type: LocalMessagesComponent, selector: "suite-rfb-local-messages", inputs: ["requiredMessage", "patternMessage", "minlengthMessage", "maxlengthMessage", "emailMessage", "cnpjMessage", "cpfMessage", "dateMessage", "dayMessage", "monthMessage", "yearMessage", "minDateMessage", "maxDateMessage", "fileSizeMessage", "fileTypeMessage", "decendioMessage", "control"] }, { kind: "directive", type: HasErrorDirective, selector: "[suiteRfbHasError]", inputs: ["suiteRfbHasError"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: YearPickerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-year-picker', providers: [{
                            provide: NG_VALUE_ACCESSOR,
                            multi: true,
                            useExisting: forwardRef(() => YearPickerComponent)
                        }], template: "<div class=\"form-group\" [suiteRfbHasError]=\"ngControl.control\">\n  <label *ngIf=\"label\" class=\"control-label\"\n    [class.obrigatorio]=\"required\">{{label}}</label>\n  <div [class]=\"inputGroupClass\">\n    <bs-datepicker-inline *ngIf=\"inline\"\n      [bsConfig]=\"bsConfig\"\n      [bsValue]=\"value\"\n      [isDisabled]=\"disabled\"\n      [datesDisabled]=\"disabledDates\"\n      [minDate]=\"minDate\"\n      [maxDate]=\"maxDate\"\n      (bsValueChange)=\"selectionInlineDone($event)\">\n    </bs-datepicker-inline>\n    <ng-container *ngIf=\"!inline\">\n      <input #dp=\"bsDatepicker\" bsDatepicker\n        type=\"text\"\n        class=\"form-control\"\n        (keyup)=\"dp.hide()\"\n        (input)=\"onInput($any($event).target.value)\"\n        (bsValueChange)=\"selectionDone($event)\"\n        [suiteRfbInputMask]=\"inputMask\"\n        [disabled]=\"disabled\"\n        [datesDisabled]=\"disabledDates\"\n        [minDate]=\"minDate\"\n        [maxDate]=\"maxDate\"\n        [placement]=\"direction\"\n        [outsideClick]=\"outsideClick\"\n        [bsConfig]=\"bsConfig\"\n        [bsValue]=\"value\"\n        [value]=\"formattedValue\">\n      <span class=\"input-group-addon\">\n        <span tooltip=\"Selecionar data do calend\u00E1rio\" container=\"body\">\n          <span class=\"material-icons date-picker-icon\"\n            [style.font-size]=\"iconSize\"\n            [class.disabled]=\"disabled\"\n            (click)=\"toggle()\">&#xE916;</span>\n        </span>\n      </span>\n    </ng-container>\n  </div>\n  <suite-rfb-local-messages [control]=\"ngControl.control\"></suite-rfb-local-messages>\n</div>", styles: [".date-picker-icon{cursor:pointer}.date-picker-icon.disabled{cursor:not-allowed;color:rgba(#00205b,.3)!important}.local-messages{color:#a8001f}::ng-deep bs-datepicker-container{z-index:1051}::ng-deep .bs-datepicker{padding:3px;background-color:#fff;border:1px solid rgba(0,0,0,.15);border-radius:4px;box-shadow:0 6px 12px #0000002d}::ng-deep .bs-datepicker-container{background-color:#f2e9d8}::ng-deep .bs-datepicker-head{background-color:#00205b;border-radius:0;color:#fff}::ng-deep .bs-datepicker-head bs-datepicker-navigation-view{display:flex;justify-content:space-between;align-items:center}::ng-deep .bs-datepicker-head bs-datepicker-navigation-view>button{background-color:#00205b;border:none;color:#fff;padding:7px 16px;font-weight:700}::ng-deep .bs-datepicker-head bs-datepicker-navigation-view>button[disabled]{opacity:.3;cursor:not-allowed}::ng-deep .bs-datepicker-body>table>thead>tr{background-color:#64a70b;border-radius:0;color:#fff;font-weight:600}::ng-deep .bs-datepicker-body>table>thead>tr>th{text-align:center;padding:4px 5px}::ng-deep .bs-datepicker-body>table>tbody>tr>td{background-color:#f2e9d8;color:#333;border-radius:0;border:none;text-align:center}::ng-deep .bs-datepicker-body>table>tbody>tr>td>span{padding:0;cursor:pointer;font-weight:600;line-height:26px;width:100%;display:block}::ng-deep .bs-datepicker-body>table{width:100%}::ng-deep .bs-datepicker-body>table>tbody>tr>td>span.disabled{cursor:not-allowed;font-weight:400;background-color:#f9f4e5}::ng-deep .bs-datepicker-body .is-other-month{opacity:.8;font-weight:400}::ng-deep .bs-datepicker-body .disabled{opacity:.65}::ng-deep .bs-datepicker-body>table>tbody>tr>td>span:hover,::ng-deep .bs-datepicker-body>table>tbody>tr>td>span:focus{background-color:#d1e5b6}::ng-deep .bs-datepicker-body>table>tbody>tr>td>.selected,::ng-deep .bs-datepicker-body>table>tbody>tr>td>.selected:hover,::ng-deep .bs-datepicker-body>table>tbody>tr>td>.selected:focus{background:#a66c00;color:#fff}::ng-deep .tabela .date-picker-container tbody tr td,::ng-deep .tabela .date-picker-container tbody tr th{border-right:inherit;border-top:inherit;padding:inherit}::ng-deep .tabela .date-picker-container thead tr th{background-color:inherit;border:inherit;border-right:inherit;color:inherit;font-weight:inherit;padding:inherit;vertical-align:inherit;white-space:inherit}::ng-deep .bs-datepicker-buttons{display:flex;flex-flow:row-reverse;justify-content:space-between;background-color:#e9dbc0}::ng-deep .bs-datepicker-buttons button,::ng-deep .bs-datepicker-buttons button:hover{background-color:inherit;color:#039;font-weight:400;border:none}::ng-deep timepicker table{margin:0 auto}::ng-deep .bs-datepicker-body .today{background:#64a70b;color:#fff}\n"] }]
        }], propDecorators: { disabledDays: [{
                type: Input
            }], disabledMonths: [{
                type: Input
            }], format: [{
                type: Input
            }] } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
defineLocale('pt-br', ptBrLocale);
class YearPickerConfig extends BsDatepickerConfig {
    constructor() {
        super(...arguments);
        this.locale = 'pt-br';
    }
}
YearPickerConfig.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: YearPickerConfig, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
YearPickerConfig.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: YearPickerConfig });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: YearPickerConfig, decorators: [{
            type: Injectable
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbYearPickerModule {
}
SuiteRfbYearPickerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbYearPickerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbYearPickerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbYearPickerModule, declarations: [YearPickerComponent], imports: [CommonModule,
        FormsModule,
        ReactiveFormsModule,
        BsDatepickerModule,
        SuiteRfbInputMaskModule,
        SuiteRfbInputValidationModule], exports: [YearPickerComponent] });
SuiteRfbYearPickerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbYearPickerModule, providers: [
        {
            provide: BsDatepickerConfig,
            useClass: YearPickerConfig
        }
    ], imports: [CommonModule,
        FormsModule,
        ReactiveFormsModule,
        BsDatepickerModule,
        SuiteRfbInputMaskModule,
        SuiteRfbInputValidationModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbYearPickerModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        ReactiveFormsModule,
                        BsDatepickerModule,
                        SuiteRfbInputMaskModule,
                        SuiteRfbInputValidationModule,
                    ],
                    declarations: [
                        YearPickerComponent,
                    ],
                    exports: [
                        YearPickerComponent,
                    ],
                    providers: [
                        {
                            provide: BsDatepickerConfig,
                            useClass: YearPickerConfig
                        }
                    ]
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
const DECENDIO_NUMBER_REGEX = /^[1-9][0-9]{3}((0?[1-9])?|(1[012])?)0[1-3]$/;
const DECENDIO_STRING_REGEX = /^[1-3]º\/((0?[1-9])?|(1[012])?)\/[1-9][0-9]{3}$/;
class DecendioService {
    asDate(decendioNumber) {
        const regex = DECENDIO_NUMBER_REGEX;
        const decendioStr = `${decendioNumber}`;
        if (regex.test(decendioStr)) {
            const index = decendioStr.substring(7, 8);
            const month = decendioStr.substring(4, 6);
            const year = decendioStr.substring(0, 4);
            return moment(`0${index}/${month}/${year}`, 'DD/MM/YYYY', true).toDate();
        }
        return undefined;
    }
    // ex: '20180103' => '3º/01/2018'
    format(decendioNumber) {
        let decendioString;
        const regex = DECENDIO_NUMBER_REGEX;
        const string = `${decendioNumber}`;
        if (regex.test(string)) {
            const index = string.substring(7, 8);
            const month = string.substring(4, 6);
            const year = string.substring(0, 4);
            decendioString = `${index}º/${month}/${year}`;
        }
        return decendioString;
    }
    // ex: '3º/01/2018' => '20180103'
    parseIndex(decendioIndex, decendioMonth) {
        let decendioNumber;
        const validIndex = decendioIndex === 1 || decendioIndex === 2 || decendioIndex === 3;
        const validMonth = decendioMonth && decendioMonth !== null && decendioMonth !== undefined;
        if (validIndex && validMonth) {
            const fullMonth = moment(decendioMonth).format('YYYYMM');
            decendioNumber = Number.parseInt(`${fullMonth}0${decendioIndex}`);
        }
        return decendioNumber;
    }
    // ex: '3º/01/2018' => '20180103'
    parse(decendioString) {
        let decendioNumber;
        const regex = DECENDIO_STRING_REGEX;
        if (regex.test(decendioString)) {
            const index = decendioString.substring(0, 1);
            const month = decendioString.substring(3, 5);
            const year = decendioString.substring(6, 10);
            decendioNumber = Number.parseInt(`${year}${month}0${index}`);
        }
        return decendioNumber;
    }
    // ex: '20180103' => 3
    index(decendioNumber) {
        let decendioIndex;
        const regex = DECENDIO_NUMBER_REGEX;
        const string = `${decendioNumber}`;
        if (regex.test(string)) {
            const index = string.substring(7, 8);
            decendioIndex = Number.parseInt(index);
        }
        return decendioIndex;
    }
    // ex: '20180103' => 2018-01-01 (as Date)
    month(decendioNumber) {
        let decendioMonth;
        const regex = DECENDIO_NUMBER_REGEX;
        const string = `${decendioNumber}`;
        if (regex.test(string)) {
            const month = string.substring(4, 6);
            const year = string.substring(0, 4);
            decendioMonth = moment(`${month}/${year}`, 'MM/YYYY', true).toDate();
        }
        return decendioMonth;
    }
}
DecendioService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: DecendioService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
DecendioService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: DecendioService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: DecendioService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class DecendioPickerComponent {
    get value() {
        return this.modelValue;
    }
    set value(value) {
        this.modelValue = value;
        this.onChange(value);
        this.onTouched();
    }
    get formattedValue() {
        return this.viewValue ? this.viewValue : '';
    }
    set formattedValue(viewValue) {
        this.viewValue = viewValue;
    }
    writeValue(value) {
        if (value) {
            this.viewValue = this.decendioService.format(value);
            this.valueAsDate = this.decendioService.asDate(value);
            this.modelValue = value;
            this.decendioIndex = this.decendioService.index(value);
            this.decendioMonth = this.decendioService.month(value);
        }
    }
    reset() {
        this.viewValue = '';
        this.valueAsDate = null;
        this.modelValue = null;
        this.decendioIndex = null;
        this.decendioMonth = null;
        this.onChange(null);
        this.onTouched();
        this.showCalendar = false;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    set required(value) {
        this._required = !(value === false || value === 'false');
    }
    get required() {
        return this._required;
    }
    constructor(injector, renderer, decendioService, document, windowService) {
        this.injector = injector;
        this.renderer = renderer;
        this.decendioService = decendioService;
        this.document = document;
        this.windowService = windowService;
        this.bsConfig = {
            monthLabel: 'MMM',
            minMode: 'month',
            showWeekNumbers: false,
            showTodayButton: false,
            showClearButton: false
        };
        this.onChange = () => { };
        this.onTouched = () => { };
        // ... ControlValueAccessor methods
        this.direction = 'bottom';
        this.inline = false;
        this.iconSize = '32px';
        this.inputGroupClass = 'input-group date';
        this.label = 'Decêndio';
        this.outsideClick = true;
        this.showCalendar = false;
        this.disabled = false;
        this._decendioValidator = (control) => {
            let errors = null;
            const isInvalidDecendio = this.formattedValue && !control.value;
            if (isInvalidDecendio) {
                errors = { 'invalidDecendio': { value: this.formattedValue } };
            }
            return errors;
        };
    }
    ngOnInit() {
        this.ngControl = this.injector.get(NgControl);
        // se não foi fornecido um valor inicial, usar o mes atual como selecionado.
        if (!this.modelValue) {
            this.decendioMonth = new Date();
        }
    }
    ngAfterViewInit() {
        this._setValidators();
        if (this.outsideClick) {
            this._handleOutsideClick();
        }
    }
    show() {
        if (!this.disabled) {
            this.showCalendar = true;
        }
    }
    hide() {
        if (!this.disabled) {
            this.showCalendar = false;
        }
    }
    toggle() {
        if (!this.disabled) {
            this.showCalendar = !this.showCalendar;
        }
    }
    onInput(viewValue) {
        console.log(viewValue);
        if (viewValue.length === 0 ||
            viewValue.length === 10 ||
            (viewValue.length === 1 && !viewValue.match('[123]'))) {
            this.formattedValue = viewValue;
            this.value = this.decendioService.parse(viewValue);
        }
    }
    selectMonth(decendioMonth) {
        const changed = !this.sameValues(this.valueAsDate, decendioMonth);
        this.decendioMonth = decendioMonth;
        if (decendioMonth && this.decendioIndex && changed) {
            this.selectIndex(this.decendioIndex, false);
        }
    }
    sameValues(a, b) {
        try {
            return a === b || a.getTime() === b.getTime();
        }
        catch {
            return false;
        }
    }
    selectIndex(index, hide = true) {
        this.decendioIndex = index;
        if (this.decendioIndex && this.decendioMonth) {
            const decendioNumber = this.decendioService.parseIndex(this.decendioIndex, this.decendioMonth);
            this.formattedValue = this.decendioService.format(decendioNumber);
            this.value = decendioNumber;
            this.valueAsDate = this.decendioService.asDate(decendioNumber);
            if (hide)
                this.hide();
        }
    }
    isSelectedIndex(index) {
        return this.decendioIndex && this.decendioIndex === index;
    }
    _setValidators() {
        const validators = [this._decendioValidator];
        if (this.ngControl.control.validator) {
            validators.push(this.ngControl.control.validator);
        }
        this.ngControl.control.setValidators(validators);
    }
    _handleOutsideClick() {
        this.window.addEventListener('click', (event) => {
            const target = event.target;
            const isInPage = (target === this.document.body) ? false : this.document.body.contains(target);
            const outsideClick = !this.divContainer.nativeElement.contains(target);
            if (isInPage && outsideClick) {
                this.hide();
            }
        });
    }
    get window() {
        return this.windowService.get();
    }
}
DecendioPickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: DecendioPickerComponent, deps: [{ token: i0.Injector }, { token: i0.Renderer2 }, { token: DecendioService }, { token: DOCUMENT }, { token: WindowService }], target: i0.ɵɵFactoryTarget.Component });
DecendioPickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: DecendioPickerComponent, selector: "suite-rfb-decendio-picker", inputs: { value: "value", direction: "direction", inline: "inline", iconSize: "iconSize", inputGroupClass: "inputGroupClass", label: "label", outsideClick: "outsideClick", required: "required" }, providers: [{
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => DecendioPickerComponent)
        }], viewQueries: [{ propertyName: "divContainer", first: true, predicate: ["divContainer"], descendants: true }], ngImport: i0, template: "<div class=\"form-group\" [suiteRfbHasError]=\"ngControl.control\">\n  <label *ngIf=\"label\" class=\"control-label\"\n      [class.obrigatorio]=\"required\">{{label}}</label>\n\n    <div [class]=\"inputGroupClass\" #divContainer>\n      <input *ngIf=\"!inline\"\n          type=\"text\" class=\"form-control\"\n          (input)=\"onInput($any($event).target.value)\"\n          suiteRfbInputMask=\"9\u00BA/99/9999\"\n          [disabled]=\"disabled\"\n          [required]=\"required\"\n          [value]=\"formattedValue\"\n          (click)=\"!disabled && toggle()\">\n\n      <div *ngIf=\"showCalendar || inline\" class=\"decendio-container\" [class.noinline]=\"!inline\">\n        <div class=\"decendio-container-inner\">\n          <bs-datepicker-inline #dpi=\"bsDatepickerInline\"\n            [bsConfig]=\"bsConfig\"\n            [bsValue]=\"valueAsDate\"\n            [isDisabled]=\"disabled\"\n            (bsValueChange)=\"selectMonth($event)\">\n          </bs-datepicker-inline>\n          <div class=\"select-decendio-container\">\n            <div class=\"select-decendio-container-inner\">\n              <button type=\"button\" class=\"btn btn-default btn-decendio\"\n                  [disabled]=\"disabled\"\n                  (click)=\"reset()\">Limpar</button>\n              <button type=\"button\" class=\"btn btn-default btn-decendio\"\n                *ngFor=\"let index of [1,2,3]\"\n                [disabled]=\"disabled\"\n                [class.active]=\"isSelectedIndex(index)\"\n                title=\"{{index}}&ordm; Dec\u00EAndio\"\n                (click)=\"selectIndex(index)\">\n                {{index}}&ordm;\n              </button>\n            </div>\n          </div>\n        </div>\n      </div>\n      <span class=\"input-group-addon\" *ngIf=\"!inline\">\n        <span tooltip=\"Selecionar dec\u00EAndio\" container=\"body\">\n          <span class=\"material-icons date-picker-icon\"\n            [style.font-size]=\"iconSize\"\n            [class.disabled]=\"disabled\"\n            (click)=\"!disabled && toggle()\">&#xE916;</span>\n        </span>\n      </span>\n    </div>\n\n    <suite-rfb-local-messages [control]=\"ngControl.control\"></suite-rfb-local-messages>\n</div>\n", styles: [".date-picker-icon{cursor:pointer}.date-picker-icon.disabled{cursor:not-allowed;color:rgba(#00205b,.3)!important}.local-messages{color:#a8001f}::ng-deep bs-datepicker-container{z-index:1051}::ng-deep .bs-datepicker{padding:3px;background-color:#fff;border:1px solid rgba(0,0,0,.15);border-radius:4px;box-shadow:0 6px 12px #0000002d}::ng-deep .bs-datepicker-container{background-color:#f2e9d8}::ng-deep .bs-datepicker-head{background-color:#00205b;border-radius:0;color:#fff}::ng-deep .bs-datepicker-head bs-datepicker-navigation-view{display:flex;justify-content:space-between;align-items:center}::ng-deep .bs-datepicker-head bs-datepicker-navigation-view>button{background-color:#00205b;border:none;color:#fff;padding:7px 16px;font-weight:700}::ng-deep .bs-datepicker-head bs-datepicker-navigation-view>button[disabled]{opacity:.3;cursor:not-allowed}::ng-deep .bs-datepicker-body>table>thead>tr{background-color:#64a70b;border-radius:0;color:#fff;font-weight:600}::ng-deep .bs-datepicker-body>table>thead>tr>th{text-align:center;padding:4px 5px}::ng-deep .bs-datepicker-body>table>tbody>tr>td{background-color:#f2e9d8;color:#333;border-radius:0;border:none;text-align:center}::ng-deep .bs-datepicker-body>table>tbody>tr>td>span{padding:0;cursor:pointer;font-weight:600;line-height:26px;width:100%;display:block}::ng-deep .bs-datepicker-body>table{width:100%}::ng-deep .bs-datepicker-body>table>tbody>tr>td>span.disabled{cursor:not-allowed;font-weight:400;background-color:#f9f4e5}::ng-deep .bs-datepicker-body .is-other-month{opacity:.8;font-weight:400}::ng-deep .bs-datepicker-body .disabled{opacity:.65}::ng-deep .bs-datepicker-body>table>tbody>tr>td>span:hover,::ng-deep .bs-datepicker-body>table>tbody>tr>td>span:focus{background-color:#d1e5b6}::ng-deep .bs-datepicker-body>table>tbody>tr>td>.selected,::ng-deep .bs-datepicker-body>table>tbody>tr>td>.selected:hover,::ng-deep .bs-datepicker-body>table>tbody>tr>td>.selected:focus{background:#a66c00;color:#fff}::ng-deep .tabela .date-picker-container tbody tr td,::ng-deep .tabela .date-picker-container tbody tr th{border-right:inherit;border-top:inherit;padding:inherit}::ng-deep .tabela .date-picker-container thead tr th{background-color:inherit;border:inherit;border-right:inherit;color:inherit;font-weight:inherit;padding:inherit;vertical-align:inherit;white-space:inherit}::ng-deep .bs-datepicker-buttons{display:flex;flex-flow:row-reverse;justify-content:space-between;background-color:#e9dbc0}::ng-deep .bs-datepicker-buttons button,::ng-deep .bs-datepicker-buttons button:hover{background-color:inherit;color:#039;font-weight:400;border:none}::ng-deep timepicker table{margin:0 auto}::ng-deep .bs-datepicker-body .today{background:#64a70b;color:#fff}\n", ".decendio-container-inner{background-color:#e9dbc0}.select-decendio-container-inner .btn.btn-decendio{background-color:#e9dbc0;border-radius:0;color:#039;font-weight:400;margin-left:0;border:none;font-weight:600}.select-decendio-container-inner .btn.btn-decendio:hover{background-color:#d1e5b6}.select-decendio-container-inner .btn.btn-decendio.active{background:#a66c00;color:#fff}.decendio-container{width:-moz-fit-content;width:fit-content;padding:3px;background-color:#fff;border:1px solid rgba(0,0,0,.15);border-radius:4px;box-shadow:0 6px 12px #0000002d}.decendio-container.noinline{position:absolute;top:37px;z-index:3}.select-decendio-container-inner{display:flex;justify-content:space-around;margin-left:3px;margin-right:3px}:host ::ng-deep .bs-datepicker{border:none;box-shadow:none;padding:unset;background-color:unset}:host ::ng-deep .bs-datepicker-body td{padding:2px 14px}:host ::ng-deep .bs-datepicker-body>table>tbody>tr>td>span{padding:5px;line-height:16px}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2$2.BsDatepickerInlineDirective, selector: "bs-datepicker-inline", inputs: ["bsConfig", "isDisabled", "minDate", "maxDate", "dateCustomClasses", "dateTooltipTexts", "datesEnabled", "datesDisabled", "bsValue"], outputs: ["bsValueChange"], exportAs: ["bsDatepickerInline"] }, { kind: "component", type: LocalMessagesComponent, selector: "suite-rfb-local-messages", inputs: ["requiredMessage", "patternMessage", "minlengthMessage", "maxlengthMessage", "emailMessage", "cnpjMessage", "cpfMessage", "dateMessage", "dayMessage", "monthMessage", "yearMessage", "minDateMessage", "maxDateMessage", "fileSizeMessage", "fileTypeMessage", "decendioMessage", "control"] }, { kind: "directive", type: HasErrorDirective, selector: "[suiteRfbHasError]", inputs: ["suiteRfbHasError"] }, { kind: "directive", type: InputMaskDirective, selector: "[suiteRfbInputMask]", inputs: ["suiteRfbInputMask", "suiteRfbInputMaskInvalidAllowed"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: DecendioPickerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-decendio-picker', providers: [{
                            provide: NG_VALUE_ACCESSOR,
                            multi: true,
                            useExisting: forwardRef(() => DecendioPickerComponent)
                        }], template: "<div class=\"form-group\" [suiteRfbHasError]=\"ngControl.control\">\n  <label *ngIf=\"label\" class=\"control-label\"\n      [class.obrigatorio]=\"required\">{{label}}</label>\n\n    <div [class]=\"inputGroupClass\" #divContainer>\n      <input *ngIf=\"!inline\"\n          type=\"text\" class=\"form-control\"\n          (input)=\"onInput($any($event).target.value)\"\n          suiteRfbInputMask=\"9\u00BA/99/9999\"\n          [disabled]=\"disabled\"\n          [required]=\"required\"\n          [value]=\"formattedValue\"\n          (click)=\"!disabled && toggle()\">\n\n      <div *ngIf=\"showCalendar || inline\" class=\"decendio-container\" [class.noinline]=\"!inline\">\n        <div class=\"decendio-container-inner\">\n          <bs-datepicker-inline #dpi=\"bsDatepickerInline\"\n            [bsConfig]=\"bsConfig\"\n            [bsValue]=\"valueAsDate\"\n            [isDisabled]=\"disabled\"\n            (bsValueChange)=\"selectMonth($event)\">\n          </bs-datepicker-inline>\n          <div class=\"select-decendio-container\">\n            <div class=\"select-decendio-container-inner\">\n              <button type=\"button\" class=\"btn btn-default btn-decendio\"\n                  [disabled]=\"disabled\"\n                  (click)=\"reset()\">Limpar</button>\n              <button type=\"button\" class=\"btn btn-default btn-decendio\"\n                *ngFor=\"let index of [1,2,3]\"\n                [disabled]=\"disabled\"\n                [class.active]=\"isSelectedIndex(index)\"\n                title=\"{{index}}&ordm; Dec\u00EAndio\"\n                (click)=\"selectIndex(index)\">\n                {{index}}&ordm;\n              </button>\n            </div>\n          </div>\n        </div>\n      </div>\n      <span class=\"input-group-addon\" *ngIf=\"!inline\">\n        <span tooltip=\"Selecionar dec\u00EAndio\" container=\"body\">\n          <span class=\"material-icons date-picker-icon\"\n            [style.font-size]=\"iconSize\"\n            [class.disabled]=\"disabled\"\n            (click)=\"!disabled && toggle()\">&#xE916;</span>\n        </span>\n      </span>\n    </div>\n\n    <suite-rfb-local-messages [control]=\"ngControl.control\"></suite-rfb-local-messages>\n</div>\n", styles: [".date-picker-icon{cursor:pointer}.date-picker-icon.disabled{cursor:not-allowed;color:rgba(#00205b,.3)!important}.local-messages{color:#a8001f}::ng-deep bs-datepicker-container{z-index:1051}::ng-deep .bs-datepicker{padding:3px;background-color:#fff;border:1px solid rgba(0,0,0,.15);border-radius:4px;box-shadow:0 6px 12px #0000002d}::ng-deep .bs-datepicker-container{background-color:#f2e9d8}::ng-deep .bs-datepicker-head{background-color:#00205b;border-radius:0;color:#fff}::ng-deep .bs-datepicker-head bs-datepicker-navigation-view{display:flex;justify-content:space-between;align-items:center}::ng-deep .bs-datepicker-head bs-datepicker-navigation-view>button{background-color:#00205b;border:none;color:#fff;padding:7px 16px;font-weight:700}::ng-deep .bs-datepicker-head bs-datepicker-navigation-view>button[disabled]{opacity:.3;cursor:not-allowed}::ng-deep .bs-datepicker-body>table>thead>tr{background-color:#64a70b;border-radius:0;color:#fff;font-weight:600}::ng-deep .bs-datepicker-body>table>thead>tr>th{text-align:center;padding:4px 5px}::ng-deep .bs-datepicker-body>table>tbody>tr>td{background-color:#f2e9d8;color:#333;border-radius:0;border:none;text-align:center}::ng-deep .bs-datepicker-body>table>tbody>tr>td>span{padding:0;cursor:pointer;font-weight:600;line-height:26px;width:100%;display:block}::ng-deep .bs-datepicker-body>table{width:100%}::ng-deep .bs-datepicker-body>table>tbody>tr>td>span.disabled{cursor:not-allowed;font-weight:400;background-color:#f9f4e5}::ng-deep .bs-datepicker-body .is-other-month{opacity:.8;font-weight:400}::ng-deep .bs-datepicker-body .disabled{opacity:.65}::ng-deep .bs-datepicker-body>table>tbody>tr>td>span:hover,::ng-deep .bs-datepicker-body>table>tbody>tr>td>span:focus{background-color:#d1e5b6}::ng-deep .bs-datepicker-body>table>tbody>tr>td>.selected,::ng-deep .bs-datepicker-body>table>tbody>tr>td>.selected:hover,::ng-deep .bs-datepicker-body>table>tbody>tr>td>.selected:focus{background:#a66c00;color:#fff}::ng-deep .tabela .date-picker-container tbody tr td,::ng-deep .tabela .date-picker-container tbody tr th{border-right:inherit;border-top:inherit;padding:inherit}::ng-deep .tabela .date-picker-container thead tr th{background-color:inherit;border:inherit;border-right:inherit;color:inherit;font-weight:inherit;padding:inherit;vertical-align:inherit;white-space:inherit}::ng-deep .bs-datepicker-buttons{display:flex;flex-flow:row-reverse;justify-content:space-between;background-color:#e9dbc0}::ng-deep .bs-datepicker-buttons button,::ng-deep .bs-datepicker-buttons button:hover{background-color:inherit;color:#039;font-weight:400;border:none}::ng-deep timepicker table{margin:0 auto}::ng-deep .bs-datepicker-body .today{background:#64a70b;color:#fff}\n", ".decendio-container-inner{background-color:#e9dbc0}.select-decendio-container-inner .btn.btn-decendio{background-color:#e9dbc0;border-radius:0;color:#039;font-weight:400;margin-left:0;border:none;font-weight:600}.select-decendio-container-inner .btn.btn-decendio:hover{background-color:#d1e5b6}.select-decendio-container-inner .btn.btn-decendio.active{background:#a66c00;color:#fff}.decendio-container{width:-moz-fit-content;width:fit-content;padding:3px;background-color:#fff;border:1px solid rgba(0,0,0,.15);border-radius:4px;box-shadow:0 6px 12px #0000002d}.decendio-container.noinline{position:absolute;top:37px;z-index:3}.select-decendio-container-inner{display:flex;justify-content:space-around;margin-left:3px;margin-right:3px}:host ::ng-deep .bs-datepicker{border:none;box-shadow:none;padding:unset;background-color:unset}:host ::ng-deep .bs-datepicker-body td{padding:2px 14px}:host ::ng-deep .bs-datepicker-body>table>tbody>tr>td>span{padding:5px;line-height:16px}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.Injector }, { type: i0.Renderer2 }, { type: DecendioService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: WindowService }]; }, propDecorators: { value: [{
                type: Input,
                args: ['value']
            }], direction: [{
                type: Input
            }], inline: [{
                type: Input
            }], iconSize: [{
                type: Input
            }], inputGroupClass: [{
                type: Input
            }], label: [{
                type: Input
            }], outsideClick: [{
                type: Input
            }], required: [{
                type: Input
            }], divContainer: [{
                type: ViewChild,
                args: ['divContainer']
            }] } });

class DecendioPipe {
    constructor(decendioService) {
        this.decendioService = decendioService;
    }
    transform(value) {
        return value ? this.decendioService.format(value) : undefined;
    }
}
DecendioPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: DecendioPipe, deps: [{ token: DecendioService }], target: i0.ɵɵFactoryTarget.Pipe });
DecendioPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: DecendioPipe, name: "decendio" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: DecendioPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'decendio'
                }]
        }], ctorParameters: function () { return [{ type: DecendioService }]; } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
defineLocale('pt-br', ptBrLocale);
class DecendioPickerConfig extends BsDatepickerInlineConfig {
    constructor() {
        super(...arguments);
        this.locale = 'pt-br';
    }
}
DecendioPickerConfig.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: DecendioPickerConfig, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
DecendioPickerConfig.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: DecendioPickerConfig });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: DecendioPickerConfig, decorators: [{
            type: Injectable
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbDecendioPickerModule {
}
SuiteRfbDecendioPickerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbDecendioPickerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbDecendioPickerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbDecendioPickerModule, declarations: [DecendioPickerComponent,
        DecendioPipe], imports: [CommonModule,
        FormsModule,
        BsDatepickerModule,
        SuiteRfbInputValidationModule,
        SuiteRfbInputMaskModule], exports: [DecendioPickerComponent,
        DecendioPipe] });
SuiteRfbDecendioPickerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbDecendioPickerModule, providers: [
        {
            provide: BsDatepickerInlineConfig,
            useClass: DecendioPickerConfig
        }
    ], imports: [CommonModule,
        FormsModule,
        BsDatepickerModule,
        SuiteRfbInputValidationModule,
        SuiteRfbInputMaskModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbDecendioPickerModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        BsDatepickerModule,
                        SuiteRfbInputValidationModule,
                        SuiteRfbInputMaskModule,
                    ],
                    declarations: [
                        DecendioPickerComponent,
                        DecendioPipe,
                    ],
                    exports: [
                        DecendioPickerComponent,
                        DecendioPipe,
                    ],
                    providers: [
                        {
                            provide: BsDatepickerInlineConfig,
                            useClass: DecendioPickerConfig
                        }
                    ]
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
const DES = {
    id: 'DES',
    description: 'Desenvolvimento',
    baseUrl: 'https://www.suiterfb.desenv.serpro'
};
const HOM = {
    id: 'HOM',
    description: 'Homologação',
    baseUrl: 'https://www.suiterfb.hom.serpro'
};
const PRO = {
    id: 'PRO',
    description: 'Produção',
    baseUrl: 'https://www.suiterfb.receita.fazenda'
};
const ENVS = [DES, HOM, PRO];
class EnvironmentService {
    constructor(configService, locationService) {
        this.configService = configService;
        this.locationService = locationService;
        const environmentId = this.configService.getEnvironmentId();
        this.environment = (environmentId) ? this._findEnvironment(environmentId) : this._parseEnvironment();
    }
    _findEnvironment(environmentId) {
        return ENVS.find(env => env.id === environmentId);
    }
    _parseEnvironment() {
        const href = this.location.href;
        let environment;
        if (href.includes('.suiterfb.desenv.serpro')) {
            environment = DES;
        }
        else if (href.includes('.suiterfb.hom.serpro')) {
            environment = HOM;
        }
        else if (href.includes('.suiterfb.receita.fazenda')) {
            environment = PRO;
        }
        else {
            environment = PRO;
        }
        return environment;
    }
    get location() {
        return this.locationService.get();
    }
}
EnvironmentService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: EnvironmentService, deps: [{ token: ConfigService }, { token: LocationService }], target: i0.ɵɵFactoryTarget.Injectable });
EnvironmentService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: EnvironmentService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: EnvironmentService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: ConfigService }, { type: LocationService }]; } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbEnvironmentModule {
}
SuiteRfbEnvironmentModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbEnvironmentModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbEnvironmentModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbEnvironmentModule, imports: [CommonModule,
        SuiteRfbCommonModule] });
SuiteRfbEnvironmentModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbEnvironmentModule, imports: [CommonModule,
        SuiteRfbCommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbEnvironmentModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        SuiteRfbCommonModule,
                    ],
                    declarations: [],
                    exports: [],
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
const FEATURES = new InjectionToken('ngx-suite-rfb.features');

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class FeatureService {
    constructor(features) {
        this.features = features;
        this.featuresSource = new BehaviorSubject(features);
        this.features$ = this.featuresSource.asObservable().pipe(delay(10));
    }
    getFeatures$() {
        return this.features$;
    }
    isAllowedFeature(feature, user) {
        if (!feature) {
            return false;
        }
        else if (!feature.rolesAllowed) {
            return true;
        }
        else if (!user || !user.roles) {
            return false;
        }
        else {
            const rolesAllowed = feature.rolesAllowed;
            const userRoles = user.roles.map(r => r.id);
            let isAllowed = false;
            rolesAllowed.forEach(roleAllowed => {
                userRoles.forEach(userRole => {
                    if (userRole === roleAllowed) {
                        isAllowed = true;
                    }
                });
            });
            return isAllowed;
        }
    }
    findFeatureWithRoute(features, route) {
        if (!features) {
            return undefined;
        }
        else {
            let feature;
            features.forEach(f => {
                if (!feature) {
                    feature = (route === f.route) ? f : this.findFeatureWithRoute(f.children, route);
                }
            });
            return feature;
        }
    }
    setFeatures(features) {
        this.featuresSource.next(features);
    }
}
FeatureService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: FeatureService, deps: [{ token: FEATURES }], target: i0.ɵɵFactoryTarget.Injectable });
FeatureService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: FeatureService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: FeatureService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [FEATURES]
                }] }]; } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class UserService {
    constructor() {
        this.userSource = new BehaviorSubject(undefined);
        this.user$ = this.userSource.asObservable();
    }
    setUser(user) {
        this.user = user;
        this.userSource.next(user);
    }
    getUser() {
        return this.user$;
    }
}
UserService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: UserService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
UserService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: UserService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: UserService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class AllowedFeaturePipe {
    constructor(featureService) {
        this.featureService = featureService;
    }
    transform(feature, user) {
        return this.featureService.isAllowedFeature(feature, user);
    }
}
AllowedFeaturePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: AllowedFeaturePipe, deps: [{ token: FeatureService }], target: i0.ɵɵFactoryTarget.Pipe });
AllowedFeaturePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: AllowedFeaturePipe, name: "allowedFeature" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: AllowedFeaturePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'allowedFeature'
                }]
        }], ctorParameters: function () { return [{ type: FeatureService }]; } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class HasMenuFeaturesPipe {
    transform(feature) {
        return (!feature || !feature.children) ? false : feature.children.filter(f => f.menu === true).length > 0;
    }
}
HasMenuFeaturesPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: HasMenuFeaturesPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
HasMenuFeaturesPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: HasMenuFeaturesPipe, name: "hasMenuFeatures" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: HasMenuFeaturesPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'hasMenuFeatures'
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class MenuFeaturesPipe {
    transform(features) {
        return (!features) ? undefined : features.filter(f => f.menu === true);
    }
}
MenuFeaturesPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: MenuFeaturesPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
MenuFeaturesPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: MenuFeaturesPipe, name: "menuFeatures" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: MenuFeaturesPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'menuFeatures'
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class ExpandedMenuComponent {
    constructor(featureService, userService, configService) {
        this.featureService = featureService;
        this.userService = userService;
        this.configService = configService;
        this.showCollapseIcon = false;
        this.features = [];
        this.ngUnsubscribe = new Subject();
    }
    ngOnInit() {
        this.expandedMenuHierarchical = this.configService.isExpandedMenuHierarchical();
        if (this.feature) {
            this.panelTitle = this.feature.title;
            this.features = this.feature.children;
        }
        else {
            this.panelTitle = 'Menu expandido';
            this.featureService.getFeatures$()
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe((features) => this.features = features);
        }
        this.userService.getUser()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((user) => this.user = user);
    }
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
}
ExpandedMenuComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: ExpandedMenuComponent, deps: [{ token: FeatureService }, { token: UserService }, { token: ConfigService }], target: i0.ɵɵFactoryTarget.Component });
ExpandedMenuComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: ExpandedMenuComponent, selector: "suite-rfb-expanded-menu", inputs: { feature: "feature", showCollapseIcon: "showCollapseIcon" }, ngImport: i0, template: "<div class=\"panel pagination\">\n  <div class=\"panel-heading\">\n    <h3 class=\"panel-title\">{{panelTitle}}</h3>\n  </div>\n  <div class=\"panel-body\">\n    <div class=\"row\">\n      <ng-container *ngFor=\"let feature of (features|menuFeatures)\">\n        <div class=\"col-md-4\" *ngIf=\"feature|allowedFeature:user\">\n\n          <ng-container *ngIf=\"!expandedMenuHierarchical\">\n            <suite-rfb-collapse [showIcon]=\"showCollapseIcon\" [title]=\"feature.title\">\n              <ul>\n                <ng-container *ngTemplateOutlet=\"featureTemplate; context: {feature: feature, user: user}\">\n                </ng-container>\n              </ul>\n            </suite-rfb-collapse>\n          </ng-container>\n\n          <ng-container *ngIf=\"expandedMenuHierarchical\">\n            <ng-container *ngTemplateOutlet=\"featureTemplate; context: {feature: feature, user: user}\"></ng-container>\n          </ng-container>\n\n        </div>\n      </ng-container>\n    </div>\n  </div>\n</div>\n\n<ng-template #featureTemplate let-feature=\"feature\" let-user=\"user\">\n  <ng-container *ngIf=\"feature|allowedFeature:user\">\n    <ng-container *ngIf=\"!(feature|hasMenuFeatures)\">\n      <li>\n        <a *ngIf=\"feature.url\" [href]=\"feature.url\" [target]=\"feature.urlTarget ? feature.urlTarget : '_blank'\">\n          <ng-container *ngTemplateOutlet=\"itemTemplate; context: { feature: feature }\"></ng-container>\n        </a>\n        <a *ngIf=\"!feature.url\" [routerLink]=\"feature.route\">\n          <ng-container *ngTemplateOutlet=\"itemTemplate; context: { feature: feature }\"></ng-container>\n        </a>\n      </li>\n    </ng-container>\n\n    <ng-container *ngIf=\"feature|hasMenuFeatures\">\n      <suite-rfb-collapse *ngIf=\"expandedMenuHierarchical\" [showIcon]=\"showCollapseIcon\" [title]=\"feature.title\">\n        <ul>\n          <ng-container *ngTemplateOutlet=\"childrenTemplate; context: {feature: feature, user: user}\"></ng-container>\n        </ul>\n      </suite-rfb-collapse>\n\n      <ng-container *ngIf=\"!expandedMenuHierarchical\">\n        <ng-container *ngTemplateOutlet=\"childrenTemplate; context: {feature: feature, user: user}\"></ng-container>\n      </ng-container>\n    </ng-container>\n  </ng-container>\n</ng-template>\n\n<ng-template #childrenTemplate let-feature=\"feature\" let-user=\"user\">\n  <ng-container *ngFor=\"let childFeature of (feature.children|menuFeatures)\">\n    <ng-container *ngTemplateOutlet=\"featureTemplate; context: {feature: childFeature, user: user}\">\n    </ng-container>\n  </ng-container>\n</ng-template>\n\n<ng-template #itemTemplate let-feature=\"feature\">\n  <span *ngIf=\"feature.icon\" class=\"material-icons icon-24\">{{feature.icon}}</span>\n  <span>{{feature.title}}</span>\n</ng-template>", styles: [":host ul h2{border-bottom:2px solid #997FC0}:host ul ul h2{border-bottom:2px solid gray}:host suite-rfb-collapse suite-rfb-collapse:not(:first-of-type),:host suite-rfb-collapse suite-rfb-collapse suite-rfb-collapse{display:inline-block;margin-top:5px}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i1$1.RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }, { kind: "component", type: CollapseComponent, selector: "suite-rfb-collapse", inputs: ["title", "vertical", "disabled", "collapsed", "showIcon"], outputs: ["collapsedChange"] }, { kind: "pipe", type: AllowedFeaturePipe, name: "allowedFeature" }, { kind: "pipe", type: HasMenuFeaturesPipe, name: "hasMenuFeatures" }, { kind: "pipe", type: MenuFeaturesPipe, name: "menuFeatures" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: ExpandedMenuComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-expanded-menu', template: "<div class=\"panel pagination\">\n  <div class=\"panel-heading\">\n    <h3 class=\"panel-title\">{{panelTitle}}</h3>\n  </div>\n  <div class=\"panel-body\">\n    <div class=\"row\">\n      <ng-container *ngFor=\"let feature of (features|menuFeatures)\">\n        <div class=\"col-md-4\" *ngIf=\"feature|allowedFeature:user\">\n\n          <ng-container *ngIf=\"!expandedMenuHierarchical\">\n            <suite-rfb-collapse [showIcon]=\"showCollapseIcon\" [title]=\"feature.title\">\n              <ul>\n                <ng-container *ngTemplateOutlet=\"featureTemplate; context: {feature: feature, user: user}\">\n                </ng-container>\n              </ul>\n            </suite-rfb-collapse>\n          </ng-container>\n\n          <ng-container *ngIf=\"expandedMenuHierarchical\">\n            <ng-container *ngTemplateOutlet=\"featureTemplate; context: {feature: feature, user: user}\"></ng-container>\n          </ng-container>\n\n        </div>\n      </ng-container>\n    </div>\n  </div>\n</div>\n\n<ng-template #featureTemplate let-feature=\"feature\" let-user=\"user\">\n  <ng-container *ngIf=\"feature|allowedFeature:user\">\n    <ng-container *ngIf=\"!(feature|hasMenuFeatures)\">\n      <li>\n        <a *ngIf=\"feature.url\" [href]=\"feature.url\" [target]=\"feature.urlTarget ? feature.urlTarget : '_blank'\">\n          <ng-container *ngTemplateOutlet=\"itemTemplate; context: { feature: feature }\"></ng-container>\n        </a>\n        <a *ngIf=\"!feature.url\" [routerLink]=\"feature.route\">\n          <ng-container *ngTemplateOutlet=\"itemTemplate; context: { feature: feature }\"></ng-container>\n        </a>\n      </li>\n    </ng-container>\n\n    <ng-container *ngIf=\"feature|hasMenuFeatures\">\n      <suite-rfb-collapse *ngIf=\"expandedMenuHierarchical\" [showIcon]=\"showCollapseIcon\" [title]=\"feature.title\">\n        <ul>\n          <ng-container *ngTemplateOutlet=\"childrenTemplate; context: {feature: feature, user: user}\"></ng-container>\n        </ul>\n      </suite-rfb-collapse>\n\n      <ng-container *ngIf=\"!expandedMenuHierarchical\">\n        <ng-container *ngTemplateOutlet=\"childrenTemplate; context: {feature: feature, user: user}\"></ng-container>\n      </ng-container>\n    </ng-container>\n  </ng-container>\n</ng-template>\n\n<ng-template #childrenTemplate let-feature=\"feature\" let-user=\"user\">\n  <ng-container *ngFor=\"let childFeature of (feature.children|menuFeatures)\">\n    <ng-container *ngTemplateOutlet=\"featureTemplate; context: {feature: childFeature, user: user}\">\n    </ng-container>\n  </ng-container>\n</ng-template>\n\n<ng-template #itemTemplate let-feature=\"feature\">\n  <span *ngIf=\"feature.icon\" class=\"material-icons icon-24\">{{feature.icon}}</span>\n  <span>{{feature.title}}</span>\n</ng-template>", styles: [":host ul h2{border-bottom:2px solid #997FC0}:host ul ul h2{border-bottom:2px solid gray}:host suite-rfb-collapse suite-rfb-collapse:not(:first-of-type),:host suite-rfb-collapse suite-rfb-collapse suite-rfb-collapse{display:inline-block;margin-top:5px}\n"] }]
        }], ctorParameters: function () { return [{ type: FeatureService }, { type: UserService }, { type: ConfigService }]; }, propDecorators: { feature: [{
                type: Input
            }], showCollapseIcon: [{
                type: Input
            }] } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class CurrentFeatureService {
    constructor() {
        this.currentRouteSource = new BehaviorSubject(undefined);
        this.currentRoute$ = this.currentRouteSource.asObservable();
        this.currentFeatureSource = new BehaviorSubject(undefined);
        this.currentFeature$ = this.currentFeatureSource.asObservable();
    }
    setCurrentRoute(route) {
        this.currentRoute = route;
        this.currentRouteSource.next(this.currentRoute);
    }
    getCurrentRoute() {
        return this.currentRoute$;
    }
    setCurrentFeature(feature) {
        this.currentFeature = feature;
        this.currentFeatureSource.next(this.currentFeature);
    }
    getCurrentFeature() {
        return this.currentFeature$;
    }
}
CurrentFeatureService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: CurrentFeatureService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CurrentFeatureService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: CurrentFeatureService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: CurrentFeatureService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
const FAVORITE_FEATURES_KEY = 'ngx-suite-rfb.favorite-features';
class FavoriteFeaturesService {
    constructor() {
        this.favoriteFeatures = [];
        this.favoriteFeaturesSource = new BehaviorSubject(this.favoriteFeatures);
        this.favoriteFeatures$ = this.favoriteFeaturesSource.asObservable();
    }
    getFavoriteFeatures$() {
        return this.favoriteFeatures$;
    }
    init() {
        this.favoriteFeatures = this.load();
        this.favoriteFeaturesSource.next(this.favoriteFeatures);
    }
    toggle(feature) {
        if (feature.favorite) {
            this.unfavorite(feature);
        }
        else {
            this.favorite(feature);
        }
    }
    favorite(feature) {
        feature.favorite = true;
        const index = this.find(feature);
        if (index < 0) {
            this.favoriteFeatures.push(feature);
            this.favoriteFeaturesSource.next(this.favoriteFeatures);
            this.save();
        }
    }
    unfavorite(feature) {
        feature.favorite = false;
        const index = this.find(feature);
        if (index >= 0) {
            this.favoriteFeatures.splice(index, 1);
            this.favoriteFeaturesSource.next(this.favoriteFeatures);
            this.save();
        }
    }
    save() {
        const encoded = this.encode(this.favoriteFeatures);
        if (encoded) {
            localStorage.setItem(FAVORITE_FEATURES_KEY, encoded);
        }
    }
    load() {
        const encoded = localStorage.getItem(FAVORITE_FEATURES_KEY);
        return (!encoded) ? [] : this.decode(encoded);
    }
    find(feature) {
        return this.favoriteFeatures.findIndex(f => f.route === feature.route && f.title === feature.title);
    }
    encode(features) {
        return features ? Base64.encodeURI(JSON.stringify(features)) : undefined;
    }
    decode(encoded) {
        return encoded ? JSON.parse(Base64.decode(encoded)) : undefined;
    }
}
FavoriteFeaturesService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: FavoriteFeaturesService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
FavoriteFeaturesService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: FavoriteFeaturesService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: FavoriteFeaturesService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class CurrentFeatureComponent {
    constructor(featureService, currentFeatureService, favoriteFeaturesService, router) {
        this.featureService = featureService;
        this.currentFeatureService = currentFeatureService;
        this.favoriteFeaturesService = favoriteFeaturesService;
        this.router = router;
        this.features = [];
        this.ngUnsubscribe = new Subject();
    }
    ngOnInit() {
        this.featureService.getFeatures$()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((features) => {
            this.features = features;
            this._updateCurrentFeature();
        });
        this.currentFeatureService.getCurrentFeature()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((feature) => this.feature = feature);
        this.currentFeatureService.getCurrentRoute()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((route) => {
            this.route = route;
            this._updateCurrentFeature();
        });
        this.router.events
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(event => {
            if (event instanceof NavigationEnd) {
                const route = (event.urlAfterRedirects) ? event.urlAfterRedirects : event.url;
                this.currentFeatureService.setCurrentRoute(route);
            }
        });
    }
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
    toggleFavorite() {
        this.favoriteFeaturesService.toggle(this.feature);
    }
    _updateCurrentFeature() {
        const currentFeature = this.featureService.findFeatureWithRoute(this.features, this.route);
        this.currentFeatureService.setCurrentFeature(currentFeature);
    }
}
CurrentFeatureComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: CurrentFeatureComponent, deps: [{ token: FeatureService }, { token: CurrentFeatureService }, { token: FavoriteFeaturesService }, { token: i1$1.Router }], target: i0.ɵɵFactoryTarget.Component });
CurrentFeatureComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: CurrentFeatureComponent, selector: "suite-rfb-current-feature", ngImport: i0, template: "<div class=\"funcionalidade\" *ngIf=\"feature\">\n  <a [routerLink]=\"feature.route\">{{feature.title}}</a>\n  <a href=\"javascript:void(0)\" class=\"toggle-favorite\" (click)=\"toggleFavorite()\">\n    <span class=\"material-icons icon-24\">{{feature.favorite ? '&#xE838;' : '&#xE83A;'}}</span>\n  </a>\n</div>\n", styles: [""], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: CurrentFeatureComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-current-feature', template: "<div class=\"funcionalidade\" *ngIf=\"feature\">\n  <a [routerLink]=\"feature.route\">{{feature.title}}</a>\n  <a href=\"javascript:void(0)\" class=\"toggle-favorite\" (click)=\"toggleFavorite()\">\n    <span class=\"material-icons icon-24\">{{feature.favorite ? '&#xE838;' : '&#xE83A;'}}</span>\n  </a>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: FeatureService }, { type: CurrentFeatureService }, { type: FavoriteFeaturesService }, { type: i1$1.Router }]; } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class FavoriteFeaturesComponent {
    constructor(featureService, favoriteFeaturesService) {
        this.featureService = featureService;
        this.favoriteFeaturesService = favoriteFeaturesService;
        this.features = [];
        this.favoriteFeatures = [];
        this.ngUnsubscribe = new Subject();
    }
    ngOnInit() {
        this.featureService.getFeatures$()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((features) => {
            this.features = features;
            this.markFavorites(this.features, this.favoriteFeatures);
        });
        this.favoriteFeaturesService.init(); // TODO: avaliar outra forma de inicializar o servico?
        this.favoriteFeaturesService.getFavoriteFeatures$()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((features) => {
            this.favoriteFeatures = features;
            this.markFavorites(this.features, this.favoriteFeatures);
            if (this.features.length) {
                this.featureService.setFeatures(this.features);
            }
        });
    }
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
    markFavorites(features, favoriteFeatures) {
        if (!features || !favoriteFeatures) {
            return;
        }
        else {
            features.forEach(feature => {
                if (favoriteFeatures.findIndex(favorite => favorite.title === feature.title && favorite.route === feature.route) >= 0) {
                    feature.favorite = true;
                }
                this.markFavorites(feature.children, favoriteFeatures);
            });
        }
    }
}
FavoriteFeaturesComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: FavoriteFeaturesComponent, deps: [{ token: FeatureService }, { token: FavoriteFeaturesService }], target: i0.ɵɵFactoryTarget.Component });
FavoriteFeaturesComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: FavoriteFeaturesComponent, selector: "suite-rfb-favorite-features", ngImport: i0, template: "", styles: [""] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: FavoriteFeaturesComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-favorite-features', template: "" }]
        }], ctorParameters: function () { return [{ type: FeatureService }, { type: FavoriteFeaturesService }]; } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class HasAllowedLinkedFeaturesPipe {
    constructor(featureService) {
        this.featureService = featureService;
    }
    transform(feature, user) {
        return (!feature || !feature.children || !user) ? false
            : feature.children.filter(f => f.linked === true && this.featureService.isAllowedFeature(f, user)).length > 0;
    }
}
HasAllowedLinkedFeaturesPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: HasAllowedLinkedFeaturesPipe, deps: [{ token: FeatureService }], target: i0.ɵɵFactoryTarget.Pipe });
HasAllowedLinkedFeaturesPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: HasAllowedLinkedFeaturesPipe, name: "hasAllowedLinkedFeatures" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: HasAllowedLinkedFeaturesPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'hasAllowedLinkedFeatures'
                }]
        }], ctorParameters: function () { return [{ type: FeatureService }]; } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class LinkedFeaturesPipe {
    transform(features) {
        return (!features) ? undefined : features.filter(f => f.linked === true);
    }
}
LinkedFeaturesPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: LinkedFeaturesPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
LinkedFeaturesPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: LinkedFeaturesPipe, name: "linkedFeatures" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: LinkedFeaturesPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'linkedFeatures'
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class LinkedFeaturesComponent {
    constructor(currentFeatureService, userService) {
        this.currentFeatureService = currentFeatureService;
        this.userService = userService;
        this.ngUnsubscribe = new Subject();
    }
    ngOnInit() {
        this.currentFeatureService.getCurrentFeature()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((feature) => this.feature = feature);
        this.userService.getUser()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((user) => this.user = user);
    }
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
}
LinkedFeaturesComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: LinkedFeaturesComponent, deps: [{ token: CurrentFeatureService }, { token: UserService }], target: i0.ɵɵFactoryTarget.Component });
LinkedFeaturesComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: LinkedFeaturesComponent, selector: "suite-rfb-linked-features", ngImport: i0, template: "<!-- Menu de funcionalidades vinculadas -->\n<div class=\"funcionalidades-vinculadas hidden-xs\" *ngIf=\"feature|hasAllowedLinkedFeatures:user\">\n  <div class=\"btn-group\" role=\"group\" aria-label=\"...\">\n    <ng-container *ngFor=\"let child of (feature.children|linkedFeatures)\">\n      <button type=\"button\" class=\"btn btn-default\" *ngIf=\"child|allowedFeature:user\" [routerLink]=\"child.route\">\n        <span *ngIf=\"child.icon\" class=\"material-icons icon-18\">{{child.icon}}</span>\n        <span>{{child.title}}</span>\n      </button>\n    </ng-container>\n  </div>\n</div>\n", styles: [""], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }, { kind: "pipe", type: AllowedFeaturePipe, name: "allowedFeature" }, { kind: "pipe", type: HasAllowedLinkedFeaturesPipe, name: "hasAllowedLinkedFeatures" }, { kind: "pipe", type: LinkedFeaturesPipe, name: "linkedFeatures" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: LinkedFeaturesComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-linked-features', template: "<!-- Menu de funcionalidades vinculadas -->\n<div class=\"funcionalidades-vinculadas hidden-xs\" *ngIf=\"feature|hasAllowedLinkedFeatures:user\">\n  <div class=\"btn-group\" role=\"group\" aria-label=\"...\">\n    <ng-container *ngFor=\"let child of (feature.children|linkedFeatures)\">\n      <button type=\"button\" class=\"btn btn-default\" *ngIf=\"child|allowedFeature:user\" [routerLink]=\"child.route\">\n        <span *ngIf=\"child.icon\" class=\"material-icons icon-18\">{{child.icon}}</span>\n        <span>{{child.title}}</span>\n      </button>\n    </ng-container>\n  </div>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: CurrentFeatureService }, { type: UserService }]; } });

function isBlank(str) {
    return (!str || str.length === 0 || str.trim().length === 0);
}

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class FeatureFilterPipe {
    transform(features, searchTerms, favoriteOnly) {
        return this.filter(features, searchTerms, favoriteOnly);
    }
    filter(features, _searchTerms, favoriteOnly) {
        const searchTerms = isBlank(_searchTerms) ? '' : _searchTerms.trim().toLowerCase();
        const ret = [];
        for (const feature of features) {
            if (feature.menu === false) {
                continue;
            }
            if (favoriteOnly) {
                if (!feature.favorite && !feature.children) {
                    continue;
                }
            }
            const newFeature = {
                title: feature.title
            };
            // TODO use spread operator to copy properties/*  */
            if (feature.route !== undefined) {
                newFeature.route = feature.route;
            }
            if (feature.url !== undefined) {
                newFeature.url = feature.url;
            }
            if (feature.urlTarget !== undefined) {
                newFeature.urlTarget = feature.urlTarget;
            }
            if (feature.icon !== undefined) {
                newFeature.icon = feature.icon;
            }
            if (feature.favorite !== undefined) {
                newFeature.favorite = feature.favorite;
            }
            if (feature.menu !== undefined) {
                newFeature.menu = feature.menu;
            }
            if (feature.linked !== undefined) {
                newFeature.linked = feature.linked;
            }
            if (feature.rolesAllowed !== undefined) {
                newFeature.rolesAllowed = feature.rolesAllowed;
            }
            if (feature.shortcut !== undefined) {
                newFeature.shortcut = feature.shortcut;
            }
            if (feature.children) {
                const newChildren = this.filter(feature.children, searchTerms, favoriteOnly);
                if (newChildren.length > 0) {
                    ret.push(newFeature);
                    newFeature.children = [...newChildren];
                    // não tem filhos que serão mostrados (talvez por ser linked features),
                    // mas tem uma rota definida, ver se atende os criterios para poder ser incluida
                    // em outras palavras: tem filhos mas não é um submenu
                }
                else if (feature.route && this.atendeCriterios(feature, searchTerms, favoriteOnly)) {
                    ret.push(newFeature);
                }
            }
            else {
                if (this.atendeCriterios(feature, searchTerms, favoriteOnly)) {
                    ret.push(newFeature);
                }
            }
        }
        return ret;
    }
    atendeCriterios(feature, searchTerms, favoriteOnly) {
        if (searchTerms.length === 0 && !favoriteOnly) {
            return true;
        }
        if ((searchTerms.length > 0 && feature.title.toLowerCase().startsWith(searchTerms))
            || (searchTerms.length === 0 && favoriteOnly && feature.favorite)) {
            return true;
        }
        return false;
    }
}
FeatureFilterPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: FeatureFilterPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
FeatureFilterPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: FeatureFilterPipe, name: "featureFilter" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: FeatureFilterPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'featureFilter'
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class HasLinkedFeaturesPipe {
    transform(feature) {
        return (!feature || !feature.children) ? false : feature.children.filter(f => f.linked === true).length > 0;
    }
}
HasLinkedFeaturesPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: HasLinkedFeaturesPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
HasLinkedFeaturesPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: HasLinkedFeaturesPipe, name: "hasLinkedFeatures" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: HasLinkedFeaturesPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'hasLinkedFeatures'
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbFeaturesModule {
    static forRoot(features, config) {
        return {
            ngModule: SuiteRfbFeaturesModule,
            providers: [
                {
                    provide: FEATURES,
                    useValue: features
                },
                {
                    provide: SUITE_RFB_MODULE_CONFIG,
                    useValue: config
                }
            ]
        };
    }
}
SuiteRfbFeaturesModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbFeaturesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbFeaturesModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbFeaturesModule, declarations: [CurrentFeatureComponent,
        FavoriteFeaturesComponent,
        LinkedFeaturesComponent,
        AllowedFeaturePipe,
        HasAllowedLinkedFeaturesPipe,
        HasMenuFeaturesPipe,
        HasLinkedFeaturesPipe,
        LinkedFeaturesPipe,
        MenuFeaturesPipe,
        FeatureFilterPipe], imports: [CommonModule, i1$1.RouterModule], exports: [CurrentFeatureComponent,
        FavoriteFeaturesComponent,
        LinkedFeaturesComponent,
        AllowedFeaturePipe,
        HasAllowedLinkedFeaturesPipe,
        HasMenuFeaturesPipe,
        HasLinkedFeaturesPipe,
        LinkedFeaturesPipe,
        MenuFeaturesPipe,
        FeatureFilterPipe] });
SuiteRfbFeaturesModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbFeaturesModule, imports: [CommonModule,
        RouterModule.forChild([])] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbFeaturesModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        RouterModule.forChild([]),
                    ],
                    declarations: [
                        CurrentFeatureComponent,
                        FavoriteFeaturesComponent,
                        LinkedFeaturesComponent,
                        AllowedFeaturePipe,
                        HasAllowedLinkedFeaturesPipe,
                        HasMenuFeaturesPipe,
                        HasLinkedFeaturesPipe,
                        LinkedFeaturesPipe,
                        MenuFeaturesPipe,
                        FeatureFilterPipe,
                    ],
                    exports: [
                        CurrentFeatureComponent,
                        FavoriteFeaturesComponent,
                        LinkedFeaturesComponent,
                        AllowedFeaturePipe,
                        HasAllowedLinkedFeaturesPipe,
                        HasMenuFeaturesPipe,
                        HasLinkedFeaturesPipe,
                        LinkedFeaturesPipe,
                        MenuFeaturesPipe,
                        FeatureFilterPipe,
                    ],
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class UserProfilesPipe {
    transform(user) {
        return (!user || !user.profiles) ? undefined : user.profiles.map((profile) => profile.name).join(', ');
    }
}
UserProfilesPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: UserProfilesPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
UserProfilesPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: UserProfilesPipe, name: "userProfiles" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: UserProfilesPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'userProfiles'
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class UserRolesPipe {
    transform(user) {
        return (!user || !user.roles) ? undefined : user.roles.map((role) => role.name).join(', ');
    }
}
UserRolesPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: UserRolesPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
UserRolesPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: UserRolesPipe, name: "userRoles" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: UserRolesPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'userRoles'
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class UserInfoComponent {
    constructor(userService) {
        this.userService = userService;
        this.ngUnsubscribe = new Subject();
    }
    ngOnInit() {
        this.userService.getUser()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((user) => this.user = user);
    }
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
}
UserInfoComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: UserInfoComponent, deps: [{ token: UserService }], target: i0.ɵɵFactoryTarget.Component });
UserInfoComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: UserInfoComponent, selector: "suite-rfb-user-info", inputs: { showUserProfiles: "showUserProfiles" }, ngImport: i0, template: "<div class=\"servidor navbar-right text-right hidden-xs\" *ngIf=\"user\">\n  <p class=\"servidor-nome\" [title]=\"user.name\">{{user.name}}</p>\n  <p class=\"servidor-perfil\" [title]=\"user|userRoles\" *ngIf=\"!showUserProfiles\">{{user|userRoles}}</p>\n  <p class=\"servidor-perfil\" [title]=\"user|userProfiles\" *ngIf=\"showUserProfiles\">{{user|userProfiles}}</p>\n</div>\n", styles: [".servidor-nome,.servidor-perfil{width:250px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: UserProfilesPipe, name: "userProfiles" }, { kind: "pipe", type: UserRolesPipe, name: "userRoles" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: UserInfoComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-user-info', template: "<div class=\"servidor navbar-right text-right hidden-xs\" *ngIf=\"user\">\n  <p class=\"servidor-nome\" [title]=\"user.name\">{{user.name}}</p>\n  <p class=\"servidor-perfil\" [title]=\"user|userRoles\" *ngIf=\"!showUserProfiles\">{{user|userRoles}}</p>\n  <p class=\"servidor-perfil\" [title]=\"user|userProfiles\" *ngIf=\"showUserProfiles\">{{user|userProfiles}}</p>\n</div>\n", styles: [".servidor-nome,.servidor-perfil{width:250px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}\n"] }]
        }], ctorParameters: function () { return [{ type: UserService }]; }, propDecorators: { showUserProfiles: [{
                type: Input
            }] } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbUserModule {
}
SuiteRfbUserModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbUserModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbUserModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbUserModule, declarations: [UserInfoComponent,
        UserProfilesPipe,
        UserRolesPipe], imports: [CommonModule], exports: [UserInfoComponent,
        UserProfilesPipe,
        UserRolesPipe] });
SuiteRfbUserModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbUserModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbUserModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                    ],
                    declarations: [
                        UserInfoComponent,
                        UserProfilesPipe,
                        UserRolesPipe,
                    ],
                    exports: [
                        UserInfoComponent,
                        UserProfilesPipe,
                        UserRolesPipe,
                    ],
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbExpandedMenuModule {
}
SuiteRfbExpandedMenuModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbExpandedMenuModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbExpandedMenuModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbExpandedMenuModule, declarations: [ExpandedMenuComponent], imports: [CommonModule, i1$1.RouterModule, SuiteRfbCollapseModule,
        SuiteRfbFeaturesModule,
        SuiteRfbUserModule], exports: [ExpandedMenuComponent] });
SuiteRfbExpandedMenuModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbExpandedMenuModule, imports: [CommonModule,
        RouterModule.forChild([]),
        SuiteRfbCollapseModule,
        SuiteRfbFeaturesModule,
        SuiteRfbUserModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbExpandedMenuModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        RouterModule.forChild([]),
                        SuiteRfbCollapseModule,
                        SuiteRfbFeaturesModule,
                        SuiteRfbUserModule,
                    ],
                    declarations: [
                        ExpandedMenuComponent,
                    ],
                    exports: [
                        ExpandedMenuComponent,
                    ],
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/* adaptado do primeng */
function isFileTypeValid(file, accept) {
    let acceptableTypes = accept.split(',').map(type => type.trim());
    for (let type of acceptableTypes) {
        let acceptable = isWildcard(type) ? getTypeClass(file.type) === getTypeClass(type)
            : file.type == type || getFileExtension(file).toLowerCase() === type.toLowerCase();
        if (acceptable) {
            return true;
        }
    }
    return false;
}
function getTypeClass(fileType) {
    return fileType.substring(0, fileType.indexOf('/'));
}
function isWildcard(fileType) {
    return fileType.indexOf('*') !== -1;
}
function getFileExtension(file) {
    return '.' + file.name.split('.').pop();
}

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class FileTypeValidatorDirective {
    constructor() {
        this.valFn = Validators.nullValidator;
    }
    ngOnInit() {
        this.valFn = fileTypeValidator(this.accept);
    }
    ngOnChanges(changes) {
        const changed = changes['suiteRfbFileType'];
        const newAccept = (changed) ? changed.currentValue : this.accept;
        this.valFn = fileTypeValidator(newAccept);
    }
    validate(control) {
        return this.valFn(control);
    }
}
FileTypeValidatorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: FileTypeValidatorDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
FileTypeValidatorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.1.2", type: FileTypeValidatorDirective, selector: "[suiteRfbFileType]", inputs: { accept: ["suiteRfbFileType", "accept"] }, providers: [{ provide: NG_VALIDATORS, useExisting: FileTypeValidatorDirective, multi: true }], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: FileTypeValidatorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[suiteRfbFileType]',
                    providers: [{ provide: NG_VALIDATORS, useExisting: FileTypeValidatorDirective, multi: true }]
                }]
        }], propDecorators: { accept: [{
                type: Input,
                args: ['suiteRfbFileType']
            }] } });
function fileTypeValidator(accept) {
    return (control) => {
        let errors = null;
        const value = control.value;
        if (value && value instanceof File && accept
            && !isFileTypeValid(value, accept)) {
            errors = { 'invalidFileType': { value: value.type, accept } };
        }
        return errors;
    };
}

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class FilePickerComponent {
    get value() {
        return this.modelValue;
    }
    set value(value) {
        this.modelValue = value;
        this.onChange(value);
        this.onTouched();
    }
    get fileName() {
        return this.viewValue ? this.viewValue : '';
    }
    set fileName(viewValue) {
        this.viewValue = viewValue;
    }
    writeValue(value) {
        this.modelValue = value;
        this.viewValue = value?.name;
        if (!value) {
            if (this.fileUploadElement?.nativeElement?.value) {
                this.fileUploadElement.nativeElement.value = null;
            }
        }
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
        if (this.fileUploadElement) {
            this.renderer.setProperty(this.fileUploadElement.nativeElement, 'disabled', isDisabled);
        }
        else {
            setTimeout(() => this.setDisabledState(isDisabled));
        }
    }
    constructor(injector, renderer) {
        this.injector = injector;
        this.renderer = renderer;
        this.onChange = () => { };
        this.onTouched = () => { };
        this.iconSize = '32px';
        this.inline = false;
        this.inputGroupClass = 'input-group';
        this.label = 'Arquivo';
        this.required = false;
        this.placeholder = 'Escolha o arquivo ou arraste e solte aqui';
        this.fileChange = new EventEmitter();
        this.disabled = false;
    }
    ngOnInit() {
        this.ngControl = this.injector.get(NgControl);
        this.iconSize = this.inline ? '24px' : '32px';
    }
    ngAfterViewInit() {
        this._setValidators();
    }
    _setValidators() {
        if (!this.accept)
            return;
        const validators = [fileTypeValidator(this.accept)];
        if (this.ngControl.control.validator) {
            validators.push(this.ngControl.control.validator);
        }
        this.ngControl.control.setValidators(validators);
    }
    openFileSelection() {
        if (!this.disabled) {
            this.fileUploadElement.nativeElement.click();
        }
    }
    clearFile() {
        this.value = undefined;
        this.fileName = '';
        this.fileChange.emit(null);
        this.fileUploadElement.nativeElement.value = null;
    }
    onFileChange(e) {
        const fileList = e.target.files;
        if (fileList && fileList.length > 0) {
            this.value = fileList[0];
            this.fileName = fileList[0].name;
            this.fileChange.emit(fileList);
        }
    }
    onDragOver() {
        if (!this.disabled && !this.value) {
            this.dragging = true;
        }
    }
    onDragLeave() {
        this.dragging = false;
    }
    ignoreEvent(e) {
        e.stopPropagation();
        e.preventDefault();
    }
    onDrop(event) {
        if (this.isAcceptingFileDrop()) {
            this.dragging = false;
            const e = {
                target: { files: event.dataTransfer.files }
            };
            this.onFileChange(e);
        }
        event.stopPropagation();
        event.preventDefault();
    }
    isAcceptingFileDrop() {
        return !this.disabled && !this.value;
    }
}
FilePickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: FilePickerComponent, deps: [{ token: i0.Injector }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component });
FilePickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: FilePickerComponent, selector: "suite-rfb-file-picker", inputs: { value: "value", accept: "accept", iconSize: "iconSize", inline: "inline", inputGroupClass: "inputGroupClass", label: "label", name: "name", required: "required", placeholder: "placeholder", disabled: "disabled" }, outputs: { fileChange: "fileChange" }, providers: [{
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => FilePickerComponent)
        }], viewQueries: [{ propertyName: "fileUploadElement", first: true, predicate: ["fileUploadElement"], descendants: true }], ngImport: i0, template: "<ng-container *ngIf=\"inline\">\n  <ng-container *ngTemplateOutlet=\"template\"></ng-container>\n</ng-container>\n\n<ng-container *ngIf=\"!inline\">\n  <div class=\"form-group\" [suiteRfbHasError]=\"ngControl.control\">\n    <label [for]=\"name\" *ngIf=\"label\"\n      class=\"control-label\"\n      [class.obrigatorio]=\"required\">\n      {{label}}\n    </label>\n    <ng-container *ngTemplateOutlet=\"template\"></ng-container>\n    <suite-rfb-local-messages [control]=\"ngControl.control\"></suite-rfb-local-messages>\n  </div>\n</ng-container>\n\n<ng-template #template>\n  <div [class]=\"inputGroupClass\">\n    <input type=\"text\"\n      (dragleave)=\"onDragLeave()\" (dragover)=\"onDragOver()\" (drop)=\"onDrop($event)\"\n      [class.dragging]=\"dragging\"\n      class=\"form-control\"\n      [placeholder]=\"placeholder\"\n      [disabled]=\"disabled\"\n      [required]=\"required\"\n      [name]=\"name\"\n      [(ngModel)]=\"fileName\"\n      (paste)=\"ignoreEvent($event)\"\n      (keydown)=\"ignoreEvent($event)\">\n    <span class=\"input-group-addon\">\n      <span *ngIf=\"!value\" tooltip=\"Selecionar arquivo\" container=\"body\">\n        <span class=\"material-icons select-icon\" [style.font-size]=\"iconSize\" [class.disabled]=\"disabled\" (click)=\"openFileSelection()\">&#xE2C6;</span>\n      </span>\n      <span *ngIf=\"value\" tooltip=\"Limpar sele\u00E7\u00E3o\" container=\"body\">\n        <span class=\"material-icons clear-icon\" [style.font-size]=\"iconSize\" [class.disabled]=\"disabled\" (click)=\"clearFile()\">&#xE872;</span>\n      </span>\n    </span>\n  </div>\n  <ng-container *ngIf=\"accept\">\n    <input type=\"file\" #fileUploadElement [accept]=\"accept\" (change)=\"onFileChange($event)\" style=\"display: none\" multiple>\n  </ng-container>\n  <ng-container *ngIf=\"!accept\">\n    <input type=\"file\" #fileUploadElement (change)=\"onFileChange($event)\" style=\"display: none\" multiple>\n  </ng-container>\n</ng-template>\n", styles: [".select-icon,.clear-icon{color:#00205b;cursor:pointer}.select-icon.disabled,.clear-icon.disabled{color:rgba(#00205b,.3);cursor:not-allowed}input.form-control{background-color:#e6e6e6}input.form-control.dragging{background-color:green}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: LocalMessagesComponent, selector: "suite-rfb-local-messages", inputs: ["requiredMessage", "patternMessage", "minlengthMessage", "maxlengthMessage", "emailMessage", "cnpjMessage", "cpfMessage", "dateMessage", "dayMessage", "monthMessage", "yearMessage", "minDateMessage", "maxDateMessage", "fileSizeMessage", "fileTypeMessage", "decendioMessage", "control"] }, { kind: "directive", type: HasErrorDirective, selector: "[suiteRfbHasError]", inputs: ["suiteRfbHasError"] }, { kind: "directive", type: i7$1.TooltipDirective, selector: "[tooltip], [tooltipHtml]", inputs: ["adaptivePosition", "tooltip", "placement", "triggers", "container", "containerClass", "boundariesElement", "isOpen", "isDisabled", "delay", "tooltipHtml", "tooltipPlacement", "tooltipIsOpen", "tooltipEnable", "tooltipAppendToBody", "tooltipAnimation", "tooltipClass", "tooltipContext", "tooltipPopupDelay", "tooltipFadeDuration", "tooltipTrigger"], outputs: ["tooltipChange", "onShown", "onHidden", "tooltipStateChanged"], exportAs: ["bs-tooltip"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: FilePickerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-file-picker', providers: [{
                            provide: NG_VALUE_ACCESSOR,
                            multi: true,
                            useExisting: forwardRef(() => FilePickerComponent)
                        }], template: "<ng-container *ngIf=\"inline\">\n  <ng-container *ngTemplateOutlet=\"template\"></ng-container>\n</ng-container>\n\n<ng-container *ngIf=\"!inline\">\n  <div class=\"form-group\" [suiteRfbHasError]=\"ngControl.control\">\n    <label [for]=\"name\" *ngIf=\"label\"\n      class=\"control-label\"\n      [class.obrigatorio]=\"required\">\n      {{label}}\n    </label>\n    <ng-container *ngTemplateOutlet=\"template\"></ng-container>\n    <suite-rfb-local-messages [control]=\"ngControl.control\"></suite-rfb-local-messages>\n  </div>\n</ng-container>\n\n<ng-template #template>\n  <div [class]=\"inputGroupClass\">\n    <input type=\"text\"\n      (dragleave)=\"onDragLeave()\" (dragover)=\"onDragOver()\" (drop)=\"onDrop($event)\"\n      [class.dragging]=\"dragging\"\n      class=\"form-control\"\n      [placeholder]=\"placeholder\"\n      [disabled]=\"disabled\"\n      [required]=\"required\"\n      [name]=\"name\"\n      [(ngModel)]=\"fileName\"\n      (paste)=\"ignoreEvent($event)\"\n      (keydown)=\"ignoreEvent($event)\">\n    <span class=\"input-group-addon\">\n      <span *ngIf=\"!value\" tooltip=\"Selecionar arquivo\" container=\"body\">\n        <span class=\"material-icons select-icon\" [style.font-size]=\"iconSize\" [class.disabled]=\"disabled\" (click)=\"openFileSelection()\">&#xE2C6;</span>\n      </span>\n      <span *ngIf=\"value\" tooltip=\"Limpar sele\u00E7\u00E3o\" container=\"body\">\n        <span class=\"material-icons clear-icon\" [style.font-size]=\"iconSize\" [class.disabled]=\"disabled\" (click)=\"clearFile()\">&#xE872;</span>\n      </span>\n    </span>\n  </div>\n  <ng-container *ngIf=\"accept\">\n    <input type=\"file\" #fileUploadElement [accept]=\"accept\" (change)=\"onFileChange($event)\" style=\"display: none\" multiple>\n  </ng-container>\n  <ng-container *ngIf=\"!accept\">\n    <input type=\"file\" #fileUploadElement (change)=\"onFileChange($event)\" style=\"display: none\" multiple>\n  </ng-container>\n</ng-template>\n", styles: [".select-icon,.clear-icon{color:#00205b;cursor:pointer}.select-icon.disabled,.clear-icon.disabled{color:rgba(#00205b,.3);cursor:not-allowed}input.form-control{background-color:#e6e6e6}input.form-control.dragging{background-color:green}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.Injector }, { type: i0.Renderer2 }]; }, propDecorators: { value: [{
                type: Input,
                args: ['value']
            }], accept: [{
                type: Input
            }], iconSize: [{
                type: Input
            }], inline: [{
                type: Input
            }], inputGroupClass: [{
                type: Input
            }], label: [{
                type: Input
            }], name: [{
                type: Input
            }], required: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], fileChange: [{
                type: Output
            }], disabled: [{
                type: Input
            }], fileUploadElement: [{
                type: ViewChild,
                args: ['fileUploadElement']
            }] } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbFilePickerModule {
}
SuiteRfbFilePickerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbFilePickerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbFilePickerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbFilePickerModule, declarations: [FilePickerComponent,
        FileTypeValidatorDirective], imports: [CommonModule,
        FormsModule,
        ReactiveFormsModule,
        SuiteRfbInputValidationModule,
        SuiteRfbOutputMaskModule, i7$1.TooltipModule], exports: [FilePickerComponent,
        FileTypeValidatorDirective] });
SuiteRfbFilePickerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbFilePickerModule, imports: [CommonModule,
        FormsModule,
        ReactiveFormsModule,
        SuiteRfbInputValidationModule,
        SuiteRfbOutputMaskModule,
        TooltipModule.forRoot()] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbFilePickerModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        ReactiveFormsModule,
                        SuiteRfbInputValidationModule,
                        SuiteRfbOutputMaskModule,
                        TooltipModule.forRoot()
                    ],
                    declarations: [
                        FilePickerComponent,
                        FileTypeValidatorDirective
                    ],
                    exports: [
                        FilePickerComponent,
                        FileTypeValidatorDirective
                    ],
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class FilterByComponent {
    constructor() {
        this.debounceTime = 300;
        this.disabled = false;
        this.inputClass = 'form-control filter-by-sm';
        this.requestFocus = false;
        this.onFilter = new EventEmitter();
        this.filter$ = new Subject();
        this.ngUnsubscribe = new Subject();
    }
    ngAfterViewInit() {
        if (this.requestFocus) {
            this.filterByRef?.nativeElement?.focus();
        }
    }
    ngOnInit() {
        this.filter$
            .pipe(debounceTime(this.debounceTime), distinctUntilChanged(), takeUntil(this.ngUnsubscribe))
            .subscribe((term) => this.onFilter.emit(term));
    }
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
    filter(searchTerms) {
        this.filter$.next(searchTerms);
    }
    resetTerm(value = '') {
        this.searchTerms = value;
    }
}
FilterByComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: FilterByComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
FilterByComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: FilterByComponent, selector: "suite-rfb-filter-by", inputs: { debounceTime: "debounceTime", disabled: "disabled", inputClass: "inputClass", inputMask: "inputMask", requestFocus: "requestFocus", searchTerms: "searchTerms" }, outputs: { onFilter: "onFilter" }, viewQueries: [{ propertyName: "filterByRef", first: true, predicate: ["filterBy"], descendants: true }], ngImport: i0, template: "<ng-container *ngIf=\"!inputMask\">\n  <input #filterBy type=\"search\" [(ngModel)]=\"searchTerms\" [class]=\"inputClass\" (input)='filter(filterBy.value)' [disabled]=\"disabled\">\n</ng-container>\n<ng-container *ngIf=\"inputMask\">\n  <input #filterBy type=\"search\" [(ngModel)]=\"searchTerms\" [class]=\"inputClass\" (input)='filter(filterBy.value)' [disabled]=\"disabled\" [suiteRfbInputMask]=\"inputMask\">\n</ng-container>\n", styles: [".filter-by-sm{width:150px}.filter-by-md{width:200px}.filter-by-lg{width:300px}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "directive", type: InputMaskDirective, selector: "[suiteRfbInputMask]", inputs: ["suiteRfbInputMask", "suiteRfbInputMaskInvalidAllowed"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: FilterByComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-filter-by', template: "<ng-container *ngIf=\"!inputMask\">\n  <input #filterBy type=\"search\" [(ngModel)]=\"searchTerms\" [class]=\"inputClass\" (input)='filter(filterBy.value)' [disabled]=\"disabled\">\n</ng-container>\n<ng-container *ngIf=\"inputMask\">\n  <input #filterBy type=\"search\" [(ngModel)]=\"searchTerms\" [class]=\"inputClass\" (input)='filter(filterBy.value)' [disabled]=\"disabled\" [suiteRfbInputMask]=\"inputMask\">\n</ng-container>\n", styles: [".filter-by-sm{width:150px}.filter-by-md{width:200px}.filter-by-lg{width:300px}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { debounceTime: [{
                type: Input
            }], disabled: [{
                type: Input
            }], inputClass: [{
                type: Input
            }], inputMask: [{
                type: Input
            }], requestFocus: [{
                type: Input
            }], onFilter: [{
                type: Output
            }], filterByRef: [{
                type: ViewChild,
                args: ['filterBy']
            }], searchTerms: [{
                type: Input
            }] } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbFilterByModule {
}
SuiteRfbFilterByModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbFilterByModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbFilterByModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbFilterByModule, declarations: [FilterByComponent], imports: [CommonModule,
        FormsModule,
        SuiteRfbInputMaskModule], exports: [FilterByComponent] });
SuiteRfbFilterByModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbFilterByModule, imports: [CommonModule,
        FormsModule,
        SuiteRfbInputMaskModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbFilterByModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        SuiteRfbInputMaskModule,
                    ],
                    declarations: [
                        FilterByComponent,
                    ],
                    exports: [
                        FilterByComponent,
                    ],
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class FooterComponent {
    constructor() {
        this.text = 'Ministério da Fazenda - Secretaria Especial da Receita Federal do Brasil | Esplanada dos Ministérios, Bloco P, Brasília - DF';
    }
}
FooterComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: FooterComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
FooterComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: FooterComponent, selector: "suite-rfb-footer", inputs: { text: "text" }, ngImport: i0, template: "<footer class=\"footer\">\n  <p>{{text}}</p>\n</footer>\n", styles: ["::ng-deep body article{min-height:calc(100vh - 198px)}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: FooterComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-footer', template: "<footer class=\"footer\">\n  <p>{{text}}</p>\n</footer>\n", styles: ["::ng-deep body article{min-height:calc(100vh - 198px)}\n"] }]
        }], propDecorators: { text: [{
                type: Input
            }] } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbFooterModule {
}
SuiteRfbFooterModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbFooterModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbFooterModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbFooterModule, declarations: [FooterComponent], imports: [CommonModule], exports: [FooterComponent] });
SuiteRfbFooterModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbFooterModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbFooterModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                    ],
                    declarations: [
                        FooterComponent,
                    ],
                    exports: [
                        FooterComponent,
                    ],
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class AccessibilityComponent {
    constructor(document) {
        this.document = document;
    }
    ngOnInit() { }
    toggleContraste() {
        this.document.body.classList.toggle('contraste'); // classList precisa de polyfill para browsers antigos (ex: IE9)
    }
}
AccessibilityComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: AccessibilityComponent, deps: [{ token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Component });
AccessibilityComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: AccessibilityComponent, selector: "suite-rfb-accessibility", ngImport: i0, template: "<p class=\"acessibilidade hidden-xs\">\n  <!-- <a href=\"#\">Acessibilidade</a> -->\n  <a class=\"pointer contraste-toggler\" (click)=\"toggleContraste()\">Contraste</a>\n</p>\n", styles: [""] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: AccessibilityComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-accessibility', template: "<p class=\"acessibilidade hidden-xs\">\n  <!-- <a href=\"#\">Acessibilidade</a> -->\n  <a class=\"pointer contraste-toggler\" (click)=\"toggleContraste()\">Contraste</a>\n</p>\n" }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class BrandComponent {
    constructor() {
        this.logoRoute = ['/'];
        this.logoUrl = 'assets/img/logo-rfb.png';
    }
    ngOnInit() { }
}
BrandComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: BrandComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
BrandComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: BrandComponent, selector: "suite-rfb-brand", inputs: { appName: "appName", appDescription: "appDescription", logoRoute: "logoRoute", logoUrl: "logoUrl" }, ngImport: i0, template: "<a *ngIf=\"logoRoute\" class=\"navbar-brand\" [routerLink]=\"logoRoute\">\n  <ng-container *ngTemplateOutlet=\"brandBody\"></ng-container>\n</a>\n<a *ngIf=\"!logoRoute\" class=\"navbar-brand\">\n  <ng-container *ngTemplateOutlet=\"brandBody\"></ng-container>\n</a>\n\n<ng-template #brandBody>\n  <img class=\"img-responsive\" [src]=\"logoUrl\" alt=\"Receita Federal\">\n  <span class=\"sistema-nome\">{{appName}}</span>\n  <span class=\"sistema-descricao hidden-xs hidden-sm\">{{appDescription}}</span>\n</ng-template>", styles: [""], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i1$1.RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: BrandComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-brand', template: "<a *ngIf=\"logoRoute\" class=\"navbar-brand\" [routerLink]=\"logoRoute\">\n  <ng-container *ngTemplateOutlet=\"brandBody\"></ng-container>\n</a>\n<a *ngIf=\"!logoRoute\" class=\"navbar-brand\">\n  <ng-container *ngTemplateOutlet=\"brandBody\"></ng-container>\n</a>\n\n<ng-template #brandBody>\n  <img class=\"img-responsive\" [src]=\"logoUrl\" alt=\"Receita Federal\">\n  <span class=\"sistema-nome\">{{appName}}</span>\n  <span class=\"sistema-descricao hidden-xs hidden-sm\">{{appDescription}}</span>\n</ng-template>" }]
        }], ctorParameters: function () { return []; }, propDecorators: { appName: [{
                type: Input
            }], appDescription: [{
                type: Input
            }], logoRoute: [{
                type: Input
            }], logoUrl: [{
                type: Input
            }] } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class LogoutService {
    constructor() {
        this.logoutSource = new Subject();
        this.logout$ = this.logoutSource.asObservable();
    }
    logout() {
        this.logoutSource.next();
    }
}
LogoutService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: LogoutService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
LogoutService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: LogoutService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: LogoutService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class LogoutComponent {
    constructor(logoutService, configService) {
        this.logoutService = logoutService;
        this.configService = configService;
        this.buttonClass = 'btn btn-default';
    }
    ngOnInit() {
        this.hidden = this.configService.isLogoutHidden();
        this.disabled = this.configService.isLogoutDisabled();
    }
    logout() {
        this.logoutService.logout();
    }
}
LogoutComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: LogoutComponent, deps: [{ token: LogoutService }, { token: ConfigService }], target: i0.ɵɵFactoryTarget.Component });
LogoutComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: LogoutComponent, selector: "suite-rfb-logout", inputs: { buttonClass: "buttonClass" }, ngImport: i0, template: "<div *ngIf=\"!hidden\">\n  <button [class]=\"buttonClass\" type=\"button\" (click)=\"logout()\" [disabled]=\"disabled\">\n    <span class=\"material-icons icon-24\">&#xE8AC;</span>\n  </button>\n</div>\n", styles: [""], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: LogoutComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-logout', template: "<div *ngIf=\"!hidden\">\n  <button [class]=\"buttonClass\" type=\"button\" (click)=\"logout()\" [disabled]=\"disabled\">\n    <span class=\"material-icons icon-24\">&#xE8AC;</span>\n  </button>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: LogoutService }, { type: ConfigService }]; }, propDecorators: { buttonClass: [{
                type: Input
            }] } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class NotificationService {
    constructor() {
        this.notifications = [];
        this.notificationsSource = new BehaviorSubject(this.notifications);
        this.notifications$ = this.notificationsSource.asObservable();
        this.notificationOpenEventSource = new Subject();
        this.notificationOpenEvent$ = this.notificationOpenEventSource.asObservable();
    }
    openEvent(notification) {
        this.notificationOpenEventSource.next(notification);
    }
    getNotifications$() {
        return this.notifications$;
    }
    push(notification) {
        notification = this._checkDefaults(notification);
        this.notifications.push(notification);
        this.notificationsSource.next(this.notifications);
    }
    pushAll(notifications) {
        notifications = this._checkCollectionDefaults(notifications);
        this.notifications = this.notifications.concat(notifications);
        this.notificationsSource.next(this.notifications);
    }
    dismiss(notification) {
        this.notifications = this.notifications.filter(n => n !== notification);
        this.notificationsSource.next(this.notifications);
    }
    dismissAll() {
        this.notifications.splice(0, this.notifications.length);
        this.notificationsSource.next(this.notifications);
    }
    _checkDefaults(notification) {
        if (!notification.timestamp) {
            notification.timestamp = new Date();
        }
        if (!notification.size) {
            notification.size = 'md';
        }
        return notification;
    }
    _checkCollectionDefaults(notifications) {
        const now = new Date();
        notifications.filter(n => !n.timestamp).forEach(n => n.timestamp = now);
        notifications.filter(n => !n.size).forEach(n => n.size = 'md');
        return notifications;
    }
}
NotificationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: NotificationService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
NotificationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: NotificationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: NotificationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class NotificationsComponent {
    constructor(notificationService, configService) {
        this.notificationService = notificationService;
        this.configService = configService;
        this.buttonClass = 'btn btn-default dropdown-toggle';
        this.notifications = [];
        this.unreadAmount = 0;
        this.ngUnsubscribe = new Subject();
    }
    ngOnInit() {
        this.hidden = this.configService.isNotificationsHidden();
        this.disabled = this.configService.isNotificationsDisabled();
        this.showOverflowedText = this.configService.isNotificationsShowOverflowedText();
        this.notificationService.getNotifications$()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((notifications) => {
            this.notifications = notifications;
            this.updateUnreadAmount();
        });
    }
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
    onNotificationCallbackClick(notification) {
        if (notification.callbackId) {
            this.notificationService.openEvent(notification);
        }
    }
    markAsRead(notification) {
        notification.read = true;
        this.updateUnreadAmount();
    }
    updateUnreadAmount() {
        this.unreadAmount = this.notifications.filter(n => n.read !== true).length;
    }
}
NotificationsComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: NotificationsComponent, deps: [{ token: NotificationService }, { token: ConfigService }], target: i0.ɵɵFactoryTarget.Component });
NotificationsComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: NotificationsComponent, selector: "suite-rfb-notifications", inputs: { buttonClass: "buttonClass" }, ngImport: i0, template: "<div class=\"dropdown\" dropdown *ngIf=\"!hidden\" [isDisabled]=\"disabled\">\n  <button type=\"button\" [class]=\"buttonClass\" dropdownToggle data-toggle=\"dropdown\" aria-haspopup=\"true\" aria-expanded=\"true\">\n    <span class=\"material-icons icon-24\">&#xE7F4;</span>\n    <span class=\"badge\" *ngIf=\"unreadAmount > 0\">{{unreadAmount}}</span>\n  </button>\n  <ul *dropdownMenu class=\"dropdown-menu mensagens\">\n    <ng-container *ngFor=\"let notification of notifications\">\n      <li *ngIf=\"notification.route\" class=\"notification routed\" [class.notification-sm]=\"notification.size === 'sm'\" [class.notification-lg]=\"notification.size === 'lg'\"\n        [title]=\"notification.message|stripHtml\" [routerLink]=\"notification.route\" (click)=\"markAsRead(notification)\">\n        <ng-container *ngTemplateOutlet=\"template; context: {notification: notification}\"></ng-container>\n      </li>\n      <li *ngIf=\"!notification.route\" class=\"notification\" [class.notification-sm]=\"notification.size === 'sm'\" [class.notification-lg]=\"notification.size === 'lg'\"\n        [title]=\"notification.message|stripHtml\" (click)=\"markAsRead(notification)\">\n        <ng-container *ngTemplateOutlet=\"template; context: {notification: notification}\"></ng-container>\n      </li>\n    </ng-container>\n    <li *ngIf=\"notifications.length === 0\">\n      <div class=\"message no-notifications\" [class.no-overflow]=\"!showOverflowedText\">Sem notifica\u00E7\u00F5es</div>\n    </li>\n  </ul>\n</div>\n<ng-template #template let-notification=\"notification\">\n  <div class=\"title\" [class.no-overflow]=\"!showOverflowedText\" [innerHTML]=\"notification.title\"></div>\n  <div class=\"message\" [class.no-overflow]=\"!showOverflowedText\" [innerHTML]=\"notification.message\"></div>\n  <div class=\"timestamp\">\n    <small>({{notification.timestamp|fromNow}})</small>\n  </div>\n  <button *ngIf=\"notification.callbackId\" type=\"button\" class=\"btn btn-default open-callback\" (click)=\"onNotificationCallbackClick(notification)\" title=\"Navegar para Funcionalidade\">\n    <span class=\"material-icons icon-24\">launch</span>\n  </button>\n</ng-template>\n", styles: [".dropdown-menu.mensagens{background-color:#bfc7d6;border:1px solid #7f8fad;max-height:480px;overflow:auto}.dropdown-menu.mensagens>li{margin:0 15px;padding:10px 20px}.dropdown-menu.mensagens>li.notification:not(:last-child){border-bottom:1px solid #00205b}.dropdown-menu.mensagens>li.notification.notification-sm{font-size:85%}.dropdown-menu.mensagens>li.notification.notification-lg{font-size:125%}.dropdown-menu.mensagens>li.notification:focus,.dropdown-menu.mensagens>li.notification:hover{color:#262626;text-decoration:none;background-color:#c0cee9}.title,.message{color:#00205b;min-width:240px;max-width:480px;padding:0;font-weight:400;line-height:1.42857143}.message.no-notifications{color:#777;min-width:160px}.title.no-overflow,.message.no-overflow{clear:both;display:block;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.timestamp{color:#333;opacity:.5}.routed{cursor:pointer}.notification{position:relative}.open-callback{position:absolute;top:0;right:0}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i1$1.RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }, { kind: "directive", type: i7.BsDropdownMenuDirective, selector: "[bsDropdownMenu],[dropdownMenu]", exportAs: ["bs-dropdown-menu"] }, { kind: "directive", type: i7.BsDropdownToggleDirective, selector: "[bsDropdownToggle],[dropdownToggle]", exportAs: ["bs-dropdown-toggle"] }, { kind: "directive", type: i7.BsDropdownDirective, selector: "[bsDropdown], [dropdown]", inputs: ["placement", "triggers", "container", "dropup", "autoClose", "isAnimated", "insideClick", "isDisabled", "isOpen"], outputs: ["isOpenChange", "onShown", "onHidden"], exportAs: ["bs-dropdown"] }, { kind: "pipe", type: FromNowPipe, name: "fromNow" }, { kind: "pipe", type: StripHtmlPipe, name: "stripHtml" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: NotificationsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-notifications', template: "<div class=\"dropdown\" dropdown *ngIf=\"!hidden\" [isDisabled]=\"disabled\">\n  <button type=\"button\" [class]=\"buttonClass\" dropdownToggle data-toggle=\"dropdown\" aria-haspopup=\"true\" aria-expanded=\"true\">\n    <span class=\"material-icons icon-24\">&#xE7F4;</span>\n    <span class=\"badge\" *ngIf=\"unreadAmount > 0\">{{unreadAmount}}</span>\n  </button>\n  <ul *dropdownMenu class=\"dropdown-menu mensagens\">\n    <ng-container *ngFor=\"let notification of notifications\">\n      <li *ngIf=\"notification.route\" class=\"notification routed\" [class.notification-sm]=\"notification.size === 'sm'\" [class.notification-lg]=\"notification.size === 'lg'\"\n        [title]=\"notification.message|stripHtml\" [routerLink]=\"notification.route\" (click)=\"markAsRead(notification)\">\n        <ng-container *ngTemplateOutlet=\"template; context: {notification: notification}\"></ng-container>\n      </li>\n      <li *ngIf=\"!notification.route\" class=\"notification\" [class.notification-sm]=\"notification.size === 'sm'\" [class.notification-lg]=\"notification.size === 'lg'\"\n        [title]=\"notification.message|stripHtml\" (click)=\"markAsRead(notification)\">\n        <ng-container *ngTemplateOutlet=\"template; context: {notification: notification}\"></ng-container>\n      </li>\n    </ng-container>\n    <li *ngIf=\"notifications.length === 0\">\n      <div class=\"message no-notifications\" [class.no-overflow]=\"!showOverflowedText\">Sem notifica\u00E7\u00F5es</div>\n    </li>\n  </ul>\n</div>\n<ng-template #template let-notification=\"notification\">\n  <div class=\"title\" [class.no-overflow]=\"!showOverflowedText\" [innerHTML]=\"notification.title\"></div>\n  <div class=\"message\" [class.no-overflow]=\"!showOverflowedText\" [innerHTML]=\"notification.message\"></div>\n  <div class=\"timestamp\">\n    <small>({{notification.timestamp|fromNow}})</small>\n  </div>\n  <button *ngIf=\"notification.callbackId\" type=\"button\" class=\"btn btn-default open-callback\" (click)=\"onNotificationCallbackClick(notification)\" title=\"Navegar para Funcionalidade\">\n    <span class=\"material-icons icon-24\">launch</span>\n  </button>\n</ng-template>\n", styles: [".dropdown-menu.mensagens{background-color:#bfc7d6;border:1px solid #7f8fad;max-height:480px;overflow:auto}.dropdown-menu.mensagens>li{margin:0 15px;padding:10px 20px}.dropdown-menu.mensagens>li.notification:not(:last-child){border-bottom:1px solid #00205b}.dropdown-menu.mensagens>li.notification.notification-sm{font-size:85%}.dropdown-menu.mensagens>li.notification.notification-lg{font-size:125%}.dropdown-menu.mensagens>li.notification:focus,.dropdown-menu.mensagens>li.notification:hover{color:#262626;text-decoration:none;background-color:#c0cee9}.title,.message{color:#00205b;min-width:240px;max-width:480px;padding:0;font-weight:400;line-height:1.42857143}.message.no-notifications{color:#777;min-width:160px}.title.no-overflow,.message.no-overflow{clear:both;display:block;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.timestamp{color:#333;opacity:.5}.routed{cursor:pointer}.notification{position:relative}.open-callback{position:absolute;top:0;right:0}\n"] }]
        }], ctorParameters: function () { return [{ type: NotificationService }, { type: ConfigService }]; }, propDecorators: { buttonClass: [{
                type: Input
            }] } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class UserConfigurationsService {
    constructor() {
        this.preferencesChange = new Subject();
        this.preferencesChange$ = this.preferencesChange.asObservable();
        this.profileChange = new Subject();
        this.profileChange$ = this.profileChange.asObservable();
        this.favoritesChange = new Subject();
        this.favoritesChange$ = this.favoritesChange.asObservable();
    }
    editPreferences() {
        this.preferencesChange.next();
    }
    editProfile() {
        this.profileChange.next();
    }
    editFavorites() {
        this.favoritesChange.next();
    }
}
UserConfigurationsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: UserConfigurationsService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
UserConfigurationsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: UserConfigurationsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: UserConfigurationsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class UserConfigurationsComponent {
    constructor(userConfigurationsService, configService) {
        this.userConfigurationsService = userConfigurationsService;
        this.configService = configService;
        this.buttonClass = 'btn btn-default dropdown-toggle';
    }
    ngOnInit() {
        this.hidden = this.configService.isUserConfigurationsHidden();
        this.disabled = this.configService.isUserConfigurationsDisabled();
    }
    editPreferences() {
        this.userConfigurationsService.editPreferences();
    }
    editProfile() {
        this.userConfigurationsService.editProfile();
    }
    editFavorites() {
        this.userConfigurationsService.editFavorites();
    }
}
UserConfigurationsComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: UserConfigurationsComponent, deps: [{ token: UserConfigurationsService }, { token: ConfigService }], target: i0.ɵɵFactoryTarget.Component });
UserConfigurationsComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: UserConfigurationsComponent, selector: "suite-rfb-user-configurations", inputs: { buttonClass: "buttonClass" }, ngImport: i0, template: "<div class=\"dropdown\" dropdown *ngIf=\"!hidden\" [isDisabled]=\"disabled\">\n  <button type=\"button\" [class]=\"buttonClass\"\n    ata-toggle=\"dropdown\"\n    aria-haspopup=\"true\"\n    aria-expanded=\"true\"\n    dropdownToggle>\n    <span class=\"material-icons icon-24\">&#xE7FD;</span>\n  </button>\n  <ul *dropdownMenu class=\"dropdown-menu configuracoes\">\n    <li><a href=\"javascript:void(0)\" (click)=\"editPreferences()\">Editar prefer\u00EAncias</a></li>\n    <li><a href=\"javascript:void(0)\" (click)=\"editProfile()\">Editar dados pessoais</a></li>\n    <li><a href=\"javascript:void(0)\" (click)=\"editFavorites()\">Organizar favoritos</a></li>\n  </ul>\n</div>\n", styles: [".dropdown-menu.configuracoes{background-color:#bfc7d6;border:1px solid #7f8fad}.dropdown-menu.configuracoes>li{margin:0 15px;padding:10px 20px}.dropdown-menu.configuracoes>li:not(:last-child){border-bottom:1px solid #00205b}.dropdown-menu.configuracoes>li>a{color:#00205b;padding:0}.dropdown-menu.configuracoes>li:focus,.dropdown-menu.configuracoes>li:hover,.dropdown-menu.configuracoes>li>a:focus,.dropdown-menu.configuracoes>li>a:hover{color:#262626;text-decoration:none;background-color:#c0cee9}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i7.BsDropdownMenuDirective, selector: "[bsDropdownMenu],[dropdownMenu]", exportAs: ["bs-dropdown-menu"] }, { kind: "directive", type: i7.BsDropdownToggleDirective, selector: "[bsDropdownToggle],[dropdownToggle]", exportAs: ["bs-dropdown-toggle"] }, { kind: "directive", type: i7.BsDropdownDirective, selector: "[bsDropdown], [dropdown]", inputs: ["placement", "triggers", "container", "dropup", "autoClose", "isAnimated", "insideClick", "isDisabled", "isOpen"], outputs: ["isOpenChange", "onShown", "onHidden"], exportAs: ["bs-dropdown"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: UserConfigurationsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-user-configurations', template: "<div class=\"dropdown\" dropdown *ngIf=\"!hidden\" [isDisabled]=\"disabled\">\n  <button type=\"button\" [class]=\"buttonClass\"\n    ata-toggle=\"dropdown\"\n    aria-haspopup=\"true\"\n    aria-expanded=\"true\"\n    dropdownToggle>\n    <span class=\"material-icons icon-24\">&#xE7FD;</span>\n  </button>\n  <ul *dropdownMenu class=\"dropdown-menu configuracoes\">\n    <li><a href=\"javascript:void(0)\" (click)=\"editPreferences()\">Editar prefer\u00EAncias</a></li>\n    <li><a href=\"javascript:void(0)\" (click)=\"editProfile()\">Editar dados pessoais</a></li>\n    <li><a href=\"javascript:void(0)\" (click)=\"editFavorites()\">Organizar favoritos</a></li>\n  </ul>\n</div>\n", styles: [".dropdown-menu.configuracoes{background-color:#bfc7d6;border:1px solid #7f8fad}.dropdown-menu.configuracoes>li{margin:0 15px;padding:10px 20px}.dropdown-menu.configuracoes>li:not(:last-child){border-bottom:1px solid #00205b}.dropdown-menu.configuracoes>li>a{color:#00205b;padding:0}.dropdown-menu.configuracoes>li:focus,.dropdown-menu.configuracoes>li:hover,.dropdown-menu.configuracoes>li>a:focus,.dropdown-menu.configuracoes>li>a:hover{color:#262626;text-decoration:none;background-color:#c0cee9}\n"] }]
        }], ctorParameters: function () { return [{ type: UserConfigurationsService }, { type: ConfigService }]; }, propDecorators: { buttonClass: [{
                type: Input
            }] } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class HeaderComponent {
    constructor(configService) {
        this.configService = configService;
        this.logoRoute = ['/'];
        this.logoUrl = 'assets/img/logo-rfb.png';
    }
    ngOnInit() {
        this.showUserProfiles = this.configService.isShowUserProfiles();
    }
}
HeaderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: HeaderComponent, deps: [{ token: ConfigService }], target: i0.ɵɵFactoryTarget.Component });
HeaderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: HeaderComponent, selector: "suite-rfb-header", inputs: { appName: "appName", appDescription: "appDescription", logoRoute: "logoRoute", logoUrl: "logoUrl" }, ngImport: i0, template: "<!-- Cabe\u00E7alho da p\u00E1gina -->\n<div class=\"cabecalho\">\n  <nav class=\"navbar navbar-default\" role=\"navigation\">\n    <div class=\"container\">\n\n      <!-- Logo da p\u00E1gina -->\n      <div class=\"navbar-header\">\n        <suite-rfb-brand\n          [appName]=\"appName\"\n          [appDescription]=\"appDescription\"\n          [logoUrl]=\"logoUrl\"\n          [logoRoute]=\"logoRoute\"\n        ></suite-rfb-brand>\n      </div>\n\n      <!-- Links de acessibilidade e contraste -->\n      <div class=\"navbar-right text-right\">\n\n        <suite-rfb-accessibility></suite-rfb-accessibility>\n\n        <!-- Menu do cabe\u00E7alho -->\n        <ul class=\"menu-cabecalho hidden-xs\">\n          <li>\n            <suite-rfb-applications-menu></suite-rfb-applications-menu>\n          </li>\n          <li>\n            <suite-rfb-user-configurations></suite-rfb-user-configurations>\n          </li>\n          <li>\n            <suite-rfb-notifications></suite-rfb-notifications>\n          </li>\n          <li>\n            <suite-rfb-contextual-help></suite-rfb-contextual-help>\n          </li>\n          <li>\n            <suite-rfb-logout></suite-rfb-logout>\n          </li>\n        </ul>\n      </div>\n\n      <!-- Informa\u00E7\u00F5es do usu\u00E1rio -->\n      <suite-rfb-user-info [showUserProfiles]=\"showUserProfiles\"></suite-rfb-user-info>\n\n    </div>\n  </nav>\n</div>\n", styles: [""], dependencies: [{ kind: "component", type: ApplicationsMenuComponent, selector: "suite-rfb-applications-menu", inputs: ["buttonClass", "suiteRfbUrl"] }, { kind: "component", type: ContextualHelpComponent, selector: "suite-rfb-contextual-help", inputs: ["buttonClass"] }, { kind: "component", type: LogoutComponent, selector: "suite-rfb-logout", inputs: ["buttonClass"] }, { kind: "component", type: NotificationsComponent, selector: "suite-rfb-notifications", inputs: ["buttonClass"] }, { kind: "component", type: UserInfoComponent, selector: "suite-rfb-user-info", inputs: ["showUserProfiles"] }, { kind: "component", type: UserConfigurationsComponent, selector: "suite-rfb-user-configurations", inputs: ["buttonClass"] }, { kind: "component", type: AccessibilityComponent, selector: "suite-rfb-accessibility" }, { kind: "component", type: BrandComponent, selector: "suite-rfb-brand", inputs: ["appName", "appDescription", "logoRoute", "logoUrl"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: HeaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-header', template: "<!-- Cabe\u00E7alho da p\u00E1gina -->\n<div class=\"cabecalho\">\n  <nav class=\"navbar navbar-default\" role=\"navigation\">\n    <div class=\"container\">\n\n      <!-- Logo da p\u00E1gina -->\n      <div class=\"navbar-header\">\n        <suite-rfb-brand\n          [appName]=\"appName\"\n          [appDescription]=\"appDescription\"\n          [logoUrl]=\"logoUrl\"\n          [logoRoute]=\"logoRoute\"\n        ></suite-rfb-brand>\n      </div>\n\n      <!-- Links de acessibilidade e contraste -->\n      <div class=\"navbar-right text-right\">\n\n        <suite-rfb-accessibility></suite-rfb-accessibility>\n\n        <!-- Menu do cabe\u00E7alho -->\n        <ul class=\"menu-cabecalho hidden-xs\">\n          <li>\n            <suite-rfb-applications-menu></suite-rfb-applications-menu>\n          </li>\n          <li>\n            <suite-rfb-user-configurations></suite-rfb-user-configurations>\n          </li>\n          <li>\n            <suite-rfb-notifications></suite-rfb-notifications>\n          </li>\n          <li>\n            <suite-rfb-contextual-help></suite-rfb-contextual-help>\n          </li>\n          <li>\n            <suite-rfb-logout></suite-rfb-logout>\n          </li>\n        </ul>\n      </div>\n\n      <!-- Informa\u00E7\u00F5es do usu\u00E1rio -->\n      <suite-rfb-user-info [showUserProfiles]=\"showUserProfiles\"></suite-rfb-user-info>\n\n    </div>\n  </nav>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: ConfigService }]; }, propDecorators: { appName: [{
                type: Input
            }], appDescription: [{
                type: Input
            }], logoRoute: [{
                type: Input
            }], logoUrl: [{
                type: Input
            }] } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbLogoutModule {
}
SuiteRfbLogoutModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbLogoutModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbLogoutModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbLogoutModule, declarations: [LogoutComponent], imports: [CommonModule,
        SuiteRfbEnvironmentModule], exports: [LogoutComponent] });
SuiteRfbLogoutModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbLogoutModule, imports: [CommonModule,
        SuiteRfbEnvironmentModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbLogoutModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        SuiteRfbEnvironmentModule,
                    ],
                    declarations: [
                        LogoutComponent,
                    ],
                    exports: [
                        LogoutComponent,
                    ],
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbNotificationsModule {
}
SuiteRfbNotificationsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbNotificationsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbNotificationsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbNotificationsModule, declarations: [NotificationsComponent], imports: [CommonModule, i1$1.RouterModule, i7.BsDropdownModule, SuiteRfbOutputMaskModule], exports: [NotificationsComponent] });
SuiteRfbNotificationsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbNotificationsModule, imports: [CommonModule,
        RouterModule.forChild([]),
        BsDropdownModule.forRoot(),
        SuiteRfbOutputMaskModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbNotificationsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        RouterModule.forChild([]),
                        BsDropdownModule.forRoot(),
                        SuiteRfbOutputMaskModule,
                    ],
                    declarations: [
                        NotificationsComponent,
                    ],
                    exports: [
                        NotificationsComponent,
                    ],
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbUserConfigurationsModule {
}
SuiteRfbUserConfigurationsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbUserConfigurationsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbUserConfigurationsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbUserConfigurationsModule, declarations: [UserConfigurationsComponent], imports: [CommonModule, i7.BsDropdownModule], exports: [UserConfigurationsComponent] });
SuiteRfbUserConfigurationsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbUserConfigurationsModule, imports: [CommonModule,
        BsDropdownModule.forRoot()] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbUserConfigurationsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        BsDropdownModule.forRoot(),
                    ],
                    declarations: [
                        UserConfigurationsComponent,
                    ],
                    exports: [
                        UserConfigurationsComponent,
                    ],
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbHeaderModule {
}
SuiteRfbHeaderModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbHeaderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbHeaderModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbHeaderModule, declarations: [AccessibilityComponent,
        BrandComponent,
        HeaderComponent], imports: [CommonModule, i1$1.RouterModule, SuiteRfbApplicationsMenuModule,
        SuiteRfbContextualHelpModule,
        SuiteRfbLogoutModule,
        SuiteRfbNotificationsModule,
        SuiteRfbUserModule,
        SuiteRfbUserConfigurationsModule], exports: [AccessibilityComponent,
        BrandComponent,
        HeaderComponent] });
SuiteRfbHeaderModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbHeaderModule, imports: [CommonModule,
        RouterModule.forChild([]),
        SuiteRfbApplicationsMenuModule,
        SuiteRfbContextualHelpModule,
        SuiteRfbLogoutModule,
        SuiteRfbNotificationsModule,
        SuiteRfbUserModule,
        SuiteRfbUserConfigurationsModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbHeaderModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        RouterModule.forChild([]),
                        SuiteRfbApplicationsMenuModule,
                        SuiteRfbContextualHelpModule,
                        SuiteRfbLogoutModule,
                        SuiteRfbNotificationsModule,
                        SuiteRfbUserModule,
                        SuiteRfbUserConfigurationsModule,
                    ],
                    declarations: [
                        AccessibilityComponent,
                        BrandComponent,
                        HeaderComponent,
                    ],
                    exports: [
                        AccessibilityComponent,
                        BrandComponent,
                        HeaderComponent,
                    ],
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class CounterService {
    constructor() {
        this.current = 0;
        this.counter = new BehaviorSubject(undefined);
        this.counter$ = this.counter.asObservable();
    }
    reset() {
        if (this.current !== 0) {
            this.current = 0;
            this.counter.next(this.current);
        }
    }
    increment() {
        this.current++;
        this.counter.next(this.current);
    }
    decrement() {
        if (this.current > 0) {
            this.current--;
            this.counter.next(this.current);
        }
    }
    onZero() {
        return this.counter$.pipe(filter(current => current === 0), map(() => { }));
    }
    onOne() {
        return this.counter$.pipe(filter(current => current === 1), map(() => { }));
    }
}
CounterService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: CounterService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CounterService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: CounterService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: CounterService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class LoadingService {
    constructor(router, configService) {
        this.router = router;
        this.configService = configService;
        this.loadingSource = new Subject();
        this.loading$ = this.loadingSource.asObservable();
        if (this.configService.isShowLoadingOnNavigation()) {
            this._showLoadingOnNavigationStart();
            this._hideLoadingOnNavigationEnd();
        }
    }
    isLoading() {
        return this.loading$;
    }
    show(url = null) {
        if (this.configService.isShowLoadingNavigationForUrl(url)) {
            this.loadingSource.next(true);
        }
    }
    hide(url = null) {
        if (this.configService.isShowLoadingNavigationForUrl(url)) {
            this.loadingSource.next(false);
        }
    }
    ngOnDestroy() {
        this.loadingSource.complete();
    }
    _showLoadingOnNavigationStart() {
        this.router.events
            .pipe(filter(event => event instanceof NavigationStart))
            .subscribe((e) => this.show(e.url));
    }
    _hideLoadingOnNavigationEnd() {
        this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe((e) => this.hide(e.url));
    }
}
LoadingService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: LoadingService, deps: [{ token: i1$1.Router }, { token: ConfigService }], target: i0.ɵɵFactoryTarget.Injectable });
LoadingService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: LoadingService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: LoadingService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1$1.Router }, { type: ConfigService }]; } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class LoadingComponent {
    constructor(loadingService, configService, counterService) {
        this.loadingService = loadingService;
        this.configService = configService;
        this.counterService = counterService;
        this.ignoreBackdropClick = false;
        this.showSpinner = false;
        this.ngUnsubscribe = new Subject();
    }
    ngOnInit() {
        this.ignoreBackdropClick = this.configService.isIgnoreLoadingBackdropClick();
        this.counterService.onOne()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(() => this.show());
        this.counterService.onZero()
            .pipe(takeUntil(this.ngUnsubscribe), delay(250))
            .subscribe(() => this.hide());
        this.loadingService.isLoading()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(show => {
            if (show) {
                this.counterService.increment();
            }
            else {
                this.counterService.decrement();
            }
        });
    }
    backdropClick() {
        if (this.ignoreBackdropClick) {
            return;
        }
        this.counterService.reset();
        this.hide();
    }
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
    show() {
        this.showSpinner = true;
    }
    hide() {
        this.showSpinner = false;
    }
}
LoadingComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: LoadingComponent, deps: [{ token: LoadingService }, { token: ConfigService }, { token: CounterService }], target: i0.ɵɵFactoryTarget.Component });
LoadingComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: LoadingComponent, selector: "suite-rfb-loading", providers: [CounterService], ngImport: i0, template: "<div *ngIf=\"showSpinner\" (click)=\"backdropClick()\" class=\"spinner-backdrop\">\n  <div class=\"spinner-body\">\n    <svg class=\"lds-spinner\" width=\"75px\" height=\"75px\" xmlns=\"http://www.w3.org/2000/svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\"\n      viewBox=\"0 0 100 100\" preserveAspectRatio=\"xMidYMid\" style=\"background: transparent none repeat scroll 0% 0%;\">\n      <g transform=\"rotate(0 50 50)\">\n        <rect x=\"47\" y=\"24\" rx=\"9.4\" ry=\"4.8\" width=\"6\" height=\"12\" fill=\"#00205b\">\n          <animate attributeName=\"opacity\" values=\"1;0\" keyTimes=\"0;1\" dur=\"1s\" begin=\"-0.9166666666666666s\"\n            repeatCount=\"indefinite\" />\n        </rect>\n      </g>\n      <g transform=\"rotate(30 50 50)\">\n        <rect x=\"47\" y=\"24\" rx=\"9.4\" ry=\"4.8\" width=\"6\" height=\"12\" fill=\"#00205b\">\n          <animate attributeName=\"opacity\" values=\"1;0\" keyTimes=\"0;1\" dur=\"1s\" begin=\"-0.8333333333333334s\"\n            repeatCount=\"indefinite\" />\n        </rect>\n      </g>\n      <g transform=\"rotate(60 50 50)\">\n        <rect x=\"47\" y=\"24\" rx=\"9.4\" ry=\"4.8\" width=\"6\" height=\"12\" fill=\"#00205b\">\n          <animate attributeName=\"opacity\" values=\"1;0\" keyTimes=\"0;1\" dur=\"1s\" begin=\"-0.75s\" repeatCount=\"indefinite\" />\n        </rect>\n      </g>\n      <g transform=\"rotate(90 50 50)\">\n        <rect x=\"47\" y=\"24\" rx=\"9.4\" ry=\"4.8\" width=\"6\" height=\"12\" fill=\"#00205b\">\n          <animate attributeName=\"opacity\" values=\"1;0\" keyTimes=\"0;1\" dur=\"1s\" begin=\"-0.6666666666666666s\"\n            repeatCount=\"indefinite\" />\n        </rect>\n      </g>\n      <g transform=\"rotate(120 50 50)\">\n        <rect x=\"47\" y=\"24\" rx=\"9.4\" ry=\"4.8\" width=\"6\" height=\"12\" fill=\"#00205b\">\n          <animate attributeName=\"opacity\" values=\"1;0\" keyTimes=\"0;1\" dur=\"1s\" begin=\"-0.5833333333333334s\"\n            repeatCount=\"indefinite\" />\n        </rect>\n      </g>\n      <g transform=\"rotate(150 50 50)\">\n        <rect x=\"47\" y=\"24\" rx=\"9.4\" ry=\"4.8\" width=\"6\" height=\"12\" fill=\"#00205b\">\n          <animate attributeName=\"opacity\" values=\"1;0\" keyTimes=\"0;1\" dur=\"1s\" begin=\"-0.5s\" repeatCount=\"indefinite\" />\n        </rect>\n      </g>\n      <g transform=\"rotate(180 50 50)\">\n        <rect x=\"47\" y=\"24\" rx=\"9.4\" ry=\"4.8\" width=\"6\" height=\"12\" fill=\"#00205b\">\n          <animate attributeName=\"opacity\" values=\"1;0\" keyTimes=\"0;1\" dur=\"1s\" begin=\"-0.4166666666666667s\"\n            repeatCount=\"indefinite\" />\n        </rect>\n      </g>\n      <g transform=\"rotate(210 50 50)\">\n        <rect x=\"47\" y=\"24\" rx=\"9.4\" ry=\"4.8\" width=\"6\" height=\"12\" fill=\"#00205b\">\n          <animate attributeName=\"opacity\" values=\"1;0\" keyTimes=\"0;1\" dur=\"1s\" begin=\"-0.3333333333333333s\"\n            repeatCount=\"indefinite\" />\n        </rect>\n      </g>\n      <g transform=\"rotate(240 50 50)\">\n        <rect x=\"47\" y=\"24\" rx=\"9.4\" ry=\"4.8\" width=\"6\" height=\"12\" fill=\"#00205b\">\n          <animate attributeName=\"opacity\" values=\"1;0\" keyTimes=\"0;1\" dur=\"1s\" begin=\"-0.25s\" repeatCount=\"indefinite\" />\n        </rect>\n      </g>\n      <g transform=\"rotate(270 50 50)\">\n        <rect x=\"47\" y=\"24\" rx=\"9.4\" ry=\"4.8\" width=\"6\" height=\"12\" fill=\"#00205b\">\n          <animate attributeName=\"opacity\" values=\"1;0\" keyTimes=\"0;1\" dur=\"1s\" begin=\"-0.16666666666666666s\"\n            repeatCount=\"indefinite\" />\n        </rect>\n      </g>\n      <g transform=\"rotate(300 50 50)\">\n        <rect x=\"47\" y=\"24\" rx=\"9.4\" ry=\"4.8\" width=\"6\" height=\"12\" fill=\"#00205b\">\n          <animate attributeName=\"opacity\" values=\"1;0\" keyTimes=\"0;1\" dur=\"1s\" begin=\"-0.08333333333333333s\"\n            repeatCount=\"indefinite\" />\n        </rect>\n      </g>\n      <g transform=\"rotate(330 50 50)\">\n        <rect x=\"47\" y=\"24\" rx=\"9.4\" ry=\"4.8\" width=\"6\" height=\"12\" fill=\"#00205b\">\n          <animate attributeName=\"opacity\" values=\"1;0\" keyTimes=\"0;1\" dur=\"1s\" begin=\"0s\" repeatCount=\"indefinite\" />\n        </rect>\n      </g>\n    </svg>\n    <span>\n      Processando...\n    </span>\n  </div>\n</div>", styles: [".spinner-backdrop{position:fixed;background-color:#00000080;top:0;left:0;width:100%;height:100%;z-index:9998}.spinner-body{position:fixed;top:30px;left:calc(50vw - 150px);width:300px;height:107px;border:1px solid rgba(0,0,0,.2);border-radius:6px;z-index:9999;background-color:#fff;box-shadow:0 5px 15px #00000080;display:flex;align-items:center;justify-content:center}\n", "::ng-deep .modal.fade{opacity:1}::ng-deep .modal.fade .modal-dialog{transform:translate(0)}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: LoadingComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-loading', providers: [CounterService], template: "<div *ngIf=\"showSpinner\" (click)=\"backdropClick()\" class=\"spinner-backdrop\">\n  <div class=\"spinner-body\">\n    <svg class=\"lds-spinner\" width=\"75px\" height=\"75px\" xmlns=\"http://www.w3.org/2000/svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\"\n      viewBox=\"0 0 100 100\" preserveAspectRatio=\"xMidYMid\" style=\"background: transparent none repeat scroll 0% 0%;\">\n      <g transform=\"rotate(0 50 50)\">\n        <rect x=\"47\" y=\"24\" rx=\"9.4\" ry=\"4.8\" width=\"6\" height=\"12\" fill=\"#00205b\">\n          <animate attributeName=\"opacity\" values=\"1;0\" keyTimes=\"0;1\" dur=\"1s\" begin=\"-0.9166666666666666s\"\n            repeatCount=\"indefinite\" />\n        </rect>\n      </g>\n      <g transform=\"rotate(30 50 50)\">\n        <rect x=\"47\" y=\"24\" rx=\"9.4\" ry=\"4.8\" width=\"6\" height=\"12\" fill=\"#00205b\">\n          <animate attributeName=\"opacity\" values=\"1;0\" keyTimes=\"0;1\" dur=\"1s\" begin=\"-0.8333333333333334s\"\n            repeatCount=\"indefinite\" />\n        </rect>\n      </g>\n      <g transform=\"rotate(60 50 50)\">\n        <rect x=\"47\" y=\"24\" rx=\"9.4\" ry=\"4.8\" width=\"6\" height=\"12\" fill=\"#00205b\">\n          <animate attributeName=\"opacity\" values=\"1;0\" keyTimes=\"0;1\" dur=\"1s\" begin=\"-0.75s\" repeatCount=\"indefinite\" />\n        </rect>\n      </g>\n      <g transform=\"rotate(90 50 50)\">\n        <rect x=\"47\" y=\"24\" rx=\"9.4\" ry=\"4.8\" width=\"6\" height=\"12\" fill=\"#00205b\">\n          <animate attributeName=\"opacity\" values=\"1;0\" keyTimes=\"0;1\" dur=\"1s\" begin=\"-0.6666666666666666s\"\n            repeatCount=\"indefinite\" />\n        </rect>\n      </g>\n      <g transform=\"rotate(120 50 50)\">\n        <rect x=\"47\" y=\"24\" rx=\"9.4\" ry=\"4.8\" width=\"6\" height=\"12\" fill=\"#00205b\">\n          <animate attributeName=\"opacity\" values=\"1;0\" keyTimes=\"0;1\" dur=\"1s\" begin=\"-0.5833333333333334s\"\n            repeatCount=\"indefinite\" />\n        </rect>\n      </g>\n      <g transform=\"rotate(150 50 50)\">\n        <rect x=\"47\" y=\"24\" rx=\"9.4\" ry=\"4.8\" width=\"6\" height=\"12\" fill=\"#00205b\">\n          <animate attributeName=\"opacity\" values=\"1;0\" keyTimes=\"0;1\" dur=\"1s\" begin=\"-0.5s\" repeatCount=\"indefinite\" />\n        </rect>\n      </g>\n      <g transform=\"rotate(180 50 50)\">\n        <rect x=\"47\" y=\"24\" rx=\"9.4\" ry=\"4.8\" width=\"6\" height=\"12\" fill=\"#00205b\">\n          <animate attributeName=\"opacity\" values=\"1;0\" keyTimes=\"0;1\" dur=\"1s\" begin=\"-0.4166666666666667s\"\n            repeatCount=\"indefinite\" />\n        </rect>\n      </g>\n      <g transform=\"rotate(210 50 50)\">\n        <rect x=\"47\" y=\"24\" rx=\"9.4\" ry=\"4.8\" width=\"6\" height=\"12\" fill=\"#00205b\">\n          <animate attributeName=\"opacity\" values=\"1;0\" keyTimes=\"0;1\" dur=\"1s\" begin=\"-0.3333333333333333s\"\n            repeatCount=\"indefinite\" />\n        </rect>\n      </g>\n      <g transform=\"rotate(240 50 50)\">\n        <rect x=\"47\" y=\"24\" rx=\"9.4\" ry=\"4.8\" width=\"6\" height=\"12\" fill=\"#00205b\">\n          <animate attributeName=\"opacity\" values=\"1;0\" keyTimes=\"0;1\" dur=\"1s\" begin=\"-0.25s\" repeatCount=\"indefinite\" />\n        </rect>\n      </g>\n      <g transform=\"rotate(270 50 50)\">\n        <rect x=\"47\" y=\"24\" rx=\"9.4\" ry=\"4.8\" width=\"6\" height=\"12\" fill=\"#00205b\">\n          <animate attributeName=\"opacity\" values=\"1;0\" keyTimes=\"0;1\" dur=\"1s\" begin=\"-0.16666666666666666s\"\n            repeatCount=\"indefinite\" />\n        </rect>\n      </g>\n      <g transform=\"rotate(300 50 50)\">\n        <rect x=\"47\" y=\"24\" rx=\"9.4\" ry=\"4.8\" width=\"6\" height=\"12\" fill=\"#00205b\">\n          <animate attributeName=\"opacity\" values=\"1;0\" keyTimes=\"0;1\" dur=\"1s\" begin=\"-0.08333333333333333s\"\n            repeatCount=\"indefinite\" />\n        </rect>\n      </g>\n      <g transform=\"rotate(330 50 50)\">\n        <rect x=\"47\" y=\"24\" rx=\"9.4\" ry=\"4.8\" width=\"6\" height=\"12\" fill=\"#00205b\">\n          <animate attributeName=\"opacity\" values=\"1;0\" keyTimes=\"0;1\" dur=\"1s\" begin=\"0s\" repeatCount=\"indefinite\" />\n        </rect>\n      </g>\n    </svg>\n    <span>\n      Processando...\n    </span>\n  </div>\n</div>", styles: [".spinner-backdrop{position:fixed;background-color:#00000080;top:0;left:0;width:100%;height:100%;z-index:9998}.spinner-body{position:fixed;top:30px;left:calc(50vw - 150px);width:300px;height:107px;border:1px solid rgba(0,0,0,.2);border-radius:6px;z-index:9999;background-color:#fff;box-shadow:0 5px 15px #00000080;display:flex;align-items:center;justify-content:center}\n", "::ng-deep .modal.fade{opacity:1}::ng-deep .modal.fade .modal-dialog{transform:translate(0)}\n"] }]
        }], ctorParameters: function () { return [{ type: LoadingService }, { type: ConfigService }, { type: CounterService }]; } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class LoadingHttpInterceptor {
    constructor(loadingService) {
        this.loadingService = loadingService;
    }
    intercept(req, next) {
        this.loadingService.show();
        return next.handle(req).pipe(finalize(() => this.loadingService.hide()));
    }
}
LoadingHttpInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: LoadingHttpInterceptor, deps: [{ token: LoadingService }], target: i0.ɵɵFactoryTarget.Injectable });
LoadingHttpInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: LoadingHttpInterceptor, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: LoadingHttpInterceptor, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: LoadingService }]; } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbLoadingModule {
}
SuiteRfbLoadingModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbLoadingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbLoadingModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbLoadingModule, declarations: [LoadingComponent], imports: [CommonModule, i1$2.ModalModule, i1$1.RouterModule], exports: [LoadingComponent] });
SuiteRfbLoadingModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbLoadingModule, imports: [CommonModule,
        ModalModule.forRoot(),
        RouterModule.forChild([])] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbLoadingModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        ModalModule.forRoot(),
                        RouterModule.forChild([]),
                    ],
                    declarations: [
                        LoadingComponent,
                    ],
                    exports: [
                        LoadingComponent,
                    ],
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbLogoutService {
    constructor(http, environmentService) {
        this.http = http;
        this.environmentService = environmentService;
        this.environment = this.environmentService.environment;
    }
    logout() {
        const baseUrl = this.environment.baseUrl;
        this.http
            .post(`${baseUrl}/api/usuario/logout`, '', { withCredentials: true })
            .subscribe(() => this._redirectAfterLogout(), (err) => {
            // TODO: a falta do cabeçalho CORS (Access-Control-Allow-Origin) na API de Logout da Suíte RFB pode levantar erros dependendo do navegador.
            this._redirectAfterLogout();
        });
    }
    _redirectAfterLogout() {
        const baseUrl = this.environment.baseUrl;
        window.location.href = `${baseUrl}/camweb/conflogout`;
    }
}
SuiteRfbLogoutService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbLogoutService, deps: [{ token: i1$3.HttpClient }, { token: EnvironmentService }], target: i0.ɵɵFactoryTarget.Injectable });
SuiteRfbLogoutService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbLogoutService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbLogoutService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1$3.HttpClient }, { type: EnvironmentService }]; } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class ShortcutService {
    constructor(configService, document) {
        this.configService = configService;
        this.document = document;
        this.shortcut = new Subject();
        this.shortcut$ = this.shortcut.asObservable();
        this.activeKeys = [];
        this.keypressListener = (event) => {
            const isShortcut = this.activeKeys.indexOf(event.key) >= 0;
            const element = event.srcElement ? event.srcElement : event.target;
            if (isShortcut && !this.isIgnoredElement(element)) {
                this.shortcut.next(event.key);
                event.preventDefault();
            }
        };
    }
    registerKey(key) {
        this.activeKeys.push(key);
        this.document.addEventListener('keypress', this.keypressListener);
        return this.shortcut.pipe(filter((keyPressed) => keyPressed === key));
    }
    unregisterAllKeys() {
        this.activeKeys = [];
        this.document.removeEventListener('keypress', this.keypressListener);
    }
    unregisterKey(key) {
        this.activeKeys = this.activeKeys.filter(k => k !== key);
        if (this.activeKeys.length === 0) {
            this.document.removeEventListener('keypress', this.keypressListener);
        }
    }
    isIgnoredElement(element) {
        const ucasedTagName = element && element.tagName && element.tagName.toUpperCase();
        if (ucasedTagName === 'INPUT') {
            return true;
        }
        if (ucasedTagName === 'TEXTAREA') {
            return true;
        }
        const ignoredClasses = this.configService.getIgnoredElementClassesInMenuShortcuts()
            || 'disable-menu-shortcuts';
        const ignoredClassesTokens = ignoredClasses.split(' ');
        for (const ignoredClass of ignoredClassesTokens) {
            if (element.classList.contains(ignoredClass)) {
                return true;
            }
        }
        return false;
    }
}
ShortcutService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: ShortcutService, deps: [{ token: ConfigService }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable });
ShortcutService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: ShortcutService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: ShortcutService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: ConfigService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class MenuFeatureService {
    constructor() {
        this.featureClickSource = new Subject();
        this.featureClick$ = this.featureClickSource.asObservable();
    }
    click(feature) {
        this.featureClickSource.next(feature);
    }
}
MenuFeatureService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: MenuFeatureService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
MenuFeatureService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: MenuFeatureService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: MenuFeatureService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class MenuItemComponent {
    constructor(shortcutService, menuFeatureService, router, document) {
        this.shortcutService = shortcutService;
        this.menuFeatureService = menuFeatureService;
        this.router = router;
        this.document = document;
        this.collapsedChange = new EventEmitter();
        this.isCollapsedMenu = false;
        this.ngUnsubscribe = new Subject();
    }
    ngOnInit() {
        this.isChildenCollapsed = this.isCollapsedMenu;
        this.registerShortcut();
    }
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
    onClick(feature) {
        this.menuFeatureService.click(feature);
        this._hideMenu();
    }
    toggleChildren() {
        this.isChildenCollapsed = !this.isChildenCollapsed;
        this.collapsedChange.emit(this.isChildenCollapsed);
    }
    registerShortcut() {
        const isShortcutFeature = this.feature.shortcut;
        const isRoutedFeature = this.feature.route;
        if (isShortcutFeature && isRoutedFeature) {
            const shortcut = this.feature.shortcut;
            this.shortcutService.registerKey(shortcut)
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe(() => {
                this.router.navigate([this.feature.route]);
                this._hideMenu();
            });
        }
    }
    _hideMenu() {
        this.document.body.classList.remove('menu-lateral-ativo'); // classList precisa de polyfill para browsers antigos (ex: IE9)
    }
}
MenuItemComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: MenuItemComponent, deps: [{ token: ShortcutService }, { token: MenuFeatureService }, { token: i1$1.Router }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Component });
MenuItemComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: MenuItemComponent, selector: "suite-rfb-menu-item", inputs: { feature: "feature", root: "root", user: "user", isCollapsedMenu: "isCollapsedMenu" }, outputs: { collapsedChange: "collapsedChange" }, ngImport: i0, template: "<ng-container *ngIf=\"feature|allowedFeature:user\">\n\n  <ng-container *ngIf=\"!feature.children\">\n    <a *ngIf=\"feature.url\" class=\"menu-item\" [href]=\"feature.url\" [target]=\"feature.urlTarget ? feature.urlTarget : '_blank'\" aria-expanded=\"false\">\n      <ng-container *ngTemplateOutlet=\"itemTemplate; context: { feature: feature }\"></ng-container>\n    </a>\n    <a *ngIf=\"!feature.url\" class=\"menu-item\" [routerLink]=\"feature.route\" aria-expanded=\"false\"\n      (click)=\"onClick(feature)\">\n      <ng-container *ngTemplateOutlet=\"itemTemplate; context: { feature: feature }\"></ng-container>\n    </a>\n  </ng-container>\n\n  <ng-container *ngIf=\"feature.children\">\n    <a href=\"javascript:void(0)\" class=\"menu-item raiz\" [attr.aria-expanded]=\"!isChildenCollapsed\"\n      (click)=\"toggleChildren()\">{{feature.title}}</a>\n    <ul class=\"menu nav-second-level collapse\" [attr.aria-expanded]=\"!isChildenCollapsed\"\n      [class.in]=\"!isChildenCollapsed\">\n      <li *ngFor=\"let childFeature of feature.children\">\n        <suite-rfb-menu-item [feature]=\"childFeature\" [user]=\"user\" [isCollapsedMenu]=\"isCollapsedMenu\" [root]=\"false\"\n          [class.active]=\"isChildenCollapsed\">\n        </suite-rfb-menu-item>\n      </li>\n    </ul>\n  </ng-container>\n\n</ng-container>\n\n<ng-template #itemTemplate let-feature=\"feature\">\n  <span *ngIf=\"feature.icon\" class=\"material-icons icon-24\">{{feature.icon}}</span>\n  <span>{{feature.title}}<ng-container *ngIf=\"feature.shortcut\">&nbsp;({{feature.shortcut}})</ng-container></span>\n  <span *ngIf=\"feature.favorite\" class=\"material-icons icon-18\">&#xE838;</span>\n</ng-template>", styles: [""], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i1$1.RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }, { kind: "component", type: MenuItemComponent, selector: "suite-rfb-menu-item", inputs: ["feature", "root", "user", "isCollapsedMenu"], outputs: ["collapsedChange"] }, { kind: "pipe", type: AllowedFeaturePipe, name: "allowedFeature" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: MenuItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-menu-item', template: "<ng-container *ngIf=\"feature|allowedFeature:user\">\n\n  <ng-container *ngIf=\"!feature.children\">\n    <a *ngIf=\"feature.url\" class=\"menu-item\" [href]=\"feature.url\" [target]=\"feature.urlTarget ? feature.urlTarget : '_blank'\" aria-expanded=\"false\">\n      <ng-container *ngTemplateOutlet=\"itemTemplate; context: { feature: feature }\"></ng-container>\n    </a>\n    <a *ngIf=\"!feature.url\" class=\"menu-item\" [routerLink]=\"feature.route\" aria-expanded=\"false\"\n      (click)=\"onClick(feature)\">\n      <ng-container *ngTemplateOutlet=\"itemTemplate; context: { feature: feature }\"></ng-container>\n    </a>\n  </ng-container>\n\n  <ng-container *ngIf=\"feature.children\">\n    <a href=\"javascript:void(0)\" class=\"menu-item raiz\" [attr.aria-expanded]=\"!isChildenCollapsed\"\n      (click)=\"toggleChildren()\">{{feature.title}}</a>\n    <ul class=\"menu nav-second-level collapse\" [attr.aria-expanded]=\"!isChildenCollapsed\"\n      [class.in]=\"!isChildenCollapsed\">\n      <li *ngFor=\"let childFeature of feature.children\">\n        <suite-rfb-menu-item [feature]=\"childFeature\" [user]=\"user\" [isCollapsedMenu]=\"isCollapsedMenu\" [root]=\"false\"\n          [class.active]=\"isChildenCollapsed\">\n        </suite-rfb-menu-item>\n      </li>\n    </ul>\n  </ng-container>\n\n</ng-container>\n\n<ng-template #itemTemplate let-feature=\"feature\">\n  <span *ngIf=\"feature.icon\" class=\"material-icons icon-24\">{{feature.icon}}</span>\n  <span>{{feature.title}}<ng-container *ngIf=\"feature.shortcut\">&nbsp;({{feature.shortcut}})</ng-container></span>\n  <span *ngIf=\"feature.favorite\" class=\"material-icons icon-18\">&#xE838;</span>\n</ng-template>" }]
        }], ctorParameters: function () { return [{ type: ShortcutService }, { type: MenuFeatureService }, { type: i1$1.Router }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; }, propDecorators: { feature: [{
                type: Input
            }], root: [{
                type: Input
            }], user: [{
                type: Input
            }], collapsedChange: [{
                type: Output
            }], isCollapsedMenu: [{
                type: Input
            }] } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class MenuComponent {
    constructor(userService, featureService, configService, document, windowService) {
        this.userService = userService;
        this.featureService = featureService;
        this.configService = configService;
        this.document = document;
        this.windowService = windowService;
        this.favoriteOnly = false;
        this.features = [];
        this.ngUnsubscribe = new Subject();
    }
    ngOnInit() {
        this.isCollapsedMenu = this.configService.isCollapsedMenu();
        this.menuOptionsHidden = this.configService.isMenuOptionsHidden();
        this.menuOptionsDisabled = this.configService.isMenuOptionsDisabled();
        this.featureService.getFeatures$()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((features) => {
            // GAMBI: forçar o refresh das indicações de feature favorita
            this.features = [];
            setTimeout(() => { this.features = features; }, 0);
        });
        this.userService.getUser()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((user) => this.user = user);
        this._handleOutsideClick();
    }
    onSearchInput(e) {
        const value = e.target.value;
        this.search(value);
    }
    search(searchTerms) {
        this.searchTerms = searchTerms;
        // if any search terms or favorites only override config collapsed menu
        this.isCollapsedMenu = (!isBlank(this.searchTerms) || this.favoriteOnly)
            ? false
            : this.configService.isCollapsedMenu();
    }
    toggleFavorite(searchTerms) {
        this.favoriteOnly = !this.favoriteOnly;
        this.search(searchTerms);
    }
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
    _handleOutsideClick() {
        this.window.addEventListener('click', (event) => {
            if (this.document.body.classList.contains('menu-lateral-ativo')) {
                const target = event.target;
                const navegacao = this.document.getElementById('navegacao');
                const menu = this.document.getElementById('suite-rfb-menu-container');
                const outsideClick = !menu.contains(target) && !navegacao.contains(target);
                if (outsideClick) {
                    this.document.body.classList.remove('menu-lateral-ativo');
                }
            }
        });
    }
    get window() {
        return this.windowService.get();
    }
}
MenuComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: MenuComponent, deps: [{ token: UserService }, { token: FeatureService }, { token: ConfigService }, { token: DOCUMENT }, { token: WindowService }], target: i0.ɵɵFactoryTarget.Component });
MenuComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: MenuComponent, selector: "suite-rfb-menu", ngImport: i0, template: "<div id=\"suite-rfb-menu-container\">\n  <div class=\"busca\">\n    <label for=\"busca\" class=\"sr-only\">Buscar funcionalidade</label>\n    <div class=\"input-group\">\n      <input #searchInput type=\"search\" id=\"busca\" class=\"form-control\" (input)=\"onSearchInput($event)\">\n      <div class=\"input-group-btn\">\n        <button title=\"Mostrar apenas os favoritos\" type=\"button\" class=\"btn btn-default btn-toggle-favorite\"\n            (click)=\"toggleFavorite(searchInput.value)\">\n          <span class=\"material-icons icon-24\">{{favoriteOnly ? '&#xE838;' : '&#xE83A;'}}</span>\n        </button>\n        <button id=\"clearFilter\" title=\"Limpar filtros\" type=\"button\" class=\"btn-suite-rfb\" (click)=\"searchInput.value=''; favoriteOnly=false; search('')\">\n            <i class=\"icon icon-suite-rfb-filter-remove icon-24\"></i>\n        </button>\n        <button type=\"button\" class=\"btn btn-default btn-menu-options\" *ngIf=\"!menuOptionsHidden\" [disabled]=\"menuOptionsDisabled\">\n          <span class=\"material-icons icon-24\">&#xE5D4;</span>\n        </button>\n      </div>\n    </div>\n  </div>\n  <ul class=\"menu\">\n    <li *ngFor=\"let menuFeature of (features | featureFilter:searchTerms:favoriteOnly)\">\n      <suite-rfb-menu-item\n        [feature]=\"menuFeature\" [user]=\"user\"\n        [isCollapsedMenu]=\"isCollapsedMenu\"\n        [root]=\"true\" [class.active]=\"!isCollapsedMenu\">\n      </suite-rfb-menu-item>\n    </li>\n  </ul>\n", styles: [":host ::ng-deep suite-rfb-menu-item suite-rfb-menu-item suite-rfb-menu-item a.menu-item:hover{background-color:#cdbfed}:host ::ng-deep suite-rfb-menu-item suite-rfb-menu-item suite-rfb-menu-item suite-rfb-menu-item a.menu-item:hover{background-color:#bfbfbf}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: MenuItemComponent, selector: "suite-rfb-menu-item", inputs: ["feature", "root", "user", "isCollapsedMenu"], outputs: ["collapsedChange"] }, { kind: "pipe", type: FeatureFilterPipe, name: "featureFilter" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: MenuComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-menu', template: "<div id=\"suite-rfb-menu-container\">\n  <div class=\"busca\">\n    <label for=\"busca\" class=\"sr-only\">Buscar funcionalidade</label>\n    <div class=\"input-group\">\n      <input #searchInput type=\"search\" id=\"busca\" class=\"form-control\" (input)=\"onSearchInput($event)\">\n      <div class=\"input-group-btn\">\n        <button title=\"Mostrar apenas os favoritos\" type=\"button\" class=\"btn btn-default btn-toggle-favorite\"\n            (click)=\"toggleFavorite(searchInput.value)\">\n          <span class=\"material-icons icon-24\">{{favoriteOnly ? '&#xE838;' : '&#xE83A;'}}</span>\n        </button>\n        <button id=\"clearFilter\" title=\"Limpar filtros\" type=\"button\" class=\"btn-suite-rfb\" (click)=\"searchInput.value=''; favoriteOnly=false; search('')\">\n            <i class=\"icon icon-suite-rfb-filter-remove icon-24\"></i>\n        </button>\n        <button type=\"button\" class=\"btn btn-default btn-menu-options\" *ngIf=\"!menuOptionsHidden\" [disabled]=\"menuOptionsDisabled\">\n          <span class=\"material-icons icon-24\">&#xE5D4;</span>\n        </button>\n      </div>\n    </div>\n  </div>\n  <ul class=\"menu\">\n    <li *ngFor=\"let menuFeature of (features | featureFilter:searchTerms:favoriteOnly)\">\n      <suite-rfb-menu-item\n        [feature]=\"menuFeature\" [user]=\"user\"\n        [isCollapsedMenu]=\"isCollapsedMenu\"\n        [root]=\"true\" [class.active]=\"!isCollapsedMenu\">\n      </suite-rfb-menu-item>\n    </li>\n  </ul>\n", styles: [":host ::ng-deep suite-rfb-menu-item suite-rfb-menu-item suite-rfb-menu-item a.menu-item:hover{background-color:#cdbfed}:host ::ng-deep suite-rfb-menu-item suite-rfb-menu-item suite-rfb-menu-item suite-rfb-menu-item a.menu-item:hover{background-color:#bfbfbf}\n"] }]
        }], ctorParameters: function () { return [{ type: UserService }, { type: FeatureService }, { type: ConfigService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: WindowService }]; } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class MenuTogglerComponent {
    constructor(shortcutService, document) {
        this.shortcutService = shortcutService;
        this.document = document;
        this.ngUnsubscribe = new Subject();
    }
    ngOnInit() {
        this.registerShortcut();
    }
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
    toggleMenu() {
        this.document.body.classList.toggle('menu-lateral-ativo'); // classList precisa de polyfill para browsers antigos (ex: IE9)
    }
    registerShortcut() {
        this.shortcutService.registerKey('/')
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((key) => this.toggleMenu());
    }
}
MenuTogglerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: MenuTogglerComponent, deps: [{ token: ShortcutService }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Component });
MenuTogglerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: MenuTogglerComponent, selector: "suite-rfb-menu-toggler", ngImport: i0, template: "<div class=\"trigger\">\n  <button class=\"btn btn-default\" type=\"button\" id=\"navegacao\" (click)=\"toggleMenu()\">\n    <span class=\"material-icons icon-28\">&#xE5D2;</span>\n  </button>\n</div>\n", styles: [""] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: MenuTogglerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-menu-toggler', template: "<div class=\"trigger\">\n  <button class=\"btn btn-default\" type=\"button\" id=\"navegacao\" (click)=\"toggleMenu()\">\n    <span class=\"material-icons icon-28\">&#xE5D2;</span>\n  </button>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: ShortcutService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbMenuModule {
}
SuiteRfbMenuModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbMenuModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbMenuModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbMenuModule, declarations: [MenuComponent,
        MenuItemComponent,
        MenuTogglerComponent], imports: [CommonModule, i1$1.RouterModule, SuiteRfbFeaturesModule], exports: [MenuComponent,
        MenuItemComponent,
        MenuTogglerComponent] });
SuiteRfbMenuModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbMenuModule, imports: [CommonModule,
        RouterModule.forChild([]),
        SuiteRfbFeaturesModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbMenuModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        RouterModule.forChild([]),
                        SuiteRfbFeaturesModule,
                    ],
                    declarations: [
                        MenuComponent,
                        MenuItemComponent,
                        MenuTogglerComponent,
                    ],
                    exports: [
                        MenuComponent,
                        MenuItemComponent,
                        MenuTogglerComponent,
                    ],
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class MonthPickerComponent extends DatePickerComponent {
    constructor() {
        super(...arguments);
        this.disabledDays = [];
        this.format = 'MM/YYYY';
        this.minMode = 'month';
        this.inputMask = '99/9999';
    }
}
MonthPickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: MonthPickerComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
MonthPickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: MonthPickerComponent, selector: "suite-rfb-month-picker", inputs: { disabledDays: "disabledDays", format: "format" }, providers: [{
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => MonthPickerComponent)
        }], usesInheritance: true, ngImport: i0, template: "<div class=\"form-group\" [suiteRfbHasError]=\"ngControl.control\">\n  <label *ngIf=\"label\" class=\"control-label\"\n    [class.obrigatorio]=\"required\">{{label}}</label>\n  <div [class]=\"inputGroupClass\">\n    <bs-datepicker-inline *ngIf=\"inline\"\n      [bsConfig]=\"bsConfig\"\n      [bsValue]=\"value\"\n      [isDisabled]=\"disabled\"\n      [datesDisabled]=\"disabledDates\"\n      [minDate]=\"minDate\"\n      [maxDate]=\"maxDate\"\n      (bsValueChange)=\"selectionInlineDone($event)\">\n    </bs-datepicker-inline>\n    <ng-container *ngIf=\"!inline\">\n      <input #dp=\"bsDatepicker\" bsDatepicker\n        type=\"text\"\n        class=\"form-control\"\n        (keyup)=\"dp.hide()\"\n        (input)=\"onInput($any($event).target.value)\"\n        (bsValueChange)=\"selectionDone($event)\"\n        [suiteRfbInputMask]=\"inputMask\"\n        [disabled]=\"disabled\"\n        [datesDisabled]=\"disabledDates\"\n        [minDate]=\"minDate\"\n        [maxDate]=\"maxDate\"\n        [placement]=\"direction\"\n        [outsideClick]=\"outsideClick\"\n        [bsConfig]=\"bsConfig\"\n        [bsValue]=\"value\"\n        [value]=\"formattedValue\">\n      <span class=\"input-group-addon\">\n        <span tooltip=\"Selecionar data do calend\u00E1rio\" container=\"body\">\n          <span class=\"material-icons date-picker-icon\"\n            [style.font-size]=\"iconSize\"\n            [class.disabled]=\"disabled\"\n            (click)=\"toggle()\">&#xE916;</span>\n        </span>\n      </span>\n    </ng-container>\n  </div>\n  <suite-rfb-local-messages [control]=\"ngControl.control\"></suite-rfb-local-messages>\n</div>", styles: [".date-picker-icon{cursor:pointer}.date-picker-icon.disabled{cursor:not-allowed;color:rgba(#00205b,.3)!important}.local-messages{color:#a8001f}::ng-deep bs-datepicker-container{z-index:1051}::ng-deep .bs-datepicker{padding:3px;background-color:#fff;border:1px solid rgba(0,0,0,.15);border-radius:4px;box-shadow:0 6px 12px #0000002d}::ng-deep .bs-datepicker-container{background-color:#f2e9d8}::ng-deep .bs-datepicker-head{background-color:#00205b;border-radius:0;color:#fff}::ng-deep .bs-datepicker-head bs-datepicker-navigation-view{display:flex;justify-content:space-between;align-items:center}::ng-deep .bs-datepicker-head bs-datepicker-navigation-view>button{background-color:#00205b;border:none;color:#fff;padding:7px 16px;font-weight:700}::ng-deep .bs-datepicker-head bs-datepicker-navigation-view>button[disabled]{opacity:.3;cursor:not-allowed}::ng-deep .bs-datepicker-body>table>thead>tr{background-color:#64a70b;border-radius:0;color:#fff;font-weight:600}::ng-deep .bs-datepicker-body>table>thead>tr>th{text-align:center;padding:4px 5px}::ng-deep .bs-datepicker-body>table>tbody>tr>td{background-color:#f2e9d8;color:#333;border-radius:0;border:none;text-align:center}::ng-deep .bs-datepicker-body>table>tbody>tr>td>span{padding:0;cursor:pointer;font-weight:600;line-height:26px;width:100%;display:block}::ng-deep .bs-datepicker-body>table{width:100%}::ng-deep .bs-datepicker-body>table>tbody>tr>td>span.disabled{cursor:not-allowed;font-weight:400;background-color:#f9f4e5}::ng-deep .bs-datepicker-body .is-other-month{opacity:.8;font-weight:400}::ng-deep .bs-datepicker-body .disabled{opacity:.65}::ng-deep .bs-datepicker-body>table>tbody>tr>td>span:hover,::ng-deep .bs-datepicker-body>table>tbody>tr>td>span:focus{background-color:#d1e5b6}::ng-deep .bs-datepicker-body>table>tbody>tr>td>.selected,::ng-deep .bs-datepicker-body>table>tbody>tr>td>.selected:hover,::ng-deep .bs-datepicker-body>table>tbody>tr>td>.selected:focus{background:#a66c00;color:#fff}::ng-deep .tabela .date-picker-container tbody tr td,::ng-deep .tabela .date-picker-container tbody tr th{border-right:inherit;border-top:inherit;padding:inherit}::ng-deep .tabela .date-picker-container thead tr th{background-color:inherit;border:inherit;border-right:inherit;color:inherit;font-weight:inherit;padding:inherit;vertical-align:inherit;white-space:inherit}::ng-deep .bs-datepicker-buttons{display:flex;flex-flow:row-reverse;justify-content:space-between;background-color:#e9dbc0}::ng-deep .bs-datepicker-buttons button,::ng-deep .bs-datepicker-buttons button:hover{background-color:inherit;color:#039;font-weight:400;border:none}::ng-deep timepicker table{margin:0 auto}::ng-deep .bs-datepicker-body .today{background:#64a70b;color:#fff}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2$2.BsDatepickerDirective, selector: "[bsDatepicker]", inputs: ["placement", "triggers", "outsideClick", "container", "outsideEsc", "isDisabled", "minDate", "maxDate", "minMode", "daysDisabled", "datesDisabled", "datesEnabled", "dateCustomClasses", "dateTooltipTexts", "isOpen", "bsValue", "bsConfig"], outputs: ["onShown", "onHidden", "bsValueChange"], exportAs: ["bsDatepicker"] }, { kind: "directive", type: i2$2.BsDatepickerInlineDirective, selector: "bs-datepicker-inline", inputs: ["bsConfig", "isDisabled", "minDate", "maxDate", "dateCustomClasses", "dateTooltipTexts", "datesEnabled", "datesDisabled", "bsValue"], outputs: ["bsValueChange"], exportAs: ["bsDatepickerInline"] }, { kind: "directive", type: i2$2.BsDatepickerInputDirective, selector: "input[bsDatepicker]" }, { kind: "directive", type: InputMaskDirective, selector: "[suiteRfbInputMask]", inputs: ["suiteRfbInputMask", "suiteRfbInputMaskInvalidAllowed"] }, { kind: "component", type: LocalMessagesComponent, selector: "suite-rfb-local-messages", inputs: ["requiredMessage", "patternMessage", "minlengthMessage", "maxlengthMessage", "emailMessage", "cnpjMessage", "cpfMessage", "dateMessage", "dayMessage", "monthMessage", "yearMessage", "minDateMessage", "maxDateMessage", "fileSizeMessage", "fileTypeMessage", "decendioMessage", "control"] }, { kind: "directive", type: HasErrorDirective, selector: "[suiteRfbHasError]", inputs: ["suiteRfbHasError"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: MonthPickerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-month-picker', providers: [{
                            provide: NG_VALUE_ACCESSOR,
                            multi: true,
                            useExisting: forwardRef(() => MonthPickerComponent)
                        }], template: "<div class=\"form-group\" [suiteRfbHasError]=\"ngControl.control\">\n  <label *ngIf=\"label\" class=\"control-label\"\n    [class.obrigatorio]=\"required\">{{label}}</label>\n  <div [class]=\"inputGroupClass\">\n    <bs-datepicker-inline *ngIf=\"inline\"\n      [bsConfig]=\"bsConfig\"\n      [bsValue]=\"value\"\n      [isDisabled]=\"disabled\"\n      [datesDisabled]=\"disabledDates\"\n      [minDate]=\"minDate\"\n      [maxDate]=\"maxDate\"\n      (bsValueChange)=\"selectionInlineDone($event)\">\n    </bs-datepicker-inline>\n    <ng-container *ngIf=\"!inline\">\n      <input #dp=\"bsDatepicker\" bsDatepicker\n        type=\"text\"\n        class=\"form-control\"\n        (keyup)=\"dp.hide()\"\n        (input)=\"onInput($any($event).target.value)\"\n        (bsValueChange)=\"selectionDone($event)\"\n        [suiteRfbInputMask]=\"inputMask\"\n        [disabled]=\"disabled\"\n        [datesDisabled]=\"disabledDates\"\n        [minDate]=\"minDate\"\n        [maxDate]=\"maxDate\"\n        [placement]=\"direction\"\n        [outsideClick]=\"outsideClick\"\n        [bsConfig]=\"bsConfig\"\n        [bsValue]=\"value\"\n        [value]=\"formattedValue\">\n      <span class=\"input-group-addon\">\n        <span tooltip=\"Selecionar data do calend\u00E1rio\" container=\"body\">\n          <span class=\"material-icons date-picker-icon\"\n            [style.font-size]=\"iconSize\"\n            [class.disabled]=\"disabled\"\n            (click)=\"toggle()\">&#xE916;</span>\n        </span>\n      </span>\n    </ng-container>\n  </div>\n  <suite-rfb-local-messages [control]=\"ngControl.control\"></suite-rfb-local-messages>\n</div>", styles: [".date-picker-icon{cursor:pointer}.date-picker-icon.disabled{cursor:not-allowed;color:rgba(#00205b,.3)!important}.local-messages{color:#a8001f}::ng-deep bs-datepicker-container{z-index:1051}::ng-deep .bs-datepicker{padding:3px;background-color:#fff;border:1px solid rgba(0,0,0,.15);border-radius:4px;box-shadow:0 6px 12px #0000002d}::ng-deep .bs-datepicker-container{background-color:#f2e9d8}::ng-deep .bs-datepicker-head{background-color:#00205b;border-radius:0;color:#fff}::ng-deep .bs-datepicker-head bs-datepicker-navigation-view{display:flex;justify-content:space-between;align-items:center}::ng-deep .bs-datepicker-head bs-datepicker-navigation-view>button{background-color:#00205b;border:none;color:#fff;padding:7px 16px;font-weight:700}::ng-deep .bs-datepicker-head bs-datepicker-navigation-view>button[disabled]{opacity:.3;cursor:not-allowed}::ng-deep .bs-datepicker-body>table>thead>tr{background-color:#64a70b;border-radius:0;color:#fff;font-weight:600}::ng-deep .bs-datepicker-body>table>thead>tr>th{text-align:center;padding:4px 5px}::ng-deep .bs-datepicker-body>table>tbody>tr>td{background-color:#f2e9d8;color:#333;border-radius:0;border:none;text-align:center}::ng-deep .bs-datepicker-body>table>tbody>tr>td>span{padding:0;cursor:pointer;font-weight:600;line-height:26px;width:100%;display:block}::ng-deep .bs-datepicker-body>table{width:100%}::ng-deep .bs-datepicker-body>table>tbody>tr>td>span.disabled{cursor:not-allowed;font-weight:400;background-color:#f9f4e5}::ng-deep .bs-datepicker-body .is-other-month{opacity:.8;font-weight:400}::ng-deep .bs-datepicker-body .disabled{opacity:.65}::ng-deep .bs-datepicker-body>table>tbody>tr>td>span:hover,::ng-deep .bs-datepicker-body>table>tbody>tr>td>span:focus{background-color:#d1e5b6}::ng-deep .bs-datepicker-body>table>tbody>tr>td>.selected,::ng-deep .bs-datepicker-body>table>tbody>tr>td>.selected:hover,::ng-deep .bs-datepicker-body>table>tbody>tr>td>.selected:focus{background:#a66c00;color:#fff}::ng-deep .tabela .date-picker-container tbody tr td,::ng-deep .tabela .date-picker-container tbody tr th{border-right:inherit;border-top:inherit;padding:inherit}::ng-deep .tabela .date-picker-container thead tr th{background-color:inherit;border:inherit;border-right:inherit;color:inherit;font-weight:inherit;padding:inherit;vertical-align:inherit;white-space:inherit}::ng-deep .bs-datepicker-buttons{display:flex;flex-flow:row-reverse;justify-content:space-between;background-color:#e9dbc0}::ng-deep .bs-datepicker-buttons button,::ng-deep .bs-datepicker-buttons button:hover{background-color:inherit;color:#039;font-weight:400;border:none}::ng-deep timepicker table{margin:0 auto}::ng-deep .bs-datepicker-body .today{background:#64a70b;color:#fff}\n"] }]
        }], propDecorators: { disabledDays: [{
                type: Input
            }], format: [{
                type: Input
            }] } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
defineLocale('pt-br', ptBrLocale);
class MonthPickerConfig extends BsDatepickerConfig {
    constructor() {
        super(...arguments);
        this.locale = 'pt-br';
    }
}
MonthPickerConfig.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: MonthPickerConfig, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
MonthPickerConfig.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: MonthPickerConfig });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: MonthPickerConfig, decorators: [{
            type: Injectable
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbMonthPickerModule {
}
SuiteRfbMonthPickerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbMonthPickerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbMonthPickerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbMonthPickerModule, declarations: [MonthPickerComponent], imports: [CommonModule,
        FormsModule,
        ReactiveFormsModule,
        BsDatepickerModule,
        SuiteRfbInputMaskModule,
        SuiteRfbInputValidationModule], exports: [MonthPickerComponent] });
SuiteRfbMonthPickerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbMonthPickerModule, providers: [
        {
            provide: BsDatepickerConfig,
            useClass: MonthPickerConfig
        }
    ], imports: [CommonModule,
        FormsModule,
        ReactiveFormsModule,
        BsDatepickerModule,
        SuiteRfbInputMaskModule,
        SuiteRfbInputValidationModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbMonthPickerModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        ReactiveFormsModule,
                        BsDatepickerModule,
                        SuiteRfbInputMaskModule,
                        SuiteRfbInputValidationModule,
                    ],
                    declarations: [
                        MonthPickerComponent,
                    ],
                    exports: [
                        MonthPickerComponent,
                    ],
                    providers: [
                        {
                            provide: BsDatepickerConfig,
                            useClass: MonthPickerConfig
                        }
                    ]
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SidebarComponent {
    constructor() { }
    ngOnInit() { }
}
SidebarComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SidebarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SidebarComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: SidebarComponent, selector: "suite-rfb-sidebar", ngImport: i0, template: "<div class=\"sidebar\">\n  <div class=\"menu-lateral\">\n    <!-- Menu do cabe\u00E7alho -->\n    <ul class=\"menu-cabecalho visible-xs\">\n      <li>\n        <suite-rfb-applications-menu buttonClass=\"btn btn-primary dropdown-toggle\"></suite-rfb-applications-menu>\n      </li>\n      <li>\n        <suite-rfb-user-configurations buttonClass=\"btn btn-primary dropdown-toggle\"></suite-rfb-user-configurations>\n      </li>\n      <li>\n        <suite-rfb-notifications buttonClass=\"btn btn-primary dropdown-toggle\"></suite-rfb-notifications>\n      </li>\n      <li>\n        <suite-rfb-contextual-help buttonClass=\"btn btn-primary\"></suite-rfb-contextual-help>\n      </li>\n      <li>\n        <suite-rfb-logout buttonClass=\"btn btn-primary\"></suite-rfb-logout>\n      </li>\n    </ul>\n    <suite-rfb-menu></suite-rfb-menu>\n  </div>\n</div>\n", styles: [""], dependencies: [{ kind: "component", type: ApplicationsMenuComponent, selector: "suite-rfb-applications-menu", inputs: ["buttonClass", "suiteRfbUrl"] }, { kind: "component", type: UserConfigurationsComponent, selector: "suite-rfb-user-configurations", inputs: ["buttonClass"] }, { kind: "component", type: NotificationsComponent, selector: "suite-rfb-notifications", inputs: ["buttonClass"] }, { kind: "component", type: ContextualHelpComponent, selector: "suite-rfb-contextual-help", inputs: ["buttonClass"] }, { kind: "component", type: LogoutComponent, selector: "suite-rfb-logout", inputs: ["buttonClass"] }, { kind: "component", type: MenuComponent, selector: "suite-rfb-menu" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SidebarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-sidebar', template: "<div class=\"sidebar\">\n  <div class=\"menu-lateral\">\n    <!-- Menu do cabe\u00E7alho -->\n    <ul class=\"menu-cabecalho visible-xs\">\n      <li>\n        <suite-rfb-applications-menu buttonClass=\"btn btn-primary dropdown-toggle\"></suite-rfb-applications-menu>\n      </li>\n      <li>\n        <suite-rfb-user-configurations buttonClass=\"btn btn-primary dropdown-toggle\"></suite-rfb-user-configurations>\n      </li>\n      <li>\n        <suite-rfb-notifications buttonClass=\"btn btn-primary dropdown-toggle\"></suite-rfb-notifications>\n      </li>\n      <li>\n        <suite-rfb-contextual-help buttonClass=\"btn btn-primary\"></suite-rfb-contextual-help>\n      </li>\n      <li>\n        <suite-rfb-logout buttonClass=\"btn btn-primary\"></suite-rfb-logout>\n      </li>\n    </ul>\n    <suite-rfb-menu></suite-rfb-menu>\n  </div>\n</div>\n" }]
        }], ctorParameters: function () { return []; } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class NavbarComponent {
    constructor() { }
    ngOnInit() { }
}
NavbarComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: NavbarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
NavbarComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: NavbarComponent, selector: "suite-rfb-navbar", ngImport: i0, template: "<!-- Breadcrumbs e funcionalidades -->\n<div class=\"container\">\n  <nav class=\"navegacao\">\n    <!-- Ativador do menu lateral -->\n    <suite-rfb-menu-toggler></suite-rfb-menu-toggler>\n    <!-- Breadcrumbs -->\n    <suite-rfb-breadcrumbs></suite-rfb-breadcrumbs>\n    <!-- Funcionalidades -->\n    <suite-rfb-current-feature></suite-rfb-current-feature>\n    <suite-rfb-favorite-features></suite-rfb-favorite-features>\n    <!-- Menu lateral -->\n    <suite-rfb-sidebar></suite-rfb-sidebar>\n  </nav>\n  <suite-rfb-linked-features></suite-rfb-linked-features>\n</div>\n", styles: [""], dependencies: [{ kind: "component", type: BreadcrumbsComponent, selector: "suite-rfb-breadcrumbs" }, { kind: "component", type: CurrentFeatureComponent, selector: "suite-rfb-current-feature" }, { kind: "component", type: FavoriteFeaturesComponent, selector: "suite-rfb-favorite-features" }, { kind: "component", type: LinkedFeaturesComponent, selector: "suite-rfb-linked-features" }, { kind: "component", type: MenuTogglerComponent, selector: "suite-rfb-menu-toggler" }, { kind: "component", type: SidebarComponent, selector: "suite-rfb-sidebar" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: NavbarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-navbar', template: "<!-- Breadcrumbs e funcionalidades -->\n<div class=\"container\">\n  <nav class=\"navegacao\">\n    <!-- Ativador do menu lateral -->\n    <suite-rfb-menu-toggler></suite-rfb-menu-toggler>\n    <!-- Breadcrumbs -->\n    <suite-rfb-breadcrumbs></suite-rfb-breadcrumbs>\n    <!-- Funcionalidades -->\n    <suite-rfb-current-feature></suite-rfb-current-feature>\n    <suite-rfb-favorite-features></suite-rfb-favorite-features>\n    <!-- Menu lateral -->\n    <suite-rfb-sidebar></suite-rfb-sidebar>\n  </nav>\n  <suite-rfb-linked-features></suite-rfb-linked-features>\n</div>\n" }]
        }], ctorParameters: function () { return []; } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbSidebarModule {
}
SuiteRfbSidebarModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbSidebarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbSidebarModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbSidebarModule, declarations: [SidebarComponent], imports: [CommonModule,
        SuiteRfbApplicationsMenuModule,
        SuiteRfbUserConfigurationsModule,
        SuiteRfbNotificationsModule,
        SuiteRfbContextualHelpModule,
        SuiteRfbLogoutModule,
        SuiteRfbMenuModule], exports: [SidebarComponent] });
SuiteRfbSidebarModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbSidebarModule, imports: [CommonModule,
        SuiteRfbApplicationsMenuModule,
        SuiteRfbUserConfigurationsModule,
        SuiteRfbNotificationsModule,
        SuiteRfbContextualHelpModule,
        SuiteRfbLogoutModule,
        SuiteRfbMenuModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbSidebarModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        SuiteRfbApplicationsMenuModule,
                        SuiteRfbUserConfigurationsModule,
                        SuiteRfbNotificationsModule,
                        SuiteRfbContextualHelpModule,
                        SuiteRfbLogoutModule,
                        SuiteRfbMenuModule,
                    ],
                    declarations: [
                        SidebarComponent,
                    ],
                    exports: [
                        SidebarComponent,
                    ],
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbNavbarModule {
}
SuiteRfbNavbarModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbNavbarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbNavbarModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbNavbarModule, declarations: [NavbarComponent], imports: [CommonModule,
        SuiteRfbBreadcrumbsModule,
        SuiteRfbFeaturesModule,
        SuiteRfbMenuModule,
        SuiteRfbSidebarModule], exports: [NavbarComponent] });
SuiteRfbNavbarModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbNavbarModule, imports: [CommonModule,
        SuiteRfbBreadcrumbsModule,
        SuiteRfbFeaturesModule,
        SuiteRfbMenuModule,
        SuiteRfbSidebarModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbNavbarModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        SuiteRfbBreadcrumbsModule,
                        SuiteRfbFeaturesModule,
                        SuiteRfbMenuModule,
                        SuiteRfbSidebarModule,
                    ],
                    declarations: [
                        NavbarComponent,
                    ],
                    exports: [
                        NavbarComponent,
                    ],
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class PaginationService {
    constructor() { }
    createPages(itemAmount, pageSize) {
        const pages = [];
        const pageAmount = Math.ceil(itemAmount / pageSize);
        let first = 0;
        let last = 0;
        for (let i = 1; i <= pageAmount; i++) {
            const isLastPage = (i === pageAmount);
            last = (isLastPage) ? itemAmount : last + pageSize;
            pages.push({
                number: i,
                first,
                last
            });
            first += pageSize;
        }
        return pages;
    }
    getPage(pages, pageNumber, fallbackPage) {
        const page = pages.filter(p => p.number === pageNumber)[0];
        return page ? page : fallbackPage;
    }
    getStartIndex(currentPage, maxVisiblePageAmount) {
        let start = currentPage - maxVisiblePageAmount;
        start = start < 0 ? 0 : start;
        return start;
    }
    getEndIndex(start, maxVisiblePageAmount, totalPages) {
        let end = start + maxVisiblePageAmount;
        end = (end > totalPages) ? totalPages : end;
        return end;
    }
}
PaginationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: PaginationService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
PaginationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: PaginationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: PaginationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_PAGE_SIZES = [10, 20, 50, 100];
const DEFAULT_MAX_VISIBLE_PAGE_AMOUNT = 5;
const FIRST_PAGE = 1;
class PaginationComponent {
    constructor(paginationService) {
        this.paginationService = paginationService;
        this.firstButtonHidden = false;
        this.previousButtonHidden = false;
        this.nextButtonHidden = false;
        this.lastButtonHidden = false;
        this.disabled = false;
        this.firstButtonLabel = 'Primeira';
        this.previousButtonLabel = 'Anterior';
        this.nextButtonLabel = 'Próxima';
        this.lastButtonLabel = 'Última';
        this.maxVisiblePageAmount = DEFAULT_MAX_VISIBLE_PAGE_AMOUNT;
        this.pageSize = DEFAULT_PAGE_SIZE;
        this.pageSizes = DEFAULT_PAGE_SIZES;
        this.server = false;
        this.setPageSizeHidden = false;
        this.setPageHidden = false;
        this.inputPageSize = false;
        this.itemAmountChange = new EventEmitter();
        this.pageSizeChange = new EventEmitter();
        this.pageChange = new EventEmitter();
        this.pages = [];
        this.invalidPage = false;
    }
    ngOnInit() {
        this._reset();
    }
    ngOnChanges(changes) {
        // item amount change
        const itemAmountChange = changes['itemAmount'];
        if (this.hasSomeChange(itemAmountChange)) {
            this.setItemAmount(itemAmountChange.currentValue);
            if (!itemAmountChange.isFirstChange()) {
                this.itemAmountChange.emit(this.itemAmount);
            }
        }
        // page size change
        const pageSizeChange = changes['pageSize'];
        const pageChange = changes['page'];
        let shouldEmitPageChangeEvent = true;
        if (pageChange) {
            shouldEmitPageChangeEvent = !pageChange.currentValue.pageChangeEventEmitted;
        }
        if (this.hasSomeChange(pageSizeChange)) {
            this.setPageSize(pageSizeChange.currentValue, shouldEmitPageChangeEvent);
        }
        else if (this.hasSomeChange(pageChange)) {
            // page change
            this._setPageNumber(pageChange.currentValue.number);
            if (shouldEmitPageChangeEvent && !pageChange.isFirstChange()) {
                this.pageChange.emit(this.page);
            }
        }
        this.clearPageInputIfPageNumberDiffers();
    }
    /*
     * Se o input com o numero da pagina ta diferente do numero da pagina corrente, limpe-o.
     */
    clearPageInputIfPageNumberDiffers() {
        if (this.setPageInput && this.setPageInput.nativeElement
            && Number(this.setPageInput.nativeElement.value) !== this.page.number) {
            this.setPageInput.nativeElement.value = '';
        }
    }
    hasSomeChange(change) {
        if (_.isNil(change) || _.isEqual(change.currentValue, change.previousValue)) {
            return false;
        }
        else {
            return true;
        }
    }
    setItemAmount(itemAmount) {
        this.itemAmount = itemAmount;
        const resetCurrentPage = !this.server;
        this._reset(resetCurrentPage);
    }
    setPageSize(pageSize, shouldEmitPageChangeEvent = true) {
        this.pageSize = pageSize;
        this.pageSizeChange.emit(this.pageSize);
        this._reset();
        if (shouldEmitPageChangeEvent) {
            this.pageChange.emit(Object.assign({ pageChangeEventEmitted: true }, _.clone(this.page)));
        }
    }
    setPage(page) {
        this._setPageNumber(page.number);
        this.pageChange.emit(Object.assign({ pageChangeEventEmitted: true }, this.page));
    }
    setPageNumber(pageNumberAux) {
        const pageNumber = Number(pageNumberAux);
        const isValidPageNumber = pageNumber && pageNumber >= FIRST_PAGE && pageNumber <= this.pageAmount;
        if (isValidPageNumber) {
            this.invalidPage = false;
            this._setPageNumber(pageNumber);
            this.pageChange.emit(this.page);
        }
        else {
            this.invalidPage = true;
        }
    }
    firstPage() {
        this._setPageNumber(FIRST_PAGE);
        this.pageChange.emit(this.page);
    }
    nextPage() {
        if (this.pageNumber < this.pageAmount) {
            this._setPageNumber(this.pageNumber + 1);
            this.pageChange.emit(this.page);
        }
    }
    previousPage() {
        if (this.pageNumber > FIRST_PAGE) {
            this._setPageNumber(this.pageNumber - 1);
            this.pageChange.emit(this.page);
        }
    }
    lastPage() {
        this._setPageNumber(this.pageAmount);
        this.pageChange.emit(this.page);
    }
    get leftContainerClass() {
        return this.setPageHidden ? 'col-md-6' : 'col-md-3';
    }
    get midContainerClass() {
        const noneHidden = !this.setPageSizeHidden && !this.setPageHidden;
        const leftOnlyHidden = this.setPageSizeHidden && !this.setPageHidden;
        const rightOnlyHidden = !this.setPageSizeHidden && this.setPageHidden;
        if (noneHidden) {
            return 'col-md-6 text-center';
        }
        else if (leftOnlyHidden) {
            return 'col-md-6';
        }
        else if (rightOnlyHidden) {
            return 'col-md-6 text-right';
        }
        else { // both hidden
            return 'col-md-12 text-center';
        }
    }
    get rightContainerClass() {
        return this.setPageSizeHidden ? 'col-md-6 text-right' : 'col-md-3 text-right';
    }
    _reset(resetCurrentPage = true) {
        this._resetPages();
        if (resetCurrentPage) {
            this.setPageNumber(FIRST_PAGE);
        }
        else if (this.pageNumber > this.pageAmount) {
            this.setPageNumber(this.pageAmount);
        }
    }
    _resetPages() {
        this.pages = this.paginationService.createPages(this.itemAmount, this.pageSize);
        this.pageAmount = this.pages.length;
    }
    _setPageNumber(pageNumber) {
        // set current page
        this.page = this.paginationService.getPage(this.pages, pageNumber, { number: FIRST_PAGE, first: 0, last: this.pageSize });
        this.pageNumber = this.page.number;
        // set starting & ending page index
        this.start = this.paginationService.getStartIndex(this.pageNumber, this.maxVisiblePageAmount);
        this.end = this.paginationService.getEndIndex(this.start, this.maxVisiblePageAmount, this.pageAmount);
    }
    onInputPageSizeChange(str) {
        const val = parseInt(str);
        if (val)
            this.setPageSize(val);
    }
}
PaginationComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: PaginationComponent, deps: [{ token: PaginationService }], target: i0.ɵɵFactoryTarget.Component });
PaginationComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: PaginationComponent, selector: "suite-rfb-pagination", inputs: { firstButtonHidden: "firstButtonHidden", previousButtonHidden: "previousButtonHidden", nextButtonHidden: "nextButtonHidden", lastButtonHidden: "lastButtonHidden", disabled: "disabled", firstButtonLabel: "firstButtonLabel", previousButtonLabel: "previousButtonLabel", nextButtonLabel: "nextButtonLabel", lastButtonLabel: "lastButtonLabel", itemAmount: "itemAmount", maxVisiblePageAmount: "maxVisiblePageAmount", page: "page", pageSize: "pageSize", pageSizes: "pageSizes", server: "server", setPageSizeHidden: "setPageSizeHidden", setPageHidden: "setPageHidden", inputPageSize: "inputPageSize" }, outputs: { itemAmountChange: "itemAmountChange", pageSizeChange: "pageSizeChange", pageChange: "pageChange" }, viewQueries: [{ propertyName: "setPageInput", first: true, predicate: ["setPageInput"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div class=\"pagination-line\">\n  <div *ngIf=\"!setPageSizeHidden\">\n    <span>Resultados por p\u00E1gina:</span>\n    <span class=\"form-inline\" *ngIf=\"inputPageSize\">\n      <input #inputPageSizeRef type=\"text\"\n          title=\"Informar tamanho da p\u00E1gina\"\n          placeholder=\"Valor\"\n          [value]=\"pageSize\"\n          class=\"form-control input-page-size\"\n          (keyup.enter)=\"onInputPageSizeChange(inputPageSizeRef.value)\"\n          (change)=\"onInputPageSizeChange(inputPageSizeRef.value)\">\n      <span class=\"separator\">|</span>\n    </span>\n    <ng-container *ngFor=\"let iPageSize of pageSizes; let last = last\">\n      <button type=\"button\" [disabled]=\"disabled\" class=\"btn-set-page-size\" [ngClass]=\"{'btn-current-page-size': (iPageSize === pageSize)}\"\n        (click)=\"setPageSize(iPageSize)\">{{iPageSize}}</button>\n      <span class=\"separator\" [class.last-item]=\"last\">|</span>\n    </ng-container>\n  </div>\n  <div>\n    <ul class=\"pagination\">\n      <!-- primeira e anterior -->\n      <ng-container *ngIf=\"!firstButtonHidden\">\n        <li>\n          <button type=\"button\" [disabled]=\"disabled\" class=\"btn-first-page\" (click)=\"firstPage()\">{{firstButtonLabel}}</button>\n        </li>\n        <li>|</li>\n      </ng-container>\n      <ng-container *ngIf=\"!previousButtonHidden\">\n        <li>\n          <button type=\"button\" [disabled]=\"disabled\" class=\"btn-previous-page\" (click)=\"previousPage()\">{{previousButtonLabel}}</button>\n        </li>\n        <li>|</li>\n      </ng-container>\n      <ng-container *ngIf=\"pageAmount <= maxVisiblePageAmount\">\n        <ng-container *ngFor=\"let page of pages; let last = last\">\n          <li>\n            <button type=\"button\" [disabled]=\"disabled\" class=\"btn-set-page\" [ngClass]=\"{'btn-current-page': (page.number === pageNumber)}\" (click)=\"setPage(page)\">{{page.number}}</button>\n          </li>\n          <li *ngIf=\"!last\">|</li>\n        </ng-container>\n      </ng-container>\n      <ng-container *ngIf=\"pageAmount > maxVisiblePageAmount\">\n        <ng-container *ngIf=\"start > 0\">\n          <li>...</li>\n          <li>|</li>\n        </ng-container>\n        <ng-container *ngFor=\"let page of pages|slice:start:end; let last = last\">\n          <li>\n            <button type=\"button\" [disabled]=\"disabled\" class=\"btn-set-page\" [ngClass]=\"{'btn-current-page': (page.number === pageNumber)}\" (click)=\"setPage(page)\">{{page.number}}</button>\n          </li>\n          <li *ngIf=\"!last\">|</li>\n        </ng-container>\n        <ng-container *ngIf=\"end < pageAmount\">\n          <li>|</li>\n          <li>...</li>\n        </ng-container>\n      </ng-container>\n      <!-- proxima e ultima -->\n      <ng-container *ngIf=\"!nextButtonHidden\">\n        <li>|</li>\n        <li>\n          <button type=\"button\" [disabled]=\"disabled\" class=\"btn-next-page\" (click)=\"nextPage()\">{{nextButtonLabel}}</button>\n        </li>\n      </ng-container>\n      <ng-container *ngIf=\"!lastButtonHidden\">\n        <li>|</li>\n        <li>\n          <button type=\"button\" [disabled]=\"disabled\" class=\"btn-last-page\" (click)=\"lastPage()\">{{lastButtonLabel}}</button>\n        </li>\n      </ng-container>\n    </ul>\n  </div>\n  <div *ngIf=\"!setPageHidden\">\n    <div class=\"form-inline\" [ngClass]=\"{'has-error': invalidPage}\">\n      <label for=\"ir-pagina-footer\" class=\"control-label\">Ir para a p\u00E1gina:</label>\n      <input #setPageInput type=\"text\" class=\"form-control set-page\" [disabled]=\"disabled\" (keyup.enter)=\"setPageNumber(setPageInput.value)\" (change)=\"setPageNumber(setPageInput.value)\">\n    </div>\n  </div>\n</div>\n", styles: ["button.btn-first-page,button.btn-previous-page,button.btn-next-page,button.btn-last-page,button.btn-set-page,button.btn-set-page-size{background:none!important;color:inherit;border-style:none;padding:0!important;font:inherit;cursor:pointer}button.btn-set-page.btn-current-page,button.btn-set-page-size.btn-current-page-size{background:#00205b!important;color:#f2e9d8!important;padding:5px 10px!important;border-width:1px;border-radius:6px;font-weight:700}button[disabled]{cursor:not-allowed;opacity:.65}.separator.last-item{display:none!important}.pagination-line .form-inline .form-control.input-page-size{width:40px;text-align:center}.pagination-line{display:flex;justify-content:space-between}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: i1.SlicePipe, name: "slice" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: PaginationComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-pagination', template: "<div class=\"pagination-line\">\n  <div *ngIf=\"!setPageSizeHidden\">\n    <span>Resultados por p\u00E1gina:</span>\n    <span class=\"form-inline\" *ngIf=\"inputPageSize\">\n      <input #inputPageSizeRef type=\"text\"\n          title=\"Informar tamanho da p\u00E1gina\"\n          placeholder=\"Valor\"\n          [value]=\"pageSize\"\n          class=\"form-control input-page-size\"\n          (keyup.enter)=\"onInputPageSizeChange(inputPageSizeRef.value)\"\n          (change)=\"onInputPageSizeChange(inputPageSizeRef.value)\">\n      <span class=\"separator\">|</span>\n    </span>\n    <ng-container *ngFor=\"let iPageSize of pageSizes; let last = last\">\n      <button type=\"button\" [disabled]=\"disabled\" class=\"btn-set-page-size\" [ngClass]=\"{'btn-current-page-size': (iPageSize === pageSize)}\"\n        (click)=\"setPageSize(iPageSize)\">{{iPageSize}}</button>\n      <span class=\"separator\" [class.last-item]=\"last\">|</span>\n    </ng-container>\n  </div>\n  <div>\n    <ul class=\"pagination\">\n      <!-- primeira e anterior -->\n      <ng-container *ngIf=\"!firstButtonHidden\">\n        <li>\n          <button type=\"button\" [disabled]=\"disabled\" class=\"btn-first-page\" (click)=\"firstPage()\">{{firstButtonLabel}}</button>\n        </li>\n        <li>|</li>\n      </ng-container>\n      <ng-container *ngIf=\"!previousButtonHidden\">\n        <li>\n          <button type=\"button\" [disabled]=\"disabled\" class=\"btn-previous-page\" (click)=\"previousPage()\">{{previousButtonLabel}}</button>\n        </li>\n        <li>|</li>\n      </ng-container>\n      <ng-container *ngIf=\"pageAmount <= maxVisiblePageAmount\">\n        <ng-container *ngFor=\"let page of pages; let last = last\">\n          <li>\n            <button type=\"button\" [disabled]=\"disabled\" class=\"btn-set-page\" [ngClass]=\"{'btn-current-page': (page.number === pageNumber)}\" (click)=\"setPage(page)\">{{page.number}}</button>\n          </li>\n          <li *ngIf=\"!last\">|</li>\n        </ng-container>\n      </ng-container>\n      <ng-container *ngIf=\"pageAmount > maxVisiblePageAmount\">\n        <ng-container *ngIf=\"start > 0\">\n          <li>...</li>\n          <li>|</li>\n        </ng-container>\n        <ng-container *ngFor=\"let page of pages|slice:start:end; let last = last\">\n          <li>\n            <button type=\"button\" [disabled]=\"disabled\" class=\"btn-set-page\" [ngClass]=\"{'btn-current-page': (page.number === pageNumber)}\" (click)=\"setPage(page)\">{{page.number}}</button>\n          </li>\n          <li *ngIf=\"!last\">|</li>\n        </ng-container>\n        <ng-container *ngIf=\"end < pageAmount\">\n          <li>|</li>\n          <li>...</li>\n        </ng-container>\n      </ng-container>\n      <!-- proxima e ultima -->\n      <ng-container *ngIf=\"!nextButtonHidden\">\n        <li>|</li>\n        <li>\n          <button type=\"button\" [disabled]=\"disabled\" class=\"btn-next-page\" (click)=\"nextPage()\">{{nextButtonLabel}}</button>\n        </li>\n      </ng-container>\n      <ng-container *ngIf=\"!lastButtonHidden\">\n        <li>|</li>\n        <li>\n          <button type=\"button\" [disabled]=\"disabled\" class=\"btn-last-page\" (click)=\"lastPage()\">{{lastButtonLabel}}</button>\n        </li>\n      </ng-container>\n    </ul>\n  </div>\n  <div *ngIf=\"!setPageHidden\">\n    <div class=\"form-inline\" [ngClass]=\"{'has-error': invalidPage}\">\n      <label for=\"ir-pagina-footer\" class=\"control-label\">Ir para a p\u00E1gina:</label>\n      <input #setPageInput type=\"text\" class=\"form-control set-page\" [disabled]=\"disabled\" (keyup.enter)=\"setPageNumber(setPageInput.value)\" (change)=\"setPageNumber(setPageInput.value)\">\n    </div>\n  </div>\n</div>\n", styles: ["button.btn-first-page,button.btn-previous-page,button.btn-next-page,button.btn-last-page,button.btn-set-page,button.btn-set-page-size{background:none!important;color:inherit;border-style:none;padding:0!important;font:inherit;cursor:pointer}button.btn-set-page.btn-current-page,button.btn-set-page-size.btn-current-page-size{background:#00205b!important;color:#f2e9d8!important;padding:5px 10px!important;border-width:1px;border-radius:6px;font-weight:700}button[disabled]{cursor:not-allowed;opacity:.65}.separator.last-item{display:none!important}.pagination-line .form-inline .form-control.input-page-size{width:40px;text-align:center}.pagination-line{display:flex;justify-content:space-between}\n"] }]
        }], ctorParameters: function () { return [{ type: PaginationService }]; }, propDecorators: { firstButtonHidden: [{
                type: Input
            }], previousButtonHidden: [{
                type: Input
            }], nextButtonHidden: [{
                type: Input
            }], lastButtonHidden: [{
                type: Input
            }], disabled: [{
                type: Input
            }], firstButtonLabel: [{
                type: Input
            }], previousButtonLabel: [{
                type: Input
            }], nextButtonLabel: [{
                type: Input
            }], lastButtonLabel: [{
                type: Input
            }], itemAmount: [{
                type: Input
            }], maxVisiblePageAmount: [{
                type: Input
            }], page: [{
                type: Input
            }], pageSize: [{
                type: Input
            }], pageSizes: [{
                type: Input
            }], server: [{
                type: Input
            }], setPageSizeHidden: [{
                type: Input
            }], setPageHidden: [{
                type: Input
            }], inputPageSize: [{
                type: Input
            }], itemAmountChange: [{
                type: Output
            }], pageSizeChange: [{
                type: Output
            }], pageChange: [{
                type: Output
            }], setPageInput: [{
                type: ViewChild,
                args: ['setPageInput']
            }] } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class CurrentPagePipe {
    transform(array, page) {
        return (!array || !page) ? array : array.slice(page.first, page.last);
    }
}
CurrentPagePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: CurrentPagePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
CurrentPagePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: CurrentPagePipe, name: "currentPage" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: CurrentPagePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'currentPage'
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbPaginationModule {
}
SuiteRfbPaginationModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbPaginationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbPaginationModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbPaginationModule, declarations: [PaginationComponent,
        CurrentPagePipe], imports: [CommonModule], exports: [PaginationComponent,
        CurrentPagePipe] });
SuiteRfbPaginationModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbPaginationModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbPaginationModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                    ],
                    declarations: [
                        PaginationComponent,
                        CurrentPagePipe,
                    ],
                    exports: [
                        PaginationComponent,
                        CurrentPagePipe,
                    ],
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class PopoverButtonComponent {
    constructor() {
        this.templateContext = {};
        this.placement = 'right';
        this.outsideClick = true;
        this.disabled = false;
        this.menuitem = false;
    }
    ngOnInit() { }
    hide() {
        this.bsPopover.hide();
    }
}
PopoverButtonComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: PopoverButtonComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
PopoverButtonComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: PopoverButtonComponent, selector: "suite-rfb-popover-button", inputs: { template: "template", templateContext: "templateContext", placement: "placement", outsideClick: "outsideClick", containerClass: "containerClass", disabled: "disabled", menuitem: "menuitem", label: "label", adaptivePosition: "adaptivePosition", container: "container", boundariesElement: "boundariesElement" }, viewQueries: [{ propertyName: "bsPopover", first: true, predicate: ["bsPopover"], descendants: true, static: true }], ngImport: i0, template: "<button type=\"button\"\n  #bsPopover=\"bs-popover\"\n  class=\"btn btn-default\"\n  [class.btn-popover]=\"!menuitem\"\n  [style.width]=\"menuitem ? '100%' : 'inherit'\"\n  [popover]=\"template\"\n  [popoverContext]=\"templateContext\"\n  [placement]=\"placement\"\n\n  [adaptivePosition]=\"adaptivePosition\"\n  [container]=\"container\"\n  [boundariesElement]=\"boundariesElement\"\n  [outsideClick]=\"outsideClick\"\n  [containerClass]=\"containerClass\"\n  [disabled]=\"disabled\">\n  <ng-container *ngIf=\"menuitem; else icon\">\n    {{label}}<span class=\"material-icons icon-18\">&#xE5DF;</span>\n  </ng-container>\n  <ng-template #icon>\n    <span class=\"material-icons icon-24\">&#xE5D2;</span>\n  </ng-template>\n</button>\n", styles: [".btn-popover{background:none!important;color:#039;border:none;padding:0!important;font:inherit;cursor:pointer}.btn-popover:hover{color:#039}.contraste .btn-popover{color:#ff0}:host ::ng-deep .popover button{text-align:left}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2$3.PopoverDirective, selector: "[popover]", inputs: ["adaptivePosition", "boundariesElement", "popover", "popoverContext", "popoverTitle", "placement", "outsideClick", "triggers", "container", "containerClass", "isOpen", "delay"], outputs: ["onShown", "onHidden"], exportAs: ["bs-popover"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: PopoverButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-popover-button', template: "<button type=\"button\"\n  #bsPopover=\"bs-popover\"\n  class=\"btn btn-default\"\n  [class.btn-popover]=\"!menuitem\"\n  [style.width]=\"menuitem ? '100%' : 'inherit'\"\n  [popover]=\"template\"\n  [popoverContext]=\"templateContext\"\n  [placement]=\"placement\"\n\n  [adaptivePosition]=\"adaptivePosition\"\n  [container]=\"container\"\n  [boundariesElement]=\"boundariesElement\"\n  [outsideClick]=\"outsideClick\"\n  [containerClass]=\"containerClass\"\n  [disabled]=\"disabled\">\n  <ng-container *ngIf=\"menuitem; else icon\">\n    {{label}}<span class=\"material-icons icon-18\">&#xE5DF;</span>\n  </ng-container>\n  <ng-template #icon>\n    <span class=\"material-icons icon-24\">&#xE5D2;</span>\n  </ng-template>\n</button>\n", styles: [".btn-popover{background:none!important;color:#039;border:none;padding:0!important;font:inherit;cursor:pointer}.btn-popover:hover{color:#039}.contraste .btn-popover{color:#ff0}:host ::ng-deep .popover button{text-align:left}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { template: [{
                type: Input
            }], templateContext: [{
                type: Input
            }], placement: [{
                type: Input
            }], outsideClick: [{
                type: Input
            }], containerClass: [{
                type: Input
            }], disabled: [{
                type: Input
            }], menuitem: [{
                type: Input
            }], label: [{
                type: Input
            }], adaptivePosition: [{
                type: Input
            }], container: [{
                type: Input
            }], boundariesElement: [{
                type: Input
            }], bsPopover: [{
                type: ViewChild,
                args: ['bsPopover', { static: true }]
            }] } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbPopoverModule {
}
SuiteRfbPopoverModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbPopoverModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbPopoverModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbPopoverModule, declarations: [PopoverButtonComponent], imports: [CommonModule, i2$3.PopoverModule], exports: [PopoverButtonComponent] });
SuiteRfbPopoverModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbPopoverModule, imports: [CommonModule,
        PopoverModule.forRoot()] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbPopoverModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        PopoverModule.forRoot(),
                    ],
                    declarations: [
                        PopoverButtonComponent,
                    ],
                    exports: [
                        PopoverButtonComponent,
                    ],
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
//
// Os estilos abaixos foram copiados a partir do arquivo "bootstrap.css" (3.3.7).
// No Bootstrap, esses estilos são ativados somente em telas de impressão ("@media print").
// Para fornecer uma pré-visualização da impressão, esses estilos são ativados por padrão (sem "@media print")
//
const PRINT_PREVIEW_STYLES = `
  *,
  *:before,
  *:after {
    color: #000 !important;
    text-shadow: none !important;
    background: transparent !important;
    -webkit-box-shadow: none !important;
            box-shadow: none !important;
  }
  a,
  a:visited {
    text-decoration: underline;
  }
  a[href]:after {
    content: " (" attr(href) ")";
  }
  abbr[title]:after {
    content: " (" attr(title) ")";
  }
  a[href^="#"]:after,
  a[href^="javascript:"]:after {
    content: "";
  }
  pre,
  blockquote {
    border: 1px solid #999;

    page-break-inside: avoid;
  }
  thead {
    display: table-header-group;
  }
  tr,
  img {
    page-break-inside: avoid;
  }
  img {
    max-width: 100% !important;
  }
  p,
  h2,
  h3 {
    orphans: 3;
    widows: 3;
  }
  h2,
  h3 {
    page-break-after: avoid;
  }
  .navbar {
    display: none;
  }
  .btn > .caret,
  .dropup > .btn > .caret {
    border-top-color: #000 !important;
  }
  .label {
    border: 1px solid #000;
  }
  .table {
    border-collapse: collapse !important;
  }
  .table td,
  .table th {
    background-color: #fff !important;
  }
  .table-bordered th,
  .table-bordered td {
    border: 1px solid #ddd !important;
  }
  .visible-print {
    display: block !important;
  }
  table.visible-print {
    display: table !important;
  }
  tr.visible-print {
    display: table-row !important;
  }
  th.visible-print,
  td.visible-print {
    display: table-cell !important;
  }
  .visible-print-block {
    display: block !important;
  }
  .visible-print-inline {
    display: inline !important;
  }
  .visible-print-inline-block {
    display: inline-block !important;
  }
  .hidden-print {
    display: none !important;
  }
`;
class PrintService {
    constructor(document, windowService) {
        this.document = document;
        this.windowService = windowService;
    }
    /**
     * @deprecated use `printElementId` instead
     */
    print(elementId) {
        this.printElementId(elementId);
    }
    printElementId(elementId) {
        const printContent = this.document.getElementById(elementId).innerHTML;
        this._printContent(printContent);
    }
    printElementRef(elementRef) {
        const printContent = elementRef.nativeElement.innerHTML;
        this._printContent(printContent);
    }
    _printContent(printContent) {
        const popupWindow = this.window.open('', '_blank', ['height=' + screen.height, 'width=' + screen.width, 'fullscreen=yes'].join(','));
        const styles = this._getStyles();
        popupWindow.document.open();
        popupWindow.document.write(`
      <html>
          <head>
              <title>Impressão</title>
          </head>
          <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet">
          <link href="https://fonts.googleapis.com/css?family=Open+Sans:400,600,700" rel="stylesheet">
          ${styles}
          <style>${PRINT_PREVIEW_STYLES}</style>
          <body onload="window.print();window.close()">${printContent}</body>
      </html>`);
        popupWindow.document.close();
    }
    _getStyles() {
        let styles = '';
        const allStylesElements = this.document.getElementsByTagName('style');
        for (let i = 0; i < allStylesElements.length; i++) {
            const style = allStylesElements.item(i);
            styles += `<style>${style.innerHTML}</style>`;
        }
        return styles;
    }
    get window() {
        return this.windowService.get();
    }
}
PrintService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: PrintService, deps: [{ token: DOCUMENT }, { token: WindowService }], target: i0.ɵɵFactoryTarget.Injectable });
PrintService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: PrintService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: PrintService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: WindowService }]; } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbPrintModule {
}
SuiteRfbPrintModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbPrintModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbPrintModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbPrintModule, imports: [CommonModule,
        SuiteRfbCommonModule] });
SuiteRfbPrintModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbPrintModule, imports: [CommonModule,
        SuiteRfbCommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbPrintModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        SuiteRfbCommonModule,
                    ],
                    declarations: [],
                    exports: [],
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SelectComponent {
    get value() {
        return this._value;
    }
    set value(value) {
        this._value = value;
        this.onChange(value);
        this.onTouched();
    }
    writeValue(value) {
        this._value = value;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    constructor(injector) {
        this.injector = injector;
        this.onChange = () => { };
        this.onTouched = () => { };
        // ... ControlValueAccessor methods
        this.compareWith = defCompareFn;
        this.inline = false;
        this.labels = [];
        this.name = 'select';
        this.required = false;
        this.selectClass = 'form-control';
        this.unselectedDisabled = false;
        this.unselectedHidden = false;
        this.unselectedLabel = 'Selecione';
        this.unselectedValue = undefined;
        this.values = [];
        this.multiple = false;
        this.customBody = false;
        this.showSelectAll = false;
        this.closeOnSelect = true;
        this.selectAllText = "Selecionar todos";
        this.addTag = false;
        this.dropdownPosition = 'auto';
        this.virtualScroll = false;
        this.clearable = true;
        this.disabled = false;
    }
    ngOnInit() {
        if (!this.customBody) {
            this.ngControl = this.injector.get(NgControl);
        }
    }
    onBlur() {
        this.onTouched();
    }
    onSelectAll() {
        this.value = this.values;
    }
}
SelectComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SelectComponent, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.Component });
SelectComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: SelectComponent, selector: "suite-rfb-select", inputs: { value: "value", compareWith: "compareWith", inline: "inline", label: "label", labels: "labels", name: "name", required: "required", selectClass: "selectClass", unselectedDisabled: "unselectedDisabled", unselectedHidden: "unselectedHidden", unselectedLabel: "unselectedLabel", unselectedValue: "unselectedValue", values: "values", multiple: "multiple", maxSelectedItems: "maxSelectedItems", customBody: "customBody", showSelectAll: "showSelectAll", closeOnSelect: "closeOnSelect", selectAllText: "selectAllText", addTag: "addTag", appendTo: "appendTo", dropdownPosition: "dropdownPosition", isOpen: "isOpen", trackByFn: "trackByFn", typeahead: "typeahead", minTermLength: "minTermLength", typeToSearchText: "typeToSearchText", loading: "loading", virtualScroll: "virtualScroll", clearable: "clearable" }, providers: [{
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => SelectComponent)
        }], ngImport: i0, template: "<ng-container *ngIf=\"inline\">\n  <ng-container *ngTemplateOutlet=\"template\"></ng-container>\n</ng-container>\n\n<div class=\"form-group\" *ngIf=\"customBody\">\n  <label [for]=\"name\" *ngIf=\"label\" class=\"control-label\" [class.obrigatorio]=\"required\">{{label}}</label>\n  <div class=\"input-group\">\n      <div class=\"suite-rfb-select-container\">\n        <ng-content></ng-content>\n      </div>\n  </div>\n</div>\n\n<ng-container *ngIf=\"!inline && !customBody\">\n  <div class=\"form-group\" [suiteRfbHasError]=\"ngControl.control\">\n    <label [for]=\"name\" *ngIf=\"label\" class=\"control-label\" [class.obrigatorio]=\"required\">{{label}}</label>\n    <ng-container *ngTemplateOutlet=\"template\"></ng-container>\n    <suite-rfb-local-messages [control]=\"ngControl.control\"></suite-rfb-local-messages>\n  </div>\n</ng-container>\n\n<ng-template #template>\n  <div class=\"suite-rfb-select-container\" [class.has-value]=\"!value || value?.length === 0 ? false : !disabled\">\n    <ng-select #selectCtrl\n          [class.multiple]=\"multiple\"\n          [class]=\"(multiple ? 'chips ' : '') + selectClass\"\n          [name]=\"name\"\n          [(ngModel)]=\"value\"\n          (blur)=\"onBlur()\"\n          [placeholder]=\"unselectedLabel\"\n          [disabled]=\"disabled\"\n          [addTag]=\"addTag\"\n          [clearSearchOnAdd]=\"true\"\n          clearAllText=\"Limpar\"\n          [required]=\"required\"\n          [multiple]=\"multiple\"\n          [appendTo]=\"appendTo\"\n          [clearable]=\"clearable\"\n          [isOpen]=\"isOpen\"\n          [dropdownPosition]=\"dropdownPosition\"\n          [maxSelectedItems]=\"maxSelectedItems\"\n          [compareWith]=\"compareWith\"\n          [closeOnSelect]=\"closeOnSelect\"\n\n          [minTermLength]=\"minTermLength\"\n          [typeToSearchText]=\"typeToSearchText\"\n          [typeahead]=\"typeahead\"\n          [loading]=\"loading\"\n          [trackByFn]=\"trackByFn\"\n\n          [virtualScroll]=\"virtualScroll\"\n          >\n\n      <ng-template ng-header-tmp *ngIf=\"showSelectAll && multiple\">\n        <div>\n          <button *ngIf=\"closeOnSelect\" type=\"button\" class=\"btn btn-link\" (click)=\"onSelectAll(); selectCtrl.close();\">{{selectAllText}}</button>\n          <button *ngIf=\"!closeOnSelect\" type=\"button\" class=\"btn btn-link\" (click)=\"onSelectAll();\">{{selectAllText}}</button>\n        </div>\n      </ng-template>\n\n      <ng-option *ngFor=\"let value of values; let i = index\" [value]=\"value\">{{labels[i] || value}}</ng-option>\n    </ng-select>\n  </div>\n</ng-template>\n", styles: [".input-group{width:100%;z-index:3}.input-group ::ng-deep .ng-select .ng-select-container .ng-value-container{margin-right:10px}.input-group ::ng-deep .ng-select .ng-select-container .ng-value-container .ng-value{white-space:normal}.suite-rfb-select-container ::ng-deep .form-control{background:linear-gradient(to left,#eee 0,#eee 31px,#999 32px,#fff 32px,#fff 0px);border:1px solid #b3b3b3;border-radius:3px;color:#000;font-weight:400;min-height:35px;height:unset;-webkit-appearance:none;appearance:none;padding:6px 10px}.suite-rfb-select-container.has-value .form-control.ng-select-clearable,.suite-rfb-select-container ::ng-deep .form-control.ng-select-clearable:has(.ng-has-value){background:linear-gradient(to left,#eee 0,#eee 47px,#999 48px,#fff 48px,#fff 0px)}.suite-rfb-select-container.has-value .form-control.ng-select-clearable.ng-select-focused,.suite-rfb-select-container ::ng-deep .form-control.ng-select-clearable.ng-select-focused:has(.ng-has-value){background:linear-gradient(to left,#dbe8fb 0,#dbe8fb 47px,#003399 48px,#fff 48px,#fff 0px)}.suite-rfb-select-container ::ng-deep .form-control.ng-select-focused{background:linear-gradient(to left,#dbe8fb 0,#dbe8fb 31px,#003399 32px,#fff 32px,#fff 0px)}.suite-rfb-select-container>::ng-deep .form-control::-ms-expand{display:none}.suite-rfb-select-container>::ng-deep .form-control[disabled]{background:#e6e6e6}::ng-deep .ng-select-focused{border-color:#039!important;outline:0;box-shadow:inset 0 1px 1px #00000013,0 0 8px #0399}::ng-deep .has-error .ng-select.form-control{border-color:#a8001f!important}::ng-deep .ng-dropdown-panel{border:1px solid #555555;background:#FFFFFF;border-radius:4px;left:-1px}::ng-deep .ng-select-focused .ng-dropdown-panel{border-color:#039!important}::ng-deep .ng-select .ng-dropdown-panel.ng-select-bottom{top:100%}::ng-deep .ng-select .ng-dropdown-panel.ng-select-top{bottom:100%}::ng-deep .ng-dropdown-panel .ng-option.ng-option-selected{background:#dbe8fb;background:#c0cee9}::ng-deep .ng-select .ng-has-value .ng-placeholder{display:none}::ng-deep .ng-dropdown-panel .ng-option{padding:4px 16px;color:#454546;font-size:16px;font-weight:500}::ng-deep .ng-dropdown-panel .ng-option .ng-option-marked{background:#DFEDF7}::ng-deep .ng-dropdown-panel .ng-option:hover{background:#dbe8fb}::ng-deep .ng-select .ng-select-container .ng-clear-wrapper{padding:0 5px}::ng-deep .ng-select span.ng-clear{font-weight:600;font-size:20px}::ng-deep .ng-select-focused span.ng-clear{color:#039}::ng-deep .ng-select .ng-arrow-wrapper{padding:0 5px;margin-right:-5px}::ng-deep .ng-select>.ng-select-container .ng-arrow{top:-2px;border-color:#000 transparent transparent;border-style:solid;border-width:5px 5px 2.5px}::ng-deep .ng-select-focused>.ng-select-container .ng-arrow{border-color:#003399 transparent transparent}::ng-deep .ng-select.ng-select-disabled>.ng-select-container .ng-arrow{border-top-color:#999}::ng-deep .ng-select.multiple.chips .ng-select-container{align-items:baseline}::ng-deep .ng-select.multiple.chips.chips-big .ng-select-container{min-height:85px}::ng-deep .ng-select.multiple.chips .ng-select-container .ng-value-container .ng-value{border-radius:3px;background-color:#e0e0e0;color:#000000de;padding-right:5px;margin-right:3px}::ng-deep .ng-select.multiple.chips .ng-select-container .ng-value-container{row-gap:3px}::ng-deep .ng-select.multiple.chips .ng-select-container .ng-value-container .ng-value-label{padding-left:5px;font-weight:550}::ng-deep .ng-select.multiple.chips .ng-select-container .ng-value-container .ng-value .ng-value-icon.left{border-color:#e0e0e0}::ng-deep .ng-select.multiple.chips .ng-select-container .ng-value-container .ng-value .ng-value-icon{background-color:#bcbcbc;border-radius:80px;color:#fff;vertical-align:middle;font-size:10px;margin-top:-3px;margin-left:5px;font-weight:bolder;padding:2px 6px;position:relative;top:-2px}::ng-deep .ng-select.multiple.chips .ng-select-container .ng-value-container .ng-value .ng-value-icon:hover{background-color:#575757}::ng-deep .ng-dropdown-header{border-bottom:1px solid #ccc;padding:5px 7px}::ng-deep .tabela .suite-rfb-select-container>select.form-control{width:100%}::ng-deep body{position:relative}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: LocalMessagesComponent, selector: "suite-rfb-local-messages", inputs: ["requiredMessage", "patternMessage", "minlengthMessage", "maxlengthMessage", "emailMessage", "cnpjMessage", "cpfMessage", "dateMessage", "dayMessage", "monthMessage", "yearMessage", "minDateMessage", "maxDateMessage", "fileSizeMessage", "fileTypeMessage", "decendioMessage", "control"] }, { kind: "directive", type: HasErrorDirective, selector: "[suiteRfbHasError]", inputs: ["suiteRfbHasError"] }, { kind: "component", type: i5.NgSelectComponent, selector: "ng-select", inputs: ["bindLabel", "bindValue", "markFirst", "placeholder", "notFoundText", "typeToSearchText", "addTagText", "loadingText", "clearAllText", "appearance", "dropdownPosition", "appendTo", "loading", "closeOnSelect", "hideSelected", "selectOnTab", "openOnEnter", "maxSelectedItems", "groupBy", "groupValue", "bufferAmount", "virtualScroll", "selectableGroup", "selectableGroupAsModel", "searchFn", "trackByFn", "clearOnBackspace", "labelForId", "inputAttrs", "tabIndex", "readonly", "searchWhileComposing", "minTermLength", "editableSearchTerm", "keyDownFn", "typeahead", "multiple", "addTag", "searchable", "clearable", "isOpen", "items", "compareWith", "clearSearchOnAdd"], outputs: ["blur", "focus", "change", "open", "close", "search", "clear", "add", "remove", "scroll", "scrollToEnd"] }, { kind: "component", type: i5.NgOptionComponent, selector: "ng-option", inputs: ["value", "disabled"] }, { kind: "directive", type: i5.NgHeaderTemplateDirective, selector: "[ng-header-tmp]" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SelectComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-select', providers: [{
                            provide: NG_VALUE_ACCESSOR,
                            multi: true,
                            useExisting: forwardRef(() => SelectComponent)
                        }], template: "<ng-container *ngIf=\"inline\">\n  <ng-container *ngTemplateOutlet=\"template\"></ng-container>\n</ng-container>\n\n<div class=\"form-group\" *ngIf=\"customBody\">\n  <label [for]=\"name\" *ngIf=\"label\" class=\"control-label\" [class.obrigatorio]=\"required\">{{label}}</label>\n  <div class=\"input-group\">\n      <div class=\"suite-rfb-select-container\">\n        <ng-content></ng-content>\n      </div>\n  </div>\n</div>\n\n<ng-container *ngIf=\"!inline && !customBody\">\n  <div class=\"form-group\" [suiteRfbHasError]=\"ngControl.control\">\n    <label [for]=\"name\" *ngIf=\"label\" class=\"control-label\" [class.obrigatorio]=\"required\">{{label}}</label>\n    <ng-container *ngTemplateOutlet=\"template\"></ng-container>\n    <suite-rfb-local-messages [control]=\"ngControl.control\"></suite-rfb-local-messages>\n  </div>\n</ng-container>\n\n<ng-template #template>\n  <div class=\"suite-rfb-select-container\" [class.has-value]=\"!value || value?.length === 0 ? false : !disabled\">\n    <ng-select #selectCtrl\n          [class.multiple]=\"multiple\"\n          [class]=\"(multiple ? 'chips ' : '') + selectClass\"\n          [name]=\"name\"\n          [(ngModel)]=\"value\"\n          (blur)=\"onBlur()\"\n          [placeholder]=\"unselectedLabel\"\n          [disabled]=\"disabled\"\n          [addTag]=\"addTag\"\n          [clearSearchOnAdd]=\"true\"\n          clearAllText=\"Limpar\"\n          [required]=\"required\"\n          [multiple]=\"multiple\"\n          [appendTo]=\"appendTo\"\n          [clearable]=\"clearable\"\n          [isOpen]=\"isOpen\"\n          [dropdownPosition]=\"dropdownPosition\"\n          [maxSelectedItems]=\"maxSelectedItems\"\n          [compareWith]=\"compareWith\"\n          [closeOnSelect]=\"closeOnSelect\"\n\n          [minTermLength]=\"minTermLength\"\n          [typeToSearchText]=\"typeToSearchText\"\n          [typeahead]=\"typeahead\"\n          [loading]=\"loading\"\n          [trackByFn]=\"trackByFn\"\n\n          [virtualScroll]=\"virtualScroll\"\n          >\n\n      <ng-template ng-header-tmp *ngIf=\"showSelectAll && multiple\">\n        <div>\n          <button *ngIf=\"closeOnSelect\" type=\"button\" class=\"btn btn-link\" (click)=\"onSelectAll(); selectCtrl.close();\">{{selectAllText}}</button>\n          <button *ngIf=\"!closeOnSelect\" type=\"button\" class=\"btn btn-link\" (click)=\"onSelectAll();\">{{selectAllText}}</button>\n        </div>\n      </ng-template>\n\n      <ng-option *ngFor=\"let value of values; let i = index\" [value]=\"value\">{{labels[i] || value}}</ng-option>\n    </ng-select>\n  </div>\n</ng-template>\n", styles: [".input-group{width:100%;z-index:3}.input-group ::ng-deep .ng-select .ng-select-container .ng-value-container{margin-right:10px}.input-group ::ng-deep .ng-select .ng-select-container .ng-value-container .ng-value{white-space:normal}.suite-rfb-select-container ::ng-deep .form-control{background:linear-gradient(to left,#eee 0,#eee 31px,#999 32px,#fff 32px,#fff 0px);border:1px solid #b3b3b3;border-radius:3px;color:#000;font-weight:400;min-height:35px;height:unset;-webkit-appearance:none;appearance:none;padding:6px 10px}.suite-rfb-select-container.has-value .form-control.ng-select-clearable,.suite-rfb-select-container ::ng-deep .form-control.ng-select-clearable:has(.ng-has-value){background:linear-gradient(to left,#eee 0,#eee 47px,#999 48px,#fff 48px,#fff 0px)}.suite-rfb-select-container.has-value .form-control.ng-select-clearable.ng-select-focused,.suite-rfb-select-container ::ng-deep .form-control.ng-select-clearable.ng-select-focused:has(.ng-has-value){background:linear-gradient(to left,#dbe8fb 0,#dbe8fb 47px,#003399 48px,#fff 48px,#fff 0px)}.suite-rfb-select-container ::ng-deep .form-control.ng-select-focused{background:linear-gradient(to left,#dbe8fb 0,#dbe8fb 31px,#003399 32px,#fff 32px,#fff 0px)}.suite-rfb-select-container>::ng-deep .form-control::-ms-expand{display:none}.suite-rfb-select-container>::ng-deep .form-control[disabled]{background:#e6e6e6}::ng-deep .ng-select-focused{border-color:#039!important;outline:0;box-shadow:inset 0 1px 1px #00000013,0 0 8px #0399}::ng-deep .has-error .ng-select.form-control{border-color:#a8001f!important}::ng-deep .ng-dropdown-panel{border:1px solid #555555;background:#FFFFFF;border-radius:4px;left:-1px}::ng-deep .ng-select-focused .ng-dropdown-panel{border-color:#039!important}::ng-deep .ng-select .ng-dropdown-panel.ng-select-bottom{top:100%}::ng-deep .ng-select .ng-dropdown-panel.ng-select-top{bottom:100%}::ng-deep .ng-dropdown-panel .ng-option.ng-option-selected{background:#dbe8fb;background:#c0cee9}::ng-deep .ng-select .ng-has-value .ng-placeholder{display:none}::ng-deep .ng-dropdown-panel .ng-option{padding:4px 16px;color:#454546;font-size:16px;font-weight:500}::ng-deep .ng-dropdown-panel .ng-option .ng-option-marked{background:#DFEDF7}::ng-deep .ng-dropdown-panel .ng-option:hover{background:#dbe8fb}::ng-deep .ng-select .ng-select-container .ng-clear-wrapper{padding:0 5px}::ng-deep .ng-select span.ng-clear{font-weight:600;font-size:20px}::ng-deep .ng-select-focused span.ng-clear{color:#039}::ng-deep .ng-select .ng-arrow-wrapper{padding:0 5px;margin-right:-5px}::ng-deep .ng-select>.ng-select-container .ng-arrow{top:-2px;border-color:#000 transparent transparent;border-style:solid;border-width:5px 5px 2.5px}::ng-deep .ng-select-focused>.ng-select-container .ng-arrow{border-color:#003399 transparent transparent}::ng-deep .ng-select.ng-select-disabled>.ng-select-container .ng-arrow{border-top-color:#999}::ng-deep .ng-select.multiple.chips .ng-select-container{align-items:baseline}::ng-deep .ng-select.multiple.chips.chips-big .ng-select-container{min-height:85px}::ng-deep .ng-select.multiple.chips .ng-select-container .ng-value-container .ng-value{border-radius:3px;background-color:#e0e0e0;color:#000000de;padding-right:5px;margin-right:3px}::ng-deep .ng-select.multiple.chips .ng-select-container .ng-value-container{row-gap:3px}::ng-deep .ng-select.multiple.chips .ng-select-container .ng-value-container .ng-value-label{padding-left:5px;font-weight:550}::ng-deep .ng-select.multiple.chips .ng-select-container .ng-value-container .ng-value .ng-value-icon.left{border-color:#e0e0e0}::ng-deep .ng-select.multiple.chips .ng-select-container .ng-value-container .ng-value .ng-value-icon{background-color:#bcbcbc;border-radius:80px;color:#fff;vertical-align:middle;font-size:10px;margin-top:-3px;margin-left:5px;font-weight:bolder;padding:2px 6px;position:relative;top:-2px}::ng-deep .ng-select.multiple.chips .ng-select-container .ng-value-container .ng-value .ng-value-icon:hover{background-color:#575757}::ng-deep .ng-dropdown-header{border-bottom:1px solid #ccc;padding:5px 7px}::ng-deep .tabela .suite-rfb-select-container>select.form-control{width:100%}::ng-deep body{position:relative}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.Injector }]; }, propDecorators: { value: [{
                type: Input,
                args: ['value']
            }], compareWith: [{
                type: Input
            }], inline: [{
                type: Input
            }], label: [{
                type: Input
            }], labels: [{
                type: Input
            }], name: [{
                type: Input
            }], required: [{
                type: Input
            }], selectClass: [{
                type: Input
            }], unselectedDisabled: [{
                type: Input
            }], unselectedHidden: [{
                type: Input
            }], unselectedLabel: [{
                type: Input
            }], unselectedValue: [{
                type: Input
            }], values: [{
                type: Input
            }], multiple: [{
                type: Input
            }], maxSelectedItems: [{
                type: Input
            }], customBody: [{
                type: Input
            }], showSelectAll: [{
                type: Input
            }], closeOnSelect: [{
                type: Input
            }], selectAllText: [{
                type: Input
            }], addTag: [{
                type: Input
            }], appendTo: [{
                type: Input
            }], dropdownPosition: [{
                type: Input
            }], isOpen: [{
                type: Input
            }], trackByFn: [{
                type: Input
            }], typeahead: [{
                type: Input
            }], minTermLength: [{
                type: Input
            }], typeToSearchText: [{
                type: Input
            }], loading: [{
                type: Input
            }], virtualScroll: [{
                type: Input
            }], clearable: [{
                type: Input
            }] } });
const defCompareFn = (value1, value2) => {
    return value1 === value2;
};

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbSelectModule {
}
SuiteRfbSelectModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbSelectModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbSelectModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbSelectModule, declarations: [SelectComponent], imports: [CommonModule,
        FormsModule,
        ReactiveFormsModule,
        SuiteRfbInputValidationModule,
        NgSelectModule], exports: [SelectComponent] });
SuiteRfbSelectModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbSelectModule, imports: [CommonModule,
        FormsModule,
        ReactiveFormsModule,
        SuiteRfbInputValidationModule,
        NgSelectModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbSelectModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        ReactiveFormsModule,
                        SuiteRfbInputValidationModule,
                        NgSelectModule
                    ],
                    declarations: [
                        SelectComponent,
                    ],
                    exports: [
                        SelectComponent,
                    ],
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SortByComponent {
    constructor() {
        this.onSort = new EventEmitter();
    }
    ngOnInit() { }
    sort(asc) {
        this.onSort.emit(asc);
        this.asc = asc;
    }
}
SortByComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SortByComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SortByComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: SortByComponent, selector: "suite-rfb-sort-by", inputs: { asc: "asc" }, outputs: { onSort: "onSort" }, ngImport: i0, template: "<span *ngIf=\"asc === true\" title=\"Em ordem ascendente\"   class=\"sort-by-asc pointer material-icons icon-18\"  (click)=\"sort(false)\">&#xE5CE;</span>\n<span *ngIf=\"asc === false\" title=\"Em ordem descendente\" class=\"sort-by-desc pointer material-icons icon-18\" (click)=\"sort(undefined)\">&#xE5CF;</span>\n<span *ngIf=\"asc === undefined\" title=\"N\u00E3o ordenado\"     class=\"sort-by-none pointer material-icons icon-18\" (click)=\"sort(true)\">&#xe5d7;</span>\n", styles: [".pointer{cursor:pointer}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SortByComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-sort-by', template: "<span *ngIf=\"asc === true\" title=\"Em ordem ascendente\"   class=\"sort-by-asc pointer material-icons icon-18\"  (click)=\"sort(false)\">&#xE5CE;</span>\n<span *ngIf=\"asc === false\" title=\"Em ordem descendente\" class=\"sort-by-desc pointer material-icons icon-18\" (click)=\"sort(undefined)\">&#xE5CF;</span>\n<span *ngIf=\"asc === undefined\" title=\"N\u00E3o ordenado\"     class=\"sort-by-none pointer material-icons icon-18\" (click)=\"sort(true)\">&#xe5d7;</span>\n", styles: [".pointer{cursor:pointer}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { asc: [{
                type: Input
            }], onSort: [{
                type: Output
            }] } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbSortByModule {
}
SuiteRfbSortByModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbSortByModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbSortByModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbSortByModule, declarations: [SortByComponent], imports: [CommonModule], exports: [SortByComponent] });
SuiteRfbSortByModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbSortByModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbSortByModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                    ],
                    declarations: [
                        SortByComponent,
                    ],
                    exports: [
                        SortByComponent,
                    ],
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class TabComponent {
    constructor() {
        this.closeable = false;
        this.activeChange = new EventEmitter();
    }
    ngOnInit() { }
    select() {
        if (!this.disabled && !this.active) {
            this.active = true;
            this.activeChange.emit(this.active);
        }
    }
    unselect() {
        if (!this.disabled && this.active) {
            this.active = false;
            this.activeChange.emit(this.active);
        }
    }
}
TabComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: TabComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
TabComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: TabComponent, selector: "suite-rfb-tab", inputs: { name: "name", active: "active", disabled: "disabled", closeable: "closeable", data: "data", tabSelectBody: "tabSelectBody" }, outputs: { activeChange: "activeChange" }, ngImport: i0, template: "<div role=\"tabpanel\" class=\"tab-pane\" [class.active]=\"active\">\n  <ng-content></ng-content>\n</div>\n", styles: [".tab-pane:not(.active){display:none}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: TabComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-tab', template: "<div role=\"tabpanel\" class=\"tab-pane\" [class.active]=\"active\">\n  <ng-content></ng-content>\n</div>\n", styles: [".tab-pane:not(.active){display:none}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { name: [{
                type: Input
            }], active: [{
                type: Input
            }], disabled: [{
                type: Input
            }], closeable: [{
                type: Input
            }], data: [{
                type: Input
            }], tabSelectBody: [{
                type: Input
            }], activeChange: [{
                type: Output
            }] } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class TabSetComponent {
    constructor() {
        this.tabCloseClick = new EventEmitter();
    }
    ngOnInit() { }
    select(tab) {
        if (!tab.disabled) {
            this.tabs.forEach(t => {
                if (t === tab) {
                    t.select();
                }
                else {
                    t.unselect();
                }
            });
        }
    }
    selectIndex(index) {
        const tab = this.tabs.find((t, i) => index === i);
        this.select(tab);
    }
    onClose(tab) {
        this.tabCloseClick.emit(tab);
    }
}
TabSetComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: TabSetComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
TabSetComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: TabSetComponent, selector: "suite-rfb-tab-set", outputs: { tabCloseClick: "tabCloseClick" }, queries: [{ propertyName: "tabs", predicate: TabComponent }], ngImport: i0, template: "<ul class=\"nav nav-tabs\" role=\"tablist\" *ngIf=\"tabs && tabs.length > 0\">\n  <li role=\"presentation\" *ngFor=\"let tab of tabs\" [class.active]=\"tab.active\">\n    <a class=\"tab\" href=\"javascript:void(0)\" [class.disabled]=\"tab.disabled\" (click)=\"select(tab)\" role=\"tab\" data-toggle=\"tab\">\n      <ng-container *ngIf=\"tab.tabSelectBody\">\n        <ng-container *ngTemplateOutlet=\"tab.tabSelectBody; context: {tab: tab}\"></ng-container>\n      </ng-container>\n      <ng-container *ngIf=\"!tab.tabSelectBody\">\n        <div class=\"name\" [title]=\"tab.name\">{{tab.name}}</div>\n      </ng-container>\n    </a>\n    <button *ngIf=\"tab.closeable\" title=\"Fechar\" class=\"close-tab\" (click)=\"onClose(tab)\">\n      <span class=\"material-icons\">clear</span>\n    </button>\n  </li>\n</ul>\n<div class=\"tab-content\">\n  <ng-content></ng-content>\n</div>\n", styles: [".disabled{cursor:not-allowed}.nav-tabs li .close-tab{position:absolute;right:0;padding:0;border:0;background:transparent}.nav-tabs li .close-tab{position:absolute;right:5px;top:3px;border-radius:50%;background-color:#00205b;color:#fff;padding:0 2px;z-index:1}.nav-tabs li .close-tab .material-icons{font-size:12px}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: TabSetComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-tab-set', template: "<ul class=\"nav nav-tabs\" role=\"tablist\" *ngIf=\"tabs && tabs.length > 0\">\n  <li role=\"presentation\" *ngFor=\"let tab of tabs\" [class.active]=\"tab.active\">\n    <a class=\"tab\" href=\"javascript:void(0)\" [class.disabled]=\"tab.disabled\" (click)=\"select(tab)\" role=\"tab\" data-toggle=\"tab\">\n      <ng-container *ngIf=\"tab.tabSelectBody\">\n        <ng-container *ngTemplateOutlet=\"tab.tabSelectBody; context: {tab: tab}\"></ng-container>\n      </ng-container>\n      <ng-container *ngIf=\"!tab.tabSelectBody\">\n        <div class=\"name\" [title]=\"tab.name\">{{tab.name}}</div>\n      </ng-container>\n    </a>\n    <button *ngIf=\"tab.closeable\" title=\"Fechar\" class=\"close-tab\" (click)=\"onClose(tab)\">\n      <span class=\"material-icons\">clear</span>\n    </button>\n  </li>\n</ul>\n<div class=\"tab-content\">\n  <ng-content></ng-content>\n</div>\n", styles: [".disabled{cursor:not-allowed}.nav-tabs li .close-tab{position:absolute;right:0;padding:0;border:0;background:transparent}.nav-tabs li .close-tab{position:absolute;right:5px;top:3px;border-radius:50%;background-color:#00205b;color:#fff;padding:0 2px;z-index:1}.nav-tabs li .close-tab .material-icons{font-size:12px}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { tabs: [{
                type: ContentChildren,
                args: [TabComponent]
            }], tabCloseClick: [{
                type: Output
            }] } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbTabsModule {
}
SuiteRfbTabsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbTabsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbTabsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbTabsModule, declarations: [TabComponent,
        TabSetComponent], imports: [CommonModule], exports: [TabComponent,
        TabSetComponent] });
SuiteRfbTabsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbTabsModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbTabsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                    ],
                    declarations: [
                        TabComponent,
                        TabSetComponent,
                    ],
                    exports: [
                        TabComponent,
                        TabSetComponent,
                    ],
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class TipsComponent {
    constructor() {
        this.tips = [];
        this.currentIndex = 0;
        this.previousDisabled = false;
        this.nextDisabled = false;
    }
    ngOnInit() {
        if (this.tips.length > 0) {
            this._setCurrentTip(0);
        }
    }
    next() {
        if (this._hasNext()) {
            this._setCurrentTip(this.currentIndex + 1);
        }
    }
    previous() {
        if (this._hasPrevious()) {
            this._setCurrentTip(this.currentIndex - 1);
        }
    }
    _hasNext() {
        const index = this.currentIndex + 1;
        return index < this.tips.length;
    }
    _hasPrevious() {
        const index = this.currentIndex - 1;
        return (index >= 0 && index < this.tips.length);
    }
    _setCurrentTip(index) {
        this.currentIndex = index;
        this.currentTip = this.tips[index];
        this.nextDisabled = !this._hasNext();
        this.previousDisabled = !this._hasPrevious();
    }
}
TipsComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: TipsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
TipsComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: TipsComponent, selector: "suite-rfb-tips", inputs: { heading: "heading", tips: "tips" }, ngImport: i0, template: "<div class=\"panel panel-warning hasFooter\">\n  <div class=\"panel-heading\">\n    <h3 class=\"panel-title\">{{heading}}</h3>\n  </div>\n  <div class=\"panel-body\" style=\"height: 192px;\" *ngIf=\"currentTip\">\n    <div [innerHTML]=\"currentTip.htmlText\" *ngIf=\"currentTip.htmlText\"></div>\n    <p *ngIf=\"!currentTip.htmlText\">{{currentTip.text}}</p>\n  </div>\n  <div class=\"panel-footer\">\n    <button class=\"btn btn-default previous-tip\" type=\"button\" [disabled]=\"previousDisabled\" (click)=\"previous()\">\n      <span class=\"material-icons icon-24\">&#xE314;</span> <!-- keyboard_arrow_left -->\n      <span>Anterior</span>\n    </button>\n    <button class=\"btn btn-default next-tip\" type=\"button\" [disabled]=\"nextDisabled\" (click)=\"next()\">\n      <span>Pr\u00F3ximo</span>\n      <span class=\"material-icons icon-24\">&#xE315;</span> <!-- keyboard_arrow_right -->\n    </button>\n  </div>\n</div>\n", styles: [""], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: TipsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-tips', template: "<div class=\"panel panel-warning hasFooter\">\n  <div class=\"panel-heading\">\n    <h3 class=\"panel-title\">{{heading}}</h3>\n  </div>\n  <div class=\"panel-body\" style=\"height: 192px;\" *ngIf=\"currentTip\">\n    <div [innerHTML]=\"currentTip.htmlText\" *ngIf=\"currentTip.htmlText\"></div>\n    <p *ngIf=\"!currentTip.htmlText\">{{currentTip.text}}</p>\n  </div>\n  <div class=\"panel-footer\">\n    <button class=\"btn btn-default previous-tip\" type=\"button\" [disabled]=\"previousDisabled\" (click)=\"previous()\">\n      <span class=\"material-icons icon-24\">&#xE314;</span> <!-- keyboard_arrow_left -->\n      <span>Anterior</span>\n    </button>\n    <button class=\"btn btn-default next-tip\" type=\"button\" [disabled]=\"nextDisabled\" (click)=\"next()\">\n      <span>Pr\u00F3ximo</span>\n      <span class=\"material-icons icon-24\">&#xE315;</span> <!-- keyboard_arrow_right -->\n    </button>\n  </div>\n</div>\n" }]
        }], ctorParameters: function () { return []; }, propDecorators: { heading: [{
                type: Input
            }], tips: [{
                type: Input
            }] } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbTipsModule {
}
SuiteRfbTipsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbTipsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbTipsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbTipsModule, declarations: [TipsComponent], imports: [CommonModule], exports: [TipsComponent] });
SuiteRfbTipsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbTipsModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbTipsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                    ],
                    declarations: [
                        TipsComponent,
                    ],
                    exports: [
                        TipsComponent,
                    ],
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

class TableService {
    constructor() {
        this.selecteds = new Map();
        this.dataSource = new Map();
        this.ctlinst = `nm${Date.now() + Math.random() * 1000}`;
        this.internalSelectedSubject = new Subject();
        this.internalSelected$ = this.internalSelectedSubject.asObservable();
        this.selectedSubject = new Subject();
        this.selecteds$ = this.selectedSubject.asObservable();
        // TODO verificar se não dá para ter apenas um desses dois EVENTOS ex: 'allSelected'.
        /**
         * Ocorre quando usuario ativa a opcao de selecionar/desselecionar todos
         */
        this.selectAllSubject = new Subject();
        this.selectAll$ = this.selectAllSubject.asObservable();
        /**
         * Ocorre quando é detectado que
         * todos os elementos estão selecionados.
         */
        this.isAllSelectedSubject = new Subject();
        this.isAllselected$ = this.isAllSelectedSubject.asObservable();
    }
    getDataKey() {
        return this.dataKey;
    }
    getSelectAll$() {
        return this.selectAll$;
    }
    getIsAllselected$() {
        return this.isAllselected$;
    }
    getSelecteds$() {
        return this.selecteds$;
    }
    getInternalSelected$() {
        return this.internalSelected$;
    }
    getSelecteds() {
        return this.selecteds;
    }
    getDataSource() {
        return this.dataSource;
    }
    delFromSelectedsMap(value) {
        const key = value[this.dataKey];
        this.selecteds.delete(key);
    }
    addToSelectedsMap(value) {
        if (this.maxSelections === 1) {
            this.selecteds.clear();
        }
        const key = value[this.dataKey];
        this.selecteds.set(key, value);
    }
    raiseSelectedsEvent() {
        const selecteds = Array.from(this.selecteds.values());
        this.selectedSubject.next(selecteds);
    }
    isAllSelected() {
        let allSelected = true;
        this.dataSource.forEach((value, key) => allSelected = allSelected && this.selecteds.has(key));
        return allSelected;
    }
    addToDataSource(value) {
        const key = value[this.dataKey];
        this.dataSource.set(key, value);
        if (this.selecteds.has(key) && this.isAllSelected()) {
            this.isAllSelectedSubject.next(true);
        }
        else {
            this.isAllSelectedSubject.next(false);
        }
    }
    removeFromDataSource(value) {
        const key = value[this.dataKey];
        this.dataSource.delete(key);
    }
    defineSelecteds(values) {
        this.selecteds.clear();
        values.forEach(value => this.addToSelectedsMap(value));
        this.internalSelectedSubject.next(values);
        this.isAllSelectedSubject.next(this.isAllSelected());
    }
    addToSelected(value) {
        this.addToSelectedsMap(value);
        this.raiseSelectedsEvent();
        if (this.isAllSelected()) {
            this.isAllSelectedSubject.next(true);
        }
    }
    removeFromSelected(value) {
        this.delFromSelectedsMap(value);
        this.raiseSelectedsEvent();
        this.isAllSelectedSubject.next(false);
    }
    isSelected(value) {
        const key = value[this.dataKey];
        return this.selecteds.has(key);
    }
    deselectAll() {
        this.dataSource.forEach((value, key) => this.delFromSelectedsMap(value));
        this.raiseSelectedsEvent();
        this.selectAllSubject.next(false);
    }
    selectAll() {
        this.dataSource.forEach((value, key) => this.addToSelectedsMap(value));
        this.raiseSelectedsEvent();
        this.selectAllSubject.next(true);
    }
    canAdd() {
        return !this.maxSelections
            || this.maxSelections === 1
            || this.selecteds.size < this.maxSelections;
    }
}
TableService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: TableService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
TableService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: TableService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: TableService, decorators: [{
            type: Injectable
        }] });

class TableSelectComponent {
    set value(value) {
        this._value = value;
        this.tableService.addToDataSource(value);
        this.checked = this.tableService.isSelected(value);
    }
    get value() {
        return this._value;
    }
    constructor(tableService) {
        this.tableService = tableService;
        this.checked = false;
    }
    ngOnInit() {
        this.radioName = this.tableService.ctlinst;
        this.maxSelections = this.tableService.maxSelections;
        this.dataKey = this.tableService.dataKey;
    }
    ngAfterViewInit() {
        this.selectedAllSubscription = this.tableService.getSelectAll$().subscribe(selected => {
            this.setChecked(selected);
        });
        this.internalSelectedSubscription = this.tableService.getInternalSelected$().subscribe(selecteds => {
            let checked;
            if (selecteds && selecteds.length) {
                checked = selecteds.findIndex(item => item[this.dataKey] === this._value[this.dataKey]) != -1;
            }
            else {
                checked = false;
            }
            this.setChecked(checked);
        });
    }
    setChecked(selected) {
        this.checked = selected;
        if (this.checkboxRef) {
            this.checkboxRef.nativeElement.checked = selected;
        }
    }
    ngOnDestroy() {
        this.tableService.removeFromDataSource(this._value);
        this.internalSelectedSubscription.unsubscribe();
        this.selectedAllSubscription.unsubscribe();
    }
    onChange(event) {
        const checked = event.target.checked;
        if (checked) {
            if (this.tableService.canAdd()) {
                this.tableService.addToSelected(this._value);
            }
            else {
                event.target.checked = false;
            }
        }
        else {
            this.tableService.removeFromSelected(this._value);
        }
    }
}
TableSelectComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: TableSelectComponent, deps: [{ token: TableService }], target: i0.ɵɵFactoryTarget.Component });
TableSelectComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: TableSelectComponent, selector: "suite-rfb-table-select", inputs: { value: "value", disabled: "disabled" }, viewQueries: [{ propertyName: "checkboxRef", first: true, predicate: ["checkboxRef"], descendants: true }], ngImport: i0, template: "<ng-container *ngIf=\"dataKey\">\n    <ng-container *ngIf=\"maxSelections == 1; else checkboxTemplate\">\n        <input [name]=\"radioName\" type=\"radio\" (change)=\"onChange($event)\"\n            [checked]=\"checked\" [disabled]=\"disabled\">\n    </ng-container>\n\n    <ng-template #checkboxTemplate>\n        <input #checkboxRef type=\"checkbox\" (change)=\"onChange($event)\"\n            [checked]=\"checked\" [disabled]=\"disabled\">\n    </ng-template>\n</ng-container>", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: TableSelectComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-table-select', template: "<ng-container *ngIf=\"dataKey\">\n    <ng-container *ngIf=\"maxSelections == 1; else checkboxTemplate\">\n        <input [name]=\"radioName\" type=\"radio\" (change)=\"onChange($event)\"\n            [checked]=\"checked\" [disabled]=\"disabled\">\n    </ng-container>\n\n    <ng-template #checkboxTemplate>\n        <input #checkboxRef type=\"checkbox\" (change)=\"onChange($event)\"\n            [checked]=\"checked\" [disabled]=\"disabled\">\n    </ng-template>\n</ng-container>" }]
        }], ctorParameters: function () { return [{ type: TableService }]; }, propDecorators: { checkboxRef: [{
                type: ViewChild,
                args: ['checkboxRef']
            }], value: [{
                type: Input
            }], disabled: [{
                type: Input
            }] } });

class TableSelectHeaderComponent {
    set dataKey(value) {
        this.tableService.dataKey = value;
    }
    get dataKey() {
        return this.tableService.dataKey;
    }
    set selecteds(value) {
        this.tableService.defineSelecteds(value);
    }
    constructor(tableService) {
        this.tableService = tableService;
        this.checked = false;
        this.selectedsChange = new EventEmitter();
    }
    ngOnChanges(changes) {
        const selectedsChange = changes['selecteds'];
        if (selectedsChange && !selectedsChange.firstChange && selectedsChange.currentValue) {
            this.selecteds = selectedsChange.currentValue;
        }
    }
    onChange(event) {
        const checked = event.target.checked;
        if (checked) {
            this.tableService.selectAll();
        }
        else {
            this.tableService.deselectAll();
        }
    }
    ngOnInit() {
        this.tableService.maxSelections = +this.maxSelections;
        this.selectedsSubscription = this.tableService.getSelecteds$().subscribe(selecteds => this.selectedsChange.emit(selecteds));
    }
    ngAfterViewInit() {
        this.isAllselectedSubscription = this.tableService.getIsAllselected$().subscribe(allSelected => {
            this.checked = allSelected;
            if (this.checkboxRef) {
                this.checkboxRef.nativeElement.checked = allSelected;
            }
        });
    }
    ngOnDestroy() {
        this.selectedsSubscription.unsubscribe();
        this.isAllselectedSubscription.unsubscribe();
    }
}
TableSelectHeaderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: TableSelectHeaderComponent, deps: [{ token: TableService }], target: i0.ɵɵFactoryTarget.Component });
TableSelectHeaderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: TableSelectHeaderComponent, selector: "suite-rfb-table-select-header", inputs: { maxSelections: "maxSelections", dataKey: "dataKey", selecteds: "selecteds", disabled: "disabled" }, outputs: { selectedsChange: "selectedsChange" }, viewQueries: [{ propertyName: "checkboxRef", first: true, predicate: ["checkboxRef"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<ng-container *ngIf=\"dataKey; else noDataKey\">\n    <input #checkboxRef *ngIf=\"!maxSelections\" type=\"checkbox\"\n        [checked]=\"checked\"\n        (change)=\"onChange($event)\" [disabled]=\"disabled\">\n</ng-container>\n\n<ng-template #noDataKey>\n    <span class=\"no-datakey\">Atributo dataKey n\u00E3o informado</span>\n</ng-template>", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: TableSelectHeaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-table-select-header', template: "<ng-container *ngIf=\"dataKey; else noDataKey\">\n    <input #checkboxRef *ngIf=\"!maxSelections\" type=\"checkbox\"\n        [checked]=\"checked\"\n        (change)=\"onChange($event)\" [disabled]=\"disabled\">\n</ng-container>\n\n<ng-template #noDataKey>\n    <span class=\"no-datakey\">Atributo dataKey n\u00E3o informado</span>\n</ng-template>" }]
        }], ctorParameters: function () { return [{ type: TableService }]; }, propDecorators: { checkboxRef: [{
                type: ViewChild,
                args: ['checkboxRef']
            }], maxSelections: [{
                type: Input
            }], dataKey: [{
                type: Input
            }], selecteds: [{
                type: Input
            }], selectedsChange: [{
                type: Output
            }], disabled: [{
                type: Input
            }] } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class TableTplActionsDirective {
}
TableTplActionsDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: TableTplActionsDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
TableTplActionsDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.1.2", type: TableTplActionsDirective, selector: "[suiteRfbTableTplActions]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: TableTplActionsDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[suiteRfbTableTplActions]' }]
        }] });

const BA_TOGGLE_FILTERING = {
    actionType: 'toggleFiltering',
    materialIcon: 'filter_list',
    actionDescription: 'Habilitar/Desabilitar filtros',
    off: false
};
const BA_ROW_FILTER = {
    actionType: 'filterRows',
    materialIcon: 'filter_alt',
    actionDescription: 'Filtrar linhas',
};
const BA_CUSTOMIZE_COLUMNS = {
    actionType: 'customizeColumns',
    materialIcon: 'tune',
    actionDescription: 'Personalizar colunas',
};
const BA_EXPORT_CSV = {
    actionType: 'exportCSV',
    materialIcon: 'file_download',
    actionDescription: 'Exportar CSV',
};
const BUILTIN_ACTIONS = [
    { ...BA_TOGGLE_FILTERING },
    { ...BA_ROW_FILTER },
    { ...BA_CUSTOMIZE_COLUMNS },
    { ...BA_EXPORT_CSV },
];
class ColumnCustomization {
    constructor(col, newOrder = undefined) {
        this.name = col.name;
        this.order = newOrder !== undefined ? newOrder : col.order;
        this.hidden = col.hidden;
    }
}

function getLastPageSize(tableKey) {
    return getNumberItem('page-size', tableKey, 10);
}
function saveLastPageSize(tableKey, pageSize) {
    localStorage.setItem(getKey('page-size', tableKey), String(pageSize));
}
function saveItem(propertyKey, sectionKey, value) {
    localStorage.setItem(getKey(propertyKey, sectionKey), value);
}
function getStringItem(propertyKey, sectionKey, def) {
    if (!sectionKey)
        return def;
    return localStorage.getItem(getKey(propertyKey, sectionKey))
        || localStorage.getItem(getKey(propertyKey, 'default'))
        || def;
}
function getNumberItem(propertyKey, sectionKey, def) {
    return parseInt(localStorage.getItem(getKey(propertyKey, sectionKey)))
        || parseInt(localStorage.getItem(getKey(propertyKey, 'default')))
        || def;
}
function getKey(propertyKey, sectionKey) {
    const norm = normalize(sectionKey);
    return `${norm}.${propertyKey}`;
}
function normalize(str) {
    return str
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\s/g, '-');
}
function readColumnsCustomizations(tableKey) {
    const str = getStringItem('columns', tableKey, '[]');
    try {
        return JSON.parse(str);
    }
    catch (e) {
        return [];
    }
}
function saveColumnsCustomizations(tableKey, rows) {
    const str = JSON.stringify(rows);
    saveItem('columns', tableKey, str);
}
function applyColumnsCustomizations(columns, tableKey) {
    if (!tableKey) {
        return [...columns];
    }
    const columnsCustomizations = readColumnsCustomizations(tableKey);
    if (!columnsCustomizations?.length) {
        return [...columns];
    }
    const ret = [];
    for (let col of columns) {
        const cc = columnsCustomizations.find(c => c.name == col.name);
        col.order = cc?.order || 0;
        col.hidden = cc?.hidden;
        if (!col.hidden)
            ret.push(col);
    }
    sortColumns(ret);
    return ret;
}
function sortColumns(ret) {
    ret.sort((a, b) => {
        const oa = a.order || 0;
        const ob = b.order || 0;
        return oa - ob;
    });
}
function getRowValues(row, cols) {
    return cols
        .map(col => getColumnValue(row, col));
}
function getColumnValue(row, col) {
    const obj = deepValueGetter(row, col.prop);
    let value = (col.pipe
        ? col.pipe.transform(obj)
        : obj) ?? '';
    return value
        .toString()
        .toLowerCase();
}

class Filters {
    constructor(rows, filterRowFn) {
        this.filterRowFn = filterRowFn;
        this.filters = [];
        this.filters = [{
                key: '', rows, term: '', label: 'Inicial'
            }];
    }
    ;
    filterColumn(col, term, filterByRef) {
        const key = col.name;
        const currentRows = this.popRows(key);
        const rows = currentRows.filter(row => col.filterFn(row, term, col));
        if (term) {
            this.filters.push({ key, rows, filterByRef, term: term.toString(), label: col.name });
        }
        return rows;
    }
    filterRows(cols, term) {
        const key = '$$all-rows';
        const currentRows = this.popRows(key);
        const rows = currentRows.filter(row => this.filterRow(cols, row, term));
        this.filters.push({ key, rows, term: term.toString(), label: 'Geral' });
        return rows;
    }
    filterRow(cols, row, term) {
        if (!term)
            return true;
        const noHiddenCols = cols.filter(col => !col.hidden);
        const line = getRowValues(row, noHiddenCols).join(' ');
        if (this.filterRowFn) {
            return this.filterRowFn(term, row, line);
        }
        return matches(line, term);
    }
    popRows(key) {
        const index = this.filters.findIndex(f => f.key === key);
        if (index > 0) {
            for (let i = index + 1; i < this.filters.length; i++) {
                this.filters[i].filterByRef.resetTerm();
            }
            this.filters.splice(index);
        }
        return this.filters[this.filters.length - 1].rows;
    }
}
function makeSearchTerm(str) {
    if (str.startsWith('/') && str.endsWith('/')) {
        return new RegExp(str.substring(1, str.length - 1), 'i');
    }
    return str.toLowerCase();
}
function defFilterFn(row, term, col) {
    if (!term)
        return true;
    const str = getColumnValue(row, col);
    return matches(str, term);
}
function matches(str, term) {
    if (term instanceof RegExp) {
        return term.test(str);
    }
    return str.includes(term);
}

class TableCsvService {
    getExportOptions() {
        return {
            columnSeparator: ',',
            lineSeparator: '\r\n'
        };
    }
    getFileName(tabKey) {
        return normalize(tabKey) + '_' + new Date().getTime() + '.csv';
    }
    getHeader(columns) {
        return columns
            .filter(col => col.exportable)
            .map(col => col.name);
    }
    getLine(columns, row) {
        const exportables = columns.filter(col => col.exportable);
        return getRowValues(row, exportables);
    }
    export(tableKey, columns, rows) {
        const options = this.getExportOptions();
        const columnSeparator = options.columnSeparator;
        const lineSeparator = options.lineSeparator;
        const file = this.getFileName(tableKey);
        let buffer = "";
        buffer += this.convertCSV(this.getHeader(columns), columnSeparator, lineSeparator);
        for (let row of rows) {
            buffer += this.convertCSV(this.getLine(columns, row), columnSeparator, lineSeparator, true);
        }
        this.saveAs(buffer, file);
    }
    convertCSV(line, columnSeparator, lineSeparator, aspas = false) {
        if (aspas) {
            return line
                .map(c => `"${c}"`)
                .join(columnSeparator)
                + lineSeparator;
        }
        return line.join(columnSeparator) + lineSeparator;
    }
    saveAs(buffer, fileName) {
        const data = new Blob([buffer], { type: 'text/csv' });
        FileSaver.saveAs(data, fileName);
    }
}
TableCsvService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: TableCsvService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
TableCsvService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: TableCsvService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: TableCsvService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class ToggleComponent {
    constructor() {
        this.expanded = new EventEmitter();
        this.title = 'Expandir/Contrair';
    }
    ngOnInit() {
    }
    toggle() {
        this.show = !this.show;
        this.expanded.emit(this.show);
    }
}
ToggleComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: ToggleComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
ToggleComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: ToggleComponent, selector: "suite-rfb-toggle", inputs: { show: "show", title: "title" }, outputs: { expanded: "expanded" }, ngImport: i0, template: "<div (click)=\"toggle()\" class=\"collapse-container\">\n    <span [title]=\"title\" *ngIf=\"show\"  class=\"material-icons expanded\">&#xE316;</span>\n    <span [title]=\"title\" *ngIf=\"!show\" class=\"material-icons noexpanded\">&#xE313;</span>\n</div>\n", styles: [".material-icons{color:#00205b;font-size:28px}.collapse-container{cursor:pointer}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: ToggleComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-toggle', template: "<div (click)=\"toggle()\" class=\"collapse-container\">\n    <span [title]=\"title\" *ngIf=\"show\"  class=\"material-icons expanded\">&#xE316;</span>\n    <span [title]=\"title\" *ngIf=\"!show\" class=\"material-icons noexpanded\">&#xE313;</span>\n</div>\n", styles: [".material-icons{color:#00205b;font-size:28px}.collapse-container{cursor:pointer}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { show: [{
                type: Input
            }], expanded: [{
                type: Output
            }], title: [{
                type: Input
            }] } });

class CustomizeColumsModalComponent {
    constructor() {
        this.confirm = new EventEmitter();
        this.SelectionType = SelectionType;
        this.ColumnMode = ColumnMode;
        this.rowIdentityFn = (row) => {
            return row.name;
        };
        this.displayCheck = (cc) => {
            const col = this.columns.find(c => c.name == cc.name);
            return col.hideable;
        };
    }
    onSelect({ selected }) {
        this.selectedRows = [...selected];
    }
    ngOnInit() {
        this.dtcolumns = [
            {
                checkboxable: true,
                headerCheckboxable: true,
                sortable: false,
                resizeable: false,
                maxWidth: 30,
            },
            { name: 'Mover', cellTemplate: this.moveTemplate, maxWidth: 70 },
            { name: 'Coluna', prop: 'name' },
        ];
        this.dtrows = this.columns.map((c, index) => new ColumnCustomization(c, index)).filter(c => c.name);
        this.selectedRows = this.getSelectedColumns();
    }
    onConfirm() {
        for (let cc of this.dtrows) {
            const col = this.columns.find(c => c.name == cc.name);
            cc.hidden = col.hideable && this.isNotSelected(cc);
        }
        saveColumnsCustomizations(this.tableKey, this.dtrows);
        this.confirm.emit(true);
    }
    isNotSelected(cc) {
        return this.selectedRows.findIndex(c => c.name == cc.name) === -1;
    }
    onCancel() {
        this.confirm.emit(false);
    }
    onClickMoveDown(row) {
        const oldIndex = this.dtrows.findIndex(r => r.order == row.order);
        let newIndex = oldIndex + 1;
        if (newIndex >= this.dtrows.length)
            newIndex = 0;
        this.swapRows(oldIndex, newIndex);
    }
    onClickMoveUp(row) {
        const oldIndex = this.dtrows.findIndex(r => r.order == row.order);
        let newIndex = oldIndex - 1;
        if (newIndex < 0)
            newIndex = this.dtrows.length - 1;
        this.swapRows(oldIndex, newIndex);
    }
    swapRows(i, j) {
        const rows = [...this.dtrows];
        const aux = rows[i];
        rows[i] = rows[j];
        rows[j] = aux;
        for (let k = 0; k < rows.length; k++) {
            rows[k].order = k + 1;
            rows[k].hidden = false;
        }
        this.dtrows = rows;
    }
    getSelectedColumns() {
        const columnsCustomizations = readColumnsCustomizations(this.tableKey);
        const ret = [];
        for (let col of this.columns) {
            if (col.hideable === false)
                continue;
            const cc = columnsCustomizations.find(c => c.name == col.name);
            if (columnsCustomizations.length == 0 || !cc?.hidden) {
                ret.push(new ColumnCustomization(col));
            }
        }
        return ret;
    }
}
CustomizeColumsModalComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: CustomizeColumsModalComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CustomizeColumsModalComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: CustomizeColumsModalComponent, selector: "suite-rfb-customize-colums", inputs: { tableKey: "tableKey", columns: "columns" }, outputs: { confirm: "confirm" }, viewQueries: [{ propertyName: "moveTemplate", first: true, predicate: ["moveTemplate"], descendants: true, static: true }], ngImport: i0, template: "<div class=\"modal-header\">\n    <h4 class=\"modal-title pull-left\">Filtrar Colunas</h4>\n    <button type=\"button\" class=\"btn-close pull-right\" aria-label=\"Close\"\n            (click)=\"onCancel()\">\n      <span aria-hidden=\"true\" class=\"visually-hidden\">&times;</span>\n    </button>\n</div>\n<div class=\"modal-body\">\n    <ngx-datatable *ngIf=\"tableKey; else noTableKey\"\n        class=\"bootstrap tabela\"\n        [rows]=\"dtrows\"\n        [columns]=\"dtcolumns\"\n        [columnMode]=\"ColumnMode.force\"\n        [rowIdentity]=\"rowIdentityFn\"\n        [footerHeight]=\"0\"\n        [selected]=\"selectedRows\"\n        (select)=\"onSelect($event)\"\n        [selectionType]=\"SelectionType.checkbox\"\n        [selectAllRowsOnPage]=\"true\"\n        [displayCheck]=\"displayCheck\"\n    />\n</div>\n<div class=\"modal-footer\">\n    <button type=\"button\" class=\"btn btn-default\"\n        (click)=\"onConfirm()\">Aplicar</button>\n\n    <button type=\"button\" class=\"btn btn-secondary\"\n        (click)=\"onCancel()\">Cancelar</button>\n</div>\n\n<ng-template #noTableKey>\n    <p>Precisa definir a propriedade \"tableKey\"</p>\n</ng-template>\n\n\n<ng-template #moveTemplate let-row=\"row\" let-index=\"index\">\n    <span role=\"button\" (click)=\"onClickMoveUp(row)\"\n        class=\"material-icons move\">arrow_upward</span>\n\n    <span role=\"button\" (click)=\"onClickMoveDown(row)\"\n        class=\"material-icons move\">arrow_downward</span>\n</ng-template>", styles: [".move{margin:0 6px}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i4.DatatableComponent, selector: "ngx-datatable", inputs: ["targetMarkerTemplate", "rows", "groupRowsBy", "groupedRows", "columns", "selected", "scrollbarV", "scrollbarH", "rowHeight", "columnMode", "headerHeight", "footerHeight", "externalPaging", "externalSorting", "limit", "count", "offset", "loadingIndicator", "selectionType", "reorderable", "swapColumns", "sortType", "sorts", "cssClasses", "messages", "rowClass", "selectCheck", "displayCheck", "groupExpansionDefault", "trackByProp", "selectAllRowsOnPage", "virtualization", "treeFromRelation", "treeToRelation", "summaryRow", "summaryHeight", "summaryPosition", "rowIdentity"], outputs: ["scroll", "activate", "select", "sort", "page", "reorder", "resize", "tableContextmenu", "treeAction"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: CustomizeColumsModalComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-customize-colums', template: "<div class=\"modal-header\">\n    <h4 class=\"modal-title pull-left\">Filtrar Colunas</h4>\n    <button type=\"button\" class=\"btn-close pull-right\" aria-label=\"Close\"\n            (click)=\"onCancel()\">\n      <span aria-hidden=\"true\" class=\"visually-hidden\">&times;</span>\n    </button>\n</div>\n<div class=\"modal-body\">\n    <ngx-datatable *ngIf=\"tableKey; else noTableKey\"\n        class=\"bootstrap tabela\"\n        [rows]=\"dtrows\"\n        [columns]=\"dtcolumns\"\n        [columnMode]=\"ColumnMode.force\"\n        [rowIdentity]=\"rowIdentityFn\"\n        [footerHeight]=\"0\"\n        [selected]=\"selectedRows\"\n        (select)=\"onSelect($event)\"\n        [selectionType]=\"SelectionType.checkbox\"\n        [selectAllRowsOnPage]=\"true\"\n        [displayCheck]=\"displayCheck\"\n    />\n</div>\n<div class=\"modal-footer\">\n    <button type=\"button\" class=\"btn btn-default\"\n        (click)=\"onConfirm()\">Aplicar</button>\n\n    <button type=\"button\" class=\"btn btn-secondary\"\n        (click)=\"onCancel()\">Cancelar</button>\n</div>\n\n<ng-template #noTableKey>\n    <p>Precisa definir a propriedade \"tableKey\"</p>\n</ng-template>\n\n\n<ng-template #moveTemplate let-row=\"row\" let-index=\"index\">\n    <span role=\"button\" (click)=\"onClickMoveUp(row)\"\n        class=\"material-icons move\">arrow_upward</span>\n\n    <span role=\"button\" (click)=\"onClickMoveDown(row)\"\n        class=\"material-icons move\">arrow_downward</span>\n</ng-template>", styles: [".move{margin:0 6px}\n"] }]
        }], propDecorators: { tableKey: [{
                type: Input
            }], columns: [{
                type: Input
            }], confirm: [{
                type: Output
            }], moveTemplate: [{
                type: ViewChild,
                args: ['moveTemplate', { static: true }]
            }] } });

class FilterRowsModalComponent {
    constructor() {
        this.filter = new EventEmitter();
    }
    onFilter(term) {
        this.filter.emit(term);
    }
    onCancel() {
        this.filter.emit(false);
    }
}
FilterRowsModalComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: FilterRowsModalComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
FilterRowsModalComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: FilterRowsModalComponent, selector: "suite-rfb-filter-rows", inputs: { term: "term" }, outputs: { filter: "filter" }, ngImport: i0, template: "<div class=\"modal-header\">\n    <h4 class=\"modal-title pull-left\">Filtrar Linhas</h4>\n    <button type=\"button\" class=\"btn-close pull-right\" aria-label=\"Close\"\n            (click)=\"onCancel()\">\n      <span aria-hidden=\"true\" class=\"visually-hidden\">&times;</span>\n    </button>\n</div>\n<div class=\"modal-body\">\n    <div class=\"form-group\">\n        <label class=\"control-label\">Termo. Ex: 'ivo' ou '/[ie]v[oa]/'</label>\n        <div class=\"input-group\">\n            <suite-rfb-filter-by\n                [searchTerms]=\"term\"\n                [requestFocus]=\"true\"\n                (onFilter)=\"onFilter($event)\" />\n            <span class=\"input-group-addon\">\n                <span tooltip=\"Para informar uma express\u00E3o regular envolva-a com '/'\" container=\"body\"><span\n                    class=\"material-icons\">&#xE887;</span></span>\n              </span>\n        </div>\n    </div>\n</div>\n", styles: [":host ::ng-deep .filter-by-sm{width:100%}\n"], dependencies: [{ kind: "component", type: FilterByComponent, selector: "suite-rfb-filter-by", inputs: ["debounceTime", "disabled", "inputClass", "inputMask", "requestFocus", "searchTerms"], outputs: ["onFilter"] }, { kind: "directive", type: i7$1.TooltipDirective, selector: "[tooltip], [tooltipHtml]", inputs: ["adaptivePosition", "tooltip", "placement", "triggers", "container", "containerClass", "boundariesElement", "isOpen", "isDisabled", "delay", "tooltipHtml", "tooltipPlacement", "tooltipIsOpen", "tooltipEnable", "tooltipAppendToBody", "tooltipAnimation", "tooltipClass", "tooltipContext", "tooltipPopupDelay", "tooltipFadeDuration", "tooltipTrigger"], outputs: ["tooltipChange", "onShown", "onHidden", "tooltipStateChanged"], exportAs: ["bs-tooltip"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: FilterRowsModalComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-filter-rows', template: "<div class=\"modal-header\">\n    <h4 class=\"modal-title pull-left\">Filtrar Linhas</h4>\n    <button type=\"button\" class=\"btn-close pull-right\" aria-label=\"Close\"\n            (click)=\"onCancel()\">\n      <span aria-hidden=\"true\" class=\"visually-hidden\">&times;</span>\n    </button>\n</div>\n<div class=\"modal-body\">\n    <div class=\"form-group\">\n        <label class=\"control-label\">Termo. Ex: 'ivo' ou '/[ie]v[oa]/'</label>\n        <div class=\"input-group\">\n            <suite-rfb-filter-by\n                [searchTerms]=\"term\"\n                [requestFocus]=\"true\"\n                (onFilter)=\"onFilter($event)\" />\n            <span class=\"input-group-addon\">\n                <span tooltip=\"Para informar uma express\u00E3o regular envolva-a com '/'\" container=\"body\"><span\n                    class=\"material-icons\">&#xE887;</span></span>\n              </span>\n        </div>\n    </div>\n</div>\n", styles: [":host ::ng-deep .filter-by-sm{width:100%}\n"] }]
        }], propDecorators: { term: [{
                type: Input
            }], filter: [{
                type: Output
            }] } });

class TableComponent {
    constructor(modalService, csvService) {
        this.modalService = modalService;
        this.csvService = csvService;
        this.filteringEnabled = true;
        this.pageSizes = [100, 50, 20, 15, 10, 5];
        this.pageSize = 10;
        this.pageSizeChange = new EventEmitter();
        this.externalPaging = false;
        this.currentPageChange = new EventEmitter();
        this.emptyMessage = 'Nenhum item encontrado';
        this.headerHeight = 50;
        this.rowHeight = 'auto';
        this.columnMode = ColumnMode.force;
        this.summaryPosition = 'bottom';
        this.scrollbarH = true;
        this.selectAllRowsOnPage = true;
        this.selected = [];
        this.selectedChange = new EventEmitter();
        this.rowExpanded = new EventEmitter();
        this.sort = new EventEmitter();
        this.sortable = true;
        this.externalSorting = false;
        this.rowIdentity = (row) => row;
        this.showPagination = 'both';
        this.builtinActions = BUILTIN_ACTIONS;
        // Remover o atributo title do component para evitar que os componentes
        // internos fiquem com o title definido atributo de entrada
        this.attrTitle = undefined;
        this.offset = 0;
        this.showFilter = false;
        this.showFiltersStats = true;
        this.inputPageSize = false;
        this.currentPaginationPage = {
            number: 1,
            first: 0,
            last: this.pageSize
        };
    }
    ngOnInit() {
        this.dtrows = this.rows || [];
        if (!this.builtinActions)
            this.builtinActions = [];
        if (!this.externalPaging && this.dtrows) {
            this.changeOfset();
            this.itemsAmount = this.dtrows.length;
        }
        this.configColumns();
        if (!this.footerHeight && (this.showPagination == 'both' || this.showPagination == 'bottom' || this.footerText)) {
            this.footerHeight = 50;
        }
    }
    configColumns() {
        this.filters = new Filters(this.rows, this.filterRowFn);
        this.dtcolumns = [];
        if (this.detailTemplate) {
            this.dtcolumns.push(this.makeColToggleDetail());
        }
        if (this.selectionType == SelectionType.checkbox) {
            this.dtcolumns.push(this.makeColToSelectRow());
        }
        if (this.columns) {
            for (const _col of this.columns) {
                const col = this.makeTableColumn(_col);
                this.configSortable(col);
                this.configFilterable(col);
                this.configDefaults(col);
                this.dtcolumns.push(col);
            }
        }
        if (this.key) {
            this.dtcolumnsCopy = [...this.dtcolumns];
            this.dtcolumns = applyColumnsCustomizations(this.dtcolumns, this.key);
        }
        if (!this.hasFiltering()) {
            this.showFiltersStats = false;
        }
        this.hideColumnFilteringIfNecessary();
    }
    hideColumnFilteringIfNecessary() {
        if (!this.hasColumnFilterable()) {
            for (let action of this.builtinActions) {
                if (action.actionType == 'toggleFiltering') {
                    action.disabled = true;
                    if (action.off)
                        this.filteringEnabled = false;
                }
            }
        }
    }
    hasColumnFilterable() {
        for (const col of this.dtcolumns) {
            if (col.filterable)
                return true;
        }
        return false;
    }
    hasFiltering() {
        if (this.hasColumnFilterable())
            return true;
        for (let action of this.builtinActions) {
            if (action.actionType == 'filterRows') {
                return true;
            }
        }
        return false;
    }
    configDefaults(col) {
        col.filterable = this.booleanDefault(col.filterable);
        col.hideable = this.booleanDefault(col.hideable);
        col.exportable = this.booleanDefault(col.exportable) && col.name?.length > 0;
    }
    booleanDefault(b, def = true) {
        if (b === false || b === true)
            return b;
        return def;
    }
    makeColToSelectRow() {
        return {
            checkboxable: true,
            headerCheckboxable: this.selectAllRowsOnPage,
            sortable: false,
            resizeable: false,
            maxWidth: 30,
        };
    }
    makeColToggleDetail() {
        return {
            cellTemplate: this.expandToggleTemplate,
            headerTemplate: this.expandToggleHeaderTemplate,
            sortable: false,
            resizeable: false,
            maxWidth: 50,
            hideable: false,
            frozenLeft: true,
        };
    }
    makeTableColumn(col) {
        if (typeof col === 'string') {
            return { name: col, sortable: this.sortable };
        }
        else {
            return col;
        }
    }
    configFilterable(col) {
        if (col.filterable) {
            col.headerTemplate = this.cellFilterText;
            if (!col.filterFn) {
                col.filterFn = defFilterFn;
            }
        }
    }
    configSortable(col) {
        if (col.sortable == undefined || col.sortable == null) {
            col.sortable = this.sortable;
        }
    }
    changeOfset() {
        this.offset = this.currentPage > 0 ? this.currentPage - 1 : 0;
    }
    onPageChange(p) {
        this.onCurrentPageChange(p.number);
    }
    onCurrentPageChange(p) {
        // lidar com eventos repetidos
        if (this._pageNumberOld === p)
            return;
        this._pageNumberOld = p;
        this.currentPageChange.emit(p);
        this.currentPage = p;
        this.changeOfset();
    }
    ngOnChanges(changes) {
        if (changes.rows && !changes.rows.firstChange) {
            this.dtrows = changes.rows.currentValue;
            this.filters = new Filters(this.rows, this.filterRowFn);
        }
        let columnsChange = false;
        if (changes.columns && !changes.columns.firstChange) {
            this.dtcolumns = changes.columns.currentValue;
            columnsChange = true;
        }
        if (!this.externalPaging) {
            if (changes.rows?.currentValue) {
                this.itemsAmount = changes.rows.currentValue.length;
            }
            if (changes.currentPage) {
                this.currentPaginationPage.number = changes.currentPage.currentValue;
                this.changeOfset();
            }
        }
        if (columnsChange || (changes.selectable && !changes.selectable.firstChange)) {
            this.configColumns();
        }
    }
    onPageSizeChange(newPageSize) {
        // lidar com eventos repetidos
        if (this._pageSizeOld === newPageSize)
            return;
        this._pageSizeOld = newPageSize;
        this.pageSizeChange.emit(newPageSize);
        this.offset = 0;
    }
    toggleExpandRow(row, status) {
        this.ngxtable.rowDetail.toggleExpandRow(row);
        this.rowExpanded.emit({ expanded: !status, row });
    }
    toggleExpandAllRows(status) {
        if (status) {
            this.ngxtable.rowDetail.expandAllRows();
        }
        else {
            this.ngxtable.rowDetail.collapseAllRows();
        }
        this.rowExpanded.emit({ expanded: !status, row: null });
    }
    onSort(event) {
        this.sort.emit(event);
        this.onCurrentPageChange(1);
    }
    onSelect({ selected }) {
        this.selected.splice(0, this.selected.length);
        if (selected) {
            this.selected.push(...selected.filter((row) => this.displayCheck ? this.displayCheck(row) : true));
        }
        this.selectedChange.emit(this.selected);
    }
    onFilter(col, value, filterByRef) {
        const term = makeSearchTerm(value);
        this.dtrows = this.filters.filterColumn(col, term, filterByRef);
        this.currentPage = 0;
        this.changeOfset();
    }
    onActionClick(action) {
        if (action.disabled)
            return;
        if (action.actionType == 'customizeColumns') {
            this.showModalFilterColumns();
        }
        else if (action.actionType == 'toggleFiltering') {
            this.toggleFiltering();
        }
        else if (action.actionType == 'filterRows') {
            this.showModalFilterRows();
        }
        else if (action.actionType == 'exportCSV') {
            this.csvService.export(this.key, this.dtcolumnsCopy, this.dtrows);
        }
    }
    toggleFiltering() {
        this.filteringEnabled = !this.filteringEnabled;
        for (let action of this.builtinActions) {
            if (action.actionType == 'toggleFiltering') {
                action.off = !this.filteringEnabled;
            }
            else if (action.actionType == 'filterRows') {
                action.disabled = !this.filteringEnabled;
            }
        }
        if (this.filteringEnabled) {
            const filter = this.filters.filters[this.filters.filters.length - 1];
            if (filter) {
                this.dtrows = filter.rows;
            }
        }
        else {
            const filter = this.filters.filters[0];
            if (filter) {
                this.dtrows = filter.rows;
            }
        }
    }
    findSearchTerm(col) {
        for (let filter of this.filters.filters) {
            if (filter.key == col.name) {
                return filter.term;
            }
        }
        return '';
    }
    showModalFilterRows() {
        this.filterRowsModalRef = this.modalService.show(this.filterRowsTemplate, {
            class: 'modal-sm'
        });
    }
    onFilterRows(term) {
        if (term === false) {
            this.filterRowsModalRef.hide();
        }
        else {
            this.applyFilterRows(term);
        }
    }
    applyFilterRows(str) {
        this.filterRowTerm = str;
        const term = makeSearchTerm(str);
        this.dtrows = this.filters.filterRows(this.dtcolumns, term);
        this.currentPage = 0;
        this.changeOfset();
    }
    showModalFilterColumns() {
        this.customizeColumsModalRef = this.modalService.show(this.customizeColumnsTemplate, {
            class: 'modal-md'
        });
    }
    onCustomizeColumnsConfirm(confirmed) {
        this.customizeColumsModalRef.hide();
        if (confirmed) {
            this.dtcolumns = applyColumnsCustomizations(this.dtcolumnsCopy, this.key);
        }
    }
}
TableComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: TableComponent, deps: [{ token: i1$2.BsModalService }, { token: TableCsvService }], target: i0.ɵɵFactoryTarget.Component });
TableComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: TableComponent, selector: "suite-rfb-table", inputs: { key: "key", pageSizes: "pageSizes", pageSize: "pageSize", rows: "rows", columns: "columns", externalPaging: "externalPaging", itemsAmount: "itemsAmount", currentPage: "currentPage", title: "title", emptyMessage: "emptyMessage", headerHeight: "headerHeight", rowHeight: "rowHeight", rowDetailHeight: "rowDetailHeight", columnMode: "columnMode", detailTemplate: "detailTemplate", summaryRow: "summaryRow", summaryPosition: "summaryPosition", summaryHeight: "summaryHeight", rowClass: "rowClass", scrollbarH: "scrollbarH", scrollbarV: "scrollbarV", selectAllRowsOnPage: "selectAllRowsOnPage", selectionType: "selectionType", selected: "selected", displayCheck: "displayCheck", filterRowFn: "filterRowFn", sortable: "sortable", externalSorting: "externalSorting", rowIdentity: "rowIdentity", tableStyle: "tableStyle", trackByProp: "trackByProp", showPagination: "showPagination", builtinActions: "builtinActions", footerText: "footerText", footerHeight: "footerHeight", showFiltersStats: "showFiltersStats", inputPageSize: "inputPageSize" }, outputs: { pageSizeChange: "pageSizeChange", currentPageChange: "currentPageChange", selectedChange: "selectedChange", rowExpanded: "rowExpanded", sort: "sort" }, host: { properties: { "attr.title": "this.attrTitle" } }, queries: [{ propertyName: "tplAcoes", first: true, predicate: TableTplActionsDirective, descendants: true, read: TemplateRef }], viewQueries: [{ propertyName: "expandToggleTemplate", first: true, predicate: ["expandToggleTemplate"], descendants: true, static: true }, { propertyName: "expandToggleHeaderTemplate", first: true, predicate: ["expandToggleHeaderTemplate"], descendants: true, static: true }, { propertyName: "ngxtable", first: true, predicate: ["ngxDatatableRef"], descendants: true }, { propertyName: "cellFilterText", first: true, predicate: ["cellFilterText"], descendants: true, static: true }, { propertyName: "customizeColumnsTemplate", first: true, predicate: ["customizeColumnsTemplate"], descendants: true, static: true }, { propertyName: "filterRowsTemplate", first: true, predicate: ["filterRowsTemplate"], descendants: true, static: true }], usesOnChanges: true, ngImport: i0, template: "<div class=\"tabela-links\" *ngIf=\"showPagination == 'both' || showPagination == 'top'\">\n    <suite-rfb-pagination [(itemAmount)]=\"itemsAmount\"\n        [(pageSize)]=\"pageSize\"\n        (pageChange)=\"onPageChange($event)\"\n        (pageSizeChange)=\"onPageSizeChange($event)\"\n        [inputPageSize]=\"false\"\n        [(page)]=\"currentPaginationPage\" />\n</div>\n\n<div *ngIf=\"title\" class=\"tabela-titulo\">{{title}}</div>\n\n<div class=\"tabela-acoes\" *ngIf=\"tplAcoes || builtinActions?.length\">\n    <div>\n        <ng-container *ngIf=\"tplAcoes\" [ngTemplateOutlet]=\"tplAcoes\"></ng-container>\n    </div>\n    <div>\n        <ng-container *ngFor=\"let action of builtinActions\">\n            <a [class.disabled]=\"action.disabled\" (click)=\"onActionClick(action)\" href=\"javascript:void(0)\">{{action.actionLabel}}\n                <span [title]=\"action.actionDescription\"\n                    class=\"material-icons icon-18 builtin-action\">{{action.materialIcon}}{{action.off || action.disabled ? '_off' : ''}}</span>\n            </a>\n        </ng-container>\n    </div>\n</div>\n\n<ngx-datatable *ngIf=\"rows\"\n        #ngxDatatableRef\n        [attr.pagination]=\"showPagination\"\n        [ngStyle]=\"tableStyle\"\n        class=\"bootstrap tabela\"\n        [rows]=\"dtrows\"\n        [columns]=\"dtcolumns\"\n        [columnMode]=\"columnMode\"\n        [rowHeight]=\"rowHeight\"\n        [headerHeight]=\"headerHeight\"\n        [footerHeight]=\"footerHeight\"\n        [offset]=\"offset\"\n        [scrollbarH]=\"scrollbarH\"\n        [externalPaging]=\"externalPaging\"\n        [count]=\"itemsAmount\"\n        [selected]=\"selected\"\n        [swapColumns]=\"true\"\n        [externalSorting]=\"externalSorting\"\n        (sort)=\"onSort($event)\"\n        [limit]=\"pageSize ? pageSize : rows.length\"\n        [summaryRow]=\"summaryRow\"\n        [summaryPosition]=\"summaryPosition\"\n        [summaryHeight]=\"summaryHeight\"\n        [rowClass]=\"rowClass\"\n        [selectionType]=\"selectionType\"\n        (select)=\"onSelect($event)\"\n        [selectAllRowsOnPage]=\"selectAllRowsOnPage\"\n        [displayCheck]=\"displayCheck\"\n        [scrollbarV]=\"scrollbarV\"\n        [messages]=\"{emptyMessage}\"\n        [rowIdentity]=\"rowIdentity\"\n        [trackByProp]=\"trackByProp\"\n    >\n\n    <ngx-datatable-row-detail *ngIf=\"detailTemplate\" [rowHeight]=\"rowDetailHeight\">\n        <ng-template let-row=\"row\" ngx-datatable-row-detail-template>\n            <ng-container *ngTemplateOutlet=\"detailTemplate; context: { row }\"></ng-container>\n        </ng-template>\n    </ngx-datatable-row-detail>\n\n    <ngx-datatable-footer>\n        <ng-template ngx-datatable-footer-template>\n            <div class=\"tabela-links\">\n                <suite-rfb-pagination *ngIf=\"showPagination == 'both' || showPagination == 'bottom'\"\n                    [(itemAmount)]=\"itemsAmount\"\n                    [(pageSize)]=\"pageSize\"\n                    [inputPageSize]=\"inputPageSize\"\n                    (pageChange)=\"onPageChange($event)\"\n                    (pageSizeChange)=\"onPageSizeChange($event)\"\n                    [(page)]=\"currentPaginationPage\" />\n                <div *ngIf=\"footerText\">{{footerText}}</div>\n                <div *ngIf=\"showFiltersStats && filters.filters.length\"\n                        [class.filter-disabled]=\"!filteringEnabled\">\n                    Estat\u00EDsticas Filtros:\n                    <ng-container *ngFor=\"let filter of filters.filters; let last=last\">\n                        <span>\n                            <span title=\"Campo\">{{filter.label}}</span>\n                            <span title=\"Termo\" *ngIf=\"filter.term\">='{{filter.term}}'</span>\n                            <span title=\"Linhas filtradas\">: {{filter.rows?.length}}</span>\n                            <span *ngIf=\"!last\"> | </span>\n                        </span>\n                    </ng-container>\n                </div>\n            </div>\n        </ng-template>\n    </ngx-datatable-footer>\n</ngx-datatable>\n\n<ng-template #expandToggleTemplate let-row=\"row\" let-value=\"value\" let-expanded=\"expanded\">\n    <suite-rfb-toggle (expanded)=\"toggleExpandRow(row, $event)\" />\n</ng-template>\n\n<ng-template #expandToggleHeaderTemplate let-expanded=\"expanded\">\n    <suite-rfb-toggle (expanded)=\"toggleExpandAllRows($event)\" />\n</ng-template>\n\n<ng-template #cellFilterText let-column=\"column\" let-sort=\"sortFn\">\n    <span class=\"datatable-header-cell-wrapper\">\n        <span class=\"datatable-header-cell-label\"\n            (click)=\"sort()\">{{ column.name }}</span>\n    </span>\n    <suite-rfb-filter-by #filterByRef *ngIf=\"filteringEnabled\"\n        [searchTerms]=\"findSearchTerm(column)\"\n        (onFilter)=\"onFilter(column, $event, filterByRef)\" />\n\n</ng-template>\n\n<ng-template #customizeColumnsTemplate>\n    <suite-rfb-customize-colums [tableKey]=\"key\"\n        [columns]=\"columns\"\n        (confirm)=\"onCustomizeColumnsConfirm($event)\"/>\n</ng-template>\n\n<ng-template #filterRowsTemplate>\n    <suite-rfb-filter-rows\n        [term]=\"filterRowTerm\"\n        (filter)=\"onFilterRows($event)\"/>\n</ng-template>\n", styles: [""], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "component", type: i4.DatatableComponent, selector: "ngx-datatable", inputs: ["targetMarkerTemplate", "rows", "groupRowsBy", "groupedRows", "columns", "selected", "scrollbarV", "scrollbarH", "rowHeight", "columnMode", "headerHeight", "footerHeight", "externalPaging", "externalSorting", "limit", "count", "offset", "loadingIndicator", "selectionType", "reorderable", "swapColumns", "sortType", "sorts", "cssClasses", "messages", "rowClass", "selectCheck", "displayCheck", "groupExpansionDefault", "trackByProp", "selectAllRowsOnPage", "virtualization", "treeFromRelation", "treeToRelation", "summaryRow", "summaryHeight", "summaryPosition", "rowIdentity"], outputs: ["scroll", "activate", "select", "sort", "page", "reorder", "resize", "tableContextmenu", "treeAction"] }, { kind: "directive", type: i4.DatatableRowDetailDirective, selector: "ngx-datatable-row-detail", inputs: ["rowHeight", "template"], outputs: ["toggle"] }, { kind: "directive", type: i4.DatatableRowDetailTemplateDirective, selector: "[ngx-datatable-row-detail-template]" }, { kind: "directive", type: i4.DataTableFooterTemplateDirective, selector: "[ngx-datatable-footer-template]" }, { kind: "directive", type: i4.DatatableFooterDirective, selector: "ngx-datatable-footer", inputs: ["footerHeight", "totalMessage", "selectedMessage", "pagerLeftArrowIcon", "pagerRightArrowIcon", "pagerPreviousIcon", "pagerNextIcon", "template"] }, { kind: "component", type: PaginationComponent, selector: "suite-rfb-pagination", inputs: ["firstButtonHidden", "previousButtonHidden", "nextButtonHidden", "lastButtonHidden", "disabled", "firstButtonLabel", "previousButtonLabel", "nextButtonLabel", "lastButtonLabel", "itemAmount", "maxVisiblePageAmount", "page", "pageSize", "pageSizes", "server", "setPageSizeHidden", "setPageHidden", "inputPageSize"], outputs: ["itemAmountChange", "pageSizeChange", "pageChange"] }, { kind: "component", type: FilterByComponent, selector: "suite-rfb-filter-by", inputs: ["debounceTime", "disabled", "inputClass", "inputMask", "requestFocus", "searchTerms"], outputs: ["onFilter"] }, { kind: "component", type: ToggleComponent, selector: "suite-rfb-toggle", inputs: ["show", "title"], outputs: ["expanded"] }, { kind: "component", type: CustomizeColumsModalComponent, selector: "suite-rfb-customize-colums", inputs: ["tableKey", "columns"], outputs: ["confirm"] }, { kind: "component", type: FilterRowsModalComponent, selector: "suite-rfb-filter-rows", inputs: ["term"], outputs: ["filter"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: TableComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-table', template: "<div class=\"tabela-links\" *ngIf=\"showPagination == 'both' || showPagination == 'top'\">\n    <suite-rfb-pagination [(itemAmount)]=\"itemsAmount\"\n        [(pageSize)]=\"pageSize\"\n        (pageChange)=\"onPageChange($event)\"\n        (pageSizeChange)=\"onPageSizeChange($event)\"\n        [inputPageSize]=\"false\"\n        [(page)]=\"currentPaginationPage\" />\n</div>\n\n<div *ngIf=\"title\" class=\"tabela-titulo\">{{title}}</div>\n\n<div class=\"tabela-acoes\" *ngIf=\"tplAcoes || builtinActions?.length\">\n    <div>\n        <ng-container *ngIf=\"tplAcoes\" [ngTemplateOutlet]=\"tplAcoes\"></ng-container>\n    </div>\n    <div>\n        <ng-container *ngFor=\"let action of builtinActions\">\n            <a [class.disabled]=\"action.disabled\" (click)=\"onActionClick(action)\" href=\"javascript:void(0)\">{{action.actionLabel}}\n                <span [title]=\"action.actionDescription\"\n                    class=\"material-icons icon-18 builtin-action\">{{action.materialIcon}}{{action.off || action.disabled ? '_off' : ''}}</span>\n            </a>\n        </ng-container>\n    </div>\n</div>\n\n<ngx-datatable *ngIf=\"rows\"\n        #ngxDatatableRef\n        [attr.pagination]=\"showPagination\"\n        [ngStyle]=\"tableStyle\"\n        class=\"bootstrap tabela\"\n        [rows]=\"dtrows\"\n        [columns]=\"dtcolumns\"\n        [columnMode]=\"columnMode\"\n        [rowHeight]=\"rowHeight\"\n        [headerHeight]=\"headerHeight\"\n        [footerHeight]=\"footerHeight\"\n        [offset]=\"offset\"\n        [scrollbarH]=\"scrollbarH\"\n        [externalPaging]=\"externalPaging\"\n        [count]=\"itemsAmount\"\n        [selected]=\"selected\"\n        [swapColumns]=\"true\"\n        [externalSorting]=\"externalSorting\"\n        (sort)=\"onSort($event)\"\n        [limit]=\"pageSize ? pageSize : rows.length\"\n        [summaryRow]=\"summaryRow\"\n        [summaryPosition]=\"summaryPosition\"\n        [summaryHeight]=\"summaryHeight\"\n        [rowClass]=\"rowClass\"\n        [selectionType]=\"selectionType\"\n        (select)=\"onSelect($event)\"\n        [selectAllRowsOnPage]=\"selectAllRowsOnPage\"\n        [displayCheck]=\"displayCheck\"\n        [scrollbarV]=\"scrollbarV\"\n        [messages]=\"{emptyMessage}\"\n        [rowIdentity]=\"rowIdentity\"\n        [trackByProp]=\"trackByProp\"\n    >\n\n    <ngx-datatable-row-detail *ngIf=\"detailTemplate\" [rowHeight]=\"rowDetailHeight\">\n        <ng-template let-row=\"row\" ngx-datatable-row-detail-template>\n            <ng-container *ngTemplateOutlet=\"detailTemplate; context: { row }\"></ng-container>\n        </ng-template>\n    </ngx-datatable-row-detail>\n\n    <ngx-datatable-footer>\n        <ng-template ngx-datatable-footer-template>\n            <div class=\"tabela-links\">\n                <suite-rfb-pagination *ngIf=\"showPagination == 'both' || showPagination == 'bottom'\"\n                    [(itemAmount)]=\"itemsAmount\"\n                    [(pageSize)]=\"pageSize\"\n                    [inputPageSize]=\"inputPageSize\"\n                    (pageChange)=\"onPageChange($event)\"\n                    (pageSizeChange)=\"onPageSizeChange($event)\"\n                    [(page)]=\"currentPaginationPage\" />\n                <div *ngIf=\"footerText\">{{footerText}}</div>\n                <div *ngIf=\"showFiltersStats && filters.filters.length\"\n                        [class.filter-disabled]=\"!filteringEnabled\">\n                    Estat\u00EDsticas Filtros:\n                    <ng-container *ngFor=\"let filter of filters.filters; let last=last\">\n                        <span>\n                            <span title=\"Campo\">{{filter.label}}</span>\n                            <span title=\"Termo\" *ngIf=\"filter.term\">='{{filter.term}}'</span>\n                            <span title=\"Linhas filtradas\">: {{filter.rows?.length}}</span>\n                            <span *ngIf=\"!last\"> | </span>\n                        </span>\n                    </ng-container>\n                </div>\n            </div>\n        </ng-template>\n    </ngx-datatable-footer>\n</ngx-datatable>\n\n<ng-template #expandToggleTemplate let-row=\"row\" let-value=\"value\" let-expanded=\"expanded\">\n    <suite-rfb-toggle (expanded)=\"toggleExpandRow(row, $event)\" />\n</ng-template>\n\n<ng-template #expandToggleHeaderTemplate let-expanded=\"expanded\">\n    <suite-rfb-toggle (expanded)=\"toggleExpandAllRows($event)\" />\n</ng-template>\n\n<ng-template #cellFilterText let-column=\"column\" let-sort=\"sortFn\">\n    <span class=\"datatable-header-cell-wrapper\">\n        <span class=\"datatable-header-cell-label\"\n            (click)=\"sort()\">{{ column.name }}</span>\n    </span>\n    <suite-rfb-filter-by #filterByRef *ngIf=\"filteringEnabled\"\n        [searchTerms]=\"findSearchTerm(column)\"\n        (onFilter)=\"onFilter(column, $event, filterByRef)\" />\n\n</ng-template>\n\n<ng-template #customizeColumnsTemplate>\n    <suite-rfb-customize-colums [tableKey]=\"key\"\n        [columns]=\"columns\"\n        (confirm)=\"onCustomizeColumnsConfirm($event)\"/>\n</ng-template>\n\n<ng-template #filterRowsTemplate>\n    <suite-rfb-filter-rows\n        [term]=\"filterRowTerm\"\n        (filter)=\"onFilterRows($event)\"/>\n</ng-template>\n" }]
        }], ctorParameters: function () { return [{ type: i1$2.BsModalService }, { type: TableCsvService }]; }, propDecorators: { key: [{
                type: Input
            }], pageSizes: [{
                type: Input
            }], pageSize: [{
                type: Input
            }], pageSizeChange: [{
                type: Output
            }], rows: [{
                type: Input
            }], columns: [{
                type: Input
            }], externalPaging: [{
                type: Input
            }], itemsAmount: [{
                type: Input
            }], currentPage: [{
                type: Input
            }], currentPageChange: [{
                type: Output
            }], title: [{
                type: Input
            }], emptyMessage: [{
                type: Input
            }], headerHeight: [{
                type: Input
            }], rowHeight: [{
                type: Input
            }], rowDetailHeight: [{
                type: Input
            }], columnMode: [{
                type: Input
            }], detailTemplate: [{
                type: Input
            }], summaryRow: [{
                type: Input
            }], summaryPosition: [{
                type: Input
            }], summaryHeight: [{
                type: Input
            }], rowClass: [{
                type: Input
            }], scrollbarH: [{
                type: Input
            }], scrollbarV: [{
                type: Input
            }], selectAllRowsOnPage: [{
                type: Input
            }], selectionType: [{
                type: Input
            }], selected: [{
                type: Input
            }], selectedChange: [{
                type: Output
            }], displayCheck: [{
                type: Input
            }], expandToggleTemplate: [{
                type: ViewChild,
                args: ['expandToggleTemplate', { static: true }]
            }], expandToggleHeaderTemplate: [{
                type: ViewChild,
                args: ['expandToggleHeaderTemplate', { static: true }]
            }], ngxtable: [{
                type: ViewChild,
                args: ['ngxDatatableRef', { static: false }]
            }], cellFilterText: [{
                type: ViewChild,
                args: ['cellFilterText', { static: true }]
            }], tplAcoes: [{
                type: ContentChild,
                args: [TableTplActionsDirective, { read: TemplateRef }]
            }], customizeColumnsTemplate: [{
                type: ViewChild,
                args: ['customizeColumnsTemplate', { static: true }]
            }], filterRowsTemplate: [{
                type: ViewChild,
                args: ['filterRowsTemplate', { static: true }]
            }], filterRowFn: [{
                type: Input
            }], rowExpanded: [{
                type: Output
            }], sort: [{
                type: Output
            }], sortable: [{
                type: Input
            }], externalSorting: [{
                type: Input
            }], rowIdentity: [{
                type: Input
            }], tableStyle: [{
                type: Input
            }], trackByProp: [{
                type: Input
            }], showPagination: [{
                type: Input
            }], builtinActions: [{
                type: Input
            }], footerText: [{
                type: Input
            }], footerHeight: [{
                type: Input
            }], attrTitle: [{
                type: HostBinding,
                args: ['attr.title']
            }], showFiltersStats: [{
                type: Input
            }], inputPageSize: [{
                type: Input
            }] } });

class TableDirective {
    constructor(renderer, elementRef) {
        this.renderer = renderer;
        this.elementRef = elementRef;
        this.tableClass = 'table table-striped tabela';
    }
    ngOnInit() {
        this.tableClass.split(' ').forEach(className => this.renderer.addClass(this.elementRef.nativeElement, className));
    }
}
TableDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: TableDirective, deps: [{ token: i0.Renderer2 }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
TableDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.1.2", type: TableDirective, selector: "table[suiteRfbTable]", inputs: { tableClass: "tableClass" }, providers: [TableService], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: TableDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'table[suiteRfbTable]',
                    providers: [TableService]
                }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: i0.ElementRef }]; }, propDecorators: { tableClass: [{
                type: Input
            }] } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbToggleModule {
}
SuiteRfbToggleModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbToggleModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbToggleModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbToggleModule, declarations: [ToggleComponent], imports: [CommonModule], exports: [ToggleComponent] });
SuiteRfbToggleModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbToggleModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbToggleModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                    ],
                    declarations: [
                        ToggleComponent,
                    ],
                    exports: [
                        ToggleComponent,
                    ],
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbTableModule {
}
SuiteRfbTableModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbTableModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbTableModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbTableModule, declarations: [TableComponent,
        TableTplActionsDirective,
        TableSelectComponent,
        TableSelectHeaderComponent,
        TableDirective,
        CustomizeColumsModalComponent,
        FilterRowsModalComponent], imports: [CommonModule,
        FormsModule,
        NgxDatatableModule,
        NgSelectModule,
        SuiteRfbPaginationModule,
        SuiteRfbFilterByModule,
        SuiteRfbSortByModule,
        SuiteRfbToggleModule,
        TooltipModule], exports: [TableComponent,
        TableTplActionsDirective,
        TableSelectComponent,
        TableSelectHeaderComponent,
        TableDirective] });
SuiteRfbTableModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbTableModule, imports: [CommonModule,
        FormsModule,
        NgxDatatableModule,
        NgSelectModule,
        SuiteRfbPaginationModule,
        SuiteRfbFilterByModule,
        SuiteRfbSortByModule,
        SuiteRfbToggleModule,
        TooltipModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbTableModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        NgxDatatableModule,
                        NgSelectModule,
                        SuiteRfbPaginationModule,
                        SuiteRfbFilterByModule,
                        SuiteRfbSortByModule,
                        SuiteRfbToggleModule,
                        TooltipModule,
                    ],
                    declarations: [
                        TableComponent,
                        TableTplActionsDirective,
                        TableSelectComponent,
                        TableSelectHeaderComponent,
                        TableDirective,
                        CustomizeColumsModalComponent,
                        FilterRowsModalComponent
                    ],
                    exports: [
                        TableComponent,
                        TableTplActionsDirective,
                        TableSelectComponent,
                        TableSelectHeaderComponent,
                        TableDirective,
                    ]
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

class ImgComponent {
    constructor(http) {
        this.http = http;
        this.blob = true;
        this.alt = '';
        this.class = '';
        this.imagemReady = false;
    }
    ngOnInit() {
        if (this.blob) {
            this.getImagem().subscribe(data => this.createImageFromBlob(data));
        }
        else {
            this.imagem = this.src;
            this.imagemReady = true;
        }
    }
    getImagem() {
        return this.http.get(this.src, { responseType: 'blob' });
    }
    createImageFromBlob(image) {
        const reader = new FileReader();
        reader.addEventListener('load', () => {
            this.imagem = reader.result;
            this.imagemReady = true;
        }, false);
        if (image) {
            reader.readAsDataURL(image);
        }
    }
}
ImgComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: ImgComponent, deps: [{ token: i1$3.HttpClient }], target: i0.ɵɵFactoryTarget.Component });
ImgComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: ImgComponent, selector: "suite-rfb-img", inputs: { src: "src", blob: "blob", alt: "alt", class: "class" }, ngImport: i0, template: "<img *ngIf=\"imagemReady\" [src]=\"imagem\" [alt]=\"alt\" [class]=\"class\"/>", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: ImgComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-img', template: "<img *ngIf=\"imagemReady\" [src]=\"imagem\" [alt]=\"alt\" [class]=\"class\"/>" }]
        }], ctorParameters: function () { return [{ type: i1$3.HttpClient }]; }, propDecorators: { src: [{
                type: Input
            }], blob: [{
                type: Input
            }], alt: [{
                type: Input
            }], class: [{
                type: Input
            }] } });

class SuiteRfbImgModule {
}
SuiteRfbImgModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbImgModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbImgModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbImgModule, declarations: [ImgComponent], imports: [CommonModule], exports: [ImgComponent] });
SuiteRfbImgModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbImgModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbImgModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [
                        ImgComponent,
                    ],
                    exports: [
                        ImgComponent,
                    ],
                }]
        }] });

class RadioComponent {
    set required(value) {
        this._required = !(value === false || value === 'false');
    }
    get required() {
        return this._required;
    }
    get disabled() {
        return this._disabled;
    }
    constructor(injector) {
        this.injector = injector;
        this.label = '';
        this.inline = true;
        this.clearable = false;
    }
    ngOnInit() {
        this.ngControl = this.injector.get(NgControl);
    }
    writeValue(value) {
        this.value = value;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this._disabled = isDisabled;
    }
    onClick(e, value) {
        if (this.clearable && value == this.value) {
            this.setValue(null);
            e.preventDefault();
        }
    }
    onChangeValue(e, value) {
        if (e.defaultPrevented)
            return;
        this.setValue(value);
    }
    setValue(value) {
        this.value = value;
        this.onChange(value);
        this.onTouched();
    }
}
RadioComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: RadioComponent, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.Component });
RadioComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.1.2", type: RadioComponent, selector: "suite-rfb-radio", inputs: { label: "label", required: "required", items: "items", labels: "labels", inline: "inline", clearable: "clearable" }, providers: [{
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => RadioComponent)
        }], ngImport: i0, template: "<div class=\"form-group\" [suiteRfbHasError]=\"ngControl.control\">\n  <label *ngIf=\"label\" class=\"control-label\" [class.obrigatorio]=\"required\">{{label}}</label>\n  <div class=\"radio-items\">\n    <div *ngFor=\"let item of items; let index=index\" [class.radio-inline]=\"inline\" class=\"radio\">\n      <label>\n        <input type=\"radio\" name=\"name\" [value]=\"item\"\n          [checked]=\"value == item\"\n          [required]=\"required\"\n          [disabled]=\"disabled\"\n          (click)=\"onClick($event, item)\"\n          (change)=\"onChangeValue($event, item)\">{{labels && labels[index] ? labels[index] : item}}\n        </label>\n    </div>\n  </div>\n  <suite-rfb-local-messages [control]=\"ngControl.control\"></suite-rfb-local-messages>\n</div>\n", styles: [".radio-inline{display:inline-block;padding-left:0}.radio,.radio-inline{margin-top:0}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: LocalMessagesComponent, selector: "suite-rfb-local-messages", inputs: ["requiredMessage", "patternMessage", "minlengthMessage", "maxlengthMessage", "emailMessage", "cnpjMessage", "cpfMessage", "dateMessage", "dayMessage", "monthMessage", "yearMessage", "minDateMessage", "maxDateMessage", "fileSizeMessage", "fileTypeMessage", "decendioMessage", "control"] }, { kind: "directive", type: HasErrorDirective, selector: "[suiteRfbHasError]", inputs: ["suiteRfbHasError"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: RadioComponent, decorators: [{
            type: Component,
            args: [{ selector: 'suite-rfb-radio', providers: [{
                            provide: NG_VALUE_ACCESSOR,
                            multi: true,
                            useExisting: forwardRef(() => RadioComponent)
                        }], template: "<div class=\"form-group\" [suiteRfbHasError]=\"ngControl.control\">\n  <label *ngIf=\"label\" class=\"control-label\" [class.obrigatorio]=\"required\">{{label}}</label>\n  <div class=\"radio-items\">\n    <div *ngFor=\"let item of items; let index=index\" [class.radio-inline]=\"inline\" class=\"radio\">\n      <label>\n        <input type=\"radio\" name=\"name\" [value]=\"item\"\n          [checked]=\"value == item\"\n          [required]=\"required\"\n          [disabled]=\"disabled\"\n          (click)=\"onClick($event, item)\"\n          (change)=\"onChangeValue($event, item)\">{{labels && labels[index] ? labels[index] : item}}\n        </label>\n    </div>\n  </div>\n  <suite-rfb-local-messages [control]=\"ngControl.control\"></suite-rfb-local-messages>\n</div>\n", styles: [".radio-inline{display:inline-block;padding-left:0}.radio,.radio-inline{margin-top:0}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.Injector }]; }, propDecorators: { label: [{
                type: Input
            }], required: [{
                type: Input
            }], items: [{
                type: Input
            }], labels: [{
                type: Input
            }], inline: [{
                type: Input
            }], clearable: [{
                type: Input
            }] } });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */
class SuiteRfbRadioModule {
}
SuiteRfbRadioModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbRadioModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SuiteRfbRadioModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbRadioModule, declarations: [RadioComponent], imports: [CommonModule,
        FormsModule,
        SuiteRfbInputValidationModule], exports: [RadioComponent] });
SuiteRfbRadioModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbRadioModule, imports: [CommonModule,
        FormsModule,
        SuiteRfbInputValidationModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.2", ngImport: i0, type: SuiteRfbRadioModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        SuiteRfbInputValidationModule,
                    ],
                    declarations: [
                        RadioComponent,
                    ],
                    exports: [
                        RadioComponent,
                    ],
                }]
        }] });

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/*
 * ngx-suite-rfb
 *
 * A biblioteca ngx-suite-rfb implementa um conjunto de componentes,
 * diretivas, pipes e serviços nativos do Angular com o objetivo de
 * promover o reuso e facilitar a implementação de aplicações frontend
 * que serão implantandas na Suíte RFB e, portanto, devem seguir os
 * Padrões de Interface Visual RFB.
 *
 * Documentação disponível em: https://dedat.gitpages.serpro/deat3/ngx-suite-rfb/
 *
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AccessibilityComponent, AlertMessageService, AlertMessagesComponent, AllowedFeaturePipe, ApplicationsMenuComponent, ApplicationsMenuService, BA_CUSTOMIZE_COLUMNS, BA_EXPORT_CSV, BA_ROW_FILTER, BA_TOGGLE_FILTERING, BUILTIN_ACTIONS, BrandComponent, BreadcrumbsComponent, BreadcrumbsService, CnpjValidatorDirective, CollapseComponent, ColumnCustomization, ConfirmationButtonComponent, ConfirmationComponent, ConfirmationLinkComponent, ContactService, ContextualHelpComponent, ContextualHelpService, CounterService, CpfValidatorDirective, CurrencyComponent, CurrencyMaskDirective, CurrentFeatureComponent, CurrentFeatureService, CurrentPagePipe, DECENDIO_NUMBER_REGEX, DECENDIO_STRING_REGEX, DEFAULT_ERRORS, DatePickerComponent, DateValidatorDirective, DecendioPickerComponent, DecendioPickerConfig, DecendioPipe, DecendioService, DisabledDaysValidatorDirective, DisabledMonthsValidatorDirective, DisabledYearsValidatorDirective, EnvironmentService, ExpandedMenuComponent, FEATURES, FavoriteApplicationsPipe, FavoriteFeaturesComponent, FavoriteFeaturesService, FeatureFilterPipe, FeatureService, FilePickerComponent, FileSizePipe, FileTypeValidatorDirective, FilterByComponent, FooterComponent, FromNowPipe, HasAllowedLinkedFeaturesPipe, HasErrorDirective, HasLinkedFeaturesPipe, HasMenuFeaturesPipe, HeaderComponent, ImgComponent, InputMaskDirective, JoinPipe, LinkedFeaturesComponent, LinkedFeaturesPipe, LoadingComponent, LoadingHttpInterceptor, LoadingService, LocalMessagesComponent, LocationService, LogoutComponent, LogoutService, MaxDateValidatorDirective, MaxFileSizeValidatorDirective, MenuComponent, MenuFeatureService, MenuFeaturesPipe, MenuItemComponent, MenuTogglerComponent, MinDateValidatorDirective, MonthPickerComponent, MonthPickerConfig, NavbarComponent, NotificationService, NotificationsComponent, NumbersOnlyDirective, OutputMaskPipe, PaginationComponent, PaginationService, PopoverButtonComponent, PrintService, RadioComponent, SUITE_RFB_MODULE_CONFIG, SelectComponent, ShortcutService, SidebarComponent, SortByComponent, StringFormatPipe, StripHtmlPipe, SuiteRfbAlertMessagesModule, SuiteRfbApplicationsMenuModule, SuiteRfbBreadcrumbsModule, SuiteRfbCollapseModule, SuiteRfbCommonModule, SuiteRfbConfirmationsModule, SuiteRfbContextualHelpModule, SuiteRfbCurrencyModule, SuiteRfbDatePickerModule, SuiteRfbDecendioPickerModule, SuiteRfbEnvironmentModule, SuiteRfbExpandedMenuModule, SuiteRfbFeaturesModule, SuiteRfbFilePickerModule, SuiteRfbFilterByModule, SuiteRfbFooterModule, SuiteRfbHeaderModule, SuiteRfbImgModule, SuiteRfbInputMaskModule, SuiteRfbInputValidationModule, SuiteRfbLoadingModule, SuiteRfbLogoutModule, SuiteRfbLogoutService, SuiteRfbMenuModule, SuiteRfbMonthPickerModule, SuiteRfbNavbarModule, SuiteRfbNotificationsModule, SuiteRfbOutputMaskModule, SuiteRfbPaginationModule, SuiteRfbPopoverModule, SuiteRfbPrintModule, SuiteRfbRadioModule, SuiteRfbSelectModule, SuiteRfbSidebarModule, SuiteRfbSortByModule, SuiteRfbTableModule, SuiteRfbTabsModule, SuiteRfbTipsModule, SuiteRfbToggleModule, SuiteRfbUserConfigurationsModule, SuiteRfbUserModule, SuiteRfbYearPickerModule, TabComponent, TabSetComponent, TableComponent, TableCsvService, TableDirective, TableSelectComponent, TableSelectHeaderComponent, TableTplActionsDirective, TipsComponent, ToggleComponent, UserConfigurationsComponent, UserConfigurationsService, UserInfoComponent, UserProfilesPipe, UserRolesPipe, UserService, WindowService, YearPickerComponent, YearPickerConfig, cnpjValidator, cpfValidator, dateValidator, disabledDaysValidator, disabledMonthsValidator, disabledYearsValidator, fileSizeValidator, fileTypeValidator, isFileTypeValid, maxDateValidator, minDateValidator };
